import {
  METERING_VARIANT_FIELD_LABELS,
  UNIT_USAGE_UNIT_LABELS,
  VIDEO_INPUT_VARIANT_LABELS,
} from '@nadoutong/contracts';
import type { zhCN } from './zh-CN.ts';

/**
 * English dictionary — parity-guaranteed mirror of the canonical zh-CN
 * dictionary. The `Record<keyof typeof zhCN, string>` annotation forces
 * every key present in `zh-CN.ts` to exist here; adding a key to the
 * canonical side without adding it here fails `pnpm check`.
 *
 * Conventions mirror `zh-CN.ts`:
 * - Placeholder syntax is `{name}`, substituted by `format.ts`.
 * - Trailing whitespace is never meaningful; punctuation follows normal
 *   English spacing rules (one space after periods, none before colons).
 * - Brand names + vendor / protocol / SQL identifiers stay in the JSX
 *   where they are referenced — they are not translatable content.
 */
export const en: Record<keyof typeof zhCN, string> = {
  // ── <head> metadata ─────────────────────────────────────────────
  'meta.title': 'Nadoutong Ops Console',
  'meta.description':
    'Operate the Nadoutong router: upstreams, model pricing, gift cards, accounts and audit, revenue and payment reconciliation.',

  // ── Common / shared ─────────────────────────────────────────────
  'common.retry': 'Retry',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.delete': 'Delete',
  'common.deleting': 'Deleting…',
  'common.loading': 'Loading…',
  'common.network_error': 'Network error',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.pagination.first': 'First page',
  'common.pagination.last': 'Last page',
  'common.pagination.skip_prev': 'Back 5 pages',
  'common.pagination.skip_next': 'Forward 5 pages',
  'common.pagination.jump': 'Go to page',
  'common.pagination.jump_submit': 'Go',
  'common.pagination.page': 'Page {page}',
  'common.never': 'Never',
  'common.copied': 'Copied to clipboard',
  'common.click_to_copy': 'Click to copy',
  'common.copy_anywhere': 'Click anywhere on this block to copy',
  'common.refresh': 'Refresh',
  'common.yes': 'Yes',
  'common.no': 'No',

  'i18n.switcher.aria_label': 'Language',

  // ── Auth guard ──────────────────────────────────────────────────
  'auth.checking': 'Checking session…',

  // ── Login ───────────────────────────────────────────────────────
  'login.brand': 'Ops Console',
  'login.subtitle': 'Operator Sign-In',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign In',
  'login.submitting': 'Signing In…',
  'login.github': 'Continue with GitHub',
  'login.feishu': 'Continue with Feishu',
  'login.error.empty_fields': 'Email and password are required',
  'login.github_error.not_linked': 'This GitHub account is not linked to an ops user.',
  'login.github_error.disabled': 'This ops user is disabled.',
  'login.github_error.state': 'GitHub sign-in expired. Please try again.',
  'login.github_error.generic': 'GitHub sign-in failed. Please try again or use password login.',
  'login.feishu_error.not_linked': 'This Feishu account is not linked to an ops user.',
  'login.feishu_error.disabled': 'This ops user is disabled.',
  'login.feishu_error.state': 'Feishu sign-in expired. Please try again.',
  'login.feishu_error.tenant': 'This Feishu account belongs to a tenant that is not allowed here.',
  'login.feishu_error.generic': 'Feishu sign-in failed. Please try again or use password login.',

  // ── Shell chrome ────────────────────────────────────────────────
  'shell.brand.title': 'Ops Console',
  'shell.brand.subtitle': 'Nadoutong · Station α-7',
  'shell.nav.aria_label': 'Primary',
  'shell.group.business': 'Business cockpit',
  'shell.group.customers': 'Customer management',
  'shell.group.finance': 'Finance',
  'shell.group.system': 'System',
  'shell.group.supply': 'Upstream supply',
  'shell.group.marketplace': 'Model marketplace',
  'shell.group.governance': 'Governance',
  'shell.nav.business_overview': 'Cockpit',
  'shell.nav.customer_manager_workspace': 'Customer Manager Workspace',
  'shell.nav.customer_manager_request_logs': 'Portfolio request logs',
  'shell.nav.customer_pool': 'Customer Pool',
  'shell.nav.crm_usage': 'Customer usage',
  'shell.nav.lead_pricing': 'Lead pricing',
  'shell.nav.crm_commission': 'Commission',
  'shell.nav.crm_leaderboard': 'Leaderboard',
  'shell.nav.invite_codes': 'Invite codes',
  'shell.nav.approval_queue': 'Approval queue',
  'shell.nav.approvals_mine': 'My approvals',
  'shell.nav.business_metrics': 'Metrics hub',
  'shell.nav.traffic_monitor': 'Traffic monitor',
  'shell.nav.business_risk': 'Risk',
  'shell.nav.request_logs': 'Request logs',
  'shell.nav.support_requests': 'Tenant request lookup',
  'shell.nav.support_tickets': 'Support tickets',
  'shell.nav.announcements': 'Tenant announcements',
  'shell.nav.upstreams': 'Upstreams',
  'shell.nav.tenant_routing': 'Tenant routing',
  'shell.nav.guaranteed_service': 'Guaranteed service',
  'shell.nav.quotas': 'Quota Center',
  'shell.nav.service_levels': 'Service levels',
  'shell.nav.egress_profiles': 'Ingress / Egress',
  'shell.nav.egress_lines': 'Edge gateways',
  'shell.nav.firewall_rulesets': 'Firewall rulesets',
  'shell.nav.models': 'Models',
  'shell.nav.gift_cards': 'Gift Cards',
  'shell.nav.billing_statements': 'Billing statements',
  'shell.nav.credit_settings': 'Credit defaults',
  'shell.nav.credit_overdue': 'Credit monitor',
  'shell.nav.credit_grants': 'Credit grant history',
  'shell.nav.accounts': 'Accounts',
  'shell.nav.customer_ownership': 'Ownership',
  'shell.nav.billing_anomalies': 'Billing Anomalies',
  'shell.nav.balance_alerts': 'Balance alerts',
  'shell.nav.codex_capacity_alerts': 'Codex capacity alerts',
  'shell.nav.email_notices': 'Email notices',
  'shell.nav.receipts': 'Receipts',
  'shell.nav.commission_settlement': 'Commission settlement',
  'shell.nav.payments_orders': 'Payment Orders',
  'shell.nav.payment_providers': 'Payment Providers',
  'shell.nav.fx_policy': 'FX policy',
  'shell.nav.namespaces': 'Namespaces',
  'shell.nav.model_market': 'Model Market',
  'shell.nav.model_plans': 'Model plans',
  'shell.nav.sku_activation_readiness': 'SKU readiness',
  'shell.nav.default_key_namespaces': 'Default key namespaces',
  'shell.nav.reports': 'Reports',
  'shell.nav.webhook_events': 'Webhook events',
  'shell.nav.vps': 'VPS nodes',
  'shell.nav.cron_runs': 'Cron runs',
  'shell.nav.router_client_identities': 'Client identities',
  'shell.nav.ops_users': 'Ops users',
  'shell.nav.dr_check': 'DR drill',
  'shell.nav.health_perspective': 'Health perspective',
  'shell.nav.audit': 'Audit',
  'shell.nav.permissions': 'Permissions',
  'shell.nav.error_policy': 'Error policy',

  'permissions.kicker': 'Governance',
  'permissions.title': 'Permissions',
  'permissions.subtitle':
    'Read-only role matrix for ops-console navigation and ops-api endpoint access.',
  'permissions.action.ops_users': 'Ops users',
  'permissions.generated': 'Generated',
  'permissions.generated.pending': 'after load',
  'permissions.loading': 'Loading permissions...',
  'permissions.error': 'Failed to load permissions.',
  'permissions.summary.roles': 'Roles',
  'permissions.summary.ui_routes': 'UI routes',
  'permissions.summary.api_endpoints': 'API endpoints',
  'permissions.summary.admin_only_endpoints': 'Admin-only endpoints',
  'permissions.role_legend': 'Role legend',
  'permissions.role.assignable': 'Assignable',
  'permissions.role.bootstrap': 'Bootstrap',
  'permissions.role.inherits': 'Inherits:',
  'permissions.role.inherits_none': 'None',
  'permissions.filter.label': 'Filter',
  'permissions.filter.placeholder': 'Path, category, or operation',
  'permissions.mode.all': 'All rows',
  'permissions.mode.admin_only': 'Admin-only',
  'permissions.mode.shared': 'Shared',
  'permissions.table.ui_routes': 'UI routes',
  'permissions.table.api_endpoints': 'API endpoints',
  'permissions.table.rows': '{count} rows',
  'permissions.table.empty': 'No permissions match the current filter.',
  'permissions.column.method': 'Method',
  'permissions.column.path': 'Path',
  'permissions.column.category': 'Category',
  'permissions.column.operation': 'Operation',
  'permissions.column.allowed_roles': 'Allowed roles',
  'permissions.roles.all_authenticated': 'All authenticated ops roles',
  'permissions.role.short.super': 'Super',
  'permissions.role.short.admin': 'Admin',
  'permissions.role.short.support': 'Support',
  'permissions.role.short.customer': 'Customer',
  'permissions.role.short.finance': 'Finance',
  'permissions.role.short.supply': 'Supply',
  'permissions.role.short.runtime': 'Runtime',
  'permissions.role.label.super': 'Super admin',
  'permissions.role.label.admin': 'Admin',
  'permissions.role.label.support': 'Support',
  'permissions.role.label.customer': 'Customer manager',
  'permissions.role.label.lead': 'Sales lead',
  'permissions.role.label.finance': 'Finance manager',
  'permissions.role.label.supply': 'Supply-demand manager',
  'permissions.role.label.runtime': 'Runtime operator',
  'permissions.role.note.super': 'Break-glass role; not grantable in normal ops user flows.',
  'permissions.role.note.admin':
    'General operator administration role; also inherits support read paths.',
  'permissions.role.note.support': 'Support request and scoped usage investigation paths.',
  'permissions.role.note.customer':
    'Customer-manager workspace, customer pool, and portfolio request logs.',
  'permissions.role.note.finance':
    'Receipt verification surfaces (verify/credit/reject/void, epic #1031); other finance endpoints remain admin-gated.',
  'permissions.role.note.supply':
    'Reserved scoped supply role; most current supply endpoints remain admin-gated.',
  'permissions.role.note.runtime':
    'Runtime command workspace access without full admin navigation.',
  'shell.signed_in_fallback': 'signed in',
  'shell.role.fallback': 'no role',
  'shell.logout': 'Log out',
  'shell.page.console_fallback': 'Ops Console',
  'shell.sidebar.collapse': 'Collapse sidebar',
  'shell.sidebar.expand': 'Expand sidebar',
  'shell.nav.open': 'Open navigation',
  'shell.nav.close': 'Close navigation',
  'shell.cmdk.trigger': 'Search pages / accounts / requests…',
  'shell.cmdk.placeholder': 'Type a page name, account email, or req_ request id',
  'shell.cmdk.empty': 'No matches',
  'shell.cmdk.aria_label': 'Command palette',
  'shell.cmdk.footer_hint': '↑↓ select · Enter open · Esc close',
  'shell.cmdk.group.requests': 'Requests',
  'shell.cmdk.group.accounts': 'Accounts',
  'shell.cmdk.group.recent': 'Recent',
  'shell.cmdk.open_request': 'Open {id} in request logs',
  'shell.risk.clear': 'No open risks',
  'shell.risk.open': 'Risks {count}',
  'shell.risk.open_aria': 'Open the risk queue',
  'shell.approvals.pending': 'Approvals {count}',
  'shell.approvals.open_aria': 'Open the approval queue',

  // ── Approval center (epic #1030 E1-S3, #1039) ───────────────────
  'approvals.title': 'Approval center',
  'approvals.subtitle':
    'The single surface for out-of-authority sales actions: the approver queue and your own submissions.',
  'approvals.tabs.aria_label': 'Approval center views',
  'approvals.tab.queue': 'Queue',
  'approvals.tab.mine': 'My submissions',
  'approvals.kind.customer_transfer': 'Customer transfer / release',
  'approvals.kind.gift_over_quota': 'Gift over quota',
  'approvals.kind.discount_below_sales_cost': 'Discount below sales cost',
  'approvals.kind.credit_application': 'Credit application',
  'approvals.kind.temporary_credit_application': 'Temporary credit',
  'approvals.status.pending': 'Pending',
  'approvals.status.approved': 'Approved',
  'approvals.status.rejected': 'Rejected',
  'approvals.status.cancelled': 'Cancelled',
  'approvals.col.kind': 'Kind',
  'approvals.col.customer': 'Customer',
  'approvals.col.amount': 'Amount',
  'approvals.col.cycle': 'Terms',
  'approvals.col.target': 'Target',
  'approvals.col.requester': 'Requester',
  'approvals.col.age': 'Age',
  'approvals.col.summary': 'Summary',
  'approvals.col.actions': 'Actions',
  'approvals.col.status': 'Status',
  'approvals.col.submitted_at': 'Submitted',
  'approvals.col.timeline': 'Timeline',
  'approvals.action.approve': 'Approve',
  'approvals.action.reject': 'Reject',
  'approvals.decide.approve_title': 'Approve this request',
  'approvals.decide.reject_title': 'Reject this request',
  'approvals.decide.note_label': 'Note',
  'approvals.decide.note_required_hint': 'A note is required when rejecting.',
  'approvals.toast.approved': 'Request approved.',
  'approvals.toast.rejected': 'Request rejected.',
  'approvals.toast.stage_actor_conflict':
    'You already decided an earlier stage of this request. A different approver must decide this stage.',
  'approvals.queue.decided_elsewhere': 'Already decided by another approver',
  'approvals.queue.own_request': 'Your own request — another approver must decide',
  'approvals.queue.empty': 'No pending approval requests.',
  'approvals.mine.empty': 'You have not submitted any approval requests yet.',
  'approvals.mine.expand': 'Show timeline',
  'approvals.mine.collapse': 'Hide timeline',
  // Credit-application approval detail (#1957).
  'approvals.credit.detail_toggle': 'Contract',
  'approvals.credit.applied_rate': 'Locked rate',
  'approvals.credit.open_customer_360': 'Open Customer 360',
  'approvals.credit.reason': 'Reason',
  'approvals.credit.reason_unset': 'Not provided',
  'approvals.credit.registered_at': 'Customer registered',
  'approvals.credit.payment_summary': 'Payment history',
  'approvals.timeline.submitted': 'Submitted',
  'approvals.timeline.stage_cleared': 'Stage {stage} approved',
  'approvals.timeline.qualified_stage_role': 'By stage role',
  'approvals.timeline.qualified_admin_bypass': 'By admin bypass',
  'approvals.stage.chip': 'Stage {current} of {total}',
  'approvals.load_error': 'Failed to load approvals.',
  'approvals.pagination.aria_label': 'Approvals pagination',

  // ── Manual receipts (epic #1031 E2-R5, #1063) ───────────────────
  // Commission settlement (epic #1034 E5-M4, #1146)
  'commission_settlement.title': 'Commission settlement',
  'commission_settlement.subtitle':
    'Finance confirms (locks) commission statements, appends post-lock adjustments, and records the off-system payout marker.',
  'commission_settlement.load_error': 'Failed to load commission statements.',
  'commission_settlement.filter.status': 'Status',
  'commission_settlement.filter.brand': 'Brand',
  'commission_settlement.filter.period': 'Period',
  'commission_settlement.filter.period_placeholder': 'YYYY-MM',
  'commission_settlement.status.all': 'All statuses',
  'commission_settlement.status.draft': 'Draft',
  'commission_settlement.status.confirmed': 'Confirmed',
  'commission_settlement.brand.all': 'All brands',
  'commission_settlement.col.owner': 'Owner',
  'commission_settlement.col.brand': 'Brand',
  'commission_settlement.col.period': 'Period',
  'commission_settlement.col.status': 'Status',
  'commission_settlement.col.stored_total': 'Confirmed total',
  'commission_settlement.col.adjustments': 'Adjustments',
  'commission_settlement.col.displayed_total': 'Displayed total',
  'commission_settlement.col.payout': 'Payout',
  'commission_settlement.col.actions': 'Actions',
  'commission_settlement.empty': 'No commission statements match these filters.',
  'commission_settlement.action.detail': 'Detail',
  'commission_settlement.pagination.aria_label': 'Commission statements pagination',
  'commission_settlement.payout.marked': 'Paid out',
  'commission_settlement.payout.pending': 'Pending',
  'commission_settlement.drawer.title': 'Commission statement',
  'commission_settlement.drawer.owner': 'Owner',
  'commission_settlement.drawer.brand': 'Brand',
  'commission_settlement.drawer.period': 'Period',
  'commission_settlement.drawer.status': 'Status',
  'commission_settlement.drawer.stored_total': 'Confirmed total (locked)',
  'commission_settlement.drawer.adjustments_total': 'Adjustments total',
  'commission_settlement.drawer.displayed_total': 'Displayed total',
  'commission_settlement.drawer.payout_marker': 'Payout marker',
  'commission_settlement.drawer.payout_none': 'Not paid out',
  'commission_settlement.drawer.negative_note':
    'Displayed total is negative: adjustments / clawbacks exceed the confirmed total.',
  'commission_settlement.drawer.rows_title': 'Rows',
  'commission_settlement.rows.source_type': 'Type',
  'commission_settlement.rows.reference': 'Source',
  'commission_settlement.rows.net': 'Net',
  'commission_settlement.rows.commission': 'Commission',
  'commission_settlement.rows.reason': 'Reason',
  'commission_settlement.rows.empty': 'No rows.',
  'commission_settlement.source_type.receipt': 'Receipt',
  'commission_settlement.source_type.payment_order': 'Online order',
  'commission_settlement.source_type.clawback': 'Clawback',
  'commission_settlement.source_type.adjustment': 'Adjustment',
  'commission_settlement.confirm.button': 'Confirm & lock',
  'commission_settlement.confirm.title': 'Confirm and lock this statement?',
  'commission_settlement.confirm.body':
    'Once confirmed the statement locks and is never rewritten by regeneration; later corrections can only be appended as adjustment rows.',
  'commission_settlement.confirm.cta': 'Confirm & lock',
  'commission_settlement.confirm.toast': 'Statement confirmed and locked.',
  'commission_settlement.confirm.period_open':
    "This period has not ended yet, so it cannot be locked: confirming early freezes the month's commission, and later receipts, orders, and refund clawbacks would be silently dropped. Lock it once the period closes.",
  'commission_settlement.adjust.title': 'Append adjustment',
  'commission_settlement.adjust.amount_label': 'Amount (USD)',
  'commission_settlement.adjust.amount_hint': 'Signed amount: positive raises, negative lowers; nonzero.',
  'commission_settlement.adjust.reason_label': 'Reason',
  'commission_settlement.adjust.reason_placeholder': 'Explain this correction (required)',
  'commission_settlement.adjust.submit': 'Append adjustment',
  'commission_settlement.adjust.toast': 'Adjustment row appended.',
  'commission_settlement.adjust.invalid_amount': 'Enter a nonzero amount (up to 6 decimal places).',
  'commission_settlement.payout.title': 'Payout marker',
  'commission_settlement.payout.reference_label': 'Payout reference',
  'commission_settlement.payout.reference_placeholder': 'e.g. bank wire ref / memo',
  'commission_settlement.payout.submit': 'Record payout',
  'commission_settlement.payout.toast': 'Payout marker recorded.',
  'commission_settlement.payout.done_note': 'Payout marker recorded (set once, immutable).',
  'commission_settlement.payout.confirm_title': 'Record the payout marker?',
  'commission_settlement.payout.confirm_body':
    'The payout marker is set once — it cannot be edited or undone through the UI. Confirm the reference is correct before recording.',
  'commission_settlement.payout.confirm_cta': 'Record payout',
  'receipts.title': 'Receipts',
  'receipts.subtitle.finance':
    'Off-system collection desk: verification queue, all receipts, and evidence attachments.',
  'receipts.subtitle.sales':
    'My submitted off-system receipts and their verification status (scoped to my book).',
  'receipts.load_error': 'Failed to load receipts.',
  'receipts.pagination.aria_label': 'Receipts pagination',
  'receipts.queue.title': 'Verification queue',
  'receipts.queue.empty': 'No receipts waiting for verification.',
  'receipts.queue.decided_elsewhere': 'Decided by another finance operator',
  'receipts.col.customer': 'Customer',
  'receipts.col.amount': 'Amount',
  'receipts.col.channel': 'Channel',
  'receipts.col.fee': 'Fee',
  'receipts.col.received_at': 'Received at',
  'receipts.col.entered_by': 'Entered by',
  'receipts.col.status': 'Status',
  'receipts.col.submitted_at': 'Submitted at',
  'receipts.col.actions': 'Actions',
  'receipts.action.credit': 'Verify & credit',
  'receipts.action.reject': 'Reject',
  'receipts.action.void': 'Void',
  'receipts.action.detail': 'Detail',
  'receipts.decide.credit_title': 'Verify and credit this receipt',
  'receipts.decide.reject_title': 'Reject this receipt',
  'receipts.decide.void_title': 'Void this receipt',
  'receipts.decide.credit_hint':
    'Credited always equals verified: CNY converts at the policy rate, USD credits 1:1 — this action submits no amounts.',
  'receipts.decide.note_label': 'Note',
  'receipts.decide.note_required_hint': 'A note is required when rejecting or voiding.',
  'receipts.decide.error.not_implemented':
    'The crediting path is not enabled in this environment yet — try again later.',
  'receipts.toast.credited': 'Receipt verified and credited.',
  'receipts.toast.rejected': 'Receipt rejected.',
  'receipts.toast.voided': 'Receipt voided.',
  'receipts.list.title': 'All receipts',
  'receipts.list.empty': 'No receipts match the current filters.',
  'receipts.filter.status': 'Status filter',
  'receipts.filter.status_all': 'All statuses',
  'receipts.filter.brand': 'Brand filter',
  'receipts.filter.brand_all': 'All brands',
  'receipts.filter.currency': 'Currency filter',
  'receipts.filter.currency_all': 'All currencies',
  'receipts.filter.channel': 'Channel filter',
  'receipts.filter.channel_all': 'All channels',
  'receipts.status.submitted': 'Submitted',
  'receipts.status.credited': 'Credited',
  'receipts.status.rejected': 'Rejected',
  'receipts.status.voided': 'Voided',
  'receipts.channel.bank': 'Bank transfer',
  'receipts.channel.alipay': 'Alipay',
  'receipts.channel.wechat': 'WeChat',
  'receipts.channel.usdt': 'USDT',
  'receipts.channel.other': 'Other',
  'receipts.customer_section.title': 'Receipts',
  'receipts.customer_section.empty': 'No receipts for this customer yet.',
  'receipts.entry.open': 'Record receipt',
  'receipts.detail.title': 'Receipt detail',
  'receipts.detail.error': 'Failed to load the receipt detail.',
  'receipts.detail.amount': 'Amount',
  'receipts.detail.fee': 'Actual fee',
  'receipts.detail.channel': 'Channel',
  'receipts.detail.received_at': 'Received at',
  'receipts.detail.contract_ref': 'Contract ref',
  'receipts.detail.org': 'Customer org',
  'receipts.detail.entered_by': 'Entered by',
  'receipts.detail.submitted_at': 'Submitted at',
  'receipts.detail.fx.title': 'Credit snapshot',
  'receipts.detail.fx.applied_rate': 'Applied rate',
  'receipts.detail.fx.credited_usd': 'Credited (USD)',
  'receipts.detail.fx.cny_gross': 'CNY gross',
  'receipts.detail.fx.cny_net': 'CNY net',
  'receipts.detail.fx.verified_by': 'Verified by',
  'receipts.detail.fx.owner_at_credit': 'Owner at credit',
  'receipts.detail.fx.credited_at': 'Credited at',
  'receipts.detail.fx.balance_tx': 'Balance tx',
  'receipts.detail.timeline.title': 'Status timeline',
  'receipts.detail.timeline.submitted': 'Submitted',
  'receipts.detail.attachments.title': 'Evidence attachments',
  'receipts.detail.attachments.upload': 'Upload evidence',
  'receipts.detail.attachments.download': 'Download',
  'receipts.detail.attachments.empty': 'No evidence attachments yet.',
  'receipts.detail.attachments.cap_hint': 'Up to {mb} MB per file.',
  'receipts.detail.attachments.toast.uploaded': 'Evidence uploaded.',
  'receipts.submit.title': 'Record an off-system receipt',
  'receipts.submit.description':
    'Enter the money actually received: amount, channel, and evidence. Brand and account are pinned server-side from the customer.',
  'receipts.submit.customer': 'Customer',
  'receipts.submit.amount_label': 'Amount received',
  'receipts.submit.amount_hint': 'In the selected currency, up to two decimals.',
  'receipts.submit.currency_label': 'Currency',
  'receipts.submit.channel_label': 'Channel',
  'receipts.submit.fee_label': 'Actual fee',
  'receipts.submit.fee_hint': 'Optional absolute amount in the receipt currency; defaults to 0.',
  'receipts.submit.received_at_label': 'Received at',
  'receipts.submit.contract_ref_label': 'Contract ref (optional)',
  'receipts.submit.note_label': 'Note (optional)',
  'receipts.submit.evidence_label': 'Evidence attachment (optional)',
  'receipts.submit.evidence_hint': 'Transfer slip or similar evidence, up to {mb} MB per file.',
  'receipts.submit.confirm': 'Submit receipt',
  'receipts.submit.retry_upload': 'Retry evidence upload',
  'receipts.submit.upload_pending':
    'The receipt was submitted but the evidence upload did not finish — retry the upload or close.',
  'receipts.submit.toast.submitted': 'Receipt submitted — waiting for finance verification.',
  'receipts.submit.error.amount': 'Amount must be a number greater than 0 with up to two decimals.',
  'receipts.submit.error.fee': 'Fee must be a number of at least 0 with up to two decimals.',
  'receipts.submit.error.received_at': 'Pick the received-at time.',
  'receipts.submit.error.oversize': 'The attachment exceeds the {mb} MB limit — compress it and retry.',
  'receipts.submit.error.upload_failed': 'Evidence upload failed: {detail}',
  'receipts.submit.error.evidence_reselect':
    'The evidence file was cleared — re-select it before retrying the upload.',
  'receipts.submit.company_missing_hint':
    'This customer has no company name yet — complete the Customer 360 contact card so finance can reconcile.',
  'receipts.customer.company_missing': '(not filled)',
  'receipts.detail.company': 'Company name',
  'receipts.filter.company_placeholder': 'Search company name…',
  'receipts.filter.company_label': 'Search by company name',
  'receipts.filter.company_submit': 'Search',
  'receipts.filter.company_clear': 'Clear',
  'receipts.col.invoice': 'Invoice',
  'receipts.filter.invoice': 'Invoice filter',
  'receipts.filter.invoice_all': 'All invoice states',
  'receipts.invoice.status.none': 'No invoice',
  'receipts.invoice.status.open': 'Invoice open',
  'receipts.invoice.status.issued': 'Invoice issued',
  'receipts.invoice.status.voided': 'Invoice voided',
  'receipts.invoice.mode.cn_tax': 'CN tax invoice',
  'receipts.invoice.mode.commercial': 'Commercial invoice',
  'receipts.invoice.title': 'Invoice',
  'receipts.invoice.status_label': 'Invoice status',
  'receipts.invoice.mode_label': 'Invoice mode',
  'receipts.invoice.tax_point_label': 'Tax point',
  'receipts.invoice.title_label': 'Invoice title',
  'receipts.invoice.tax_no_label': 'Tax no.',
  'receipts.invoice.decided_by': 'Decided by (finance)',
  'receipts.invoice.decided_at': 'Decided at',
  'receipts.invoice.action.issue': 'Issue invoice',
  'receipts.invoice.action.void': 'Void invoice',
  'receipts.invoice.issue_title': 'Issue this invoice?',
  'receipts.invoice.void_title': 'Void this invoice?',
  'receipts.invoice.toast.issued': 'Invoice issued.',
  'receipts.invoice.toast.voided': 'Invoice voided.',
  'receipts.invoice.await_credit': 'Credit the receipt before deciding the invoice',
  'receipts.submit.need_invoice_label': 'Customer wants an invoice',
  'receipts.submit.invoice_mode_hint.cn_tax':
    'CN tax invoice - record the confirmed tax point. It reduces the company net at commission time, never the customer credit.',
  'receipts.submit.invoice_mode_hint.commercial':
    'This brand issues commercial invoices - no CN tax point applies.',
  'receipts.submit.tax_point_label': 'Tax point (%)',
  'receipts.submit.tax_point_hint': 'Up to two decimals; cap {cap}.',
  'receipts.submit.invoice_title_label': 'Invoice title (optional)',
  'receipts.submit.tax_no_label': 'Tax no. (optional)',
  'receipts.submit.error.tax_point':
    'Tax point must be a percentage of at least 0 with up to two decimals.',
  'receipts.submit.error.tax_point_cap': 'Tax point exceeds the configured cap of {cap}.',
  'receipts.invoice_settings.title': 'Brand invoice settings',
  'receipts.invoice_settings.hint':
    'Per-brand invoicing config read by the submit form: mode, default tax point, and the accepted cap. Changes apply to new submissions immediately.',
  'receipts.invoice_settings.brand': 'Brand',
  'receipts.invoice_settings.mode': 'Mode',
  'receipts.invoice_settings.default_tax_point': 'Default tax point (%)',
  'receipts.invoice_settings.max_tax_point': 'Tax point cap (%)',
  'receipts.invoice_settings.error.max':
    'cn-tax mode needs a cap above 0% with up to two decimals.',
  'receipts.invoice_settings.error.default':
    'Default tax point must parse and stay within the cap.',
  'receipts.invoice_settings.toast.saved': 'Invoice settings saved for {brand}.',

  // ── Unified risk items (Epic #853 / #858) ──────────────────────
  'risk.dimension.supply': 'Supply',
  'risk.dimension.finance': 'Finance',
  'risk.dimension.traffic': 'Traffic',
  'risk.window.supply': '24h snapshot',
  'risk.window.finance': 'Unresolved backlog',
  'risk.window.traffic': 'Current window',
  'risk.window.preset': 'Window {preset}',
  'risk.action.view_requests': 'View requests',

  // ── Risk workbench (epic #997 / #1005) ─────────────────────────
  'risk_workbench.title': 'Risk workbench',
  'risk_workbench.subtitle':
    'Risk queue grouped by type — acknowledge, resolve, and jump straight to the remediation surface.',
  'risk_workbench.updated': 'Updated',
  'risk_workbench.loading': 'Loading the risk queue…',
  'risk_workbench.error': 'Could not load the risk queue',
  'risk_workbench.empty': 'No open risks right now.',
  'risk_workbench.empty_filtered': 'No risks match the current filters.',
  'risk_workbench.summary.open': 'Open',
  'risk_workbench.summary.critical': 'Critical',
  'risk_workbench.summary.warning': 'Warning',
  'risk_workbench.summary.acked': 'Acked',
  'risk_workbench.summary.resolved': 'Resolved',
  'risk_workbench.filter.severity': 'Severity',
  'risk_workbench.filter.dimension': 'Dimension',
  'risk_workbench.filter.status': 'Status',
  'risk_workbench.filter.all': 'All',
  'risk_workbench.severity.critical': 'Critical',
  'risk_workbench.severity.warning': 'Warning',
  'risk_workbench.severity.info': 'Info',
  'risk_workbench.status.open': 'Open',
  'risk_workbench.status.acked': 'Acked',
  'risk_workbench.status.resolved': 'Resolved',
  'risk_workbench.group.count': '{count} items',
  'risk_workbench.group.expand': 'Expand group',
  'risk_workbench.group.collapse': 'Collapse group',
  'risk_workbench.item.state_by': '{status} · by {actor}',
  'risk_workbench.action.ack': 'Ack',
  'risk_workbench.action.resolve': 'Resolve',
  'risk_workbench.action.reopen': 'Reopen',
  'risk_workbench.action.open_target': 'Open target',
  'risk_workbench.dialog.ack_title': 'Acknowledge risk',
  'risk_workbench.dialog.resolve_title': 'Resolve risk',
  'risk_workbench.dialog.reopen_title': 'Reopen risk',
  'risk_workbench.dialog.note_label': 'Note (optional)',
  'risk_workbench.dialog.note_placeholder': 'Record the handling context or reason',
  'risk_workbench.dialog.submit': 'Submit',
  'risk_workbench.dialog.submitting': 'Submitting…',
  'risk_workbench.toast.acked': 'Risk acknowledged',
  'risk_workbench.toast.resolved': 'Risk resolved',
  'risk_workbench.toast.reopened': 'Risk reopened',

  // ── Authenticated landing ──────────────────────────────────────
  'ops_landing.status': 'Feishu sign-in complete',
  'ops_landing.title': 'Access pending',
  'ops_landing.body': 'This account has no ops role yet. Ask an administrator to assign roles in ops users.',
  'ops_landing.guidance':
    'Until a role is assigned, every workspace in this console stays off-limits. Contact your ops administrator to get a role, then sign in again — nothing else here will work in the meantime.',
  'ops_landing.logout': 'Log out',
  'ops_landing.account': 'Account',
  'ops_landing.role': 'Role',
  'ops_landing.role_pending': 'Pending',

  // ── Idle authenticated home (router#2260) ──────────────────────
  'ops_home.title': 'Console',
  'ops_home.body': 'Choose a workspace from the sidebar. This page loads no operational data.',

  // ── Business dashboard ─────────────────────────────────────────
  'business.kicker': 'Operating system',
  'business.supply.priced_skus': 'Priced SKUs',
  'business.supply.unrouted_skus': 'Unrouted SKUs',
  'business.supply.single_point_skus': 'Single-point SKUs',
  'business.supply.low_funding': 'Low funding',
  'business.supply.unhealthy': 'Unhealthy',
  'business.supply.sku_matrix': 'SKU x upstream coverage matrix',
  'business.supply.low_funding_title': 'Low funding runway',
  'business.sku_econ.table_title': 'SKU economics',
  'business.sku_econ.legacy_bucket': 'Unattributed historical usage',
  'business.sku_econ.legacy_hint': 'Historical usage recorded before SKU attribution was available',
  'business.sku_econ.col_margin': 'Margin',
  'business.sku_econ.col_margin_pct': 'Margin %',
  'business.sku_econ.col_revenue': 'Revenue',
  // #1327 PR-9: the four-multiplier naming — this window's realized spend
  // is procurement cost, the same quantity the marketplace's procurement
  // multiplier prices. Label only; the aggregate is unchanged.
  'business.sku_econ.col_cost': 'Procurement cost',
  'business.sku_econ.col_requests': 'Requests',
  'business.sku_econ.col_share': 'Share',
  'business.sku_econ.totals_label': 'Window total',
  'business.sku_econ.runway_days': 'Runway (days)',
  'business.sku_econ.runway_pending': 'pending',
  'business.sku_econ.empty': 'No SKU usage in this window.',
  'business.sku_econ.window_label': 'Window',
  'business.filters.window': 'Window',
  'business.filters.command_bar': 'Business overview filter command bar',
  'business.filters.brand': 'Brand',
  'business.window.today': 'Today',
  'business.brand.all': 'All',
  'business.status.updated': 'Updated',
  'business.status.covered': 'covered',
  'business.status.single_point': 'single point',
  'business.status.unrouted': 'unrouted',
  'business.metric.active_customers': 'Active customers',
  'business.metric.new_accounts': 'New accounts',
  'business.metric.verified_accounts': 'Verified accounts',
  'business.metric.keyed_accounts': 'Keyed accounts',
  'business.metric.paying_customers': 'Paying customers',
  'business.table.sku': 'SKU',
  'business.table.coverage': 'Coverage',
  'business.table.status': 'Status',
  'business.loading.title': 'Loading business data',
  'business.loading.body': 'Aggregating metrics from gateway and platform.',
  'business.error.prefix': 'Unable to load business dashboard:',
  'business.empty.title': 'No data',
  'business.empty.body': 'No business data for this window.',
  'business.empty.no_low_funding': 'No low-funding upstreams.',
  'business.empty.no_risks': 'No open risks.',
  'business.section.unavailable': 'Data temporarily unavailable',

  // ── Morning cockpit (#1004) ─────────────────────────────────────
  'cockpit.title': 'Morning cockpit',
  'cockpit.subtitle': 'Five minutes to see yesterday\'s true margin, cash in/out, upstream prepaid runway, who to chase for top-ups, open fires, and the growth funnel.',
  'cockpit.envelope.window': 'Window',
  'cockpit.envelope.compare': 'Compared to',
  'cockpit.envelope.generated': 'Generated',
  'cockpit.envelope.stale': 'may be stale',
  'cockpit.window.today': 'Today',
  'cockpit.window.yesterday': 'Yesterday',
  'cockpit.window.current': 'This window',
  'cockpit.window.previous': 'Previous window',
  'cockpit.margin.title': 'True margin',
  'cockpit.margin.link': 'Open true P&L',
  'cockpit.margin.margin': 'Margin',
  'cockpit.margin.revenue': 'Cash revenue',
  'cockpit.margin.cost': 'Upstream cost',
  'cockpit.margin.gifted': 'Gifted cost',
  'cockpit.quality.balanced': 'Balanced',
  'cockpit.quality.partial': 'Partial attribution',
  'cockpit.quality.unreliable': 'Unreliable',
  'cockpit.cash.title': 'Cash in / out',
  'cockpit.cash.net': 'Net cashflow',
  'cockpit.cash.in': 'User top-ups',
  'cockpit.cash.fees': 'Payment fees',
  'cockpit.cash.out': 'Upstream payouts',
  'cockpit.upstream.title': 'Upstream prepaid runway',
  'cockpit.upstream.link': 'Open upstreams',
  'cockpit.upstream.total': 'Total prepaid remaining',
  'cockpit.upstream.stranded': 'Plus, locked inside depleted batches and no longer spendable',
  'cockpit.upstream.empty': 'No upstream holds prepaid balance.',
  'cockpit.chase.title': 'Balance chase list',
  'cockpit.chase.link': 'Open accounts',
  'cockpit.chase.low_balance_link': 'View all low-balance accounts',
  'cockpit.chase.hint': 'Days-left ascending — chase the soonest-to-run-out first.',
  'cockpit.chase.empty': 'No customer needs a top-up nudge in this window.',
  'cockpit.runway.burn_window': 'At the trailing {days}-day burn rate',
  'cockpit.runway.days': '{days} d',
  'cockpit.runway.no_burn': 'No burn in window',
  'cockpit.table.upstream': 'Upstream',
  'cockpit.table.customer': 'Customer',
  'cockpit.table.days_left': 'Days left',
  'cockpit.table.remaining': 'Remaining',
  'cockpit.table.burn_per_day': 'Burn / day',
  'cockpit.fires.title': 'Open fires',
  'cockpit.fires.link': 'Open risk workbench',
  'cockpit.fires.showing': 'Showing the {shown} most severe of {total}',
  'cockpit.funnel.title': 'Growth funnel',
  'cockpit.funnel.link': 'Open customer accounts',
  'cockpit.funnel.cohort': 'Signup cohort (registered in window)',
  'cockpit.funnel.activity': 'Window activity (all accounts)',
  'cockpit.funnel.conversion': '{pct} conversion',
  'cockpit.funnel.caveat': 'Cohort stages count accounts registered in the window; active/paying count window activity across all accounts — two populations, not one seamless funnel.',

  // ── Finance P&L ─────────────────────────────────────────────────
  'finance.pnl.window.this_month': 'Month to date',
  'finance.pnl.window.last_month': 'Last month',
  'finance.pnl.window.all': 'All time',
  'finance.pnl.currency_note': 'Primary money values use CNY. USD-credit balances are converted with the current RMB display rate.',
  'finance.pnl.materialized_through': 'Usage rollup through {date}',
  'finance.pnl.error': 'Failed to load finance P&L',
  'finance.pnl.retry': 'Retry',
  'finance.pnl.metric.operating_profit': 'Net operating P&L',
  'finance.pnl.metric.cash_gross_profit': 'Cash gross profit',
  'finance.pnl.metric.gifted_cost': 'Gifted credit cost',
  'finance.pnl.metric.net_cashflow': 'Net cashflow',
  'finance.pnl.metric.margin': 'Margin {pct}',
  'finance.pnl.metric.cash_revenue': 'cash revenue {amount}',
  'finance.pnl.metric.gifted_usage': 'gift usage {amount}',
  'finance.pnl.metric.cash_in': 'cash in {amount}',
  'finance.pnl.flow.title': 'Money flow',
  'finance.pnl.flow.user_cash': 'User cash',
  'finance.pnl.flow.cash_in': 'Cash in',
  'finance.pnl.flow.payment_fee': 'Payment fee',
  'finance.pnl.flow.cash_liability': 'Cash liability',
  'finance.pnl.flow.usage': 'Usage realization',
  'finance.pnl.flow.cash_revenue': 'Cash-funded revenue',
  'finance.pnl.flow.cash_cost': 'Cash-funded upstream cost',
  'finance.pnl.flow.gift_cost': 'Gift/credit upstream cost',
  'finance.pnl.flow.upstream': 'Upstream',
  'finance.pnl.flow.upstream_cash_out': 'Funding cash out',
  'finance.pnl.flow.upstream_prepaid': 'Prepaid remaining',
  'finance.pnl.flow.gift_liability': 'Gift liability',
  'finance.pnl.quality.title': 'Attribution quality',
  'finance.pnl.quality.balance_coverage': 'Balance coverage',
  'finance.pnl.quality.cost_coverage': 'Cost coverage',
  'finance.pnl.quality.unknown_source': 'Unknown source',
  'finance.pnl.quality.missing_cost': 'Missing cost',
  'finance.pnl.quality.unallocated_debits': 'Unallocated debits',
  'finance.pnl.quality.mismatch_accounts': 'Mismatch accounts',
  'finance.pnl.quality.status.balanced': 'Balanced',
  'finance.pnl.quality.status.partial': 'Partial',
  'finance.pnl.quality.status.unreliable': 'Unreliable',
  'finance.pnl.drill.payments': 'Payment orders',
  'finance.pnl.drill.gift_cards': 'Gift cards',
  'finance.pnl.drill.upstreams': 'Upstream funding',
  'finance.pnl.drill.reports': 'Monthly reports',

  // ── Metrics hub (epic #1643) ────────────────────────────────────
  'metrics.title': 'Metrics hub',
  'metrics.subtitle': 'One daily view of revenue, upstream cost and profit — sliced by model / tenant / SKU.',
  'metrics.window.aria': 'Metrics window',
  'metrics.window.today': 'Today',
  'metrics.window.7d': 'Last 7 days',
  'metrics.window.30d': 'Last 30 days',
  'metrics.window.custom': 'Custom',
  'metrics.custom.from': 'From',
  'metrics.custom.to': 'To',
  'metrics.brand': 'Brand',
  'metrics.brand.all': 'All',
  'metrics.tabs.aria': 'Metrics hub tabs',
  'metrics.tab.overview': 'Overview',
  'metrics.tab.models': 'Models',
  'metrics.tab.platform_sku': 'Platform SKU',
  'metrics.tab.upstream': 'Upstream SKU',
  'metrics.tab.tenants': 'Tenants',
  'metrics.tab.usage': 'Usage',
  'metrics.tab.pnl': 'P&L',
  'metrics.tab.sku': 'SKU',
  'metrics.trend.days_aria': 'Trend day count',
  'metrics.trend.days': '{days}d',
  'metrics.chart.view_aria': 'Chart view',
  'metrics.chart.view.chart': 'Chart',
  'metrics.chart.view.table': 'Table',
  'metrics.chart.rollup_aria': 'Rollup granularity',
  'metrics.chart.rollup.day': 'Day',
  'metrics.chart.rollup.week': 'Week',
  'metrics.chart.rollup.month': 'Month',
  'metrics.chart.rollup.note': 'Weeks are natural ISO weeks (Monday-start; an edge-of-window week may hold fewer than 7 days); months are UTC+8 calendar months.',
  'metrics.chart.col.bucket': 'Period',
  'metrics.chart.legend.show_all': 'Show all',
  'metrics.chart.legend.hide_all': 'Clear',
  'metrics.chart.all_hidden': 'All series are hidden — click a legend item to show it.',
  'metrics.chart.tooltip_more': '…{count} more',
  'metrics.overview.requests': 'Requests',
  'metrics.overview.requests.sub': 'ok {success} · failed {error}',
  'metrics.overview.tokens': 'Total tokens',
  'metrics.overview.tokens.sub': 'in {input} · out {output} · cache read {cacheRead}',
  'metrics.overview.revenue': 'Revenue (CNY)',
  'metrics.overview.revenue.sub': 'incl. gift-funded {amount}',
  'metrics.overview.upstream_cost': 'Upstream cost',
  'metrics.overview.upstream_cost.sub': 'procurement snapshot basis',
  'metrics.overview.l2_profit': 'L2 profit',
  'metrics.overview.l2_profit.sub': 'gross margin {pct}',
  'metrics.overview.operating_profit': 'Operating profit',
  'metrics.overview.operating_profit.sub': 'operating basis · incl. gifted cost',
  'metrics.overview.quality.balanced': 'Balanced',
  'metrics.overview.quality.partial': 'Partial attribution',
  'metrics.overview.quality.unreliable': 'Unreliable',
  'metrics.overview.usage_error': 'Usage data unavailable; other metrics are unaffected.',
  'metrics.overview.revenue_error': 'Revenue data unavailable; other metrics are unaffected.',
  'metrics.overview.pnl_error': 'Operating profit unavailable; other metrics are unaffected.',
  'metrics.overview.trend.title': 'Daily revenue & profit trend',
  'metrics.overview.trend.revenue': 'Revenue',
  'metrics.overview.trend.profit': 'L2 profit',
  'metrics.overview.trend.empty': 'No daily revenue in this window.',
  'metrics.overview.trend.error': 'Unable to load the daily trend.',
  'metrics.overview.trend.rate_unavailable': 'Revenue series hidden: display FX rate unavailable',
  'metrics.models.trend.title': 'Top models · daily charged trend',
  'metrics.models.trend.truncated': 'Top {shown} by charged shown · {total} models total',
  'metrics.models.trend.empty': 'No per-model revenue in this window.',
  'metrics.models.trend.error': 'Unable to load the model trend.',
  'metrics.models.trend.rate_unavailable': 'Display FX rate unavailable — the CNY model trend cannot be rendered.',
  'metrics.models.cost_missing': '{count} usage rows in the trend window have unknown cost; cost and profit may be understated',
  'metrics.models.table.title': 'Model summary',
  'metrics.models.table.sub': 'window totals · by L2 profit desc',
  'metrics.models.table.cost_note': 'Upstream cost is the revenue-ledger sum; rows missing a procurement snapshot are not included, so cost and profit may be understated.',
  'metrics.models.table.empty': 'No model revenue in this window.',
  'metrics.models.table.error': 'Unable to load the model summary.',
  'metrics.models.col.model': 'Model',
  'metrics.models.col.calls': 'Calls',
  'metrics.models.col.charged': 'Charged',
  'metrics.models.col.cost': 'Upstream cost',
  'metrics.models.col.profit': 'L2 profit',
  'metrics.models.col.margin': 'Margin',
  'metrics.models.table.col.total': 'Total',
  'metrics.group.kpi.calls': 'Total calls',
  'metrics.group.kpi.charged': 'Total charged (CNY)',
  'metrics.group.kpi.cost': 'Total upstream cost',
  'metrics.group.kpi.profit': 'Total L2 profit',
  'metrics.group.cost_missing':
    '{count} usage rows in this window have unknown cost; cost and profit may be understated',
  'metrics.group.hourly_note':
    'This window spans at most 24 hours — trends are bucketed hourly (UTC+8).',
  'metrics.group.clamp_note':
    'Custom window exceeds 90 days — this tab covers only the trailing 90 days before the end bound.',
  'metrics.group.rate_unavailable':
    'Display FX rate unavailable — charged amounts cannot be converted to CNY.',
  'metrics.group.error': 'Unable to load the trend series.',
  'metrics.group.empty': 'No data in this window.',
  'metrics.group.metric_aria': 'Metric',
  'metrics.group.metric.charged': 'Charged (CNY)',
  'metrics.group.metric.calls': 'Calls',
  'metrics.group.metric.profit': 'L2 profit (CNY)',
  'metrics.group.default_visible_note':
    'Top {count} by charged shown by default — toggle the rest in the legend.',
  'metrics.group.col.name': 'Name',
  'metrics.group.col.calls': 'Calls',
  'metrics.group.col.charged': 'Charged',
  'metrics.group.col.cost': 'Upstream cost',
  'metrics.group.col.profit': 'L2 profit',
  'metrics.group.col.margin': 'Margin',
  'metrics.group.col.cost_missing': 'Cost unknown',
  'metrics.platform_sku.trends.title': 'Platform SKU trend comparison',
  'metrics.platform_sku.table.title': 'Platform SKU summary',
  'metrics.platform_sku.table.sub': 'derived from series totals · by charged desc',
  'metrics.platform_sku.table.note':
    'Charged and margin are converted at the current display FX rate; upstream cost and L2 profit are ledger CNY values. Rows missing a procurement snapshot are not included in cost, so cost and profit may be understated.',
  'metrics.upstream.trends.title': 'Upstream SKU trend comparison',
  'metrics.upstream.table.title': 'Upstream summary',
  'metrics.upstream.table.sub': 'window totals · per-row ledger FX conversion',
  'metrics.upstream.table.error': 'Unable to load the upstream summary.',
  'metrics.throughput.sku.title': 'Platform SKU throughput (RPM / TPM)',
  'metrics.throughput.upstream.title': 'Upstream SKU throughput (RPM / TPM)',
  'metrics.throughput.metric_aria': 'Throughput metric',
  'metrics.throughput.metric.rpm': 'RPM',
  'metrics.throughput.metric.tpm': 'TPM',
  'metrics.throughput.note':
    'Values are per-bucket averages per minute: RPM = requests ÷ bucket minutes, TPM = tokens ÷ bucket minutes (TPM sums input, output and cache read/write tokens); the first and last buckets divide by their actual overlap with the query window. Throughput counts every request, including failures and unpriced calls — a different basis from the Calls metric above.',
  'metrics.throughput.default_visible_note':
    'Top {count} by request volume shown by default — toggle the rest in the legend.',
  'metrics.throughput.error': 'Unable to load the throughput series.',
  'metrics.tenants.title': 'Top tenants',
  'metrics.tenants.sort.aria': 'Tenant ordering',
  'metrics.tenants.sort.spend': 'By spend',
  'metrics.tenants.sort.profit': 'By profit',
  'metrics.tenants.col.account': 'Tenant',
  'metrics.tenants.col.requests': 'Requests',
  'metrics.tenants.col.charged': 'Charged',
  'metrics.tenants.col.cost': 'Upstream cost',
  'metrics.tenants.col.profit': 'L2 profit',
  'metrics.tenants.col.margin': 'Margin',
  'metrics.tenants.view_requests': 'View requests →',
  'metrics.tenants.empty': 'No tenant revenue in this window.',
  'metrics.tenants.error': 'Unable to load the tenant leaderboard.',
  'metrics.usage.loading': 'Loading usage…',
  'metrics.usage.error': 'Unable to load usage: {message}',
  'metrics.usage.empty': 'No usage in this window.',
  'metrics.usage.metric.requests': 'Requests',
  'metrics.usage.metric.requests.sub': 'ok {success} · failed {error}',
  'metrics.usage.metric.tokens': 'Tokens',
  'metrics.usage.metric.tokens.sub': 'in {input} · out {output}',
  'metrics.usage.metric.cache': 'Cache tokens',
  'metrics.usage.metric.cache.sub': 'read {read} · write {write}',
  'metrics.usage.metric.charged': 'User charged',
  'metrics.usage.metric.charged.sub': 'Gateway {amount}',
  'metrics.usage.metric.upstream_cost': 'Upstream cost',
  'metrics.usage.metric.upstream_cost.sub': 'revenue ledger',
  'metrics.usage.metric.l2_profit': 'L2 profit',
  'metrics.usage.metric.l2_profit.sub': 'gross margin {pct}',
  'metrics.usage.metric.gross_margin': 'Total gross margin',
  'metrics.usage.metric.gross_margin.sub': 'L1 + L2',
  'metrics.usage.metric.window': 'Window',
  'metrics.usage.metric.window.sub': 'local time',
  'metrics.usage.table.tenants': 'Top tenants',
  'metrics.usage.table.models': 'By model',
  'metrics.usage.table.brands': 'By brand',
  'metrics.usage.table.upstreams': 'By upstream',
  'metrics.usage.table.keys': 'By key',
  'metrics.usage.empty.tenants': 'No tenant usage',
  'metrics.usage.empty.models': 'No model usage',
  'metrics.usage.empty.brands': 'No brand usage',
  'metrics.usage.empty.upstreams': 'No upstream usage',
  'metrics.usage.empty.keys': 'No key usage',
  'metrics.usage.col.object': 'Object',
  'metrics.usage.col.requests': 'Requests',
  'metrics.usage.col.io': 'In/out',
  'metrics.usage.col.charged': 'User charged',
  'metrics.usage.col.l2_profit': 'L2 profit',
  'metrics.usage.col.gateway_cost': 'Gateway cost',
  'metrics.usage.col.errors': 'Errors',
  'metrics.usage.col.cache_rw': 'Cache read/write',
  'metrics.usage.view_requests': 'View requests →',
  'metrics.usage.unnamed_key': 'Unnamed key',
  'metrics.usage.unknown_channel': 'Unknown channel',

  // ── Not found ───────────────────────────────────────────────────
  'notfound.title': 'Page not found',
  'notfound.body_prefix': 'The route you requested does not exist yet.',
  'notfound.back': 'Back to dashboard',

  // ── Tenant Routing ─────────────────────────────────────────────
  'tenant_routing.title': 'System Routing',
  'tenant_routing.subtitle': 'Config library, SKU target pools, and execution policy',
  'tenant_routing.loading': 'Loading tenant routing…',
  'tenant_routing.action.route_check': 'Route check',
  'tenant_routing.action.edit_default': 'Edit',
  'tenant_routing.status.healthy': 'Healthy',
  'tenant_routing.status.attention': 'Needs attention',
  'tenant_routing.status.checking': 'Checking routes…',
  'tenant_routing.status.enabled': 'Enabled',
  'tenant_routing.status.disabled': 'Disabled',
  'tenant_routing.status.target_enabled': 'Target enabled',
  'tenant_routing.status.target_disabled': 'Target disabled',
  // Config library list + dialogs
  'tenant_routing.library.open': 'Open',
  'tenant_routing.configs.title': 'Route Configs',
  'tenant_routing.configs.create': 'New config',
  'tenant_routing.configs.default_new_name': 'Route config from global default',
  'tenant_routing.configs.global_default_badge': 'Global default',
  'tenant_routing.configs.empty': 'No route configs are available.',
  'tenant_routing.configs.pool_targets': 'Pool targets',
  'tenant_routing.configs.name': 'Config',
  'tenant_routing.configs.type': 'Type',
  'tenant_routing.configs.users': 'Customers using',
  'tenant_routing.configs.assigned_customers': 'Assigned customers',
  'tenant_routing.configs.no_assignments': 'No customers are assigned to this config.',
  'tenant_routing.configs.assign': 'Assign',
  'tenant_routing.configs.make_global': 'Make global',
  'tenant_routing.configs.rename': 'Rename',
  'tenant_routing.configs.make_global_title': 'Make global default',
  'tenant_routing.configs.make_global_target': 'Switching global default to',
  'tenant_routing.configs.make_global_save': 'Switch global default',
  'tenant_routing.configs.switch_reason': 'Reason for this switch',
  'tenant_routing.configs.switch_reason_hint':
    'Recorded on the switch event and read during the next incident review. Required.',
  'tenant_routing.configs.switch_reason_placeholder':
    'e.g. primary upstream returning 5xx, failing over to the standby config',
  'tenant_routing.configs.rename_title': 'Rename route config',
  'tenant_routing.configs.rename_save': 'Save name',
  'tenant_routing.configs.create_title': 'Create route config',
  'tenant_routing.configs.description': 'Description',
  'tenant_routing.configs.assign_org_optional': 'Assign org now',
  'tenant_routing.configs.create_from_global_default': 'Create from global default',
  'tenant_routing.configs.clone': 'Clone',
  'tenant_routing.configs.clone_title': 'Clone route config',
  'tenant_routing.configs.clone_source': 'Clone source',
  'tenant_routing.configs.clone_save': 'Clone config',
  'tenant_routing.configs.clone_default_name': '{name} (copy)',
  'tenant_routing.configs.assign_title': 'Assign route config',
  'tenant_routing.configs.selected_config': 'Route config',
  'tenant_routing.configs.assign_org': 'Org ID',
  'tenant_routing.configs.assign_replaces_stack':
    'This replaces every dedicated routing binding this customer currently has.',
  'tenant_routing.configs.assignment_position': '{position}',
  'tenant_routing.configs.assign_save': 'Assign config',
  'tenant_routing.configs.type.system': 'System',
  'tenant_routing.configs.type.customer': 'Customer',
  'tenant_routing.configs.type.experiment': 'Experiment',
  'tenant_routing.configs.status.draft': 'Draft',
  'tenant_routing.configs.status.active': 'Active',
  'tenant_routing.configs.status.archived': 'Archived',
  'tenant_routing.configs.toast.created': 'Route config created',
  'tenant_routing.configs.toast.cloned': 'Route config cloned',
  'tenant_routing.configs.toast.cloned_incompatible_pins':
    'Copied, but {count} pinned target(s) no longer match their egress profile and will be skipped when routing. Review them in the config.',
  'tenant_routing.configs.toast.global_set': 'Global route config updated',
  'tenant_routing.configs.toast.assigned': 'Route config assigned',
  'tenant_routing.configs.toast.renamed': 'Route config renamed',
  'tenant_routing.configs.error.name_required': 'Enter a route config name.',
  'tenant_routing.configs.error.reason_required':
    'Enter a reason for this switch. It is recorded on the switch event.',
  'tenant_routing.configs.error.reason_too_long':
    'That reason is longer than 1000 characters. Shorten it — the switch event stores a summary, not the full incident write-up.',
  'tenant_routing.configs.error.switch_head_moved':
    'The global routing config changed while this was open, so this rollback would undo more than one step. Reload the switch history and decide again.',
  'tenant_routing.configs.error.preflight_stale':
    'The preflight result no longer matches this config or the current global default — someone changed one of them. Re-run the preflight and read it again before switching.',
  'tenant_routing.configs.error.assign_required': 'Choose a route config and enter an Org ID.',

  // ── DR plans, preflight report and switch history (#1421 P3b) ───
  'routing_dr.plans.title': 'DR plans',
  'routing_dr.plans.mark': 'Mark DR plan',
  'routing_dr.plans.mark_title': 'Mark a route config as a DR plan',
  'routing_dr.plans.edit_title': 'Edit DR plan',
  'routing_dr.plans.save_mark': 'Mark as DR plan',
  'routing_dr.plans.save_edit': 'Save plan',
  'routing_dr.plans.loading': 'Loading DR plans…',
  'routing_dr.plans.empty_title': 'No route config is marked as a DR plan yet.',
  'routing_dr.plans.empty_hint':
    'Mark the configs you would fail over to, so an incident does not start with reading the config library — and so how stale each verification is stays visible.',
  'routing_dr.plans.column.plan': 'Plan',
  'routing_dr.plans.column.rank': 'Rank',
  'routing_dr.plans.column.config_status': 'Config status',
  'routing_dr.plans.column.last_verified': 'Last verified',
  'routing_dr.plans.column.result': 'Verification result',
  'routing_dr.plans.column.config': 'Route config',
  'routing_dr.plans.config_hint': 'Marking adds a record. It never changes the route config itself.',
  'routing_dr.plans.label': 'Plan name',
  'routing_dr.plans.label_placeholder': 'e.g. standby for a primary-upstream outage',
  'routing_dr.plans.rank': 'Rank',
  'routing_dr.plans.rank_hint': 'Lower goes first. Left empty it is 0.',
  'routing_dr.plans.notes': 'Notes',
  'routing_dr.plans.notes_hint': 'Say when to switch to this plan — this is the line read during an incident.',
  'routing_dr.plans.never_verified': 'Never verified',
  'routing_dr.plans.unreadable_verified_at': 'Verification time unreadable',
  'routing_dr.plans.verdict_undated': 'Result stored, but not datable',
  'routing_dr.plans.verdict_counts':
    'No route {uncovered} · Unusable targets {unhealthy} · Not reached {unreachable}',
  'routing_dr.plans.unmark_title': 'Unmark DR plan',
  'routing_dr.plans.unmark_body':
    'Unmarking “{label}” removes it from this list. The route config, its targets and its assignments stay exactly as they are.',
  'routing_dr.plans.action.preflight': 'Preflight',
  'routing_dr.plans.action.edit': 'Edit',
  'routing_dr.plans.action.unmark': 'Unmark',
  'routing_dr.plans.toast.marked': 'Marked as a DR plan',
  'routing_dr.plans.toast.updated': 'DR plan updated',
  'routing_dr.plans.toast.unmarked': 'DR plan mark removed',
  'routing_dr.plans.error.config_required': 'Choose the route config to mark.',
  'routing_dr.plans.error.label_required': 'Enter a plan name — it is how this plan is recognised during an incident.',
  'routing_dr.plans.error.rank_invalid': 'Rank must be an integer of 0 or more.',
  'routing_dr.age.days_one': '{count} day ago',
  'routing_dr.age.days_many': '{count} days ago',
  'routing_dr.age.hours_one': '{count} hour ago',
  'routing_dr.age.hours_many': '{count} hours ago',
  'routing_dr.age.just_now': 'Just now',
  'routing_dr.verdict.clean': 'Passed',
  'routing_dr.verdict.caution': 'Notes to read',
  'routing_dr.verdict.blocking': 'Blocking findings',

  'routing_dr.preflight.title': 'Switch preflight',
  'routing_dr.preflight.window': 'Traffic window',
  'routing_dr.preflight.window_hours': 'Last {count} hours',
  'routing_dr.preflight.run': 'Run preflight',
  'routing_dr.preflight.verify': 'Verify and record on the plan',
  'routing_dr.preflight.read_only_hint':
    'The preflight writes nothing and is safe to re-run. “Verify and record” stores this result on the plan and resets its last-verified time.',
  'routing_dr.preflight.running': 'Running the preflight…',
  'routing_dr.preflight.empty_title': 'No preflight result yet.',
  'routing_dr.preflight.empty_hint': 'Run one to see what switching to this config would change right now.',
  'routing_dr.preflight.toast.verified': 'Preflight result recorded on the plan',
  'routing_dr.preflight.generated_at': 'Generated at {at}',
  'routing_dr.preflight.window_summary': 'Evaluated {skus} SKUs against the last {hours} hours of traffic.',
  'routing_dr.preflight.window_truncated': 'The SKU set exceeded the evaluation cap and was trimmed.',
  'routing_dr.preflight.candidate_not_active': 'This config is not active, so it cannot be switched to right now.',
  'routing_dr.preflight.show_all': 'Show the remaining {count} rows',
  'routing_dr.preflight.section_capped': 'Showing {shown} of {total} — the endpoint capped this list',
  'routing_dr.preflight.all_skus': 'Every SKU',
  'routing_dr.preflight.unpinned': 'no pinned capability',
  'routing_dr.preflight.skus_outside_window':
    'This config also binds {count} SKUs that saw no traffic in the window, so they were not evaluated.',
  'routing_dr.preflight.headline.uncovered': 'Some SKUs would have no route',
  'routing_dr.preflight.headline.unhealthy': 'Some targets would not serve',
  'routing_dr.preflight.headline.unreachable': 'Some customers would not be reached',
  'routing_dr.preflight.headline.changed': 'Routing would change',
  'routing_dr.preflight.headline.clean': 'Nothing found',
  'routing_dr.preflight.stat.uncovered': 'No route',
  'routing_dr.preflight.stat.fall_through': 'Falls through',
  'routing_dr.preflight.stat.covered': 'Covered here',
  'routing_dr.preflight.stat.unhealthy_targets': 'Unusable targets',
  'routing_dr.preflight.stat.degraded_targets': 'Degraded targets',
  'routing_dr.preflight.stat.changed_skus': 'Routing changes',
  'routing_dr.preflight.stat.unreachable_orgs': 'Customers not reached',
  'routing_dr.preflight.section.unreachable_orgs': 'Customers this switch would not reach',
  'routing_dr.preflight.section.unreachable_orgs_hint':
    'These customers resolve at a tier above the global one, so a global switch does not apply to them.',
  'routing_dr.preflight.section.unreachable_orgs_none':
    'Every customer with traffic in the window follows this global switch.',
  'routing_dr.preflight.section.coverage': 'SKUs this config does not cover',
  'routing_dr.preflight.section.coverage_hint': 'Another {covered} SKUs are served by this config and are not listed.',
  'routing_dr.preflight.section.coverage_none': 'Every SKU in the window is served by this config directly.',
  'routing_dr.preflight.section.health': 'Targets with findings',
  'routing_dr.preflight.section.health_hint':
    'Blocking first: the selector never attempts a blocked target, and still attempts a degraded one.',
  'routing_dr.preflight.section.health_none': 'No finding on any target this config references.',
  'routing_dr.preflight.section.diff': 'Per-SKU routing diff',
  'routing_dr.preflight.section.diff_hint': 'Only SKUs that land somewhere else after the switch, busiest first.',
  'routing_dr.preflight.section.diff_none': 'No SKU in the window changes where it lands.',
  'routing_dr.preflight.column.org': 'Customer',
  'routing_dr.preflight.column.reason': 'Reason',
  'routing_dr.preflight.column.skus': 'Affected SKUs',
  'routing_dr.preflight.column.requests': 'Requests',
  'routing_dr.preflight.column.sku': 'SKU',
  'routing_dr.preflight.column.status': 'Status',
  'routing_dr.preflight.column.detail': 'Detail',
  'routing_dr.preflight.column.target': 'Target',
  'routing_dr.preflight.column.findings': 'Findings',
  'routing_dr.preflight.column.sku_count': 'SKUs',
  'routing_dr.preflight.column.before': 'Before',
  'routing_dr.preflight.column.after': 'After',
  'routing_dr.coverage_status.covered': 'Covered here',
  'routing_dr.coverage_status.fall_through': 'Falls through',
  'routing_dr.coverage_status.uncovered': 'No route',
  'routing_dr.fallback_tier.sku_default': 'Falls through to the SKU default route',
  'routing_dr.fallback_tier.global_default': 'Falls through to the platform global default',
  'routing_dr.fallback_tier.legacy_fallback': 'Falls through to the legacy fallback tier',
  'routing_dr.uncovered_reason.uncovered_by_exclusive_candidate':
    'The config is exclusive and has neither a target nor a policy row for this SKU, so the request has no candidate at all.',
  'routing_dr.uncovered_reason.candidate_bound_but_no_live_target':
    'The config claims this SKU, but nothing behind the binding survives the live-capability chain.',
  'routing_dr.uncovered_reason.no_candidate_anywhere':
    'The whole ladder, defaults and legacy tier included, produced nothing.',
  'routing_dr.unreachable_reason.exclusive_org_config':
    'The customer sits on an exclusive dedicated config, so the global tier is never consulted.',
  'routing_dr.unreachable_reason.inherit_org_config_covers_sku':
    'The customer’s own config already covers these SKUs and takes its own branch.',
  'routing_dr.unreachable_reason.org_sku_pool':
    'The customer has frozen org-level SKU routes, and that tier is terminal for every SKU.',
  'routing_dr.unreachable_reason.org_sku_override':
    'The customer has live org-level overrides for these SKUs.',
  'routing_dr.health.degraded': 'Degraded',
  'routing_dr.health.blocking': 'Blocking',
  'routing_dr.health.breaker': 'Breaker: {state}',
  'routing_dr.breaker_state.closed': 'closed',
  'routing_dr.breaker_state.open': 'open',
  'routing_dr.breaker_state.half_open': 'half open',
  'routing_dr.breaker_state.forced': 'forced',
  'routing_dr.breaker_state.unknown': 'unknown',
  'routing_dr.finding.upstream_missing': 'Upstream missing',
  'routing_dr.finding.upstream_disabled': 'Upstream disabled',
  'routing_dr.finding.upstream_not_forward': 'Upstream is not a forward upstream',
  'routing_dr.finding.account_disabled': 'Account disabled',
  'routing_dr.finding.key_bucket_disabled': 'Key bucket disabled',
  'routing_dr.finding.capability_missing': 'Capability row missing',
  'routing_dr.finding.capability_not_live': 'Capability status outside the live set',
  'routing_dr.finding.capability_binding_missing': 'Key-to-channel binding missing',
  'routing_dr.finding.egress_profile_unresolved': 'No egress profile resolves enabled',
  'routing_dr.finding.capability_not_passed': 'Capability probe not passed (still attempted)',
  'routing_dr.finding.breaker_open': 'Breaker open',
  'routing_dr.finding.breaker_half_open': 'Breaker half open (still probed)',
  'routing_dr.finding.breaker_forced': 'Breaker forced',
  'routing_dr.finding.capacity_depleted': 'Capacity depleted',
  'routing_dr.caveat.openai_wire_api_not_modelled':
    'The preflight does not model the OpenAI wire filter, so an openai-protocol SKU reported as covered may still have no compatible candidate at request time.',
  'routing_dr.caveat.unreachable_orgs_truncated':
    'The unreachable-customer list hit its cap and was trimmed. Trust the total in the summary, not the row count.',

  'routing_dr.events.title': 'Global switch history',
  'routing_dr.events.bound': 'Latest {count} only',
  'routing_dr.events.loading': 'Loading switch history…',
  'routing_dr.events.empty_title': 'No global switch has been recorded yet.',
  'routing_dr.events.empty_hint':
    'Every global-default switch lands here, and a rollback replays the latest one backwards.',
  'routing_dr.events.column.when': 'When',
  'routing_dr.events.column.change': 'Change',
  'routing_dr.events.column.actor': 'Actor',
  'routing_dr.events.column.reason': 'Reason',
  'routing_dr.events.column.propagation': 'Propagation',
  'routing_dr.events.no_previous': '(no previous)',
  'routing_dr.events.preflighted': 'Carried a preflight token',
  'routing_dr.events.rollback_to': 'Roll back to {config}',
  'routing_dr.events.rollback_title': 'Roll back the latest global switch',
  'routing_dr.events.rollback_save': 'Roll back',
  'routing_dr.events.rollback_explainer':
    'This reverses the latest global switch — the one below, replayed backwards:',
  'routing_dr.events.rollback_syncing': 'Re-reading the latest switch…',
  'routing_dr.events.rollback_head_changed':
    'Someone switched the global default in the meantime, so the target above is now that newer switch. Confirm it before rolling back.',
  'routing_dr.events.rollback_reconfirm': 'Confirm this target',
  'routing_dr.events.rollback_reread_failed':
    'The latest switch could not be re-read, so the target above may no longer be the current one. Retry once the connection recovers — do not roll back from this state.',
  'routing_dr.events.rollback_retry_reread': 'Retry the re-read',
  'routing_dr.events.rollback_reason_placeholder': 'e.g. error rate went up after the switch, going back',
  'routing_dr.events.error.head_moved':
    'Someone switched the global default while this was open, so rolling back now would undo their switch too. Re-read the switch history instead of retrying.',
  'routing_dr.events.error.no_target':
    'The latest switch has no previous config to return to, so there is nothing to roll back to.',
  'routing_dr.events.error.target_gone':
    'The config this would roll back to no longer exists or is no longer active. Nothing was changed.',
  'routing_dr.events.toast.rollback_unavailable':
    'The latest switch can no longer be rolled back — it was superseded, or it has no previous config — so the rollback was closed.',
  'routing_dr.events.toast.rolled_back': 'Global route config rolled back',
  'routing_dr.events.toast.rolled_back_degraded':
    'The rollback committed, but the cache publish was partial — the fleet catches up within about {seconds} seconds, and traffic until then still uses the old config.',
  'routing_dr.events.toast.switched_override_in_force':
    'The global routing config was changed and recorded — but the emergency override is engaged, so it sits above the global config and no tenant is being routed by this change. Disengage the override to make it take effect.',
  'routing_dr.events.toast.rolled_back_override_in_force':
    'The rollback was recorded — but the emergency override is engaged, so it sits above the global config and no tenant traffic moved. Disengage the override to make it take effect.',
  'routing_dr.override.repoint_action': 'Repoint override',
  'routing_dr.override.repoint_title': 'Repoint the emergency routing override',
  'routing_dr.override.repoint_description':
    'Currently routing every tenant through {current}. Repointing is a single write — it does not drop the fleet back onto the normal ladder in between.',
  'routing_dr.override.repoint_confirm_title': 'Move every tenant to a different config?',
  'routing_dr.override.repoint_confirm_body':
    'The whole fleet moves from {current} to {next} in one write. Type the new config name to confirm.',
  'routing_dr.override.repoint_confirm_action': 'Repoint for every tenant',
  'routing_dr.override.repointed_toast': 'Emergency override repointed: every tenant now routes through {config}.',
  'routing_dr.events.toast.rolled_back_unknown':
    'The rollback committed, but this response carried no cache convergence, so whether the fleet is serving it cannot be confirmed. Give it about {seconds} seconds before drawing conclusions from traffic.',
  'routing_dr.events.toast.switched_degraded':
    'The switch committed, but the cache publish was partial — the fleet catches up within about {seconds} seconds, and traffic until then still uses the old config.',
  'routing_dr.events.toast.switched_unknown':
    'The switch committed, but this response carried no cache convergence, so whether the fleet is serving it cannot be confirmed. Give it about {seconds} seconds before drawing conclusions from traffic.',
  'routing_dr.convergence.immediate': 'Fleet converged',
  'routing_dr.convergence.degraded': 'Delayed',
  'routing_dr.convergence.pending': 'Not recorded',
  'routing_dr.convergence.unknown': 'Unknown',
  'routing_dr.propagation.hint':
    'The switch takes effect in the database immediately; gateway instances catch up within about {seconds} seconds, and both configs are live until they do.',
  // Emergency override (#1422) — the fleet-wide lever, above every ladder tier
  'routing_dr.override.caption': 'Emergency routing override',
  'routing_dr.override.status_off': 'Not engaged',
  'routing_dr.override.status_in_force': 'IN FORCE — every tenant',
  'routing_dr.override.status_expired': 'Engaged but expired',
  'routing_dr.override.status_archived_target': 'Engaged but its config is archived',
  'routing_dr.override.idle_body':
    'Routes every tenant through one config, above org-scoped assignments. Use it when a global switch is not enough — an org assigned an exclusive config never consults the global one, so a global switch does not move those tenants at all.',
  'routing_dr.override.in_force_body':
    'Every tenant is currently routed through this config, including orgs with an exclusive org-scoped assignment. Org assignments, SKU defaults and the global config are all bypassed.',
  'routing_dr.override.expired_body':
    'This override passed its expiry, so the request path already ignores it and traffic has returned to the normal ladder. The row is still engaged — disengage it so the next responder is not misled.',
  'routing_dr.override.archived_target_body':
    'The config this override points at is no longer active, so the request path disregards the override entirely and every tenant has fallen back to the ordinary ladder — the ladder this override was engaged to escape. Engage onto an active config, or disengage and use another lever.',
  'routing_dr.override.in_force_notice':
    'The emergency override is in force, routing every tenant through {config}. Changing or rolling back the global routing config will be recorded and will change nothing observable until the override is disengaged.',
  'routing_dr.override.reason_label': 'Reason',
  'routing_dr.override.engaged_by_label': 'Engaged by',
  'routing_dr.override.expires_label': 'Expires',
  'routing_dr.override.expires_never': 'No expiry set',
  'routing_dr.override.engage_action': 'Engage override',
  'routing_dr.override.disengage_action': 'Disengage',
  'routing_dr.override.engage_title': 'Engage the emergency routing override',
  'routing_dr.override.engage_description':
    'This is not the global switch. It ignores org scoping entirely.',
  'routing_dr.override.blast_radius_warning':
    'Blast radius: every tenant. This routes the whole fleet through one config and bypasses every org-scoped assignment. There is no per-org form of this control.',
  'routing_dr.override.config_label': 'Route through this config',
  'routing_dr.override.config_placeholder': 'Select an active routing config',
  'routing_dr.override.preflight_action': 'Run preflight',
  'routing_dr.override.preflight_hint':
    'Required. The preflight is what the gateway gates on, and re-running it is free. Changing the target config clears the token, because it was computed against the previous one.',
  'routing_dr.override.preflight_missing': 'Preflight not run',
  'routing_dr.override.preflight_ready': 'Preflight token ready',
  'routing_dr.override.preflight_ran':
    'Preflight complete. {unreachable} org(s) would be unreachable by an ordinary global switch.',
  'routing_dr.override.reason_hint':
    'Recorded on the switch-event log. Name the incident — this is the row the review reads first.',
  'routing_dr.override.expiry_label': 'Self-expire after',
  'routing_dr.override.expiry_hint':
    'Strongly recommended. The failure this guards is a forgotten override, not a missing one.',
  'routing_dr.override.expiry_hours': '{hours} hours',
  'routing_dr.override.expiry_none': 'No expiry (not recommended)',
  'routing_dr.override.confirm_title': 'Route every tenant through this config?',
  'routing_dr.override.confirm_body':
    'Confirming moves the entire fleet immediately, including tenants a global switch cannot reach. Type the config name to confirm.',
  'routing_dr.override.confirm_type_label': 'Config name',
  'routing_dr.override.confirm_action': 'Engage for every tenant',
  'routing_dr.override.engaged_toast': 'Emergency override engaged: every tenant now routes through {config}.',
  'routing_dr.override.disengage_title': 'Disengage the emergency routing override',
  'routing_dr.override.disengage_description':
    'Traffic returns to the ordinary ladder for every tenant.',
  'routing_dr.override.disengage_hint':
    'Traffic returns to whatever the ladder resolves now, which is not necessarily where it was before the override was engaged. Re-check a representative org afterwards.',
  'routing_dr.override.disengaged_toast': 'Emergency override disengaged.',
  'routing_dr.override.error_config_required': 'Select the routing config to override onto.',
  'routing_dr.override.error_reason_required': 'A reason is required.',
  'routing_dr.override.error_preflight_required': 'Run the preflight before engaging.',
  // Detail sheet + SKU target pools
  'tenant_routing.sheet.error': 'Failed to load the route config detail.',
  'tenant_routing.ladder.title': 'Resolution ladder',
  'tenant_routing.ladder.tier_override': 'Emergency override',
  'tenant_routing.ladder.tier_org': 'Customer assignment',
  'tenant_routing.ladder.tier_global': 'Global default config',
  'tenant_routing.ladder.tier_default_tables': 'Platform default tables',
  'tenant_routing.ladder.body':
    'The first tier that matches wins. A customer assignment beats the global default, so switching the global default does not move a customer that carries its own assignment; an engaged emergency override beats everything, assignments included. When an inherit config covers a SKU with neither a target nor a policy, it falls through to the GLOBAL DEFAULT CONFIG — not to the platform default tables, which are only reachable when the global default is itself on inherit.',
  'tenant_routing.parity.open': 'Compare with default tables',
  'tenant_routing.parity.title': 'Global default config vs platform default tables',
  'tenant_routing.parity.description': 'Two independent tables with nothing keeping them in step.',
  'tenant_routing.parity.loading': 'Comparing…',
  'tenant_routing.parity.failed': 'Comparison failed. Try again shortly.',
  'tenant_routing.parity.no_global_default': 'No config is assigned as the global default, so there is nothing to compare.',
  'tenant_routing.parity.in_sync': 'In sync',
  'tenant_routing.parity.in_sync_body': 'Compared targets and policies across {configSkus} SKUs. No differences.',
  'tenant_routing.parity.drift': '{targets} target differences / {policies} policy differences',
  'tenant_routing.parity.drift_body_live':
    '{count} of these are broken right now. The global default is Exclusive, so resolution stops unconditionally at tier 3, and these SKUs exist only in the platform default tables — the config does not carry them. For every customer on the global default they resolve to no candidates at all: requests fail and the SKU is absent from /v1/models. Either add them to the global default config or remove them from the platform default tables.',
  'tenant_routing.parity.drift_body_latent':
    'No customer is affected by these today: every SKU involved is already covered by the global default config (coverage mode {mode}), so the platform default tables one tier below are unreachable. They become real routing behaviour when the global default is replaced or emptied — which is exactly when nobody re-reads these two tables.',
  'tenant_routing.parity.section_targets': 'Target differences',
  'tenant_routing.parity.section_policies': 'Policy differences',
  'tenant_routing.parity.column_sku': 'SKU',
  'tenant_routing.parity.column_kind': 'Difference',
  'tenant_routing.parity.column_config': 'Global default config',
  'tenant_routing.parity.column_default': 'Platform default tables',
  'tenant_routing.parity.kind_missing_in_config': 'Only in default tables',
  'tenant_routing.parity.kind_missing_in_default': 'Only in global default config',
  'tenant_routing.parity.kind_mismatched': 'Different on each side',
  'tenant_routing.deprecation.group_toggle': 'Marked deprecated ({count})',
  'tenant_routing.deprecation.badge': 'Deprecated',
  'tenant_routing.deprecation.mark': 'Mark deprecated',
  'tenant_routing.deprecation.unmark': 'Clear deprecation',
  'tenant_routing.deprecation.mark_title': 'Mark this config deprecated?',
  'tenant_routing.deprecation.mark_body':
    'It can no longer be assigned to new customers and folds into the "Marked deprecated" group. The {count} customers already bound to it are unaffected — their routing does not change. You can clear the mark at any time.',
  'tenant_routing.deprecation.unmark_title': 'Clear the deprecation mark?',
  'tenant_routing.deprecation.unmark_body': 'It returns to the config library and can be assigned to new customers again.',
  'tenant_routing.deprecation.toast_marked': 'Marked deprecated',
  'tenant_routing.deprecation.toast_unmarked': 'Deprecation mark cleared',
  'tenant_routing.deprecation.error.blocked':
    'This config is marked deprecated and can no longer be assigned to new customers. Customers already on it are unaffected; clear the mark first if you really do want to keep using it.',
  'tenant_routing.configs.coverage': 'Coverage',
  'tenant_routing.coverage.short_exclusive': 'Exclusive',
  'tenant_routing.coverage.short_inherit': 'Inherit',
  'tenant_routing.coverage.short_unknown': 'Unknown',
  'tenant_routing.coverage.exclusive_warning': 'This config is exclusive for assigned customers. A SKU without a target has no candidate and does not fall back to the platform default pool.',
  'tenant_routing.coverage.inherit_hint': 'SKUs with neither targets nor policies continue through the current platform global/default route.',
  'tenant_routing.coverage.mode_exclusive': 'Exclusive (fail closed)',
  'tenant_routing.coverage.mode_inherit': 'Inherit platform default',
  'tenant_routing.coverage.mode_label': 'Coverage mode',
  'tenant_routing.coverage.namespace': 'Coverage {covered}/{total} SKUs · {missing} new missing',
  'tenant_routing.coverage.preview_fill': 'Preview fill',
  'tenant_routing.coverage.preview_title': 'Preview SKU coverage fill',
  'tenant_routing.coverage.preview': 'Generate preview',
  'tenant_routing.coverage.apply': 'Apply fill',
  'tenant_routing.coverage.select_upstream': 'Select an explicit upstream identity',
  'tenant_routing.coverage.identity_label': 'Target upstream identity',
  'tenant_routing.coverage.affected_customers': 'Affected assigned customers: {count}',
  'tenant_routing.coverage.missing_list': 'Exact missing SKUs: {skus}',
  'tenant_routing.coverage.non_overwrite': 'Only missing targets are added. Existing weights, retry order, and disabled state are preserved.',
  'tenant_routing.coverage.add': 'Add',
  'tenant_routing.coverage.conflict': 'Conflict',
  'tenant_routing.coverage.skipped': 'Skipped',
  'tenant_routing.coverage.column_sku': 'SKU',
  'tenant_routing.coverage.column_outcome': 'Outcome',
  'tenant_routing.coverage.column_reason': 'Reason',
  'tenant_routing.coverage.column_identity': 'Selected upstream identity',
  'tenant_routing.coverage.column_mode': 'Coverage mode',
  'tenant_routing.coverage.reason_missing': 'Missing coverage',
  'tenant_routing.coverage.reason_already_exists': 'The exact target already exists',
  'tenant_routing.coverage.reason_explicit_coverage_exists': 'This SKU already has an explicit target or policy',
  'tenant_routing.coverage.reason_capability_not_live': 'The selected canonical capability is no longer live',
  'tenant_routing.coverage.applied': 'Added {count} missing targets',
  'tenant_routing.sheet.shared_warning': 'This config is assigned to {count} customers — saved edits take effect for all of them immediately.',
  'tenant_routing.pool.add_target': 'Add target',
  'tenant_routing.pool.write_blocked_targets_unknown': 'This SKU’s target pool failed to load, so which row this save would land on is unknown — it could overwrite or delete an existing binding. Refresh first.',
  'tenant_routing.pool.write_hits_unpinned_row': 'This config already carries a target with no pinned capability on this four-tuple ({channel}, {id}), so saving would land on that row — moving it onto the capability you picked and overwriting its weight, retry order and enabled flag — instead of the target you are saving. Nothing was saved. Pin the capability from that row’s own edit button instead.',
  'tenant_routing.pool.write_hits_bound_row': 'This config is already bound to that capability on this four-tuple ({channel}, {id}), so saving would land on that row and overwrite its weight, retry order and enabled flag instead of the target you are saving. Nothing was saved. Edit that row directly in the list below.',
  'tenant_routing.pool.reserve': 'Reserve',
  'tenant_routing.pool.retry_order': 'Retry order',
  'tenant_routing.pool.retry_order_auto': 'Auto',
  'tenant_routing.pool.retry_order_hint': 'Optional. Pins a fixed retry-chain position (positive integer, lower tries earlier); leave empty to rank by weight after explicitly ordered targets.',
  'tenant_routing.pool.weight_hint': 'Session placement: a session stays pinned to one target (preserving prompt cache). Weight sets how new sessions are distributed across targets — not a per-request percentage split. 0 = pure reserve that never takes first attempts.',
  'tenant_routing.pool.share_title': 'First-attempt traffic split',
  'tenant_routing.pool.share_hint': 'Share of session placements — not a per-request percentage split.',
  'tenant_routing.pool.share_none': 'No target currently takes first-attempt traffic.',
  'tenant_routing.pool.share_label': '{channel} takes {percent}% of first attempts',
  'tenant_routing.pool.share_reserve_count': '{count} pure-reserve target(s) take no first-attempt traffic.',
  // ── Routing time windows (0044) ─────────────────────────────────
  'tenant_routing.time_windows.title': 'Schedule',
  'tenant_routing.time_windows.now': 'Now {time} → {schedule}',
  'tenant_routing.time_windows.standard': 'Standard',
  'tenant_routing.time_windows.in_force': 'In force now',
  'tenant_routing.time_windows.add': 'Add window',
  'tenant_routing.time_windows.none': 'No schedule touches this SKU; the standard pool serves it all day.',
  'tenant_routing.time_windows.unloaded': 'The schedule failed to load, so the weights below are the standard pool and may not be what is in force right now. Refresh first.',
  'tenant_routing.time_windows.write_blocked': 'The schedule failed to load, so whether a new window would overlap an existing one is unknown. Refresh first.',
  'tenant_routing.time_windows.no_legs': 'This SKU has an empty target pool',
  'tenant_routing.time_windows.fallback_note': 'Any time no window covers uses the standard configuration. A window changes weight and retry order only; it never adds or removes an upstream.',
  'tenant_routing.time_windows.dialog_title_new': 'Add time window',
  'tenant_routing.time_windows.dialog_title_edit': 'Edit time window',
  'tenant_routing.time_windows.dialog_description': 'A window can override weight and retry order only: it cannot change an upstream, a channel or a capability, and it cannot switch a leg off.',
  'tenant_routing.time_windows.label': 'Name',
  'tenant_routing.time_windows.start': 'Start (local)',
  'tenant_routing.time_windows.end': 'End (local)',
  'tenant_routing.time_windows.timezone': 'Timezone (IANA)',
  'tenant_routing.time_windows.range_note': 'Evaluated in that zone’s local time; start inclusive, end exclusive.',
  'tenant_routing.time_windows.wrap_note': 'Start after end = crosses local midnight and belongs to the starting weekday (Friday 22:00–08:00 covers Saturday morning).',
  'tenant_routing.time_windows.enabled': 'Enabled',
  'tenant_routing.time_windows.legs': 'Weight overrides in this window',
  'tenant_routing.time_windows.legs_hint': 'Empty = this window leaves that column on the standard value. Both empty = the window does not touch that leg at all.',
  'tenant_routing.time_windows.base': 'standard {weight} / {retry}',
  'tenant_routing.time_windows.save': 'Save window',
  'tenant_routing.time_windows.delete': 'Delete window',
  'tenant_routing.time_windows.saved': 'Window saved.',
  'tenant_routing.time_windows.deleted': 'Window deleted; its legs are back on the standard configuration.',
  'tenant_routing.time_windows.day_mon': 'Mo',
  'tenant_routing.time_windows.day_tue': 'Tu',
  'tenant_routing.time_windows.day_wed': 'We',
  'tenant_routing.time_windows.day_thu': 'Th',
  'tenant_routing.time_windows.day_fri': 'Fr',
  'tenant_routing.time_windows.day_sat': 'Sa',
  'tenant_routing.time_windows.day_sun': 'Su',
  'tenant_routing.pool.dialog_title_new': 'Add pool target',
  'tenant_routing.pool.dialog_title_edit': 'Edit pool target',
  'tenant_routing.pool.save': 'Save target',
  'tenant_routing.pool.remove_target': 'Remove target',
  'tenant_routing.pool.remove_confirm_title': 'Remove this SKU target?',
  'tenant_routing.pool.remove_confirm_body':
    'This target stops receiving new traffic as soon as the removal succeeds. Existing SKU targets are not changed.',
  'tenant_routing.pool.remove_last_enabled_title':
    'Last enabled target for {protocols}',
  'tenant_routing.pool.remove_last_enabled_body':
    'After removal, this config has no candidate for {sku} on {protocols}. Every org resolved to this config for that SKU and protocol will fail closed; matching requests are unroutable. Currently assigned orgs: {count}.',
  'tenant_routing.pool.toast_saved': 'Pool target saved',
  'tenant_routing.pool.toast_deleted': 'Target removed from the pool',
  // SKU target pools (epic #898 P5)
  'tenant_routing.sku_pools.title': 'SKU target pools',
  'tenant_routing.sku_pools.hint': 'One unified pool per SKU (namespace/model): weight > 0 targets take first attempts via session placement, weight 0 is pure reserve. The retry chain runs retry order → weight → priority.',
  'tenant_routing.sku_pools.namespace_empty': 'No SKUs in this namespace.',
  'tenant_routing.sku_pools.empty': 'No targets in this SKU pool.',
  'tenant_routing.sku_pools.empty_pool': 'No enabled targets',
  'tenant_routing.sku_pools.coverage_ready': 'Has enabled targets',
  'tenant_routing.sku_pools.coverage_disabled': 'All targets disabled',
  'tenant_routing.sku_pools.coverage_missing': 'No targets',
  'tenant_routing.sku_pools.coverage_unknown': 'Pool not loaded',
  'tenant_routing.sku_pools.loading_pools': 'Loading each SKU’s target pool…',
  'tenant_routing.sku_pools.no_selection': 'No SKUs to select yet — add models to a namespace, then come back to configure its target pool.',
  'tenant_routing.sku_pools.missing_routes': 'SKUs missing routes',
  'tenant_routing.sku_pools.apply_namespace': 'Apply to all {count} SKUs in this namespace',
  'tenant_routing.sku_pools.capability_single_sku': 'Physical capabilities are pinned one SKU at a time. Configure sibling models separately.',
  'tenant_routing.sku_pools.toast_saved_many': 'Pool target saved to {count} SKUs',
  'tenant_routing.sku_pools.save_failed': '{sku} failed to save: {message}',
  'tenant_routing.sku_pools.stale_row_warning': 'Target saved under the new identity, but the previous row {id} is still live and taking traffic — save again to retry the cleanup, or remove {id} manually.',
  'tenant_routing.sku_pools.exclusive_impact_title': 'Exclusive configs do not fall back to the default pool — {count} assigned exclusive config(s) cannot route this SKU yet.',
  'tenant_routing.sku_pools.exclusive_impact_row': '{name} ({id}) · {scope} · {orgs} assigned orgs',
  'tenant_routing.sku_pools.exclusive_scope_global': 'global',
  'tenant_routing.sku_pools.exclusive_scope_org': 'org',
  'tenant_routing.sku_pools.exclusive_fix': 'Copy default target',
  'tenant_routing.sku_pools.exclusive_fix_unpinned': 'First default target is unpinned; copy the identity manually.',
  'tenant_routing.default.platform_channel': 'Platform Channel',
  'tenant_routing.default.upstream': 'Upstream',
  'tenant_routing.default.account': 'Account',
  'tenant_routing.default.key_bucket': 'API Key',
  'tenant_routing.default.upstream_channel': 'Upstream Channel',
  'tenant_routing.default.capability': 'Physical capability',
  'tenant_routing.default.capability_hint': 'Required. The selected capability ID and upstream model are sent exactly; the router does not guess a physical model.',
  'tenant_routing.default.capability_legacy_warning': 'This legacy target is not pinned. Select a physical capability before saving any change.',
  'tenant_routing.default.capability_stale_warning': 'The previously selected capability is no longer live for this identity. Select a current capability before saving.',
  'tenant_routing.default.capability_last_passed': 'Last passed: {time}',
  'tenant_routing.default.capability_status.passed': 'Passed',
  'tenant_routing.default.capability_status.needs_probe': 'Needs probe',
  'tenant_routing.default.capability_status.probe_failed': 'Probe failed',
  'tenant_routing.default.capability_status.missing_from_list': 'Missing from list',
  'tenant_routing.default.capability_status.mismatch': 'Mismatch',
  'tenant_routing.default.capability_status.ignored': 'Ignored',
  'tenant_routing.target.capability_legacy': 'Legacy unpinned',
  'tenant_routing.target.capability_not_live': 'Pinned capability is not live',
  'tenant_routing.default.weight': 'Weight',
  'tenant_routing.default.enabled': 'Enable this target',
  'tenant_routing.default.routability_hint':
    'Only upstream channels the gateway reports as bindable for this SKU can be saved.',
  'tenant_routing.default.routability_loading': 'Checking verified model targets...',
  'tenant_routing.default.not_routable_option': 'Not routable',
  'tenant_routing.default.not_routable_unpriced': 'Not priced',
  'tenant_routing.default.no_routable_channels':
    'No verified model target matches this API key and upstream channel for this SKU. Discover and classify the capability first.',
  'tenant_routing.default.routability_unavailable':
    'The supply-candidate read failed, so which channels are bindable is unknown — this does not mean there is no supply. Retry, and check the gateway if it keeps failing.',
  'tenant_routing.default.missing_key_channel':
    'This API key is not bound to the upstream channel for {channel}. Configure Channels & egress on the upstream account first.',
  'tenant_routing.default.open_upstream': 'Open upstream account',
  'tenant_routing.default_channels.platform_channel': 'Platform Channel',
  'tenant_routing.default_channels.upstream_channel': 'Upstream Channel',
  'tenant_routing.default_channels.weight': 'Weight',
  'tenant_routing.default_channels.policy_mode': 'Policy',
  'tenant_routing.default_channels.save_policy': 'Save policy',
  'tenant_routing.default_channels.target_count_one': '{count} target',
  'tenant_routing.default_channels.target_count_many': '{count} targets',
  'tenant_routing.policy_mode.full_chain': 'Full chain',
  'tenant_routing.policy_mode.network_only': 'Network only',
  'tenant_routing.policy_mode.same_cache_domain': 'Same cache domain',
  'tenant_routing.policy_mode.fail_fast': 'Fail fast',
  'tenant_routing.policy_mode.full_chain_hint': 'Current behavior: retry eligible upstream failures along the pool retry chain.',
  'tenant_routing.policy_mode.network_only_hint': 'Retry only network exceptions and retryable upstream 5xx responses.',
  'tenant_routing.policy_mode.same_cache_domain_hint': 'Retry only targets that share vendor, protocol, egress type, upstream, account, and key bucket.',
  'tenant_routing.policy_mode.fail_fast_hint': 'Do not automatically retry other pool targets.',
  'tenant_routing.table.title': 'Tenants with custom routes',
  'tenant_routing.table.count': '{count} tenants',
  'tenant_routing.table.org': 'Tenant',
  'tenant_routing.table.custom_count': 'Custom routes',
  'tenant_routing.table.custom_count_one': '{count} custom route',
  'tenant_routing.table.custom_count_many': '{count} custom routes',
  'tenant_routing.table.sku_examples': 'SKU examples',
  'tenant_routing.table.target': 'Target',
  'tenant_routing.table.status': 'Status',
  'tenant_routing.table.updated': 'Updated',
  'tenant_routing.table.actions': 'Actions',
  'tenant_routing.table.open_detail': 'Open detail',
  'tenant_routing.table.empty': 'No tenants have custom routes',
  'tenant_routing.override.org': 'Org ID',
  'tenant_routing.override.filter': 'Filter by tenant',
  // Routing circuit breaker (epic #886 PR-6)
  'tenant_routing.probe.settings.title': 'Target probes',
  'tenant_routing.probe.card_title': 'Target probes',
  'tenant_routing.probe.settings.hint': 'Disable converges within 30s per isolate, plus CN replica lag.',
  'tenant_routing.probe.summary': '{eligible} eligible legs · {tripped} tripped (probe {probe} / chain {chain} / Jev {jev}) · {backoff} in backoff · next probe {at}',
  'tenant_routing.probe.summary_hint': 'Edit probe switches and intervals on the SKU table below. A tripped leg is retired from first pick and stays at the tail of the retry chain.',
  'tenant_routing.probe.settings.enabled': 'Probe',
  'tenant_routing.probe.settings.interval': 'Interval (minutes)',
  'tenant_routing.probe.settings.interval_invalid': 'Interval must be an integer from 1 to 1440',
  'tenant_routing.probe.settings.save': 'Save interval',
  'tenant_routing.probe.settings.saved': 'Probe settings saved',
  'tenant_routing.probe.tripped': 'Retired from first pick',
  'tenant_routing.probe.ok': 'Last probe {at}',
  'tenant_routing.probe.next': 'Next probe {at}',
  'tenant_routing.probe.backoff': 'Backoff level {level}',
  'tenant_routing.probe.tripped_hint': 'Legs retired from first pick (including SKUs whose probes are now off). They stay out of the weighted draw and keep their own order at the tail of the retry chain. Five clean chain hits, or a clean probe round, put them back on first pick. Clearing restores first pick at once.',
  'tenant_routing.probe.col_sku': 'SKU',
  'tenant_routing.probe.col_leg': 'Leg (upstream · channel)',
  'tenant_routing.probe.col_reason': 'Tripped by',
  'tenant_routing.probe.col_last_error': 'Last error',
  'tenant_routing.probe.col_tripped_at': 'Tripped at',
  'tenant_routing.probe.col_next': 'Next probe',
  'tenant_routing.probe.col_actions': 'Actions',
  'tenant_routing.probe.reason.probe': 'probe',
  'tenant_routing.probe.reason.chain': 'chain evidence',
  'tenant_routing.probe.reason.jev': 'Jev',
  'tenant_routing.probe.reset': 'Clear trip',
  'tenant_routing.probe.reset_done': 'Cleared; the leg is back in selection',
  'tenant_routing.probe.probe_now': 'Probe now',
  'tenant_routing.probe.probe_now_recovered': 'Probe passed; leg recovered',
  'tenant_routing.probe.probe_now_still_tripped': 'Probe still failing; the leg stays retired from first pick',
  'tenant_routing.probe.disable_target': 'Disable target',
  'tenant_routing.probe.disable_title': 'Disable this target?',
  'tenant_routing.probe.disable_body': 'Disabling removes the leg from first pick and from the retry chain until it is re-enabled. Retired-from-first-pick is separate and auto-recovers after clean chain hits or a clean probe round.',
  'tenant_routing.probe.disable_confirm': 'Disable target',
  'tenant_routing.probe.disable_done': 'Target disabled',
  'tenant_routing.probe.disable_missing': 'No routing target found for this leg; nothing was changed',
  'tenant_routing.disabled_note.label': 'Removal note (optional)',
  'tenant_routing.disabled_note.placeholder': 'e.g. out of balance, waiting for top-up',
  'tenant_routing.disabled_note.count': '{count}/{max} chars',
  'tenant_routing.disabled_note.none': 'No note',
  'tenant_routing.disabled_note.tip': 'Manually removed · {detail}',
  'tenant_routing.disabled_note.edit': 'Edit removal note (opens the target editor)',
  'tenant_routing.ago.just_now': 'just now',
  'tenant_routing.ago.minutes': '{n} min ago',
  'tenant_routing.ago.hours': '{n} h ago',
  'tenant_routing.ago.days': '{n} d ago',
  'probe_try.button': 'Try probe (no side effects)',
  'probe_try.tip': 'Try probe (no side effects): pick a probe body and fire one shot to see whether it gets through. Does not count toward probe state, never removes or restores, never touches routing; the shot is logged as probe usage.',
  'probe_try.title': 'Try probe',
  'probe_try.description': 'One shot to see whether it gets through: no probe state, no removal or restore, no routing change. The shot is logged as probe usage.',
  'probe_try.send': 'Send',
  'probe_try.result': 'HTTP {code} · {ms} ms · {tokens} output tokens',
  'tenant_routing.breaker.strip_label': 'Breakers',
  'tenant_routing.breaker.chip_open': 'Open · {seconds}s left',
  'tenant_routing.breaker.chip_half_open': 'Probing',
  'tenant_routing.breaker.chip_forced': 'Forced out · until {until}',
  'tenant_routing.breaker.state.open': 'Open',
  'tenant_routing.breaker.state.half_open': 'Probing',
  'tenant_routing.breaker.state.forced': 'Forced out',
  'tenant_routing.breaker.reset': 'Recover now',
  'tenant_routing.breaker.reset_confirm_title': 'Recover this target now?',
  'tenant_routing.breaker.reset_confirm_body': 'After recovery the target immediately returns to its configured retry-chain position and takes traffic again; if the upstream is still unstable, passive breaking may trip it again.',
  'tenant_routing.breaker.trip': 'Force out',
  'tenant_routing.breaker.trip_title': 'Force out target',
  'tenant_routing.breaker.trip_body': 'While forced out, this target moves to the tail of every retry chain that references it and stops taking first-attempt traffic. It recovers automatically at the deadline, and you can "Recover now" at any time. Applies globally to every channel and config that uses this key.',
  'tenant_routing.breaker.trip_duration': 'Duration',
  'tenant_routing.breaker.trip_duration.30m': '30 minutes',
  'tenant_routing.breaker.trip_duration.1h': '1 hour',
  'tenant_routing.breaker.trip_duration.6h': '6 hours',
  'tenant_routing.breaker.trip_duration.24h': '24 hours',
  'tenant_routing.breaker.trip_until_preview': 'Recovers at: {until}',
  'tenant_routing.breaker.trip_save': 'Force out',
  'tenant_routing.breaker.drawer_title': 'Routing breakers',
  'tenant_routing.breaker.drawer_hint': 'Passive breaking with half-open recovery: open targets are demoted to the retry-chain tail, never removed.',
  'tenant_routing.breaker.table.identity': 'Target',
  'tenant_routing.breaker.table.state': 'State',
  'tenant_routing.breaker.table.reason': 'Last error',
  'tenant_routing.breaker.table.opened': 'Opened at',
  'tenant_routing.breaker.table.actions': 'Actions',
  'tenant_routing.breaker.empty': 'No targets are currently broken.',
  'tenant_routing.breaker.disabled': 'The routing breaker is not enabled (no storage binding configured).',
  'tenant_routing.breaker.toast.reset': 'Target recovered',
  'tenant_routing.breaker.toast.tripped': 'Target forced out',
  'tenant_routing.breaker.reason_none': '—',
  'tenant_routing.breaker.sheet_summary_one': 'Breakers · {count} target affected',
  'tenant_routing.breaker.sheet_summary_many': 'Breakers · {count} targets affected',
  'tenant_routing.breaker.open_panel': 'Open breaker panel',
  // Routing breaker threshold settings (epic #886 settings PR)
  'tenant_routing.breaker.settings.open': 'Threshold settings',
  'tenant_routing.breaker.settings.back': 'Back to list',
  'tenant_routing.breaker.settings.title': 'Breaker threshold settings',
  'tenant_routing.breaker.settings.hint': 'Empty fields follow the env var or built-in default; filled fields become customizations applied to every gateway instance.',
  'tenant_routing.breaker.settings.loading': 'Loading settings…',
  'tenant_routing.breaker.settings.load_failed': 'Failed to load settings. Please retry.',
  'tenant_routing.breaker.settings.kill_switch': 'The breaker is force-disabled by the environment (BREAKER_ENABLED=false); settings here take no effect until the env override is lifted.',
  'tenant_routing.breaker.settings.enabled_label': 'Enable breaker',
  'tenant_routing.breaker.settings.enabled_desc': 'When off, persistently failing targets are never auto-demoted; targets are always attempted in configured order.',
  'tenant_routing.breaker.settings.source.default': 'Default',
  'tenant_routing.breaker.settings.source.env': 'Env var',
  'tenant_routing.breaker.settings.source.custom': 'Customized',
  'tenant_routing.breaker.settings.effective': 'Effective: {value}',
  'tenant_routing.breaker.settings.restore': 'Restore default',
  'tenant_routing.breaker.settings.save': 'Save settings',
  'tenant_routing.breaker.settings.save_hint': 'Takes effect on every instance within ~30 seconds of saving.',
  'tenant_routing.breaker.settings.toast.saved': 'Threshold settings saved',
  'tenant_routing.breaker.settings.toast.restored': 'Restored to default; effective within ~30 seconds',
  'tenant_routing.breaker.settings.invalid_int': 'Enter a positive integer',
  'tenant_routing.breaker.settings.invalid_rate': 'Enter a number greater than 0 and at most 1',
  'tenant_routing.breaker.settings.field.consecutive_failures': 'Consecutive failure threshold',
  'tenant_routing.breaker.settings.field.consecutive_failures_desc': 'Trip after N consecutive retryable failures on the same target.',
  'tenant_routing.breaker.settings.field.window_seconds': 'Window length (s)',
  'tenant_routing.breaker.settings.field.window_seconds_desc': 'Sliding-window length: successes and failures over the last X seconds feed the failure-rate check.',
  'tenant_routing.breaker.settings.field.window_min_failures': 'Min failures in window',
  'tenant_routing.breaker.settings.field.window_min_failures_desc': 'The failure-rate condition can only trip after Z failures inside the window, so low traffic never false-trips.',
  'tenant_routing.breaker.settings.field.window_failure_rate': 'Window failure rate',
  'tenant_routing.breaker.settings.field.window_failure_rate_desc': 'Trip when the in-window failure rate exceeds Y (0–1, e.g. 0.5 = 50%) and the minimum failure count is met.',
  'tenant_routing.breaker.settings.field.cooldown_base_seconds': 'Cooldown base (s)',
  'tenant_routing.breaker.settings.field.cooldown_base_seconds_desc': 'After tripping, cool down B seconds before a trial; doubles on each repeated failure.',
  'tenant_routing.breaker.settings.field.cooldown_cap_seconds': 'Cooldown cap (s)',
  'tenant_routing.breaker.settings.field.cooldown_cap_seconds_desc': 'Upper bound for the doubling cooldown; must be at least the cooldown base.',
  'tenant_routing.breaker.settings.field.entry_ttl_seconds': 'Auto-recover after (s)',
  'tenant_routing.breaker.settings.field.entry_ttl_seconds_desc': 'An entry with no new failures for N seconds expires and the target reads as recovered.',
  'tenant_routing.breaker.settings.field.half_open_trial_ttl_seconds': 'Half-open trial TTL (s)',
  'tenant_routing.breaker.settings.field.half_open_trial_ttl_seconds_desc': 'After the cooldown one real request probes the target; this is the claim TTL of that single trial slot.',
  // Load-aware routing (#2310)
  'tenant_routing.load.strip_label': 'Load-aware routing',
  'tenant_routing.load.strip.events': 'Events',
  'tenant_routing.load.strip.l1': 'L1',
  'tenant_routing.load.strip.l2': 'L2',
  'tenant_routing.load.strip.l3': 'L3',
  'tenant_routing.load.strip.l4': 'L4',
  'tenant_routing.load.strip.warn': 'Needs attention',
  'tenant_routing.load.drawer_hint': 'Switch load-aware routing by layer, lane, or SKU. Runtime view only confirms it is moving after you turn a layer on.',
  'tenant_routing.load.view.settings': 'Settings',
  'tenant_routing.load.view.scope': 'By lane / SKU',
  'tenant_routing.load.view.runtime': 'Runtime',
  'tenant_routing.load.settings.title': 'Load-aware routing settings',
  'tenant_routing.load.settings.hint': 'Takes effect on every gateway instance within ~30 seconds of saving.',
  'tenant_routing.load.settings.order_hint': 'Recommended order: events → L1 tenant limits → L2 and L3 (including inferred caps) → L4 and first-byte timeout.',
  'tenant_routing.load.settings.loading': 'Loading settings…',
  'tenant_routing.load.settings.load_failed': 'Failed to load settings. Please retry.',
  'tenant_routing.load.settings.save': 'Save settings',
  'tenant_routing.load.settings.save_hint': 'Takes effect on every instance within ~30 seconds of saving.',
  'tenant_routing.load.settings.toast.saved': 'Load-aware settings saved',
  'tenant_routing.load.settings.toast.restored': 'Restored to default; effective within ~30 seconds',
  'tenant_routing.load.settings.restore': 'Restore default',
  'tenant_routing.load.settings.source.default': 'Default',
  'tenant_routing.load.settings.source.env': 'Env var',
  'tenant_routing.load.settings.source.custom': 'Customized',
  'tenant_routing.load.settings.effective': 'Effective: {value}',
  'tenant_routing.load.settings.invalid_int': 'Enter a positive integer',
  'tenant_routing.load.settings.invalid_decimal': 'Enter a number greater than 0 and at most the field maximum',
  'tenant_routing.load.settings.advanced': 'Advanced',
  'tenant_routing.load.settings.enable_l2_with_l3': 'Turn L2 on as well',
  'tenant_routing.load.settings.warning.l3_without_l2': 'L3 is on while L2 is off, so adaptive shedding has no target usage to track.',
  'tenant_routing.load.settings.kill_switch.l1': 'L1 is force-disabled by the environment (LOAD_L1_ENABLED=false); settings here take no effect until the env override is lifted.',
  'tenant_routing.load.settings.kill_switch.l2': 'L2 is force-disabled by the environment (LOAD_L2_ENABLED=false); settings here take no effect until the env override is lifted.',
  'tenant_routing.load.settings.kill_switch.l3': 'L3 is force-disabled by the environment (LOAD_L3_ENABLED=false); settings here take no effect until the env override is lifted.',
  'tenant_routing.load.settings.kill_switch.l4': 'L4 is force-disabled by the environment (LOAD_L4_ENABLED=false); settings here take no effect until the env override is lifted.',
  'tenant_routing.load.settings.group.events': 'Events',
  'tenant_routing.load.settings.group.l1': 'L1 tenant limits',
  'tenant_routing.load.settings.group.l2': 'L2 target usage',
  'tenant_routing.load.settings.group.l3': 'L3 adaptive shedding',
  'tenant_routing.load.settings.group.l4': 'L4 cache stickiness',
  'tenant_routing.load.settings.group.timeout': 'First-byte timeout',
  'tenant_routing.load.settings.l1_limits_hint': 'Configure limits on the SKU detail page and the customer detail page.',
  'tenant_routing.load.settings.timeout_enable': 'Enable',
  'tenant_routing.load.settings.timeout_enable_desc': 'When on, each attempt waits for a first byte up to the base value; when off, there is no first-byte timeout.',
  'tenant_routing.load.settings.field.events_enabled': 'Enable events',
  'tenant_routing.load.settings.field.events_enabled_desc': 'Record load-aware events so patrol and the runtime view can confirm movement.',
  'tenant_routing.load.settings.field.l1_enabled': 'Enable L1',
  'tenant_routing.load.settings.field.l1_enabled_desc': 'Reject over-limit requests using SKU defaults and tenant overrides.',
  'tenant_routing.load.settings.field.l2_enabled': 'Enable L2',
  'tenant_routing.load.settings.field.l2_enabled_desc': 'Track per-target usage against configured or observed caps.',
  'tenant_routing.load.settings.field.l3_enabled': 'Enable L3',
  'tenant_routing.load.settings.field.l3_enabled_desc': 'Shed adaptively from target usage. L2 must also be on.',
  'tenant_routing.load.settings.field.l4_enabled': 'Enable L4',
  'tenant_routing.load.settings.field.l4_enabled_desc': 'Session stickiness: keep a session on the same target when possible.',
  'tenant_routing.load.settings.field.l2_observed_caps_enabled': 'Read upstream rate-limit headers',
  'tenant_routing.load.settings.field.l2_observed_caps_enabled_desc': 'Treat upstream rate-limit headers as observed caps.',
  'tenant_routing.load.settings.field.l3_inferred_caps_enabled': 'Inferred pool caps',
  'tenant_routing.load.settings.field.l3_inferred_caps_enabled_desc': 'Use self-learned RPM/TPM caps when no configured or observed cap exists.',
  'tenant_routing.load.settings.field.session_rehome_enabled': 'Session re-homing',
  'tenant_routing.load.settings.field.session_rehome_enabled_desc': 'On pool upstreams, move a session that keeps getting 429s onto the fallback. Other sessions stay on the pool.',
  'tenant_routing.load.settings.field.session_rehome_after_429': 'Re-home after 429s',
  'tenant_routing.load.settings.field.session_rehome_after_429_desc': 'Consecutive pool 429s before pinning the session to the fallback. Empty = 2.',
  'tenant_routing.load.settings.field.session_rehome_ttl_seconds': 'Re-home TTL (seconds)',
  'tenant_routing.load.settings.field.session_rehome_ttl_seconds_desc': 'How long the pin lasts before normal L4 stickiness resumes. Empty = 120.',
  'tenant_routing.load.settings.kill_switch.session_rehome': 'Session re-homing is process-killed (LOAD_SESSION_REHOME_ENABLED=false).',
  'tenant_routing.load.settings.field.affinity_conversation_enabled': 'Stick by conversation prefix',
  'tenant_routing.load.settings.field.affinity_conversation_enabled_desc': 'When there is no explicit session hint, stick by conversation prefix instead of the account-wide default session.',
  'tenant_routing.load.settings.field.shed_step': 'Shed step',
  'tenant_routing.load.settings.field.shed_step_desc': 'Additive step L3 uses each time it raises shed. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.shed_quiet_seconds': 'Quiet window (s)',
  'tenant_routing.load.settings.field.shed_quiet_seconds_desc': 'Seconds of quiet before L3 starts multiplicative decay. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.shed_decay': 'Shed decay',
  'tenant_routing.load.settings.field.shed_decay_desc': 'Multiplicative decay factor after the L3 quiet window. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.half_open_trials': 'Half-open trials',
  'tenant_routing.load.settings.field.half_open_trials_desc': 'Trial quota L3 admits while half-open. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.shed_target_ema_alpha': 'Target-tracking smooth',
  'tenant_routing.load.settings.field.shed_target_ema_alpha_desc': 'L3 target-tracking smooth factor per control tick. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.shed_tick_seconds': 'Control tick (s)',
  'tenant_routing.load.settings.field.shed_tick_seconds_desc': 'Interval of the L3 control law. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.shed_warm_threshold': 'Warm-session shed bar',
  'tenant_routing.load.settings.field.shed_warm_threshold_desc': 'Shed bar at which L4 warm sessions start yielding. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.shed_warm_429_ratio': 'Warm-session 429 ratio',
  'tenant_routing.load.settings.field.shed_warm_429_ratio_desc': 'Recent 429 ratio that makes an L4 warm hold yield. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.session_bucket_rpm': 'Session-bucket RPM',
  'tenant_routing.load.settings.field.session_bucket_rpm_desc': 'Batch-session RPM at which L4 splits the affinity key. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.pool_key_rpm_ceiling': 'Pool-key RPM ceiling',
  'tenant_routing.load.settings.field.pool_key_rpm_ceiling_desc': 'Per-key 60s ceiling on session_pool targets before the sub-key and wire session id rotate. Empty restores the built-in default (120).',
  'tenant_routing.load.settings.group.lifecycle_sampling': 'Lifecycle timeline sampling',
  'tenant_routing.load.settings.group.lifecycle_sampling_hint':
    'Sampling only drops timelines (event batches); per-request metrics are never sampled. 1 or empty is today\'s keep-all behaviour.',
  'tenant_routing.load.settings.empty.keep_all': 'keep all (=1)',
  'tenant_routing.load.settings.empty.off': 'off',
  'tenant_routing.load.settings.field.lifecycle_sample_p_jp': 'JP gateway sample rate',
  'tenant_routing.load.settings.field.lifecycle_sample_p_jp_desc': 'Fraction of JP-gateway timelines to keep. Empty = keep all (=1).',
  'tenant_routing.load.settings.field.lifecycle_sample_p_cn': 'CN gateway sample rate',
  'tenant_routing.load.settings.field.lifecycle_sample_p_cn_desc': 'Fraction of CN-gateway timelines to keep. Empty = keep all (=1).',
  'tenant_routing.load.settings.field.lifecycle_keep_slow_ms': 'Slow-request keep threshold (ms)',
  'tenant_routing.load.settings.field.lifecycle_keep_slow_ms_desc':
    'Successful requests whose total duration is ≥ this threshold keep their timeline at 100%.',
  'tenant_routing.load.settings.field.lifecycle_keep_org_ids': 'Orgs whose timelines are always kept',
  'tenant_routing.load.settings.field.lifecycle_keep_org_ids_desc':
    'Failures, probes, and breaker-reshuffle / multi-attempt requests are already kept unconditionally; list only orgs you are debugging.',
  'request_lifecycle.timeline.unsampled': 'Not sampled (p={p})',
  'request_lifecycle.timeline.kept': 'Kept (p={p})',
  'tenant_routing.load.settings.field.sticky_escape_ttft_ms': 'Sticky-escape first byte (ms)',
  'tenant_routing.load.settings.field.sticky_escape_ttft_ms_desc': 'First-byte bar that lets L4 leave a sticky target. Empty turns this off.',
  'tenant_routing.load.settings.field.sticky_escape_error_rate': 'Sticky-escape error rate',
  'tenant_routing.load.settings.field.sticky_escape_error_rate_desc': 'Error-rate bar that lets L4 leave a sticky target. Empty turns this off.',
  'tenant_routing.load.settings.field.attempt_first_byte_timeout_ms': 'First-byte timeout base (ms)',
  'tenant_routing.load.settings.field.attempt_first_byte_timeout_ms_desc': 'Base wait for a first byte on each attempt. Empty turns this off.',
  'tenant_routing.load.settings.field.attempt_timeout_ttft_multiplier': 'Timeout vs smooth multiple',
  'tenant_routing.load.settings.field.attempt_timeout_ttft_multiplier_desc': 'Adaptive first-byte timeout = this multiple × the target first-byte smooth. Empty restores the built-in default.',
  'tenant_routing.load.settings.field.attempt_timeout_max_multiplier': 'Timeout ceiling multiple',
  'tenant_routing.load.settings.field.attempt_timeout_max_multiplier_desc': 'Ceiling of the adaptive first-byte timeout as a multiple of the base. Empty restores the built-in default.',
  'tenant_routing.load.runtime.title': 'Load-aware runtime',
  'tenant_routing.load.runtime.loading': 'Loading runtime…',
  'tenant_routing.load.runtime.empty': 'No target usage yet. This table moves after you turn layers on.',
  'tenant_routing.load.runtime.table.identity': 'Target',
  'tenant_routing.load.runtime.table.rpm': 'RPM',
  'tenant_routing.load.runtime.table.tpm': 'TPM',
  'tenant_routing.load.runtime.table.cap': 'Cap',
  'tenant_routing.load.runtime.table.shed': 'Shed',
  'tenant_routing.load.runtime.table.breaker': 'Breaker',
  'tenant_routing.load.runtime.table.ttft': 'First-byte smooth',
  'tenant_routing.load.runtime.table.rehomed': 'Re-homed sessions',
  'tenant_routing.load.runtime.table.r429_rehomed': 'Re-homed 429s',
  'tenant_routing.load.runtime.cap_source.configured': 'Configured',
  'tenant_routing.load.runtime.cap_source.observed': 'Observed',
  'tenant_routing.load.runtime.cap_source.inferred': 'Inferred',
  'tenant_routing.load.runtime.cap_source.min': 'Min',
  'tenant_routing.load.runtime.confidence': 'confidence {value}',
  'tenant_routing.load.runtime.unlimited': 'Unlimited',
  'tenant_routing.load.runtime.breaker.closed': 'Closed',
  'tenant_routing.load.runtime.breaker.open': 'Open',
  'tenant_routing.load.runtime.breaker.half_open': 'Half-open',
  'tenant_routing.load.runtime.breaker.forced': 'Forced out',
  'tenant_routing.load.runtime.table.layers': 'Effective layers',
  'tenant_routing.load.runtime.layers.sku': 'SKU',
  'tenant_routing.load.runtime.layers.namespace': 'Lane',
  'tenant_routing.load.runtime.layers.global': 'Global',
  'tenant_routing.load.runtime.layers.unknown': 'Unknown (old snapshot)',
  'tenant_routing.load.scope.title': 'By lane / SKU',
  'tenant_routing.load.scope.hint': 'Turn load-aware routing on per lane, then narrow or shut it with SKU exceptions. Empty fields follow the parent: SKU → lane → global. An environment kill switch still wins.',
  'tenant_routing.load.scope.loading': 'Loading scope settings…',
  'tenant_routing.load.scope.load_failed': 'Failed to load scope settings. Please retry.',
  'tenant_routing.load.scope.lanes_title': 'Lane shortcuts',
  'tenant_routing.load.scope.lanes_hint': 'One row per platform lane. One-click enable turns on load-aware layers for that lane; each field can still be set to follow, on, or off.',
  'tenant_routing.load.scope.lanes_empty': 'No lanes yet.',
  'tenant_routing.load.scope.skus_title': 'SKU exceptions',
  'tenant_routing.load.scope.skus_hint': 'Only SKUs with a stored overlay appear here. Unlisted SKUs follow their lane or the global default.',
  'tenant_routing.load.scope.skus_empty': 'No SKU exceptions yet.',
  'tenant_routing.load.scope.add_sku': 'Add SKU exception',
  'tenant_routing.load.scope.add_sku_title': 'Add SKU exception',
  'tenant_routing.load.scope.add_sku_pick': 'Select a SKU',
  'tenant_routing.load.scope.add_sku_search': 'Filter by name or id',
  'tenant_routing.load.scope.add_sku_confirm': 'Add',
  'tenant_routing.load.scope.add_sku_none': 'No SKUs left to add.',
  'tenant_routing.load.scope.enable_lane': 'Turn load-aware on',
  'tenant_routing.load.scope.enable_lane_title': 'Turn load-aware routing on for this lane?',
  'tenant_routing.load.scope.enable_lane_body': 'These fields will be written:',
  'tenant_routing.load.scope.enable_lane_confirm': 'Turn on',
  'tenant_routing.load.scope.restore': 'Follow global again',
  'tenant_routing.load.scope.restore_title': 'Follow global again?',
  'tenant_routing.load.scope.restore_body': 'Deletes this overlay so the parent layer applies.',
  'tenant_routing.load.scope.restore_confirm': 'Restore',
  'tenant_routing.load.scope.tri.follow': 'Follow',
  'tenant_routing.load.scope.tri.on': 'On',
  'tenant_routing.load.scope.tri.off': 'Off',
  'tenant_routing.load.scope.effective': 'Effective',
  'tenant_routing.load.scope.source.sku': 'SKU',
  'tenant_routing.load.scope.source.namespace': 'Lane',
  'tenant_routing.load.scope.source.global': 'Global',
  'tenant_routing.load.scope.toast.saved': 'Scope settings saved',
  'tenant_routing.load.scope.toast.enabled': 'Load-aware routing turned on for this lane',
  'tenant_routing.load.scope.toast.restored': 'Restored to follow the parent',
  'tenant_routing.load.scope.toast.sku_added': 'SKU exception added',
  'tenant_routing.target.rpm_cap': 'RPM cap',
  'tenant_routing.target.tpm_cap': 'TPM cap',
  'tenant_routing.target.rpm_cap_hint': 'Optional. Empty leaves the cap to observed or inferred values.',
  'tenant_routing.target.tpm_cap_hint': 'Optional. Empty leaves the cap to observed or inferred values.',
  'tenant_routing.error.cap': 'Cap must be a positive integer, or empty for none.',
  'tenant_routing.smart.mode': 'Routing mode',
  'tenant_routing.smart.mode.manual': 'Manual',
  'tenant_routing.smart.mode.smart': 'Smart',
  'tenant_routing.smart.role': 'Role',
  'tenant_routing.smart.role.self_pool': 'Self pool',
  'tenant_routing.smart.role.external': 'External',
  'tenant_routing.smart.role.reserve': 'Reserve',
  'tenant_routing.smart.role.unknown': '—',
  'tenant_routing.smart.cost_multiplier': 'Cost multiplier',
  'tenant_routing.smart.missing_cost': 'Missing cost row',
  'tenant_routing.smart.no_runtime': 'no runtime state yet',
  'tenant_routing.smart.initial_score': 'Initial score',
  'tenant_routing.smart.initial_score.empty': '—',
  'tenant_routing.smart.initial_score_hint': '0–1, optional. Empty means no prior.',
  'tenant_routing.smart.readonly': 'Read-only',
  'tenant_routing.smart.settings_loading': 'Loading routing mode…',
  'tenant_routing.smart.settings_error': 'Failed to load routing mode.',
  'tenant_routing.smart.restore_default': 'Clear to default',
  'tenant_routing.smart.error.score': 'Initial score must be between 0 and 1, or empty.',
  'tenant_routing.smart.enabled': 'Enabled',
  'tenant_routing.smart.exploration_budget': 'Exploration budget',
  'tenant_routing.smart.exploration_budget_hint': 'USD per day.',
  'tenant_routing.smart.refire_budget': 'Refire budget',
  'tenant_routing.smart.refire_budget_hint': 'Per-target refire share of dials per minute (%).',
  'tenant_routing.smart.retire_after_days': 'Retire after days',
  'tenant_routing.smart.retire_after_days_hint': 'Days the posterior must stay below the threshold.',
  'tenant_routing.smart.retire_below': 'Retire threshold',
  'tenant_routing.smart.retire_below_hint': 'Posterior mean below this value for the retire window auto-retires the target (0–1).',
  'tenant_routing.smart.save_settings': 'Save smart settings',
  'tenant_routing.smart.stability': 'Stability',
  'tenant_routing.smart.stability_hint': 'Weight on success rate. 100 = full.',
  'tenant_routing.smart.economy': 'Economy',
  'tenant_routing.smart.economy_hint': 'Weight on procurement cost. 0 ignores cost.',
  'tenant_routing.smart.speed': 'Speed',
  'tenant_routing.smart.speed_hint': 'Weight on slow TTFT (over 2s). 0 ignores latency.',
  'tenant_routing.smart.slider_default': 'default',
  'tenant_routing.smart.error.slider': 'Must be an integer 0–100, or empty for default.',
  'tenant_routing.smart.toast_mode': 'Routing mode updated',
  'tenant_routing.smart.toast_settings': 'Smart routing settings saved',
  'tenant_routing.smart.error.number': 'Must be a non-negative number.',
  'tenant_routing.smart.error.percent': 'Must be a percentage between 0 and 100.',
  'tenant_routing.smart.error.days': 'Must be a positive integer number of days.',
  'tenant_routing.smart.error.threshold': 'Must be a posterior threshold between 0 and 1.',
  'tenant_routing.smart.overview.title': 'Learning state',
  'tenant_routing.smart.overview.dials': 'Dials 1m/1h/24h',
  'tenant_routing.smart.overview.share': 'Share 1m/1h/24h',
  'tenant_routing.smart.overview.first_dial': 'First-dial success 1m/1h/24h',
  'tenant_routing.smart.overview.refire_saved': 'Refire save rate',
  'tenant_routing.smart.overview.ttft': 'TTFT p50/p95',
  'tenant_routing.smart.overview.cost_per_1k': 'Cost per 1k tokens',
  'tenant_routing.smart.overview.posterior': 'Posterior',
  'tenant_routing.smart.overview.probe': 'Probe',
  'tenant_routing.smart.overview.state': 'State',
  'tenant_routing.smart.state.warming': 'Warming',
  'tenant_routing.smart.state.serving': 'Serving',
  'tenant_routing.smart.state.quarantined': 'Quarantined',
  'tenant_routing.smart.state.half_open': 'Half-open',
  'tenant_routing.smart.state.retired': 'Retired',
  'tenant_routing.smart.warning.self_pool_unprobeable':
    'Self-pool target cannot be probed; recovery uses trickle traffic.',
  'tenant_routing.smart.overview.empty': 'No learning samples yet.',
  'tenant_routing.smart.overview.loading': 'Loading learning state…',
  'tenant_routing.smart.overview.error': 'Failed to load learning state.',
  'tenant_routing.smart.overview.stale': 'Data may be stale',
  'tenant_routing.smart.overview.host': 'host {host}',
  'tenant_routing.smart.overview.default_pool': 'Default pool',
  'tenant_routing.smart.overview.config': '{name} ({id})',
  'tenant_routing.smart.overview.folded':
    '{configs} other configs ({targets} targets) are hidden — only the system default, default pool, and org-assigned configs are listed.',
  'tenant_routing.smart.chart.title': 'Share over time',
  'tenant_routing.smart.chart.empty': 'No per-minute decision samples yet.',
  'tenant_routing.smart.reset': 'Reset learning',
  'tenant_routing.smart.reset_title': 'Reset learning state for this SKU?',
  'tenant_routing.smart.reset_body': 'Clears this SKU’s runtime posterior and share memory. Mode, budgets, and target config are kept.',
  'tenant_routing.smart.reset_confirm': 'Reset',
  'tenant_routing.smart.reset_type_label': 'Type this SKU name to confirm',
  'tenant_routing.smart.toast_reset': 'Learning state reset',
  'tenant_routing.smart.probe.ok': 'OK',
  'tenant_routing.smart.probe.fail': 'Failed',
  // Route check drawer
  'tenant_routing.effective.title': 'Effective Route Check',
  'tenant_routing.effective.body': 'Secondary inspector for a single org + SKU.',
  'tenant_routing.effective.check': 'Check',
  'tenant_routing.effective.sku_chain_hint': 'Checks only the SKU partition: org-assigned config → SKU target pool. Retryable failures continue by retry order, weight, then priority.',
  'tenant_routing.effective.no_sku_target': 'No available target in this SKU partition',
  'tenant_routing.effective.route_config_ref': '{name} ({id})',
  'tenant_routing.field.sku': 'SKU',
  'tenant_routing.field.protocol': 'Protocol',
  'tenant_routing.source.emergency_override': 'Emergency override — every tenant',
  'tenant_routing.source.global_default': 'Global default singleton',
  'tenant_routing.source.legacy_fallback': 'Legacy fallback binding',
  'tenant_routing.effective.gap_uncovered_by_emergency_override':
    'The engaged emergency override does not cover {sku}. Tier 0 is fail-closed, so this SKU is currently failing for every tenant — widen {config} or disengage the override.',
  'tenant_routing.effective.gap_uncovered_by_assigned_config':
    'The assigned config {config} does not cover {sku}, and it is exclusive, so this SKU has no candidate for the affected customers.',
  'tenant_routing.source.route_config': 'Route config',
  'tenant_routing.source.org_sku': 'Org SKU route',
  'tenant_routing.source.org_override': 'Org SKU override',
  'tenant_routing.source.sku_default': 'SKU default route',
  'tenant_routing.source.none': 'No route',
  // Toasts + errors
  'tenant_routing.toast.action.view_upstream': 'View upstream',
  'tenant_routing.toast.policy_saved': 'SKU policy saved',
  'tenant_routing.error.default_required': 'Select platform channel, upstream, account, API key, and upstream channel.',
  'tenant_routing.error.default_weight': 'Weight must be a non-negative integer.',
  'tenant_routing.error.retry_order': 'Retry order must be a positive integer or left empty.',
  'tenant_routing.error.default_routability_pending': 'Wait for verified target checks to finish.',
  'tenant_routing.error.default_not_routable':
    'Choose an upstream channel with a live capability for this SKU target.',
  'tenant_routing.error.effective_required': 'Enter an org and select a SKU and protocol.',

  // ── Dashboard (Mission Control) ─────────────────────────────────
  'dashboard.title': 'Mission Control',
  'dashboard.subtitle': 'Router traffic grouped by upstream',
  'dashboard.window.label': 'Window',
  'dashboard.window.7d': 'Last 7 days',
  'dashboard.window.30d': 'Last 30 days',
  'dashboard.window.90d': 'Last 90 days',
  'dashboard.stat.upstreams.label': 'Active Upstreams',
  'dashboard.stat.upstreams.delta': 'Seen in last {days} days',
  'dashboard.stat.requests.label': 'Total Requests',
  'dashboard.stat.requests.delta': 'Sum across {days}-day window',
  'dashboard.stat.failure_rate.label': 'Failure Rate',
  'dashboard.stat.failure_rate.delta': 'HTTP 4xx/5xx / total',
  'dashboard.stat.cost.label': 'Spend',
  'dashboard.stat.cost.delta': 'Summed gateway cost',
  'dashboard.loading_aria': 'Loading router stats…',
  'dashboard.error.title': 'Failed to load router stats',
  'dashboard.empty.title': 'No traffic in this window',
  'dashboard.empty.body_prefix': 'No rows in ',
  'dashboard.empty.body_suffix':
    ' landed in the last {days} days. Widen the window or check that the gateway is receiving requests.',
  'dashboard.column.upstream': 'Upstream',
  'dashboard.column.requests': 'Requests',
  'dashboard.column.failure_rate': 'Failure rate',
  'dashboard.column.latency': 'p95 (avg)',
  'dashboard.column.cost': 'Cost',
  'dashboard.row.deleted_upstream': 'deleted upstream',
  'dashboard.latency_note':
    'Latency column is currently an AVG approximation of p95 in the gateway (see router-stats contract).',

  // ── Upstreams list ──────────────────────────────────────────────
  'upstreams.title': 'Upstreams',
  'upstreams.subtitle': 'Manage forward + pool + BYOK supply sources routed from gateway.',
  'upstreams.search.placeholder': 'Search name, endpoint…',
  'upstreams.search.aria_label': 'Search upstreams',
  'upstreams.new': 'New Upstream',
  'upstreams.new.title':
    'Launch the 4-step onboarding wizard: basics → model routes → register funding → probe',
  'upstreams.error.title': 'Failed to load upstreams',
  'upstreams.loading_aria': 'Loading upstreams…',
  'upstreams.empty.title': 'No upstreams configured',
  'upstreams.empty.search_body':
    'No upstreams match this search. Clear the filter or create a new one.',
  'upstreams.empty.initial_body':
    'Create your first upstream via the admin bootstrap flow (gateway /internal/upstreams) to start routing traffic.',
  'upstreams.stats.total': 'Total',
  'upstreams.stats.enabled': 'Enabled',
  'upstreams.stats.accounts': 'Accounts',
  'upstreams.stats.funding': 'Active Funding',
  'upstreams.column.name': 'Name',
  'upstreams.column.endpoint': 'Endpoint',
  'upstreams.column.source': 'Source',
  'upstreams.column.status': 'Status',
  'upstreams.column.accounts': 'Accounts',
  'upstreams.column.models': 'Models',
  'upstreams.column.funding': 'Funding',
  'upstreams.column.priority': 'Priority',
  'upstreams.column.actions': 'Actions',
  'upstreams.status.enabled': 'enabled',
  'upstreams.status.disabled': 'disabled',
  'upstreams.row.copy_endpoint': 'Copy endpoint URL',
  'upstreams.row.probe': 'Probe',
  'upstreams.row.edit': 'Edit',
  'upstreams.row.delete': 'Delete',
  'upstreams.delete_modal.title': 'Soft-delete upstream?',
  'upstreams.delete_modal.body_suffix':
    ' will be marked deleted (gateway stops routing to it). Reverse by editing `deleted_at` via a follow-up migration.',

  // ── Upstream detail ─────────────────────────────────────────────
  'upstream_detail.error.title': 'Failed to load upstream',
  'upstream_detail.back': 'Back to upstreams',
  'upstream_detail.breadcrumb.upstreams': 'Upstreams',
  'upstream_detail.meta.kind': 'kind · {kind}',
  'upstream_detail.chip.enabled': 'enabled',
  'upstream_detail.chip.disabled': 'disabled',
  'upstream_detail.config.title': 'Configuration',
  'upstream_detail.config.display_name': 'Display name',
  'upstream_detail.config.endpoint_url': 'Endpoint URL',
  'upstream_detail.config.priority': 'Priority',
  'upstream_detail.config.enabled': 'Enabled',
  'upstream_detail.config.auth_secret': 'Stored secret',
  'upstream_detail.config.auth_secret.write_only': 'write-only secret present',
  'upstream_detail.config.auth_secret.empty': 'no secret stored',
  'upstream_detail.config.auth_secret.hint':
    'Gateway stores this secret encrypted at rest. Operators can rotate it, but cannot read it back. The asynchronous task lane polls upstream task status with this credential.',
  'upstream_detail.config.auth_secret.rotate': 'Rotate secret',
  'upstream_detail.config.auth_secret.rotate_placeholder': 'Paste a new upstream credential',
  'upstream_detail.config.session_pool': 'Account pool (session-keyed)',
  'upstream_detail.config.session_pool.state.enabled': 'This upstream picks accounts by client session. Session re-homing only arms here.',
  'upstream_detail.config.session_pool.state.disabled': 'Not an account pool. Session re-homing will not rewrite traffic on this upstream.',
  'upstream_detail.config.session_pool.hint': 'When marked, load-aware routing can pin 429ing sessions onto the fallback without raising pool-wide shed.',
  'upstream_detail.config.cookie_passthrough': 'Cookie passthrough',
  'upstream_detail.config.cookie_passthrough.state.enabled':
    'Tenant cookies are forwarded to this upstream.',
  'upstream_detail.config.cookie_passthrough.state.disabled':
    'Tenant cookies are stripped before this upstream request leaves the gateway.',
  'upstream_detail.config.cookie_passthrough.hint':
    'Leave this off unless the upstream explicitly requires tenant cookies for auth or session continuity.',
  'upstream_detail.config.state.enabled': 'Upstream is active',
  'upstream_detail.config.state.disabled': 'Upstream is disabled',
  'upstream_detail.config.model_discovery': 'Model discovery',
  'upstream_detail.config.model_discovery.hint':
    'Controls how "Pull models" obtains this upstream’s model list. All fields save together as one object.',
  'upstream_detail.config.model_discovery.save': 'Save model discovery',
  'upstream_detail.config.model_discovery.saved': 'Model discovery configuration saved',
  // ── Model discovery (shared: wizard step 1 + detail config card, #1658) ──
  'model_discovery.mode.label': 'Model discovery mode',
  'model_discovery.mode.compatible': 'compatible · protocol default list',
  'model_discovery.mode.list': 'list · custom list endpoint',
  'model_discovery.mode.manual': 'manual · manual import',
  'model_discovery.mode.compatible.hint':
    'Uses the protocol-derived /v1/models or /v1beta/models list endpoint.',
  'model_discovery.mode.manual.hint':
    'No remote list request. Paste model ids in "Pull models" instead.',
  'model_discovery.path.label': 'List path',
  'model_discovery.path.hint':
    'Origin-relative absolute path, query string allowed, e.g. /v1/models?type=text',
  'model_discovery.response_path.label': 'Response field path',
  'model_discovery.response_path.hint':
    'JSON keys leading to the model array, dot-separated (1 to 8 segments), e.g. data or result.models',
  'model_discovery.auth.label': 'Credential carrier',
  'model_discovery.error.path_required': 'List path is required (a same-origin path starting with /)',
  'model_discovery.error.path_format':
    'List path must start with a single /, contain no # fragment, and be at most 2048 characters',
  'model_discovery.error.path_origin': 'List path must resolve to the upstream endpoint origin',
  'model_discovery.error.endpoint_absolute': 'list mode requires the endpoint URL to be an absolute URL',
  'model_discovery.error.response_path':
    'Response field path needs 1 to 8 non-empty JSON keys of at most 128 characters each',
  'model_discovery.error.auth': 'Credential carrier must be anthropic, bearer, or google-api-key',
  'model_discovery.error.mode': 'Model discovery mode must be compatible, list, or manual',
  'model_discovery.error.mode_fields':
    'The selected mode does not accept list configuration fields; switch to list mode first',
  'model_discovery.error.manual_models_required': 'Manual mode requires a non-empty list of model ids',
  'model_discovery.error.manual_protocol_required':
    'This channel has no protocol classification; manual import requires an explicit protocol',
  'model_discovery.error.models_limit': 'At most 200 model ids per batch, each 1 to 512 characters',
  'model_discovery.error.limit_range': 'limit must be an integer between 1 and 200',
  'model_discovery.error.list_exceeds': 'The upstream model list exceeds the 200-item cap',
  'upstream_detail.probe.title': 'Probe',
  'upstream_detail.probe.hint': 'Results persist for this session only',
  'upstream_detail.probe.vendor': 'Vendor',
  'upstream_detail.probe.vendor_placeholder': 'Select vendor',
  'upstream_detail.probe.channel': 'Channel',
  'upstream_detail.probe.channel_placeholder': 'Select channel',
  'upstream_detail.probe.model': 'Model',
  'upstream_detail.probe.select.placeholder': 'Select model',
  'upstream_detail.probe.submit': 'Probe {name}',
  'upstream_detail.probe.submitting': 'Probing…',
  'upstream_detail.probe.error.select_model': 'Select vendor, channel and model first',
  'upstream_detail.probe_history.title': 'Probe history',
  'upstream_detail.probe_history.body':
    'Persisted probe outcomes for this upstream. Auto-refreshes after each new probe.',
  'upstream_detail.probe_history.count': '{count} recent',
  'upstream_detail.probe_history.loading': 'Loading probe history…',
  'upstream_detail.probe_history.error': 'Failed to load probe history.',
  'upstream_detail.probe_history.empty': 'No probe results recorded yet.',
  'upstream_detail.probe_history.column.time': 'Time (local)',
  'upstream_detail.probe_history.column.model': 'Vendor / model',
  'upstream_detail.probe_history.column.outcome': 'Outcome',
  'upstream_detail.probe_history.column.latency': 'Latency',
  'upstream_detail.probe_history.column.cost': 'Cost (USD)',
  'upstream_detail.probe_history.column.status': 'HTTP',
  // ── Upstream detail · model SKU table (epic #1327 PR-5) ─────────
  'upstream_detail.routing.title': 'Upstream model SKUs',
  'upstream_detail.routing.subtitle':
    'Manage discovered upstream models per namespace: protocol classification, probes, enablement, and procurement multiplier. Platform SKU binding and routing live on the SKU detail page in the model market.',
  'upstream_detail.routing.channels.empty': 'This upstream has no live namespace. Create and enable a channel on the Channels tab first.',
  'upstream_detail.routing.load_error': 'Failed to load upstream models: {message}',
  'upstream_detail.routing.namespace.disabled': 'disabled',
  'upstream_detail.routing.namespace.stale': 'historical evidence',
  'upstream_detail.routing.namespace.count': '{count} models',
  'upstream_detail.routing.namespace.empty': 'No models in this namespace yet. Pull models to start discovery.',
  'upstream_detail.routing.discover': 'Pull models',
  'upstream_detail.routing.discovering': 'Pulling…',
  'upstream_detail.routing.discover.blocked': 'The namespace is disabled, so models cannot be pulled.',
  'upstream_detail.routing.discover.failed': 'Pulling models failed: {message}. The previous discovery evidence is kept below.',
  'upstream_detail.routing.manual.title': 'Manual model import · {channel}',
  'upstream_detail.routing.manual.body':
    'This upstream uses manual discovery: paste model ids (one per line or comma-separated). More than 200 ids are submitted in serial batches automatically.',
  'upstream_detail.routing.manual.models.label': 'Model ids',
  'upstream_detail.routing.manual.protocol.label': 'Protocol',
  'upstream_detail.routing.manual.protocol.placeholder': 'Select protocol',
  'upstream_detail.routing.manual.parsed': 'Parsed {count} model ids.',
  'upstream_detail.routing.manual.batches': 'Will submit in {count} batches (at most 200 each).',
  'upstream_detail.routing.manual.progress': 'Submitting batch {current}/{total}…',
  'upstream_detail.routing.manual.submit': 'Import models',
  'upstream_detail.routing.manual.submitting': 'Importing…',
  'upstream_detail.routing.manual.error.empty': 'Paste at least one model id first',
  'upstream_detail.routing.manual.error.too_long': 'A model id exceeds 512 characters; fix it and retry',
  'upstream_detail.routing.manual.error.protocol': 'Select a protocol',
  'upstream_detail.routing.manual.partial':
    'Batch {failed}/{total} failed: {message} The first {succeeded} batches ({models} models) were imported and are kept. You can re-import the whole list after fixing it: already-imported models are upserted rather than duplicated, so resubmitting everything is safe.',
  'upstream_detail.routing.col.model': 'Model',
  'upstream_detail.routing.col.protocol': 'Protocol classification',
  'upstream_detail.routing.col.probe': 'Probe status',
  'upstream_detail.routing.col.enabled': 'Enabled',
  'upstream_detail.routing.col.cost': 'Procurement multiplier',
  'upstream_detail.routing.col.serving': 'Platform SKUs served',
  'upstream_detail.routing.model.canonical': 'canonical {vendor} / {model}',
  'upstream_detail.routing.model.key_bucket': 'key {key}',
  'upstream_detail.routing.protocol.unclassified': 'Unclassified',
  'upstream_detail.routing.protocol.label': 'Protocol classification for {model}',
  'upstream_detail.routing.probe.run': 'Probe',
  'upstream_detail.routing.probe.rerun': 'Re-probe',
  'upstream_detail.routing.probe.running': 'Probing…',
  'upstream_detail.routing.probe.waiting': 'Waiting for result…',
  'upstream_detail.routing.probe.classify_first': 'Classify the protocol first',
  'upstream_detail.routing.probe.last_passed': 'last passed {time}',
  'upstream_detail.routing.enable.label': 'Enable model {model}',
  'upstream_detail.routing.enable.on': 'Enabled',
  'upstream_detail.routing.enable.off': 'Disabled',
  'upstream_detail.routing.cost.label': 'Procurement multiplier for {model}',
  'upstream_detail.routing.cost.save': 'Save',
  'upstream_detail.routing.cost.saving': 'Saving…',
  'upstream_detail.routing.cost.explicit': '×{value}',
  'upstream_detail.routing.cost.missing': 'No price',
  'upstream_detail.routing.cost.unavailable': 'Multiplier unavailable',
  'upstream_detail.routing.cost.invalid': 'The multiplier must be greater than 0 and at most {max}',
  'upstream_detail.routing.cost.load_error': 'Failed to read procurement multipliers: {message}',
  'upstream_detail.routing.cost.save_error': 'Failed to save the procurement multiplier: {message}',
  'upstream_detail.routing.cost.saved': 'Saved the procurement multiplier for {model}',
  'upstream_detail.routing.cost.unconfirmed': 'The upstream did not echo a multiplier for this model; retry the save.',
  'upstream_detail.routing.cost.windows.configure': 'Windows',
  'upstream_detail.routing.cost.windows.none': 'No window / all-day base rate',
  'upstream_detail.routing.cost.windows.active': 'In force: {label}',
  'upstream_detail.routing.cost.windows.base': 'Base ×{value}',
  'upstream_detail.routing.cost.windows.effective': 'Now ×{value}',
  'upstream_detail.routing.cost.windows.dialog_title': 'Peak/off-peak windows for {model}',
  'upstream_detail.routing.cost.windows.dialog_description': 'Saving replaces the whole window set for this model. An empty list clears every window so the base rate applies all day.',
  'upstream_detail.routing.cost.windows.add': 'Add window',
  'upstream_detail.routing.cost.windows.remove': 'Remove window',
  'upstream_detail.routing.cost.windows.label': 'Label',
  'upstream_detail.routing.cost.windows.timezone': 'Time zone',
  'upstream_detail.routing.cost.windows.days': 'Days of week',
  'upstream_detail.routing.cost.windows.day.mon': 'Mon',
  'upstream_detail.routing.cost.windows.day.tue': 'Tue',
  'upstream_detail.routing.cost.windows.day.wed': 'Wed',
  'upstream_detail.routing.cost.windows.day.thu': 'Thu',
  'upstream_detail.routing.cost.windows.day.fri': 'Fri',
  'upstream_detail.routing.cost.windows.day.sat': 'Sat',
  'upstream_detail.routing.cost.windows.day.sun': 'Sun',
  'upstream_detail.routing.cost.windows.start': 'Start',
  'upstream_detail.routing.cost.windows.end': 'End',
  'upstream_detail.routing.cost.windows.cross_midnight': 'End is earlier than start, so the window crosses midnight into the next day.',
  'upstream_detail.routing.cost.windows.multiplier': 'Window multiplier',
  'upstream_detail.routing.cost.windows.advanced': 'Advanced: per-dimension multipliers',
  'upstream_detail.routing.cost.windows.input': 'Input',
  'upstream_detail.routing.cost.windows.output': 'Output',
  'upstream_detail.routing.cost.windows.cache_read': 'Cache read',
  'upstream_detail.routing.cost.windows.cache_write': 'Cache write',
  'upstream_detail.routing.cost.windows.reasoning': 'Reasoning',
  'upstream_detail.routing.cost.windows.enabled': 'Enabled',
  'upstream_detail.routing.cost.windows.save': 'Save windows',
  'upstream_detail.routing.cost.windows.cancel': 'Cancel',
  'upstream_detail.routing.cost.windows.empty': 'No windows yet. The base multiplier applies all day.',
  'upstream_detail.routing.cost.windows.need_price': 'Save a base multiplier before configuring windows.',
  'upstream_detail.routing.cost.windows.close': 'Close',
  'upstream_detail.routing.cost.windows.days_required': 'Pick at least one day',
  'upstream_detail.routing.cost.windows.label_required': 'Enter a label',
  'upstream_detail.routing.cost.windows.timezone_required': 'Enter a time zone',
  'upstream_detail.routing.cost.windows.clock_invalid': 'Time must be HH:MM (end may be 24:00)',
  'upstream_detail.routing.cost.windows.start_end_equal': 'Start and end must be different times',
  'upstream_detail.routing.cost.windows.start_not_end_of_day': '24:00 can only be used as the end time',
  'upstream_detail.routing.cost.windows.multiplier_invalid': 'Must be a number greater than 0 and at most {max}',
  'upstream_detail.routing.serving.count': 'Serving {count} platform SKUs',
  'upstream_detail.routing.serving.unknown': 'Unknown',
  'upstream_detail.routing.serving.open_market': 'Open the model market',
  'upstream_detail.routing.serving.error': 'Failed to read served platform SKUs: {message}',
  'upstream_detail.routing.serving.truncated': 'The routing summary is bounded ({loaded}/{total}), so served counts are not trustworthy.',
  'upstream_detail.routing.bulk.selected': '{count} models selected',
  'upstream_detail.routing.bulk.select_all': 'Select all visible models',
  'upstream_detail.routing.bulk.select_row': 'Select model {model}',
  'upstream_detail.routing.bulk.probe': 'Probe selected',
  'upstream_detail.routing.bulk.probing': 'Probing…',
  'upstream_detail.routing.bulk.protocol': 'Classify as {protocol}',
  'upstream_detail.routing.bulk.protocol_setting': 'Classifying…',
  'upstream_detail.routing.bulk.enable': 'Enable selected',
  'upstream_detail.routing.bulk.enabling': 'Enabling…',
  'upstream_detail.routing.bulk.disable': 'Disable selected',
  'upstream_detail.routing.bulk.disabling': 'Disabling…',
  'upstream_detail.routing.bulk.clear': 'Clear selection',
  'upstream_detail.routing.toast.models_loaded': 'Pulled {count} models',
  'upstream_detail.routing.toast.probe_queued': 'Probe queued',
  'upstream_detail.routing.toast.bulk_probe_queued': 'Queued probes for {count} models',
  'upstream_detail.routing.toast.protocol_set': 'Classified {count} models as {protocol}',
  'upstream_detail.routing.toast.enabled': 'Model enabled',
  'upstream_detail.routing.toast.disabled': 'Model disabled',
  'upstream_detail.routing.toast.bulk_enabled': 'Enabled {count} models',
  'upstream_detail.routing.toast.bulk_disabled': 'Disabled {count} models',
  'upstream_detail.routing.impact.ignore.title': 'Confirm disabling upstream models',
  'upstream_detail.routing.impact.unignore.title': 'Confirm enabling upstream models',
  'upstream_detail.routing.impact.subtitle': 'Preview and mutation use the same exact capability IDs; the write revalidates under lock.',
  'upstream_detail.routing.impact.exact_ids': 'Exact capability IDs',
  'upstream_detail.routing.impact.protected': 'This would affect protected SKU targets, so confirmation is disabled. Resolve the exact references below; the backend still returns 409 against races.',
  'upstream_detail.routing.impact.truncated_protected': 'The impact preview is bounded ({skuShown}/{skuTotal} SKU targets shown). Disabling stays blocked until every protected reference can be reviewed.',
  'upstream_detail.routing.impact.safe': 'No protected references were found in this preview.',
  'upstream_detail.routing.impact.examples': 'Showing {shown} of {total} exact references',
  'upstream_detail.routing.impact.confirm_ignore': 'Disable selected models',
  'upstream_detail.routing.impact.confirm_unignore': 'Enable selected models',
  'upstream_detail.routing.impact.scope.default': 'Default',
  'upstream_detail.routing.impact.scope.config': 'Config',
  'upstream_detail.routing.impact.scope.org': 'Org',
  'upstream_detail.routing.impact.scope.org_override': 'Org override',
  'upstream_detail.funding.new': '+ New batch',
  'upstream_detail.funding.loading_aria': 'Loading funding batches…',
  'upstream_detail.funding.error.load': 'Failed to load funding batches',
  'upstream_detail.funding.empty':
    'No funding batches recorded. Add one as needed to log current balance or prepaid quota.',
  'upstream_detail.funding.account.unassigned': 'Unassigned',
  'upstream_detail.funding.form.account.label': 'Account',
  'upstream_detail.funding.form.account.none': 'No accounts',
  'upstream_detail.funding.register.title': 'Register funding batch',
  'upstream_detail.funding.register.submit': 'Register batch',
  'upstream_detail.funding.register.submitting': 'Saving…',
  'upstream_detail.funding.batch.mode_rate': 'mode · {mode} · rate {rate}',
  'upstream_detail.funding.batch.chip.committed': 'committed',
  'upstream_detail.funding.batch.chip.draft': 'draft',
  'upstream_detail.funding.batch.chip.active': 'active',
  'upstream_detail.funding.batch.chip.depleted': 'depleted',
  'upstream_detail.funding.batch.chip.correction': 'correction',
  'upstream_detail.funding.batch.chip.alert_95': 'alert 95%',
  'upstream_detail.funding.batch.chip.alert_80': 'alert 80%',
  'upstream_detail.funding.batch.meta.paid': 'Paid',
  'upstream_detail.funding.batch.meta.capacity': 'Capacity',
  'upstream_detail.funding.batch.meta.consumed': 'Consumed',
  'upstream_detail.funding.batch.meta.funded_at': 'Funded at',
  'upstream_detail.funding.batch.pct_consumed': '{pct}% consumed',
  'upstream_detail.funding.batch.invoice': 'invoice',
  'upstream_detail.funding.batch.note': 'note',
  'upstream_detail.funding.form.cny_paid_yuan.label': 'CNY paid (yuan)',
  'upstream_detail.funding.form.correction.label': 'Reconciliation correction',
  'upstream_detail.funding.form.usd_delta.label': 'USD difference (negative, dollars)',
  'upstream_detail.funding.form.exchange_rate.label': 'Exchange rate (CNY/USD)',
  'upstream_detail.funding.form.exchange_rate.loading': 'Loading rate…',
  'upstream_detail.funding.form.computed_usd.label': 'Computed USD capacity',
  'upstream_detail.funding.form.computed_cny.label': 'Derived CNY paid',
  'upstream_detail.funding.form.invoice_ref.label': 'Invoice reference (optional)',
  'upstream_detail.funding.form.note.label': 'Note (optional)',
  'upstream_detail.funding.form.note.required_label': 'Note (required)',
  'upstream_detail.funding.error.cny_paid_required': 'Please enter the CNY amount paid',
  'upstream_detail.funding.error.usd_delta_required': 'Enter a negative USD difference',
  'upstream_detail.funding.error.correction_note_required':
    'A note is required for a correction batch',
  'upstream_detail.funding.error.account_required': 'Please choose an upstream account',
  'upstream_detail.funding.error.rate_unavailable':
    'Exchange rate not available. Set it on the Pricing page first.',
  'upstream_detail.funding.toast.registered': 'Funding batch {id} registered',
  'upstream_detail.funding.toast.committed': 'Funding batch committed',
  'upstream_detail.funding.toast.updated': 'Funding batch updated',
  'upstream_detail.funding.batch.action.edit': 'Edit',
  'upstream_detail.funding.batch.action.commit': 'Commit',
  'upstream_detail.danger.title': 'Danger zone',
  'upstream_detail.danger.body':
    'Soft-delete the upstream. Gateway stops selecting its capabilities; SKU target audit history remains available.',
  'upstream_detail.danger.already_deleted': 'Already soft-deleted',
  'upstream_detail.danger.delete': 'Soft-delete upstream',
  'upstream_detail.delete_modal.title': 'Soft-delete {name}?',
  'upstream_detail.delete_modal.body':
    'Gateway will stop routing to this upstream immediately. SKU target audit history remains available.',

  // ── Upstream retirement impact dialog ───────────────────────────
  'upstream_detail.retirement.loading': 'Analyzing retirement impact…',
  'upstream_detail.retirement.load_error':
    'Could not load retirement impact. Retry or contact the platform admin.',
  'upstream_detail.retirement.retry': 'Retry',
  'upstream_detail.retirement.uniquely_served.title': '🚨 Uniquely served models',
  'upstream_detail.retirement.uniquely_served.body':
    'Retiring this upstream leaves {count} model(s) with no route. Tenant calls will 404 immediately:',
  'upstream_detail.retirement.uniquely_served.none':
    'No uniquely served models — every mapped model route has another upstream that can route it.',
  'upstream_detail.retirement.total_attached_suffix':
    '({total} mapped in total, {unique} served only by this upstream)',
  'upstream_detail.retirement.funding.title': '💰 Active funding batches',
  'upstream_detail.retirement.funding.body':
    '{count} batch(es) not yet depleted, {remaining} remaining. Retirement stops further consumption and breaks audit traceability.',
  'upstream_detail.retirement.funding.none': 'No active funding batches.',
  'upstream_detail.retirement.traffic.title': '📊 Traffic share — last 24 h',
  'upstream_detail.retirement.traffic.body':
    'Handled {percentage}% of gateway traffic in the last 24 h ({handled}/{total} requests).',
  'upstream_detail.retirement.traffic.no_traffic':
    'No traffic in the last 24 h. Share cannot be estimated.',
  'upstream_detail.retirement.ack':
    'I understand the consequences of retiring this upstream and wish to proceed with the soft-delete.',
  'upstream_detail.retirement.ack_required': 'Please acknowledge the box above to continue.',

  // ── Models list ─────────────────────────────────────────────────
  'models.title': 'Models',
  'models.subtitle':
    'Catalog of registered (vendor, model) pairs. Pricing history and protocol bindings are managed on the per-model detail page.',
  'models.search.placeholder': 'Search model, display name…',
  'models.search.aria_label': 'Search models',
  'models.filter.vendor': 'Vendor',
  'models.filter.vendor_all': 'All vendors',
  'models.new': 'Discover Models',
  'models.create.open': 'Create Model',
  'models.create.title': 'Create model',
  'models.create.subtitle':
    'Register a platform model directly when discovery cannot provide the row.',
  'models.create.vendor.label': 'Vendor',
  'models.create.vendor.placeholder': 'anthropic',
  'models.create.model.label': 'Model',
  'models.create.model.placeholder': 'claude-opus-4-7',
  'models.create.display_name.label': 'Display name',
  'models.create.display_name.placeholder': 'Claude Opus 4.7',
  'models.create.error.vendor_model_required': 'Vendor and model are required.',
  'models.create.submit': 'Create model',
  'models.create.toast.created': 'Created {model}',
  'models.error.title': 'Failed to load models',
  'models.loading_aria': 'Loading models…',
  'models.empty.search.title': 'No models match this search',
  'models.empty.search.body': 'Clear the filter, or discover models from the vendor page first.',
  'models.empty.initial.title': 'No models discovered',
  'models.empty.initial.body':
    'Run official discovery from the vendor page; discovered results will appear here as delisted candidates.',
  'models.column.vendor': 'Vendor',
  'models.column.model': 'Model',
  'models.column.display_name': 'Display name',
  'models.column.price': 'Price',
  'models.column.family': 'Family',
  'models.column.context_window': 'Context window',
  'models.column.actions': 'Actions',
  'models.price.unit': 'input / output · USD/MTok',
  'models.price.cache_line': 'cache read {read} / write {write}',
  'models.price.input_output': 'input / output · USD/MTok',
  'models.price.input': 'Input',
  'models.price.output': 'Output',
  'models.price.mode.anthropic': 'Anthropic · 5m / 1h cache writes',
  'models.price.mode.openai_hit_only': 'OpenAI · cache hit only',
  'models.price.mode.openai_write_billed': 'OpenAI · cache hit + write',
  'models.price.mode.legacy': 'Legacy · aggregate cache write',
  'models.price.cache_read': 'Cache read',
  'models.price.cache_hit': 'Cache hit',
  'models.price.cache_write': 'Cache write',
  'models.price.cache_write_5m': 'Write 5m',
  'models.price.cache_write_1h': 'Write 1h',
  'models.price.reasoning': 'Reasoning',
  'models.price.not_configured': 'Not set',
  'models.price.not_billed': 'Not billed',
  'models.price.unpriced': 'Unpriced',
  'models.row.empty_value': '—',
  'models.row.manage': 'Manage',
  'models.row.delete': 'Delete',
  'models.delete_modal.title': 'Soft-delete model?',
  'models.delete_modal.body_suffix':
    ' will be marked deleted. Pricing and protocol rows are preserved for historical reconstruction; reverse by editing ',
  'models.delete_modal.body_deleted_at_code': 'deleted_at',
  'models.delete_modal.body_tail': ' via a follow-up migration.',

  // ── Model detail ────────────────────────────────────────────────
  'model_detail.loading_aria': 'Loading model…',
  'model_detail.error.title': 'Failed to load model',
  'model_detail.back': 'Back to models',
  'model_detail.breadcrumb.models': 'Models',
  'model_detail.meta.family': 'family · {family}',
  'model_detail.meta.soft_deleted': 'soft-deleted · {date}',
  'model_detail.meta.deprecated': 'deprecated · {date}',
  'model_detail.basic.title': 'Basic info',
  'model_detail.basic.display_name': 'Display name',
  'model_detail.basic.family': 'Family',
  'model_detail.basic.context_window': 'Context window',
  'model_detail.basic.max_output_tokens': 'Max output tokens',
  'model_detail.aliases.title': 'Model aliases',
  'model_detail.aliases.subtitle':
    'Accept alternate tenant-facing model names that resolve to this canonical model.',
  'model_detail.aliases.empty': 'No aliases yet.',
  'model_detail.aliases.add.label': 'Add alias',
  'model_detail.aliases.add.placeholder': 'claude-latest',
  'model_detail.aliases.add.submit': 'Add',
  'model_detail.aliases.add.adding': 'Adding…',
  'model_detail.aliases.remove_aria': 'Remove {alias}',
  'model_detail.aliases.error.required': 'Alias is required',
  'model_detail.aliases.error.duplicate': 'Alias already exists or matches the canonical model',
  'model_detail.pricing.title': 'Pricing',
  'model_detail.pricing.lane.token': 'Token pricing',
  'model_detail.pricing.loading_aria': 'Loading pricing…',
  'model_detail.pricing.error.load': 'Network error loading pricing',
  'model_detail.pricing.empty': 'No pricing rows yet. Add the first generation below.',
  'model_detail.pricing.column.effective_from': 'Effective from',
  'model_detail.pricing.column.input': 'Input $/Mtok',
  'model_detail.pricing.column.output': 'Output $/Mtok',
  'model_detail.pricing.column.cache_read': 'Cache read $/Mtok',
  'model_detail.pricing.column.cache_hit': 'Cache hit $/Mtok',
  'model_detail.pricing.column.cache_write': 'Cache write $/Mtok',
  'model_detail.pricing.column.cache_write_5m': 'Cache write 5m $/Mtok',
  'model_detail.pricing.column.cache_write_1h': 'Cache write 1h $/Mtok',
  'model_detail.pricing.column.cache_mode': 'Cache billing mode',
  'model_detail.pricing.cache_mode.hit_only': 'Cache hit only',
  'model_detail.pricing.cache_mode.write_billed': 'Cache hit + write',
  'model_detail.pricing.column.reasoning': 'Reasoning $/Mtok',
  'model_detail.pricing.column.image_input': 'Image input $/Mtok',
  'model_detail.pricing.column.single_rate': 'Price $/Mtok',
  'model_detail.pricing.column.status': 'Status',
  'model_detail.pricing.row.current': 'Current',
  'model_detail.pricing.row.archived': 'archived',
  'model_detail.pricing.row.anchor': 'Anchor row (not used for tier billing)',
  'model_detail.pricing.row.anchor_unit':
    'Anchor row (this model is billed per unit; this row is not used for billing)',
  'model_detail.pricing.add.title': 'Add pricing row',
  'model_detail.pricing.add.effective_from.label': 'Effective from',
  'model_detail.pricing_effective.immediate': 'Take effect immediately',
  'model_detail.pricing_effective.scheduled_date': 'From a chosen date',
  'model_detail.pricing_effective.scheduled_datetime': 'From a chosen time',
  'model_detail.pricing.add.input_rate.label': 'Input $/Mtok',
  'model_detail.pricing.add.input_rate.placeholder': '3.000000',
  'model_detail.pricing.add.output_rate.label': 'Output $/Mtok',
  'model_detail.pricing.add.output_rate.placeholder': '15.000000',
  'model_detail.pricing.add.cache_read.label': 'Cache read $/Mtok',
  'model_detail.pricing.add.cache_read.placeholder': '0.300000',
  'model_detail.pricing.add.cache_hit.label': 'Cache hit $/Mtok',
  'model_detail.pricing.add.cache_hit.placeholder': '0.200000',
  'model_detail.pricing.add.cache_write.label': 'Cache write $/Mtok',
  'model_detail.pricing.add.cache_write.placeholder': '3.750000',
  'model_detail.pricing.add.cache_write_5m.label': 'Cache write 5m $/Mtok',
  'model_detail.pricing.add.cache_write_5m.placeholder': '3.750000',
  'model_detail.pricing.add.cache_write_1h.label': 'Cache write 1h $/Mtok',
  'model_detail.pricing.add.cache_write_1h.placeholder': '5.000000',
  'model_detail.pricing.add.cache_mode.label': 'Cache billing mode',
  'model_detail.pricing.add.reasoning.label': 'Reasoning $/Mtok',
  'model_detail.pricing.add.reasoning.placeholder': '4.000000',
  'model_detail.pricing.add.image_input.label': 'Image input $/Mtok',
  'model_detail.pricing.add.image_input.placeholder': '8.000000',
  'model_detail.pricing.add.single_rate.label': 'Price $/Mtok',
  'model_detail.pricing.add.single_rate.placeholder': '6.400000',
  'model_detail.pricing.add.submit': 'Add pricing row',
  'model_detail.pricing.add.submitting': 'Adding…',
  'model_detail.pricing.add.base_anchor_note':
    'This model is sold in price tiers: billing matches a tier rate exactly, so the base rates do not price tiered usage — they only anchor the listing. When a current generation exists, its base rates are carried in automatically; edit if needed.',
  'model_detail.pricing.unit_anchor.note':
    'Listing requires one token pricing generation per model; this model is billed per unit, so token rates never price its usage. Create the anchor row at zero rates with one click.',
  'model_detail.pricing.unit_anchor.create': 'Create anchor row (zero rates)',
  'model_detail.pricing.error.effective_from_required': 'Effective from is required',
  'model_detail.pricing.error.effective_from_invalid': 'Effective from must be a valid date',
  'model_detail.pricing.error.rates_required': 'Input and output rates are required',
  'model_detail.pricing.error.input_rate_invalid': 'Input rate must be a non-negative number',
  'model_detail.pricing.error.output_rate_invalid': 'Output rate must be a non-negative number',
  'model_detail.pricing.error.cache_read_invalid': 'Cache read must be a non-negative number',
  'model_detail.pricing.error.cache_write_invalid': 'Cache write must be a non-negative number',
  'model_detail.pricing.error.cache_write_5m_invalid': 'Cache write 5m must be a non-negative number',
  'model_detail.pricing.error.cache_write_1h_invalid': 'Cache write 1h must be a non-negative number',
  'model_detail.pricing.error.cache_write_ttl_required': 'Anthropic pricing requires both cache write 5m and 1h rates',
  'model_detail.pricing.error.cache_write_positive_required': 'Cache hit + write mode requires a cache write rate greater than zero',
  'model_detail.pricing.error.metering_unknown': 'Cannot load this model’s metering profiles — pricing is held until they load, so a billable dimension is not missed',
  'model_detail.pricing.error.reasoning_invalid': 'Reasoning rate must be a non-negative number',
  'model_detail.pricing.error.reasoning_required': 'This model meters reasoning tokens — leaving the rate blank would publish a price list that bills thinking at nothing. Enter 0 if it really is free.',
  'model_detail.pricing.error.image_input_invalid': 'Image input rate must be a non-negative number',
  'model_detail.pricing.error.image_input_required':
    'This model meters image-input tokens — leaving the rate blank would publish a price list that bills them at the text input rate, dropping a configured image-input price. Enter 0 if they really are free.',
  'model_detail.pricing.error.invalid': 'Invalid rate',
  'model_detail.pricing.error.single_rate_invalid': 'Price must be a non-negative number',
  'model_detail.pricing.error.single_rate_required': 'Price is required',
  'model_detail.pricing.error.rated_anchor_conflict':
    'This model already has billed usage at or after the chosen effective time — the latest billed event is at {latest}. A new price list must take effect after that instant: choose “Take effect immediately”, or a later date.',
  'model_detail.pricing.error.rated_anchor_conflict_generic':
    'This model already has billed usage at or after the chosen effective time. A new price list must take effect after the latest billed event: choose “Take effect immediately”, or a later date.',
  'model_detail.pricing.variants.title': 'Price tiers (request shape)',
  'model_detail.pricing.variants.generation_current': 'Current',
  'model_detail.pricing.variants.column.variant': 'Tier',
  'model_detail.pricing.variants.add.title': 'Price tiers for the new generation',
  'model_detail.pricing.variants.add.hint':
    'This model prices by request shape, and tiers do not carry over — a new generation starts with none. Restate every tier here, or the new price list prices none of them.',
  'model_detail.pricing.variants.add.key.label': 'Tier key',
  'model_detail.pricing.variants.add.add_row': 'Add tier',
  'model_detail.pricing.variants.add.row_count': '{count} / {max} tiers',
  'model_detail.pricing.variants.add.remove_aria': 'Remove tier {row}',
  'model_detail.pricing.variants.error.too_many': 'At most {max} price tiers per generation',
  'model_detail.pricing.variants.error.key_invalid':
    'Tier {row}: key must match ^[a-z0-9][a-z0-9-]{0,62}$ (the base rates own the empty key)',
  'model_detail.pricing.variants.error.duplicate': 'Duplicate tier key within one generation: {variant}',
  'model_detail.pricing.variants.error.rates_required': 'Tier {row}: input and output rates are required',
  'model_detail.pricing.variants.error.single_rate_required': 'Tier {row}: price is required',
  'model_detail.pricing.variants.error.rate_invalid': 'Tier {row}: rates must be non-negative numbers',
  'model_detail.pricing.variants.error.reasoning_required':
    'Tier {row}: this model meters reasoning tokens, and a tier replaces the base rates outright — leaving it blank bills this tier’s thinking at nothing. Enter 0 if it really is free.',
  'model_detail.pricing.variants.error.image_input_required':
    'Tier {row}: this model meters image-input tokens, and a tier replaces the base rates outright — leaving it blank bills this tier’s image input at the text input rate. Enter 0 if they really are free.',
  'model_detail.pricing.variants.error.fields_required':
    'Tier {row}: fill in every tier field (lowercase letters and digits only)',
  'model_detail.pricing.variants.add.hint_derived':
    'This model prices in tiers by request shape; the tier key is derived from the fields below — never hand-assembled. Tiers do not carry over: restate every tier’s rates for the new generation here.',
  'metering.variant_field.size': METERING_VARIANT_FIELD_LABELS.size.en,
  'metering.variant_field.quality': METERING_VARIANT_FIELD_LABELS.quality.en,
  'metering.variant_field.resolution': METERING_VARIANT_FIELD_LABELS.resolution.en,
  'metering.variant_field.has_video_input': METERING_VARIANT_FIELD_LABELS.has_video_input.en,
  'metering.variant_field.select_placeholder': 'Select…',
  'metering.variant_field.value_placeholder': 'e.g. 480p',
  'metering.video_input.absent': VIDEO_INPUT_VARIANT_LABELS.absent.en,
  'metering.video_input.present': VIDEO_INPUT_VARIANT_LABELS.present.en,
  'metering.variant_key.preview': 'Derived tier key:',
  'metering.variant_key.preview_incomplete':
    'Tier key pending: fill in every field (lowercase letters and digits only)',
  'metering.variant_key.preview_default': 'All fields empty = the default row (empty key)',
  'metering.shape.lane_token': 'token-billed',
  'metering.shape.lane_unit': 'unit-billed',
  'metering.shape.no_tiers': 'untiered',
  'metering.shape.tiered_by': 'tiered by {fields}',
  'metering.shape.summary': 'Billing shape: {lane}, {tiers}',
  'metering.shape.fixed.ark_video_token_v1':
    'Billing shape: token-billed · two tiers by video-input presence (Ark official pricing)',
  'metering.shape.fixed.ark_video_token_tiered_v1':
    'Billing shape: token-billed · tiered by video-input presence × resolution (Ark official per-resolution rates)',
  'metering.shape.fixed.duration_seconds_v1':
    'Billing shape: billed per second · tiered by resolution (Ark official pricing)',
  'metering.shape.fixed.ark_video_seconds_v1':
    'Billing shape: billed per second · tiered by video-input presence × resolution (Ark task pricing)',
  'metering.shape.fixed.openai_images_unit_v1':
    'Billing shape: billed per image · tiered by size × quality (OpenAI images pricing)',
  'metering.shape.fixed.ark_video_seconds_ref_v1':
    'Billing shape: billed per second · tiered by resolution, plus per-image surcharge (MiniMax A)',
  'metering.shape.fixed.grok_video_seconds_v1':
    'Billing shape: billed per second · tiered by resolution (Grok video pricing)',
  'metering.shape.fixed.grok_image_unit_v1':
    'Billing shape: billed per delivered image · flat empty variant (Grok still-image pricing)',
  'model_detail.unit_pricing.title': 'Unit pricing',
  'model_detail.unit_pricing.loading': 'Loading unit pricing…',
  'model_detail.unit_pricing.error.load': 'Network error loading unit pricing',
  'model_detail.unit_pricing.empty': 'No unit pricing yet. Add the first generation below.',
  'model_detail.unit_pricing.generation.current': 'Current',
  'model_detail.unit_pricing.generation.scheduled': 'Scheduled',
  'model_detail.unit_pricing.generation.archived': 'archived',
  'model_detail.unit_pricing.column.unit': 'Unit',
  'model_detail.unit_pricing.column.variant': 'Variant',
  'model_detail.unit_pricing.column.rate_micros': 'Rate (micro-USD)',
  'model_detail.unit_pricing.column.rate_human': 'Converted',
  'model_detail.unit_pricing.variant.default': 'default',
  'model_detail.unit_pricing.unit.call': UNIT_USAGE_UNIT_LABELS.call.en,
  'model_detail.unit_pricing.unit.second': UNIT_USAGE_UNIT_LABELS.second.en,
  'model_detail.unit_pricing.unit.character': UNIT_USAGE_UNIT_LABELS.character.en,
  'model_detail.unit_pricing.unit.image': UNIT_USAGE_UNIT_LABELS.image.en,
  'model_detail.unit_pricing.add.title': 'Add a pricing generation',
  'model_detail.unit_pricing.add.effective_from.label': 'Effective from',
  'model_detail.unit_pricing.add.row.unit.label': 'Unit',
  'model_detail.unit_pricing.add.row.variant.label': 'Variant (empty = default row)',
  'model_detail.unit_pricing.add.row.variant.placeholder': 'e.g. 1080p, empty for default',
  'model_detail.unit_pricing.add.row.rate.label': 'Rate (micro-USD / unit)',
  'model_detail.unit_pricing.add.row.rate.placeholder': '40000',
  'model_detail.unit_pricing.add.row.remove_aria': 'Remove row {row}',
  'model_detail.unit_pricing.add.add_row': 'Add row',
  'model_detail.unit_pricing.add.row_count': '{count} / {max} rows',
  'model_detail.unit_pricing.add.submit': 'Add generation',
  'model_detail.unit_pricing.add.submitting': 'Submitting…',
  'model_detail.unit_pricing.error.effective_from_invalid': 'Effective from must be a valid timestamp',
  'model_detail.unit_pricing.error.rows_required': 'At least one rate row is required',
  'model_detail.unit_pricing.error.too_many_rows': 'At most {max} rate rows per generation',
  'model_detail.unit_pricing.error.unit_invalid': 'Row {row}: unit must be one of the known units',
  'model_detail.unit_pricing.error.variant_invalid': 'Row {row}: variant must be empty or match ^[a-z0-9][a-z0-9-]{0,62}$',
  'model_detail.unit_pricing.error.rate_invalid': 'Row {row}: rate must be a positive integer (micro-USD per unit)',
  'model_detail.unit_pricing.error.duplicate_row': 'Duplicate (unit, variant) within one generation: {unit} / {variant}',
  'model_detail.unit_pricing.error.variant_fields_invalid':
    'Row {row}: variant fields are incomplete or carry illegal characters (lowercase letters and digits only; all empty = default row)',
  'model_detail.protocols.title': 'Protocols',
  'model_detail.protocols.loading_aria': 'Loading protocols…',
  'model_detail.protocols.error.load': 'Network error loading protocols',
  'model_detail.protocols.empty': 'No protocols registered. Pool selectors will skip this model.',
  'model_detail.protocols.add.label': 'Add protocol',
  'model_detail.protocols.add.submit': 'Add',
  'model_detail.protocols.add.adding': 'Adding…',
  'model_detail.protocols.error.already_registered': '{protocol} is already registered',
  'model_detail.protocols.remove_aria': 'Remove {protocol}',
  'model_detail.protocols.all_registered':
    'All three protocols are registered on this model — remove one to change how it meters.',
  'model_detail.protocols.profile.label': 'Metering profile',
  'model_detail.protocols.profile.default': 'Protocol default ({profile})',
  'model_detail.protocols.profile.unset': 'no profile',
  'model_detail.protocols.profile.pick': 'Pick a metering profile',
  'model_detail.protocols.profile.required':
    'Protocol {protocol} has no default metering profile; pick one explicitly.',
  'model_detail.protocols.spec.present': 'spec',
  'model_detail.protocols.spec.absent': '—',
  'model_detail.protocols.edit': 'Edit',
  'model_detail.protocols.edit_title':
    'Re-register {protocol} (a full overwrite: documents missing from the form are cleared; the stored configuration has been prefilled)',
  'model_detail.protocols.edit_submit': 'Re-register',
  'model_detail.protocols.docs.section.accepts.title': 'What is accepted',
  'model_detail.protocols.docs.section.accepts.hint':
    'The parameter vocabulary is part of the protocol and ships in code — only type errors and obvious garbage are rejected here. Value ranges belong to the upstream model itself, and whether a tier can be billed is decided by the price matrix before any upstream cost is spent.',
  'model_detail.protocols.docs.section.billing.title': 'How it bills',
  'model_detail.protocols.docs.section.billing.hint':
    'The billing shape is fixed by the metering profile and generated at registration — nothing to fill in.',
  'model_detail.protocols.docs.spec_json.label': 'Metering spec (metering_spec, JSON, optional)',
  'model_detail.protocols.docs.spec_json.hint':
    'Empty keeps the profile’s built-in spec; duration_seconds_v1 / ark_video_seconds_v1 / ark_video_seconds_ref_v1 / grok_video_seconds_v1 have no built-in, so leaving this empty makes the SKU unbillable. Checked with the exact validators the write face refuses with, before anything is posted.',
  'model_detail.protocols.docs.spec_json.placeholder':
    '{"spec_version":1,"lane":"unit","unit":"second","quantity":{"field":"duration_seconds","a":1,"b":0},"quantity_by_variant":{"480p":{"field":"duration_seconds","a":1,"b":0}},"variant_key_template":"{resolution}","delivery_gate":"positive_quantity"}',
  'model_detail.protocols.docs.vocabulary.label': 'Protocol parameter vocabulary (read-only)',
  'model_detail.protocols.docs.vocabulary.hint':
    'Ships with the protocol version in code. A vendor generation that adds a parameter is a new protocol version, not a form edit; value ranges are the upstream’s own.',
  'model_detail.protocols.docs.vocabulary.col_parameter': 'Parameter',
  'model_detail.protocols.docs.vocabulary.col_type': 'Type',
  'model_detail.protocols.docs.vocabulary.col_commands': 'Prompt commands',
  'model_detail.protocols.docs.admission.label': 'Concurrency admission (task_admission)',
  'model_detail.protocols.docs.admission.hint':
    'Caps one account’s in-flight tasks per tier class. The tier classes are this map’s own keys: a key is a servable tier, and a request whose tier value matches no key is refused before any spend.',
  'model_detail.protocols.docs.admission.tier_class': 'Tier class parameter',
  'model_detail.protocols.docs.admission.tier_class_placeholder': 'Pick a vocabulary parameter',
  'model_detail.protocols.docs.admission.tier_class_value': 'Tier class',
  'model_detail.protocols.docs.admission.tier_class_value_placeholder': 'e.g. 480P',
  'model_detail.protocols.docs.admission.allowance': '{tier} in-flight cap',
  'model_detail.protocols.docs.admission.add_tier': 'Add tier class',
  'model_detail.protocols.docs.admission.remove_aria': 'Remove tier class {row}',
  'model_detail.protocols.docs.error.spec_invalid': 'The metering spec failed validation: {violations}',
  'model_detail.protocols.docs.error.spec_json_invalid': 'The metering spec is not valid JSON',
  'model_detail.protocols.docs.error.spec_lane_mismatch':
    'This metering spec is on the wrong lane for {profile} (a token profile’s tier spec is generated from its fixed template, not typed as JSON)',
  'model_detail.protocols.docs.error.spec_template_fixed':
    '{profile} is a fixed sales form: its variant_key_template must be exactly {fixed}, got {template}',
  'model_detail.protocols.docs.error.spec_template_fixed_none':
    'none (this profile sells no tiers)',
  'model_detail.protocols.docs.error.spec_descriptor_not_produced':
    '{profile} does not produce descriptor field {field}; the write face refuses this spec',
  'model_detail.protocols.docs.error.spec_protocol_excluded':
    'The spec’s protocols do not include {protocol}, so this registration could never be metered',
  'model_detail.protocols.docs.error.spec_surcharge_not_sold':
    '{profile} does not sell a surcharge; a surcharge is enumerated on the profile, not configured per registration',
  'model_detail.protocols.docs.error.spec_surcharge_unit':
    '{profile} sells surcharge unit {admitted}, got {unit}',
  'model_detail.protocols.docs.error.spec_surcharge_descriptor':
    '{profile} does not sell surcharge descriptor {field}',
  'model_detail.protocols.docs.error.admission_invalid':
    'The admission surface failed validation: {violations}',
  'model_detail.deprecation.title': 'Deprecation',
  'model_detail.deprecation.body':
    'Marking this model deprecated keeps it listable, chargeable, and routable — ops is just signalling new adoption should be discouraged. The notice surfaces in the detail header and the ops audit log.',
  'model_detail.deprecation.chip.deprecated': 'Deprecated',
  'model_detail.deprecation.notice.label': 'Notice (required)',
  'model_detail.deprecation.notice.placeholder':
    'e.g. Vendor EOL on 2026-06-01. Migrate tenants to claude-opus-4-8.',
  'model_detail.deprecation.notice.counter': '{count} / {limit}',
  'model_detail.deprecation.deprecate.submit': 'Mark deprecated',
  'model_detail.deprecation.deprecate.pending': 'Marking…',
  'model_detail.deprecation.undeprecate.submit': 'Clear deprecation',
  'model_detail.deprecation.undeprecate.pending': 'Clearing…',
  'model_detail.deprecation.error.notice_required': 'Enter a deprecation notice',
  'model_detail.deprecation.error.notice_too_long': 'Notice must be at most {limit} characters',
  'model_detail.deprecation.toast.deprecated': 'Model marked deprecated',
  'model_detail.deprecation.toast.undeprecated': 'Deprecation cleared',
  'model_detail.danger.title': 'Danger zone',
  'model_detail.danger.body':
    'Soft-delete the model. Pricing rows and protocol bindings stay in SQL for historical cost reconstruction; gateway pool selectors will stop offering this model.',
  'model_detail.danger.already_deleted': 'Already soft-deleted',
  'model_detail.danger.delete': 'Soft-delete model',
  'model_detail.delete_modal.title': 'Soft-delete {vendor}/{model}?',
  'model_detail.delete_modal.body':
    'Gateway pool selectors will stop offering this model immediately. Historical usage logs continue to resolve via the preserved pricing rows.',

  // ── Gift cards ──────────────────────────────────────────────────
  'gift_cards.title': 'Gift Cards',
  'gift_cards.subtitle':
    'Issue by batch, inspect redemption state, and keep tenant self-redeem plus ops redeem-on-behalf visible in one ledger.',
  'gift_cards.batch.unbatched': 'Unbatched',
  'gift_cards.batches.title': 'Batches',
  'gift_cards.batches.empty': 'No batches yet',
  'gift_cards.batches.status.active': 'active',
  'gift_cards.batches.status.closed': 'closed',
  'gift_cards.batches.close': 'Close',
  'gift_cards.batches.reopen': 'Reopen',
  'gift_cards.batches.metric.issued': 'Issued',
  'gift_cards.batches.metric.active': 'Active',
  'gift_cards.batches.metric.redeemed': 'Redeemed',
  'gift_cards.batches.metric.value': 'Face value',
  'gift_cards.batches.create.name': 'Batch name',
  'gift_cards.batches.create.description': 'Description (optional)',
  'gift_cards.batches.create.submit': 'Create batch',
  'gift_cards.batches.create.submitting': 'Creating…',
  'gift_cards.batches.error.name_required': 'Batch name is required',
  'gift_cards.chip.revoked': 'revoked',
  'gift_cards.chip.redeemed': 'redeemed',
  'gift_cards.chip.expired': 'expired',
  'gift_cards.chip.active': 'active',
  'gift_cards.issue.title': 'Issue',
  'gift_cards.issue.subtitle': 'Hot path · creates a credit voucher',
  'gift_cards.issue.amount.label': 'Amount (RMB)',
  'gift_cards.issue.amount.placeholder': '50',
  'gift_cards.issue.code_hint.label': 'Code hint (optional)',
  'gift_cards.issue.code_hint.placeholder': 'auto-generated 32-hex',
  'gift_cards.issue.batch_id.label': 'Batch id (optional)',
  'gift_cards.issue.batch_id.placeholder': '2026-q2-promo',
  'gift_cards.issue.expires_at.label': 'Expires at (optional)',
  'gift_cards.issue.submit': 'Issue gift card',
  'gift_cards.issue.submitting': 'Issuing…',
  'gift_cards.issued.title': 'New gift card issued',
  'gift_cards.issued.code_label': 'Code (click to copy)',
  'gift_cards.issued.code_aria_label': 'Copy gift card code',
  'gift_cards.issued.copy_status.copied': 'Copied to clipboard',
  'gift_cards.issued.copy_status.idle': 'Click anywhere on this block to copy',
  'gift_cards.issued.face_value': 'Face value',
  'gift_cards.issued.batch': 'Batch',
  'gift_cards.issued.expires_at': 'Expires at',
  'gift_cards.issued.never': 'Never',
  'gift_cards.issued.credit_value': 'USD credit',
  'gift_cards.issued.applied_rate': 'Locked rate',
  'gift_cards.issued.issue_another': 'Issue another',
  'gift_cards.lookup.title': 'Lookup',
  'gift_cards.lookup.clear': 'Clear',
  'gift_cards.lookup.code.label': 'Gift card code',
  'gift_cards.lookup.code.placeholder': '32-hex code',
  'gift_cards.lookup.submit': 'Lookup',
  'gift_cards.lookup.submitting': 'Looking up…',
  'gift_cards.lookup.error.network': 'Network error',
  'gift_cards.list.title': 'Inventory',
  'gift_cards.list.batch.all': 'All batches',
  'gift_cards.list.status.all': 'All statuses',
  'gift_cards.list.account.placeholder': 'Redeemed account',
  'gift_cards.list.column.code': 'Code',
  'gift_cards.list.column.status': 'Status',
  'gift_cards.list.column.amount': 'Amount',
  'gift_cards.list.column.batch': 'Batch',
  'gift_cards.list.column.redeemed_by': 'Redeemed account',
  'gift_cards.list.column.updated': 'Latest state time',
  'gift_cards.list.column.actions': 'Actions',
  'gift_cards.list.column.source': 'Source',
  'gift_cards.list.show_code': 'Show',
  'gift_cards.list.hide_code': 'Hide',
  'gift_cards.list.copy_code': 'Copy',
  'gift_cards.list.empty': 'No gift cards match the filters',
  'gift_cards.list.total': '{total} total',
  'gift_cards.result.code': 'Code',
  'gift_cards.result.face_value': 'Face value',
  'gift_cards.result.issued_at': 'Issued at',
  'gift_cards.result.expires_at': 'Expires at',
  'gift_cards.result.never': 'Never',
  'gift_cards.result.batch_id': 'Batch id',
  'gift_cards.result.batch_id_empty': '—',
  'gift_cards.result.redeemed_at': 'Redeemed at',
  'gift_cards.result.redeemed_by': 'Redeemed by account',
  'gift_cards.result.redeemed_by_empty': '—',
  'gift_cards.result.credit_value': 'USD credit',
  'gift_cards.result.applied_rate': 'Locked rate',
  'gift_cards.result.revoked_at': 'Revoked at',
  'gift_cards.result.revoke': 'Revoke',
  'gift_cards.result.redeem': 'Redeem on behalf',
  'gift_cards.revoke_modal.title': 'Revoke gift card?',
  'gift_cards.revoke_modal.body_prefix': 'This permanently disables ',
  'gift_cards.revoke_modal.body_separator': ' (face value ',
  'gift_cards.revoke_modal.body_suffix': '). Cannot be undone.',
  'gift_cards.revoke_modal.reason.label': 'Reason (optional, recorded in audit log)',
  'gift_cards.revoke_modal.reason.placeholder': 'duplicate / accidental / fraud',
  'gift_cards.revoke_modal.submit': 'Revoke',
  'gift_cards.revoke_modal.submitting': 'Revoking…',
  'gift_cards.revoke_modal.toast': 'Gift card {code} revoked',
  'gift_cards.redeem_modal.title': 'Redeem on behalf',
  'gift_cards.redeem_modal.body_prefix': 'This will credit ',
  'gift_cards.redeem_modal.body_middle': ' to the target account for card ',
  'gift_cards.redeem_modal.body_suffix':
    '. RMB face value: {faceValue}. Funds settle on the platform ledger immediately and cannot be reversed without a balance adjustment.',
  'gift_cards.redeem_modal.account.label': 'Target account id (UUID)',
  'gift_cards.redeem_modal.account.placeholder': 'acct_…',
  'gift_cards.redeem_modal.confirm.label': 'Confirm amount (type {amount})',
  'gift_cards.redeem_modal.reason.label': 'Reason (optional)',
  'gift_cards.redeem_modal.reason.placeholder': 'customer support / promo redemption',
  'gift_cards.redeem_modal.idempotency': 'Idempotency key:',
  'gift_cards.redeem_modal.submit': 'Credit {amount}',
  'gift_cards.redeem_modal.submitting': 'Crediting…',
  'gift_cards.redeem_modal.toast': 'Credited {amount} to {account} — new balance {balance}',
  'gift_cards.redeem_modal.error.account_required': 'Account id is required',
  'gift_cards.redeem_modal.error.amount_mismatch':
    'Confirm amount must equal {amount} (defense-in-depth on a money move)',
  'gift_cards.bulk.title': 'Bulk activity log',
  'gift_cards.bulk.body':
    'No bulk activity tracking in Phase 1. Issued / revoked / redeemed events live on the platform ledger and gateway audit logs; a dedicated activity surface is deferred.',
  'gift_cards.issue.error.amount_positive': 'Amount must be a positive RMB value',
  'gift_cards.issue.error.amount_whole_yuan': 'RMB gift card amount must be whole yuan',
  'gift_cards.issue.error.expires_at_invalid': 'Expires-at must be a valid date',
  'gift_cards.issue.error.expires_at_future': 'Expires-at must be in the future',

  // ── Accounts list ───────────────────────────────────────────────
  'accounts.title': 'Accounts',
  'accounts.subtitle':
    'Tenant accounts with org membership, current balance, and recent ledger access. Click an email to open the detail / balance-adjustment surface.',
  'accounts.search.placeholder': 'Search email…',
  'accounts.search.aria_label': 'Search account email',
  'accounts.has_balance': 'Has balance',
  'accounts.low_balance': 'Low balance',
  'accounts.sort.aria_label': 'Sort accounts',
  'accounts.sort.created': 'Newest first',
  'accounts.sort.balance': 'Lowest balance first',
  'accounts.search.submit': 'Search',
  'accounts.search.clear': 'Clear',
  'accounts.error.title': 'Failed to load accounts',
  'accounts.loading_aria': 'Loading accounts…',
  'accounts.empty.filtered.title': 'No accounts match these filters',
  'accounts.empty.filtered.body': 'Clear the filters or refine the search.',
  'accounts.empty.initial.title': 'No accounts registered',
  'accounts.empty.initial.body':
    'Accounts appear here once a tenant signs up through the public API.',
  'accounts.column.email': 'Email',
  'accounts.column.org_id': 'Org id',
  'accounts.column.discount': 'Discount',
  'accounts.column.sku_rates': 'SKU rates',
  'accounts.column.balance': 'Balance',
  'accounts.column.brand': 'Brand',
  'accounts.column.kind': 'Kind',
  'accounts.column.created': 'Created',
  'accounts.chip.system': 'system',
  'accounts.chip.tenant': 'tenant',
  'accounts.discount.no_discount': 'no discount',
  'accounts.discount.discounted': 'discounted',
  'accounts.sku_rates.default': 'default',
  'accounts.sku_rates.overrides': 'overrides',
  'accounts.pagination.aria_label': 'Pagination',
  'accounts.pagination.range': 'Showing {start}–{end} of {total}',

  // ── Brand authority (epic #1030 E1-S4, #1040) ──────────────────
  'brand_scope.cross_brand': 'Cross-brand view',
  'brand_scope.self_scoped': 'Brand {brand} · my customers',
  'brand_scope.brand_wide': 'Brand {brand} · whole brand book',
  'brand_scope.no_profile': 'No sales profile configured — ask an admin to assign a brand',
  'brand_scope.filter.aria_label': 'Filter by brand',
  'brand_scope.filter.all': 'All brands',
  'brand_edit.action_aria': 'Edit customer brand',
  'brand_edit.kicker': 'Brand authority',
  'brand_edit.title': 'Correct customer brand',
  'brand_edit.customer': 'Customer',
  'brand_edit.brand': 'Brand',
  'brand_edit.guard_hint':
    'The change is refused while a manager of a different brand actively owns this customer.',
  'brand_edit.error.conflict':
    'This customer is actively owned by a manager of a different brand; reassign or release the customer first.',
  'brand_edit.toast.saved': 'Customer brand updated.',

  // ── Customer ownership ─────────────────────────────────────────
  'customer_ownership.kicker': 'Customer-manager workspace',
  'customer_ownership.admin.title': 'Customer Ownership',
  'customer_ownership.admin.subtitle':
    'Inspect customer owner status and maintain org-level customer-manager assignments.',
  'customer_ownership.manager.title': 'My Customers',
  'customer_ownership.manager.subtitle':
    'Only customer organizations assigned to the current manager are visible.',
  'customer_ownership.manager.readonly':
    'This view is scoped to the customer-manager role and is read-only.',
  'customer_ownership.metric.total': 'Total',
  'customer_ownership.metric.assigned_page': 'Assigned on page',
  'customer_ownership.metric.unassigned_page': 'Unassigned on page',
  'customer_ownership.search.placeholder': 'Search customer email / company…',
  'customer_ownership.search.aria_label': 'Search customer email or company name',
  'customer_ownership.search.submit': 'Filter',
  'customer_ownership.search.clear': 'Clear',
  'customer_ownership.error.title': 'Failed to load customer ownership',
  'customer_ownership.loading_aria': 'Loading customer ownership…',
  'customer_ownership.empty.filtered.title': 'No customers match these filters',
  'customer_ownership.empty.filtered.body': 'Clear the filters or refine the search.',
  'customer_ownership.empty.admin.title': 'No customers',
  'customer_ownership.empty.admin.body':
    'Customer ownership queues appear here once tenant accounts are registered.',
  'customer_ownership.empty.manager.title': 'No assigned customers',
  'customer_ownership.empty.manager.body':
    'This customer manager does not have any customer assignments yet.',
  'customer_ownership.column.customer': 'Customer',
  'customer_ownership.column.org': 'Org',
  'customer_ownership.column.balance': 'Balance',
  'customer_ownership.column.brand': 'Brand',
  'customer_ownership.column.owner': 'Owner',
  'customer_ownership.column.assigned_at': 'Assigned at',
  'customer_ownership.column.created': 'Created',
  'customer_ownership.column.actions': 'Actions',
  'customer_ownership.owner.assigned': 'assigned',
  'customer_ownership.owner.unassigned': 'unassigned',
  'customer_ownership.action.assign': 'Assign',
  'customer_ownership.action.reassign': 'Reassign',
  'customer_ownership.action.revoke': 'Revoke',
  'customer_ownership.action.readonly': 'Read only',
  'customer_ownership.pagination.aria_label': 'Customer ownership pagination',
  'customer_ownership.pagination.range': 'Showing {start}–{end} of {total}',
  'customer_ownership.assignment.kicker': 'Ownership action',
  'customer_ownership.assignment.title': 'Assign customer manager',
  'customer_ownership.assignment.customer': 'Customer',
  'customer_ownership.assignment.manager': 'Customer manager',
  'customer_ownership.assignment.select_placeholder': 'Select customer manager',
  'customer_ownership.assignment.current_out_of_band': 'Current owner: {owner}',
  'customer_ownership.assignment.duplicate_name_warning':
    'Several customer managers are named "{name}". Check the sign-in method and email to confirm you picked the right account.',
  'customer_ownership.toast.assigned': 'Customer-manager ownership updated',
  'customer_ownership.toast.revoked': 'Customer-manager ownership revoked',

  // ── Customer manager workspace ─────────────────────────────────
  'customer_manager_workspace.kicker': 'Ops v2 · live workspace',
  'customer_manager_workspace.title': 'Customer Manager Workspace',
  'customer_manager_workspace.subtitle.admin':
    'Inspect the live customer-manager portfolio with the same scoped signals available to customer managers.',
  'customer_manager_workspace.subtitle.manager':
    'Track assigned customers, risk signals, usage posture, API key state, and lifecycle evidence.',
  'customer_manager_workspace.generated_at': 'generated {time}',
  'customer_manager_workspace.generated_pending': 'waiting for live data',
  'customer_manager_workspace.metric.customers': 'Assigned customers',
  'customer_manager_workspace.metric.balance': 'Total RMB book balance',
  'customer_manager_workspace.metric.active_keys': 'Active API keys',
  'customer_manager_workspace.metric.requests': '30d requests',
  'customer_manager_workspace.metric.errors': '30d errors',
  'customer_manager_workspace.search.placeholder': 'Search customer email / company…',
  'customer_manager_workspace.search.aria_label': 'Search customer email or company name',
  'customer_manager_workspace.search.submit': 'Filter',
  'customer_manager_workspace.search.clear': 'Clear',
  'customer_manager_workspace.error.title': 'Failed to load customer-manager workspace',
  'customer_manager_workspace.empty.title': 'No assigned customers',
  'customer_manager_workspace.empty.body':
    'Customer-manager portfolio rows appear here after org-level assignments are active.',
  'customer_manager_workspace.empty.filtered.title': 'No customers match these filters',
  'customer_manager_workspace.empty.filtered.body': 'Clear the filter or refine the search.',
  'customer_manager_workspace.risk_queue.title': 'Risk queue',
  'customer_manager_workspace.risk_queue.count': '{count} customers',
  'customer_manager_workspace.risk_queue.item_detail': '{count} signals',
  'customer_manager_workspace.column.customer': 'Customer',
  'customer_manager_workspace.column.org': 'Org',
  'customer_manager_workspace.column.balance': 'RMB book balance',
  'customer_manager_workspace.column.usage': 'Usage',
  'customer_manager_workspace.column.keys': 'API keys',
  'customer_manager_workspace.column.owner': 'Owner',
  'customer_manager_workspace.column.risks': 'Risks',
  'customer_manager_workspace.column.actions': 'Actions',
  'customer_manager_workspace.usage.errors': '{count} errors',
  'customer_manager_workspace.usage.no_errors': 'no errors',
  'customer_manager_workspace.keys.active': '{active}/{total} active',
  'customer_manager_workspace.keys.last_used': 'last used {time}',
  'customer_manager_workspace.risk.none': 'stable',
  'customer_manager_workspace.action.customer_detail': 'Customer detail',
  'customer_manager_workspace.action.record_receipt': 'Record receipt',
  'customer_manager_workspace.pagination.aria_label': 'Customer-manager pagination',
  'customer_manager_workspace.pagination.range': 'Showing {start}–{end} of {total}',
  'customer_manager_workspace.drawer.kicker': 'Customer detail',
  'customer_manager_workspace.drawer.error': 'Failed to load customer detail',
  'customer_manager_workspace.drawer.balance': 'RMB book balance',
  'customer_manager_workspace.drawer.requests': 'Requests',
  'customer_manager_workspace.drawer.errors': 'Errors',
  'customer_manager_workspace.drawer.active_keys': 'Active keys',
  'customer_manager_workspace.drawer.owner': 'Owner and posture',
  'customer_manager_workspace.drawer.owner_name': 'Owner',
  'customer_manager_workspace.drawer.assigned_at': 'Assigned at',
  'customer_manager_workspace.drawer.created_at': 'Created at',
  'customer_manager_workspace.drawer.last_request': 'Last request',
  'customer_manager_workspace.drawer.recent_failures': 'Recent failures',
  'customer_manager_workspace.drawer.latest_failure': 'Latest failure',
  'customer_manager_workspace.drawer.open_account': 'Open account detail',
  'customer_manager_workspace.drawer.api_keys': 'API key state',
  'customer_manager_workspace.drawer.key_name': 'Name',
  'customer_manager_workspace.drawer.key_prefix': 'Prefix',
  'customer_manager_workspace.drawer.key_channel': 'Channel',
  'customer_manager_workspace.drawer.key_state': 'State',
  'customer_manager_workspace.drawer.key_active': 'active',
  'customer_manager_workspace.drawer.key_revoked': 'revoked',
  'customer_manager_workspace.drawer.lifecycle': 'Lifecycle evidence',
  'customer_manager_workspace.drawer.no_requests': 'No recent request evidence.',
  'customer_manager_workspace.column.spend_30d': '30d consumption (RMB)',
  'customer_manager_workspace.money.error.title': 'Failed to load portfolio consumption data',
  'customer_manager_workspace.money.consumption_note':
    'Consumption lens: tenant charge (usd_charged) shown in RMB — not cash revenue.',
  'customer_manager_workspace.money.funnel.owned': 'Owned customers',
  'customer_manager_workspace.money.funnel.active_30d': 'Active customers (30d)',
  'customer_manager_workspace.money.funnel.paying_30d': 'Paying customers (30d)',
  'customer_manager_workspace.money.funnel.conversion': 'Active → paying conversion',
  'customer_manager_workspace.money.funnel.conversion_pending': 'No active customers',
  'customer_manager_workspace.money.trend.title': 'Portfolio consumption trend',
  'customer_manager_workspace.money.trend.days_30': '30 days',
  'customer_manager_workspace.money.trend.days_90': '90 days',
  'customer_manager_workspace.money.trend.aria': 'Daily portfolio consumption (RMB)',
  'customer_manager_workspace.money.trend.empty': 'No consumption inside this window.',
  'customer_manager_workspace.money.trend.tooltip': '{date} · consumption {amount} · {count} requests',
  'customer_manager_workspace.money.top.title': 'Top customers by consumption (30d)',
  'customer_manager_workspace.money.top.column.customer': 'Customer',
  'customer_manager_workspace.money.top.column.requests': '30d requests',
  'customer_manager_workspace.money.top.column.spend': '30d consumption (RMB)',
  'customer_manager_workspace.money.top.column.actions': 'Actions',
  'customer_manager_workspace.money.top.empty': 'No customer consumption in the last 30 days.',
  'customer_manager_workspace.money.top.open_360': 'Customer detail',
  'customer_manager_workspace.money.top.unlinked': 'No customer record',
  'customer_manager_workspace.runway.title': 'Top-up chase (runway)',
  'customer_manager_workspace.runway.error.title': 'Failed to load the top-up chase list',
  'customer_manager_workspace.runway.note':
    'Days left = remaining credit ÷ burn rate over the last {days} days. Consumption lens, not cash revenue.',
  'customer_manager_workspace.runway.threshold': '≤ {days}d',
  'customer_manager_workspace.runway.threshold_aria': 'Days-left threshold',
  'customer_manager_workspace.runway.column.customer': 'Customer',
  'customer_manager_workspace.runway.column.days_left': 'Days left',
  'customer_manager_workspace.runway.column.remaining': 'Remaining (RMB)',
  'customer_manager_workspace.runway.column.burn_per_day': 'Burn / day (RMB)',
  'customer_manager_workspace.runway.column.actions': 'Actions',
  'customer_manager_workspace.runway.days_value': '{days}d',
  'customer_manager_workspace.runway.empty': 'No owned customer runs dry within {days} days.',
  'customer_manager_workspace.runway.open_360': 'Customer detail',
  // Scoped usage overview (epic #1035 E6-D4, #1068)
  'crm_usage.title': 'Customer usage',
  'crm_usage.sub': 'Requests, tokens, and charged spend across your book, by model and SKU.',
  'crm_usage.window_aria': 'usage window',
  'crm_usage.window.today': 'Today',
  'crm_usage.window.custom': 'Custom',
  'crm_usage.from_local': 'From local',
  'crm_usage.to_local': 'To local',
  'crm_usage.account_filter': 'Customer filter',
  'crm_usage.clear_filter': 'Clear filter',
  'crm_usage.export_csv': 'Export CSV',
  'crm_usage.loading': 'Loading usage…',
  'crm_usage.error': 'Failed to load usage: {message}',
  'crm_usage.metric.requests': 'Requests',
  'crm_usage.metric.requests_sub': 'Success {success} · Errors {error}',
  'crm_usage.metric.tokens': 'Tokens',
  'crm_usage.metric.tokens_sub': 'Input {input} · Output {output}',
  'crm_usage.metric.cache': 'Cache tokens',
  'crm_usage.metric.cache_sub': 'Read {read} · Write {write}',
  'crm_usage.metric.charged': 'Charged',
  'crm_usage.metric.charged_sub': '{usd} USD',
  'crm_usage.metric.window': 'Window',
  'crm_usage.metric.window_book_sub': 'Covers {count} customers in your book',
  'crm_usage.metric.window_customer_sub': 'Single-customer view',
  'crm_usage.metric.window_all_sub': 'Current scope',
  'crm_usage.table.models': 'By model',
  'crm_usage.table.skus': 'By SKU',
  'crm_usage.table.empty_models': 'No model usage in this window',
  'crm_usage.table.empty_skus': 'No SKU usage in this window',
  'crm_usage.col.object': 'Object',
  'crm_usage.col.requests': 'Requests',
  'crm_usage.col.io_tokens': 'In/Out',
  'crm_usage.col.cache_tokens': 'Cache R/W',
  'crm_usage.col.charged': 'Charged',
  'crm_usage.legacy_sku': 'Unattributed historical usage',
  // Lead pricing panel (epic #1032 E3-P1, #1102)
  'lead_pricing.title': 'Lead pricing',
  'lead_pricing.sub': 'Per-SKU sales costs for your brand. The sales floor everywhere derives from these values via the USD_CNY policy rate; a sales cost can never sit below procurement.',
  'lead_pricing.brand_aria': 'brand',
  'lead_pricing.policy_rate': 'USD_CNY policy rate {rate}',
  'lead_pricing.error.load': 'Failed to load sales costs',
  'lead_pricing.empty.title': 'No SKUs in the catalog',
  'lead_pricing.empty.hint': 'Sales costs attach to marketplace SKUs; add channel models first.',
  'lead_pricing.bulk.label': 'Bulk sales cost (CNY/USD)',
  'lead_pricing.bulk.apply': 'Apply to all',
  'lead_pricing.toast.saved': 'Sales cost saved',
  'lead_pricing.toast.cleared': 'Sales cost cleared',
  'lead_pricing.toast.bulk_applied': 'Applied sales cost to {count} SKUs atomically',
  'lead_pricing.table.model': 'Model',
  'lead_pricing.table.procurement': 'Procurement (CNY/USD)',
  'lead_pricing.table.procurement_floor': 'Procurement floor ×',
  'lead_pricing.table.sales_cost': 'Sales cost (CNY/USD)',
  'lead_pricing.table.sales_floor': 'Sales floor ×',
  'lead_pricing.table.note': 'Note',
  'lead_pricing.table.actions': 'Actions',
  'lead_pricing.row.save': 'Save',
  'lead_pricing.row.clear': 'Clear',
  'lead_pricing.row.not_set': 'not set',
  'lead_pricing.alerts.title': 'Procurement rose above sales cost',
  'lead_pricing.alerts.sub': 'These SKUs now cost more to buy than your sales cost — raise the sales cost to restore the spread.',
  'lead_pricing.alerts.table.model': 'SKU',
  'lead_pricing.alerts.table.procurement': 'Procurement (CNY/USD)',
  'lead_pricing.alerts.table.sales_cost': 'Sales cost (CNY/USD)',
  'lead_pricing.alerts.table.since': 'Since',
  // Salesperson invite codes (epic #1475). One page, three scopes — the
  // copy avoids promising a total quota the session cannot see: a CM
  // reads today's count, not a headroom the server computes per code.
  'invite_codes.title': 'Invite codes',
  'invite_codes.sub':
    'One long-lived code per salesperson. New customers open an account with an invite code plus an email address and receive their trial credit on the spot. Each code carries its own trial-credit amount and validity; how many activations it can do per day is capped on a UTC+8 day boundary that resets at midnight and does not carry over.',
  'invite_codes.readonly': 'Read-only',
  'invite_codes.brand_aria': 'Brand',
  'invite_codes.filter.brand_all': 'All brands',
  'invite_codes.filter.aria': 'Status filter',
  'invite_codes.filter.all': 'All statuses',
  'invite_codes.filter.active': 'Active only',
  'invite_codes.filter.disabled': 'Disabled only',
  'invite_codes.empty': 'No invite codes yet',
  'invite_codes.error.load': 'Could not load invite codes',
  'invite_codes.no_profile.title': 'No sales profile configured',
  'invite_codes.no_profile.hint':
    'Invite codes are partitioned by brand, and this account has no sales profile, so there is no scope to show. Ask an admin to assign a brand on the Ops users page, then retry.',
  'invite_codes.column.code': 'Code',
  'invite_codes.column.owner': 'Salesperson',
  'invite_codes.column.brand': 'Brand',
  'invite_codes.column.status': 'Status',
  'invite_codes.column.today': 'Used today',
  'invite_codes.column.quota': 'Daily quota',
  'invite_codes.column.created': 'Created',
  'invite_codes.column.plan': 'Model plan',
  'invite_codes.column.actions': 'Actions',
  'invite_codes.plan.none': '—',
  'invite_codes.status.active': 'Active',
  'invite_codes.status.disabled': 'Disabled',
  'invite_codes.action.quota': 'Edit config',
  'invite_codes.action.disable': 'Disable',
  'invite_codes.action.enable': 'Enable',
  'invite_codes.action.activations': 'Activations',
  'invite_codes.action.rebind': 'Rebind plan',
  'invite_codes.activations.title': 'Activations for {code}',
  'invite_codes.activations.summary':
    '{total} customers invited in total, {today} today, against a daily quota of {quota}.',
  'invite_codes.activations.column.email': 'Customer email',
  'invite_codes.activations.column.org': 'Organization',
  'invite_codes.activations.column.activated_at': 'Activated',
  'invite_codes.activations.column.grant': 'Trial credit',
  'invite_codes.activations.column.grant_expires': 'Credit expires',
  'invite_codes.activations.empty': 'Nobody has opened an account with this code yet',
  'invite_codes.activations.error': 'Could not load activations',
  'invite_codes.activations.truncated':
    'Showing the {shown} most recent of {total} activations.',
  'invite_codes.toast.disabled': 'Invite code disabled',
  'invite_codes.toast.enabled': 'Invite code enabled',
  'invite_codes.disable.title': 'Disable this invite code?',
  'invite_codes.disable.body':
    '{code} can no longer be used to activate an account. Customers already activated are unaffected. Disabling is what frees this salesperson to be issued a new code.',
  'invite_codes.enable.title': 'Re-enable this invite code?',
  'invite_codes.enable.body':
    '{code} becomes usable again. A salesperson may hold only one active code at a time — if they already have another active code, this is rejected.',
  'invite_codes.mint.open': 'New invite code',
  'invite_codes.mint.title': 'New invite code',
  'invite_codes.mint.description':
    'The code itself is generated — there is nothing to type. A salesperson may hold only one active code at a time.',
  'invite_codes.mint.description_admin':
    'The code itself is generated — there is nothing to type. A salesperson may hold only one active code at a time. Pick a brand below to choose from that brand’s salespeople.',
  'invite_codes.mint.submit': 'Generate',
  'invite_codes.mint.owner_label': 'Salesperson',
  'invite_codes.mint.owner_hint':
    'Customers who activate with this code are assigned to this salesperson automatically.',
  'invite_codes.mint.owner_placeholder': 'Select a salesperson',
  'invite_codes.mint.owner_empty': 'No salespeople found for this brand.',
  'invite_codes.mint.owner_load_error': 'Could not load salespeople. Try reopening this dialog.',
  'invite_codes.mint.quota_label': 'Daily quota',
  'invite_codes.mint.quota_hint':
    'Prefilled with the recommended value. Whatever is in this box is written onto the code.',
  'invite_codes.mint.grant_label': 'Trial credit (CNY)',
  'invite_codes.mint.grant_hint':
    'Prefilled with the recommended value. Whatever is in this box is written onto the code.',
  'invite_codes.mint.expiry_label': 'Trial credit validity (days)',
  'invite_codes.mint.expiry_hint':
    'Prefilled with the recommended value. Whatever is in this box is written onto the code.',
  'invite_codes.mint.plan_label': 'Model plan',
  'invite_codes.mint.plan_hint':
    'Optional. Customers who activate with this code follow the plan for models and discounts. Mutually exclusive with one-shot per-code discounts.',
  'invite_codes.mint.plan_none': 'No plan',
  'invite_codes.mint.plan_placeholder': 'Select an active plan',
  'invite_codes.mint.plan_empty': 'No active model plans for this brand.',
  'invite_codes.mint.error.owner': 'Select a salesperson',
  'invite_codes.mint.error.quota': 'Daily quota must be a positive integer',
  'invite_codes.mint.error.grant':
    'Trial credit must be greater than 0, with at most two decimal places',
  'invite_codes.mint.error.grant_cap': 'Trial credit exceeds the grant amount cap of {max}',
  'invite_codes.mint.error.expiry': 'Trial credit validity must be a positive whole number of days',
  'invite_codes.mint.error.defaults': 'Could not load the global activation defaults. Close and retry.',
  'invite_codes.mint.toast': 'Invite code {code} generated',
  'invite_codes.rebind.title': 'Rebind the model plan on {code}',
  'invite_codes.rebind.description':
    'Customers hang off a plan; the code only decides which plan a FUTURE activation lands on. Choose below whether this rebind also moves the customers this code already activated.',
  'invite_codes.rebind.current_plan': 'Currently bound plan: {name}',
  'invite_codes.rebind.current_plan_none': 'No plan currently bound — activations use the legacy flow.',
  'invite_codes.rebind.plan_label': 'Model plan',
  'invite_codes.rebind.unbind': 'Unbind (no plan)',
  'invite_codes.rebind.scope_label': 'Who does this rebind affect?',
  'invite_codes.rebind.scope_future': 'Future activations only',
  'invite_codes.rebind.scope_future_hint':
    'Customers already activated by this code keep their current plan and prices, untouched.',
  'invite_codes.rebind.scope_restate': 'Also move the customers this code already activated',
  'invite_codes.rebind.scope_restate_hint':
    'Orgs still attributed to this code migrate onto the selected plan and their prices change with it — review the preview below before confirming. This is a bulk reprice.',
  'invite_codes.rebind.scope_restate_admin_only': 'Admin only — a bulk reprice of live customers.',
  'invite_codes.rebind.scope_required': 'Choose who this rebind affects before confirming.',
  'invite_codes.rebind.unbind_note':
    'Unbinding affects future activations only: new customers from this code will no longer bind a plan. Already-activated customers keep their current plan — with no destination plan there is nothing to migrate them onto.',
  'invite_codes.rebind.preview_loading': 'Loading the restate preview…',
  'invite_codes.rebind.preview_error': 'Could not load the restate preview',
  'invite_codes.rebind.preview_empty': 'No orgs still attributed to this code would be restated.',
  'invite_codes.rebind.preview_orgs': '{count} customers would move onto the selected plan',
  'invite_codes.rebind.legacy_discounts':
    'This code still carries one-shot rate discounts. Generate a plan from them below, or clear them in Edit config before binding another plan.',
  'invite_codes.rebind.from_discounts.name': 'Plan name',
  'invite_codes.rebind.from_discounts.hint': 'Brand is this code’s brand. Floor checks run before anything is written.',
  'invite_codes.rebind.from_discounts.name_required': 'Enter a plan name',
  'invite_codes.rebind.from_discounts.submit': 'Generate a plan from this code’s discounts',
  'invite_codes.rebind.from_discounts.created': 'Created plan {name}',
  'invite_codes.rebind.from_discounts.open': 'Open model plans',
  'invite_codes.rebind.from_discounts.toast': 'Plan {name} created and bound',
  'invite_codes.rebind.submit': 'Confirm rebind',
  'invite_codes.rebind.toast': 'Model plan updated',
  'invite_codes.rebind.toast_unbound': 'Model plan unbound',
  'invite_codes.quota.title': 'Activation config for {code}',
  'invite_codes.quota.label': 'Daily quota',
  'invite_codes.quota.hint':
    'Written onto this code as an explicit value. Restore recommended refills grant, expiry, and daily quota. Already-issued grants keep their original amount and expiry; today’s used count is unaffected.',
  'invite_codes.quota.grant_label': 'Trial credit (CNY)',
  'invite_codes.quota.grant_hint':
    'Written onto this code as an explicit value. Already-issued grants are not restated.',
  'invite_codes.quota.expiry_label': 'Trial credit validity (days)',
  'invite_codes.quota.expiry_hint':
    'Written onto this code as an explicit value. Already-issued grants are not restated.',
  'invite_codes.quota.restore': 'Restore recommended grant, expiry, and quota',
  'invite_codes.quota.submit': 'Save',
  'invite_codes.quota.error.quota': 'Daily quota must be a positive integer',
  'invite_codes.quota.error.grant':
    'Trial credit must be greater than 0, with at most two decimal places',
  'invite_codes.quota.error.grant_cap': 'Trial credit exceeds the grant amount cap of {max}',
  'invite_codes.quota.error.expiry':
    'Trial credit validity must be a positive whole number of days',
  'invite_codes.quota.toast': 'Activation config updated',
  // Admin-only activation settings. Snapshot semantics are the load-
  // bearing part of this copy: changing the grant does not retroactively
  // top up or claw back balances already issued.
  'invite_codes.settings.panel': 'Activation settings (global)',
  'invite_codes.settings.load_error': 'Could not load activation settings',
  'invite_codes.settings.snapshot_note':
    'Recommended trial credit is currently {current}. Changing the three recommended values only changes what the mint dialog prefills next time (and still-NULL legacy codes). The grant cap and login-code limits take effect immediately. Grants already issued keep their original amount and expiry.',
  'invite_codes.settings.save': 'Save settings',
  'invite_codes.settings.toast.saved': 'Activation settings saved',
  'invite_codes.settings.grant_amount_label': 'Recommended trial credit (CNY)',
  'invite_codes.settings.grant_amount_hint':
    'Prefilled into the mint dialog. Already-minted codes keep the value written on them. Spends under the normal gift-balance rules: gift balance first, soonest-to-expire first.',
  'invite_codes.settings.grant_expiry_label': 'Recommended trial credit validity (days)',
  'invite_codes.settings.grant_expiry_hint':
    'Prefilled into the mint dialog. Counted from the moment of the grant. Whatever is left at expiry is deducted by the hourly sweep and does not carry over.',
  'invite_codes.settings.default_quota_label': 'Recommended daily quota',
  'invite_codes.settings.default_quota_hint':
    'Prefilled into the mint dialog. Day boundary is UTC+8. Already-minted codes keep the quota written on them.',
  'invite_codes.settings.max_grant_label': 'Per-code trial credit cap (CNY)',
  'invite_codes.settings.max_grant_hint':
    'Hard cap, takes effect immediately. A mint or an edit that changes a code’s grant may not exceed this. The recommended trial credit above must not be higher than this cap.',
  'invite_codes.settings.code_ttl_label': 'Login code lifetime (seconds)',
  'invite_codes.settings.code_ttl_hint':
    'How long a login code stays valid after it is sent.',
  'invite_codes.settings.resend_interval_label': 'Resend interval (seconds)',
  'invite_codes.settings.resend_interval_hint':
    'Minimum gap between two code requests for the same email address.',
  'invite_codes.settings.send_limit_label': 'Daily sends per email',
  'invite_codes.settings.send_limit_hint':
    'How many login-code emails one address can receive in a day.',
  'invite_codes.settings.max_attempts_label': 'Attempts per code',
  'invite_codes.settings.max_attempts_hint':
    'How many wrong guesses one code tolerates before it is burned and a new one must be requested.',
  'invite_codes.settings.error.grant_amount':
    'Trial credit must be greater than 0, with at most two decimal places',
  'invite_codes.settings.error.grant_expiry':
    'Trial credit validity must be a positive whole number of days',
  'invite_codes.settings.error.default_quota':
    'Recommended daily quota must be a positive integer',
  'invite_codes.settings.error.max_grant':
    'Per-code trial credit cap must be greater than 0, with at most two decimal places',
  'invite_codes.settings.error.grant_above_cap':
    'Recommended trial credit cannot exceed the per-code grant cap',
  'invite_codes.settings.error.code_ttl':
    'Login code lifetime must be a positive whole number of seconds',
  'invite_codes.settings.error.resend_interval':
    'Resend interval must be a positive whole number of seconds',
  'invite_codes.settings.error.send_limit':
    'Daily sends per email must be a positive integer',
  'invite_codes.settings.error.max_attempts':
    'Attempts per code must be a positive integer',
  // Model plans (epic #2292 / #2297). A plan is a live catalog template
  // bound to invite codes: allowed model groups, per-group default
  // discount, per-SKU exceptions and visibility.
  'model_plans.title': 'Model plans',
  'model_plans.sub':
    'Catalog templates bound to invite codes. Editing a plan restates assigned customers; trial grants and quotas stay as mint-time snapshots.',
  'model_plans.readonly': 'Read-only',
  'model_plans.brand_aria': 'Brand',
  'model_plans.filter.brand_all': 'All brands',
  'model_plans.filter.aria': 'Status filter',
  'model_plans.filter.all': 'All statuses',
  'model_plans.filter.active': 'Active only',
  'model_plans.filter.archived': 'Archived only',
  'model_plans.empty': 'No model plans yet',
  'model_plans.error.load': 'Could not load model plans',
  'model_plans.no_profile.title': 'No sales profile configured',
  'model_plans.no_profile.hint':
    'Model plans are partitioned by brand, and this account has no sales profile, so there is no scope to show. Ask an admin to assign a brand on the Ops users page, then retry.',
  'model_plans.column.name': 'Name',
  'model_plans.column.brand': 'Brand',
  'model_plans.column.status': 'Status',
  'model_plans.column.entries': 'Groups / SKUs',
  'model_plans.column.bound_codes': 'Bound codes',
  'model_plans.column.assigned_orgs': 'Assigned orgs',
  'model_plans.column.updated': 'Updated',
  'model_plans.column.actions': 'Actions',
  'model_plans.status.active': 'Active',
  'model_plans.status.archived': 'Archived',
  'model_plans.unsynced_badge': '{count} new',
  'model_plans.action.create': 'New plan',
  'model_plans.action.edit': 'Edit / details',
  'model_plans.action.clone': 'Clone',
  'model_plans.action.archive': 'Archive',
  'model_plans.action.unarchive': 'Unarchive',
  'model_plans.action.detail': 'Sync history',
  'model_plans.action.sync': 'Sync new models',
  'model_plans.create.title': 'New model plan',
  'model_plans.edit.title': 'Edit {name}',
  'model_plans.editor.brand': 'Brand',
  'model_plans.editor.name': 'Name',
  'model_plans.editor.name_placeholder': 'e.g. Standard package',
  'model_plans.editor.description': 'Description',
  'model_plans.editor.description_placeholder': 'Optional',
  'model_plans.editor.add_namespace': 'Add model group',
  'model_plans.editor.namespace_placeholder': 'Select a group',
  'model_plans.editor.namespace_empty':
    'No listed groups for this brand, or every group is already on the plan.',
  'model_plans.editor.default_rate': 'Group default multiplier',
  'model_plans.editor.sales_floor': 'Sales floor {floor}',
  'model_plans.editor.sales_floor_unset': 'No sales floor configured',
  'model_plans.editor.sales_floor_partial': 'Some SKUs have no sales floor',
  'model_plans.editor.remove_namespace': 'Remove group',
  'model_plans.editor.sku_visible': 'Visible',
  'model_plans.editor.sku_hidden': 'Hidden',
  'model_plans.editor.sku_rate': 'SKU multiplier (blank inherits the group default)',
  'model_plans.editor.sku_inherit': 'Inherit',
  'model_plans.editor.sku_floor_header': 'Sales floor',
  'model_plans.editor.no_skus': 'No listed SKUs in this group.',
  'model_plans.editor.new_arrival_group':
    '{count} new models not on this plan (currently hidden from customers, charged at list price)',
  'model_plans.editor.new_arrival_sku': 'NEW',
  'model_plans.editor.submit_create': 'Create',
  'model_plans.editor.submit_edit': 'Preview blast radius',
  'model_plans.editor.error.name': 'Enter a plan name',
  'model_plans.editor.error.brand': 'Select a brand',
  'model_plans.editor.error.entries': 'Add at least one model group',
  'model_plans.editor.error.rate': 'Multiplier must be a positive number',
  'model_plans.floor.breaches_title': 'These SKUs resolve below the sales floor',
  'model_plans.floor.row':
    '{sku} · resolved {rate} < sales floor {floor} (from {source})',
  'model_plans.floor.row_no_rate': '{sku} · sales floor {floor}',
  'model_plans.floor.row_no_source': '{sku} · resolved {rate} < sales floor {floor}',
  'model_plans.floor.row_unknown_floor':
    '{sku} · below the price floor (no sales floor configured; checked against the procurement baseline)',
  'model_plans.floor.row_unknown_floor_rate':
    '{sku} · resolved rate {rate} is below the price floor (no sales floor configured; checked against the procurement baseline)',
  'model_plans.floor.unconfigured_hint':
    'SKUs without a configured sales floor are checked against the procurement baseline, whose value is not shown on this surface. Configure their sales floor on the pricing page, or raise the rate and retry.',
  'model_plans.floor.source.group_default': 'group default',
  'model_plans.floor.source.sku_override': 'this-row override',
  'model_plans.floor.hint':
    'Resolved {rate} < sales floor {floor} (from {source})',
  'model_plans.floor.group_default_error':
    'This default puts one or more SKUs below the sales floor',
  'model_plans.floor.block_title': 'Rates below the sales floor cannot be saved',
  'model_plans.floor.block_body':
    'Raise the group default or the per-SKU override, then submit again.',
  'model_plans.floor.block_back': 'Go back and edit',
  'model_plans.floor.unparsable':
    'A rate sits below the sales floor, so this cannot be saved; the breach details could not be loaded — refresh and retry.',
  'model_plans.dry_run.title': 'Confirm the blast radius',
  'model_plans.dry_run.body':
    'Saving restates assigned customers. Trial grants, quotas, and per-code discounts are never rewritten.',
  'model_plans.dry_run.bound_codes': '{count} bound invite codes',
  'model_plans.dry_run.assigned_orgs': '{count} assigned orgs',
  'model_plans.dry_run.skipped_manual': '{count} manual rows estimated skipped',
  'model_plans.dry_run.added': '{count} SKUs added',
  'model_plans.dry_run.removed': '{count} SKUs removed',
  'model_plans.dry_run.rate_changed': '{count} rates changed',
  'model_plans.dry_run.visibility_changed': '{count} visibility changes',
  'model_plans.dry_run.no_sku_deltas': 'No SKU set, rate, or visibility changes.',
  'model_plans.dry_run.confirm': 'Save and propagate',
  'model_plans.dry_run.blocked': 'Rates below the sales floor cannot be saved.',
  'model_plans.clone.title': 'Clone plan',
  'model_plans.clone.body':
    'Copy into a new active plan of the same brand. Cloning from an archived plan is how a template is resurrected.',
  'model_plans.clone.name': 'New plan name',
  'model_plans.clone.submit': 'Clone',
  'model_plans.clone.error.name': 'Enter a name for the new plan',
  'model_plans.archive.title': 'Archive this plan?',
  'model_plans.archive.body':
    'An archived plan cannot be edited or bound to new invite codes. Archive is refused while any active invite code still points at it. Assigned orgs are not detached automatically.',
  'model_plans.archive.active_codes':
    'Active invite codes still point at this plan. Rebind or disable them first.',
  'model_plans.unarchive.title': 'Unarchive this plan?',
  'model_plans.unarchive.body': '{name} becomes active again and can be bound to invite codes.',
  'model_plans.detail.title': '{name}',
  'model_plans.detail.runs': 'Propagation runs',
  'model_plans.detail.runs_empty': 'No propagation runs yet',
  'model_plans.detail.runs_error': 'Could not load propagation runs',
  'model_plans.detail.trigger.items_update': 'Plan edit',
  'model_plans.detail.trigger.rebind_restate': 'Rebind restate',
  'model_plans.detail.trigger.new_model_sync': 'New-model sync',
  'model_plans.detail.trigger.manual_propagate': 'Manual propagate',
  'model_plans.detail.trigger.rollback': 'Rollback',
  'model_plans.detail.status.running': 'Running',
  'model_plans.detail.status.done': 'Done',
  'model_plans.detail.status.partial_failed': 'Partial failure',
  'model_plans.detail.stats':
    '{orgs} orgs · {updated} updated · {upserted} upserted · {deleted} deleted',
  'model_plans.detail.skipped': 'Skipped {reason}: {count}',
  'model_plans.detail.rollback': 'Roll back this run',
  'model_plans.detail.rollback_title': 'Roll back this run?',
  'model_plans.detail.rollback_body':
    'Only rows still owned by this plan are restored. Manual rows are never overwritten. This records a new propagation run.',
  'model_plans.detail.rollback_confirm': 'Confirm rollback',
  'model_plans.sync.title': 'Sync new models · {name}',
  'model_plans.sync.empty': 'Every listed SKU in this plan’s groups is already on the plan.',
  'model_plans.sync.admit': 'Admit to plan',
  'model_plans.sync.hint':
    'Unchecked SKUs stay absent (hidden at full price). Checked ones start hidden at full price; flip visibility or the rate before saving.',
  'model_plans.sync.submit': 'Preview blast radius',
  'model_plans.toast.created': 'Plan created',
  'model_plans.toast.updated': 'Plan saved and propagated',
  'model_plans.toast.cloned': 'Cloned as {name}',
  'model_plans.toast.archived': 'Plan archived',
  'model_plans.toast.unarchived': 'Plan restored to active',
  'model_plans.toast.rolled_back': 'Rolled back',
  'model_plans.toast.propagated': 'Propagated to assigned orgs',
  'model_plans.run.orgs_total': '{count} orgs total',
  'model_plans.run.orgs_updated': '{count} updated',
  'model_plans.run.rows_upserted': '{count} upserted',
  'model_plans.run.rows_deleted': '{count} deleted',
  'model_plans.select.none': 'No plan',
  'model_plans.select.placeholder': 'Select an active plan',
  'model_plans.select.empty': 'No active plans',
  'model_plans.select.load_error': 'Could not load model plans',
  // Commission + book-performance report (epic #1034 E5-M5, #1147).
  'commission_report.title': 'Commission & performance',
  'commission_report.sub':
    'Commission settles on collected receipts; performance follows funding-lot ownership — different bases, never summed',
  'commission_report.period_aria': 'Select settlement period',
  'commission_report.kpi.commission': 'Commission total (receipt-based)',
  'commission_report.kpi.book_margin': 'Book margin (lot-based)',
  'commission_report.commission.title': 'Commission statements',
  'commission_report.commission.attribution': 'Receipt attribution (owner at verification)',
  'commission_report.commission.table.period': 'Period',
  'commission_report.commission.table.brand': 'Brand',
  'commission_report.commission.table.status': 'Status',
  'commission_report.commission.table.total': 'Commission total',
  'commission_report.commission.table.updated': 'Updated',
  'commission_report.commission.empty': 'No commission statements this period',
  'commission_report.status.draft': 'Draft',
  'commission_report.status.confirmed': 'Confirmed',
  'commission_report.detail.title': 'Statement rows',
  'commission_report.detail.table.source_type': 'Source',
  'commission_report.detail.table.source_id': 'Source ID',
  'commission_report.detail.table.net': 'Net',
  'commission_report.detail.table.rate': 'Rate',
  'commission_report.detail.table.commission': 'Commission',
  'commission_report.detail.empty': 'No rows',
  'commission_report.source.receipt': 'Receipt',
  'commission_report.source.payment_order': 'Online order',
  'commission_report.source.clawback': 'Clawback',
  'commission_report.source.adjustment': 'Adjustment',
  'commission_report.book.title': 'Book performance',
  'commission_report.book.attribution': 'Lot attribution (owner at lot creation)',
  'commission_report.book.table.customer': 'Customer',
  'commission_report.book.table.revenue': 'Revenue (CNY)',
  'commission_report.book.table.gift_cost': 'Gift cost (CNY)',
  'commission_report.book.table.margin': 'Margin (CNY)',
  'commission_report.book.table.charges': 'Charges',
  'commission_report.book.house': 'House (unowned)',
  'commission_report.book.empty': 'No book activity this period',
  'commission_report.book.lag_caveat':
    'Book numbers are lot-attributed consumption and may lag lower while a charge’s allocation is pending replay; commission is receipt-based and unaffected.',
  'commission_report.error.load': 'Failed to load',
  // Lead brand leaderboard (epic #1034 E5-M5, #1147). Its own page —
  // finance-reachable, off the procurement-bearing pricing page.
  'lead_leaderboard.title': 'Brand leaderboard',
  'lead_leaderboard.sub':
    'Each manager’s commission (receipt-based) alongside book margin (lot-based) — different bases, never summed',
  'lead_leaderboard.attribution': 'Commission by receipt, margin by lot — different bases',
  'lead_leaderboard.period_aria': 'Select period',
  'lead_leaderboard.brand_aria': 'Select brand',
  'lead_leaderboard.all_brands': 'All brands',
  'lead_leaderboard.table.manager': 'Manager',
  'lead_leaderboard.table.commission': 'Commission (receipt)',
  'lead_leaderboard.table.book_margin': 'Book margin (lot)',
  'lead_leaderboard.table.customers': 'Customers',
  'lead_leaderboard.house': 'House',
  'lead_leaderboard.empty': 'No data this period',
  'lead_leaderboard.error': 'Failed to load leaderboard',
  'lead_leaderboard.lag_caveat':
    'Book margin is lot-attributed consumption and may lag lower while a charge’s allocation is pending replay; commission is receipt-based and unaffected.',
  // SKU procurement-vs-sales spread (epic #1034 E5-M5, #1147).
  'sku_spread.title': 'SKU spread',
  'sku_spread.sub': 'Procurement vs sales cost (procurement visible to lead/admin)',
  'sku_spread.table.model': 'Model',
  'sku_spread.table.procurement': 'Procurement (CNY/USD)',
  'sku_spread.table.sales_cost': 'Sales cost (CNY/USD)',
  'sku_spread.table.spread': 'Spread (CNY/USD)',
  'sku_spread.empty': 'No SKUs',
  'sku_spread.error': 'Failed to load spread',
  'commission_rate.title': 'Commission rate',
  'commission_rate.sub': "This brand's sales commission rate — effective by settlement month, append-only.",
  'commission_rate.current.unknown': 'Loading the current rate…',
  'commission_rate.current.none': 'No rate is effective for the {period} settlement month yet.',
  'commission_rate.current.value': 'Current rate for {period}: {rate} (effective from {effective}).',
  'commission_rate.form.percent_label': 'Rate (%)',
  'commission_rate.form.percent_hint': 'Enter a percentage, e.g. 5 for 5%.',
  'commission_rate.form.percent_preview': '= {bps} bps',
  'commission_rate.form.effective_label': 'Effective month',
  'commission_rate.form.effective_hint': 'The settlement month this rate starts from (no retroactive changes).',
  'commission_rate.form.effective_min': 'Earliest allowed is {period} (no retroactive changes).',
  'commission_rate.form.append': 'Append rate',
  'commission_rate.error.percent': 'Rate must be a percentage between 0 and 100 that maps to a whole basis point.',
  'commission_rate.error.period': 'Effective month is malformed (expected YYYY-MM).',
  'commission_rate.error.retroactive': 'Effective month cannot be before the current settlement month {period}; commission rates are not retroactive.',
  'commission_rate.toast.appended': 'Commission rate appended',
  'commission_rate.empty.title': 'No commission rates yet',
  'commission_rate.empty.hint': 'Append a rate effective from a settlement month to get started.',
  'commission_rate.table.effective_from': 'Effective month',
  'commission_rate.table.rate': 'Rate',
  'commission_rate.table.created_by': 'Created by',
  'commission_rate.table.created_at': 'Created at',
  'commission_rate.table.current_badge': 'Current',
  'customer_manager_360.back': 'Back to customer manager workspace',
  'customer_manager_360.kicker': 'Customer detail',
  'customer_manager_360.usage_link': 'Customer usage',
  'customer_manager_360.error': 'Failed to load customer detail',
  'customer_manager_360.missing_org': 'Missing customer organization ID',
  'customer_manager_360.metric.balance': 'RMB book balance',
  'customer_manager_360.metric.requests': '30d requests',
  'customer_manager_360.metric.cost': '30d RMB spend',
  // #1958: with no readable rate we draw no bar — a zero-length bar reads as
  // "the amount is zero".
  'customer_manager_360.metric.rate_unavailable': 'Exchange rate unavailable; RMB amount cannot be converted.',
  'customer_manager_360.metric.active_keys': 'Active API keys',
  'customer_manager_360.metric.errors': '30d errors',
  'customer_manager_360.tabs_aria': 'Customer detail sections',
  'customer_manager_360.tab.overview': 'Overview',
  'customer_manager_360.tab.usage': 'Usage',
  'customer_manager_360.usage.window_aria': 'Usage time window',
  'customer_manager_360.usage.window.today': 'Today',
  'customer_manager_360.usage.window.week': 'This week',
  'customer_manager_360.usage.window.month': 'This month',
  'customer_manager_360.usage.window.7d': 'Last 7 days',
  'customer_manager_360.usage.window.30d': 'Last 30 days',
  'customer_manager_360.usage.window.custom': 'Custom',
  'customer_manager_360.usage.from_utc8': 'From (UTC+8)',
  'customer_manager_360.usage.to_utc8': 'To (UTC+8)',
  'customer_manager_360.usage.tz_hint': 'Days cut at Beijing time (UTC+8)',
  'customer_manager_360.usage.export_csv': 'Export CSV',
  'customer_manager_360.usage.loading': 'Loading usage…',
  'customer_manager_360.usage.error': 'Could not load usage: {message}',
  'customer_manager_360.usage.empty.title': 'No requests in this window',
  'customer_manager_360.usage.empty.hint': 'Pick a different window, or wait for this customer to generate usage.',
  'customer_manager_360.usage.kpi.spend': 'Spend ({window} to now)',
  'customer_manager_360.usage.kpi.spend_sub': '{usd} · rate {rate}{delta} vs {baseline}',
  'customer_manager_360.usage.kpi.avg': 'Daily average spend',
  'customer_manager_360.usage.kpi.avg_hour': 'Hourly average spend',
  'customer_manager_360.usage.kpi.avg_sub': 'Previous full period {amount}',
  'customer_manager_360.usage.kpi.requests': 'Requests',
  'customer_manager_360.usage.kpi.requests_sub': '{baseline} {amount}',
  'customer_manager_360.usage.kpi.success': 'Success rate',
  'customer_manager_360.usage.kpi.success_sub': 'Errors {error} · rate-limited {limited}',
  'customer_manager_360.usage.kpi.tokens': 'Tokens (in / out)',
  'customer_manager_360.usage.kpi.tokens_sub': 'Overall cache hit rate {pct}',
  'customer_manager_360.usage.compare.at_point': 'previous at the same point',
  'customer_manager_360.usage.compare.same_span': 'previous matching span',
  'customer_manager_360.usage.runway.title': 'Balance runway',
  'customer_manager_360.usage.runway.burn_aria': 'Burn-rate baseline',
  'customer_manager_360.usage.runway.burn_caption': 'Burn-rate baseline',
  'customer_manager_360.usage.runway.burn.1': 'Last 1 day',
  'customer_manager_360.usage.runway.burn.3': 'Last 3 days',
  'customer_manager_360.usage.runway.burn.7': 'Last 7 days',
  'customer_manager_360.usage.runway.burn.30': 'Last 30 days',
  'customer_manager_360.usage.runway.days_label': 'Days of runway left',
  'customer_manager_360.usage.runway.days_unit': 'days',
  'customer_manager_360.usage.runway.infinite': 'Will not deplete at the current rate',
  'customer_manager_360.usage.runway.depletes_on': 'Projected to run out on {date} ({weekday})',
  'customer_manager_360.usage.runway.remaining': 'Current balance',
  'customer_manager_360.usage.runway.burn_rate': 'Burn rate',
  'customer_manager_360.usage.runway.burn_rate_value': '{amount} / day · {baseline}',
  'customer_manager_360.usage.runway.alert_threshold': 'Alert threshold',
  'customer_manager_360.usage.runway.alert_days': '{days} days',
  'customer_manager_360.usage.runway.alert_window': 'Alert window',
  'customer_manager_360.usage.runway.depleted': 'Balance exhausted',
  'customer_manager_360.usage.runway.today': 'Today',
  'customer_manager_360.usage.runway.plus_days': '+{days}d',
  'customer_manager_360.usage.runway.note':
    'Same remaining-credit ÷ burn-rate as the chase list. Default is 7 days so this panel and the chase list agree. 1-day and 3-day windows can diverge by a week because of weekday rhythm, so the range of the other baselines sits under the headline number.',
  'customer_manager_360.usage.runway.empty': 'No runway is available.',
  'customer_manager_360.usage.runway.sensitivity': 'Other baselines: {min} – {max} days',
  'customer_manager_360.usage.runway.sensitivity_infinite':
    'Other baselines: {min} – {max} days (includes will not deplete)',
  'customer_manager_360.usage.runway.sensitivity_only_infinite': 'Other baselines include will not deplete',
  'customer_manager_360.usage.runway.sensitivity_pending': 'Loading other baselines…',
  'customer_manager_360.usage.runway.sensitivity_error': 'Some baselines failed to load',
  'customer_manager_360.usage.trend.title': 'Usage trend',
  'customer_manager_360.usage.trend.metric.usd': 'Spend ¥',
  'customer_manager_360.usage.trend.metric.reqs': 'Requests',
  'customer_manager_360.usage.trend.metric.tok': 'Tokens',
  'customer_manager_360.usage.trend.mode.point': 'Per bucket',
  'customer_manager_360.usage.trend.mode.cum': 'Cumulative',
  'customer_manager_360.usage.trend.compare.on': 'Compare previous',
  'customer_manager_360.usage.trend.compare.off': 'Off',
  'customer_manager_360.usage.trend.legend.current': 'Current · {window}',
  'customer_manager_360.usage.trend.legend.previous': 'Previous',
  'customer_manager_360.usage.trend.legend.now': 'Vertical line = now; shaded region has not occurred',
  'customer_manager_360.usage.trend.cmp.current': '{window} to now · {metric}',
  'customer_manager_360.usage.trend.cmp.previous': '{baseline}',
  'customer_manager_360.usage.trend.cmp.delta': 'Delta',
  'customer_manager_360.usage.trend.cmp.previous_full': 'Previous full period',
  'customer_manager_360.usage.trend.cmp.projection': 'Projected full period from previous shape',
  'customer_manager_360.usage.trend.cmp.projection_estimate': 'Estimate',
  'customer_manager_360.usage.trend.cmp.no_projection': 'No projection on a rolling window',
  'customer_manager_360.usage.trend.cmp.no_projection_hint': 'The current window is complete except for today',
  'customer_manager_360.usage.trend.note.rolling':
    'The previous window is shifted by a multiple of 7 days so each day lands on the same weekday. Rolling windows are not projected.',
  'customer_manager_360.usage.trend.note.calendar':
    'Projection = current-to-now × (previous full ÷ previous at the same point). It borrows the previous shape for the remaining time — an estimate, not a fact.',
  'customer_manager_360.usage.trend.hover.mode.point': 'this bucket',
  'customer_manager_360.usage.trend.hover.mode.cum': 'cumulative to here',
  'customer_manager_360.usage.trend.series.current': 'Current',
  'customer_manager_360.usage.trend.series.previous': 'Previous',
  'customer_manager_360.usage.trend.empty': 'No usage points to chart in this window.',
  'customer_manager_360.usage.trend.aria': 'Customer usage trend',
  'customer_manager_360.usage.models.title': 'Model mix',
  'customer_manager_360.usage.skus.title': 'SKU mix',
  'customer_manager_360.usage.share.by_spend': 'By spend',
  'customer_manager_360.usage.share.by_requests': 'By requests',
  'customer_manager_360.usage.share.by_tokens': 'By tokens',
  'customer_manager_360.usage.share.model': 'Model',
  'customer_manager_360.usage.share.sku': 'SKU',
  'customer_manager_360.usage.share.requests': 'Requests',
  'customer_manager_360.usage.share.tokens': 'Tokens in / out',
  'customer_manager_360.usage.share.spend': 'Spend ¥',
  'customer_manager_360.usage.share.share': 'Share',
  'customer_manager_360.usage.share.other': '{count} more',
  'customer_manager_360.usage.share.empty': 'No mix data in this window.',
  'customer_manager_360.usage.daily.title': 'Daily detail',
  'customer_manager_360.usage.daily.title_hour': 'Hourly detail',
  'customer_manager_360.usage.daily.compare': 'Compare previous same day',
  'customer_manager_360.usage.daily.compare_hour': 'Compare previous same hour',
  'customer_manager_360.usage.daily.nonzero': 'Days with spend only',
  'customer_manager_360.usage.daily.nonzero_hour': 'Hours with spend only',
  'customer_manager_360.usage.daily.date': 'Date',
  'customer_manager_360.usage.daily.date_hour': 'Hour',
  'customer_manager_360.usage.daily.requests': 'Requests',
  'customer_manager_360.usage.daily.input': 'Tokens in',
  'customer_manager_360.usage.daily.output': 'Tokens out',
  'customer_manager_360.usage.daily.spend': 'Spend ¥',
  'customer_manager_360.usage.daily.prev_spend': 'Previous same day ¥',
  'customer_manager_360.usage.daily.prev_spend_hour': 'Previous same hour ¥',
  'customer_manager_360.usage.daily.delta': 'Delta',
  'customer_manager_360.usage.daily.lead_model': 'Lead model',
  'customer_manager_360.usage.daily.in_progress': 'in progress',
  'customer_manager_360.usage.daily.empty': 'No daily rows in this window.',
  'customer_manager_360.usage.daily.empty_hour': 'No hourly rows in this window.',
  'customer_manager_360.usage.daily.expand': '… show remaining {count} days …',
  'customer_manager_360.usage.daily.expand_hour': '… show remaining {count} hours …',
  'customer_manager_360.usage.weekday.0': 'Sun',
  'customer_manager_360.usage.weekday.1': 'Mon',
  'customer_manager_360.usage.weekday.2': 'Tue',
  'customer_manager_360.usage.weekday.3': 'Wed',
  'customer_manager_360.usage.weekday.4': 'Thu',
  'customer_manager_360.usage.weekday.5': 'Fri',
  'customer_manager_360.usage.weekday.6': 'Sat',
  'metrics.chart.now': 'now',
  'metrics.chart.hover.pending': 'not yet',
  'metrics.chart.hover.delta': 'Delta',
  // Per-customer service kill switch (epic #1033 E4-C7, #1131).
  'customer_manager_360.service_suspension.title': 'Service switch',
  'customer_manager_360.service_suspension.status.active': 'Service active',
  'customer_manager_360.service_suspension.status.suspended': 'Suspended',
  'customer_manager_360.service_suspension.description':
    'When suspended, all of this customer’s model requests are rejected at the gateway (SERVICE_SUSPENDED), independent of balance or credit. Reversible at any time.',
  'customer_manager_360.service_suspension.action.suspend': 'Suspend service',
  'customer_manager_360.service_suspension.action.resume': 'Resume service',
  'customer_manager_360.service_suspension.confirm.title_suspend': 'Suspend service?',
  'customer_manager_360.service_suspension.confirm.title_resume': 'Resume service?',
  'customer_manager_360.service_suspension.confirm.body_suspend':
    'The customer will immediately be unable to call any model until service is resumed. This action is recorded in the audit log.',
  'customer_manager_360.service_suspension.confirm.body_resume':
    'The customer will be able to call models normally again. This action is recorded in the audit log.',
  'customer_manager_360.service_suspension.reason_label': 'Reason (optional)',
  'customer_manager_360.service_suspension.reason_placeholder': 'Record why you are making this change, for the audit trail.',
  'customer_manager_360.service_suspension.toast.suspended': 'Service suspended for this customer.',
  'customer_manager_360.service_suspension.toast.resumed': 'Service resumed for this customer.',
  'customer_manager_360.tab.requests': 'Request logs',
  'customer_manager_360.tab.receipts': 'Receipts',
  'customer_manager_360.tab.api_keys': 'API Keys',
  'customer_manager_360.tab.gift_cards': 'Gift cards',
  'customer_manager_360.tab.sku_rates': 'SKU rates',
  'customer_manager_360.tab.rate_limits': 'Limits',
  'customer_manager_360.tab.admin_actions': 'Admin controls',
  'customer_manager_360.tab.task_admission': 'Task admission',
  'customer_manager_360.rate_limits.title': 'Tenant limits',
  'customer_manager_360.rate_limits.hint': 'Overrides win over SKU defaults; empty on both means unlimited. Edit these in Quota Center.',
  'customer_manager_360.rate_limits.l1_off': 'L1 is off, so these limits do not take effect.',
  'customer_manager_360.rate_limits.sku': 'SKU',
  'customer_manager_360.rate_limits.sku_default': 'SKU default',
  'customer_manager_360.rate_limits.org_override': 'Tenant override',
  'customer_manager_360.rate_limits.effective': 'Effective',
  'customer_manager_360.rate_limits.rpm': 'RPM',
  'customer_manager_360.rate_limits.tpm': 'TPM',
  'customer_manager_360.rate_limits.save': 'Save',
  'customer_manager_360.rate_limits.clear': 'Clear',
  'customer_manager_360.rate_limits.use_clear': 'When both dimensions are empty, use Clear instead of Save.',
  'customer_manager_360.rate_limits.effective_hint': 'Effective = override ?? default ?? unlimited',
  'customer_manager_360.rate_limits.unlimited': 'Unlimited',
  'customer_manager_360.rate_limits.invalid': 'Must be a positive integer, or empty for no override.',
  'customer_manager_360.rate_limits.toast_saved': 'Tenant limits saved',
  'customer_manager_360.rate_limits.toast_cleared': 'Tenant limits cleared',
  'customer_manager_360.rate_limits.empty': 'This tenant has no visible SKUs.',
  'customer_manager_360.rate_limits.loading': 'Loading limits…',
  'customer_manager_360.admin.hint':
    'Admin-only account controls. Every action below requires the ops-admin role, is confirmed by typing, and is written to the audit log.',
  'customer_manager_360.overview.account': 'Account posture',
  'customer_manager_360.overview.risks': 'Risk signals',
  'customer_manager_360.overview.transactions': 'Recent transactions',
  'customer_manager_360.overview.lifecycle': 'Recent lifecycle',
  'customer_manager_360.overview.money_dashboard': 'RMB book balance and spend',
  'customer_manager_360.overview.money_dashboard_hint': 'Book balance and usage spend are displayed in RMB.',
  'customer_manager_360.overview.usage_dashboard': 'Request outcome',
  'customer_manager_360.overview.usage_dashboard_hint': '30-day success, error, and key posture.',
  'customer_manager_360.overview.account_dashboard_hint': 'Account state and risk signals.',
  'customer_manager_360.overview.transaction_dashboard_hint': 'Recent ledger motion uses the original credit ledger amounts.',
  'customer_manager_360.overview.lifecycle_dashboard_hint': 'Latest request evidence without raw trace bodies.',
  'customer_manager_360.overview.success_rate': 'Success rate',
  'customer_manager_360.overview.errors': 'errors',
  'customer_manager_360.overview.successful_requests': 'Successful requests',
  'customer_manager_360.overview.key_posture': 'API key posture',
  'customer_manager_360.risk.none': 'Stable',
  'customer_manager_360.empty.transactions': 'No recent transactions.',
  'customer_manager_360.empty.lifecycle': 'No recent request evidence.',
  'customer_manager_360.money.orders': 'Top-up orders',
  'customer_manager_360.money.orders_hint':
    'The customer’s top-up order status — answers “where did my top-up go”. Read-only facts; reconciliation stays platform-side.',
  'customer_manager_360.money.anomalies': 'Billing anomalies',
  'customer_manager_360.money.anomalies_hint':
    'The customer’s currently unresolved billing anomalies. Read-only facts; resolution stays platform-side.',
  'customer_manager_360.money.loading': 'Loading…',
  'customer_manager_360.money.error': 'Failed to load. Try again later.',
  'customer_manager_360.money.empty.orders': 'No top-up orders.',
  'customer_manager_360.money.empty.anomalies': 'No unresolved billing anomalies.',
  'customer_manager_360.money.col.created': 'Created',
  'customer_manager_360.money.col.order_no': 'Order no.',
  'customer_manager_360.money.col.pay_type': 'Payment method',
  'customer_manager_360.money.col.amount': 'Amount',
  'customer_manager_360.money.col.status': 'Status',
  'customer_manager_360.money.col.paid_at': 'Paid at',
  'customer_manager_360.money.status.pending': 'Pending',
  'customer_manager_360.money.status.paid': 'Paid',
  'customer_manager_360.money.status.expired': 'Expired',
  'customer_manager_360.money.status.failed': 'Failed',
  'customer_manager_360.money.status.refunded': 'Refunded',
  'customer_manager_360.money.pay_type.alipay': 'Alipay',
  'customer_manager_360.money.pay_type.wxpay': 'WeChat Pay',
  'customer_manager_360.money.anomaly.open': 'Open',
  'customer_manager_360.money.anomaly.resolved': 'Resolved',
  'customer_manager_360.empty.requests': 'No request logs match these filters.',
  'customer_manager_360.empty.api_keys': 'No API keys.',
  'customer_manager_360.empty.sku_rates': 'No SKUs.',
  'customer_manager_360.sku_rates.title': 'SKU rates',
  'customer_manager_360.sku_rates.sku_count': '{count} SKUs',
  'customer_manager_360.sku_rates.bulk_rate_label': 'Namespace rate',
  'customer_manager_360.sku_rates.bulk_apply': 'Apply',
  'customer_manager_360.sku_rates.state_unpriced': 'Awaiting price',
  'customer_manager_360.sku_rates.state_discount_pending': 'Discount approval pending, not open',
  'customer_manager_360.sku_rates.state_priced': 'Priced, not open',
  'customer_manager_360.sku_rates.state_open': 'Open',
  'customer_manager_360.sku_rates.state_converging': 'Cache convergence pending',
  'customer_manager_360.sku_rates.state_degraded': 'Cache convergence degraded',
  'customer_manager_360.sku_rates.state_unknown': 'Convergence unknown',
  'customer_manager_360.sku_rates.convergence_refresh_failed':
    'Status refresh failed. Effective state is still unconfirmed; retry the refresh.',
  'customer_manager_360.sku_rates.convergence_unknown_hint':
    'The service returned no valid effective deadline. State remains unknown; refresh to verify.',
  'customer_manager_360.sku_rates.retry_convergence': 'Retry status refresh',
  'customer_manager_360.sku_rates.open_blocked_unpriced':
    'Save an applied customer price before opening this SKU.',
  'customer_manager_360.sku_rates.open_blocked_pending_discount':
    'A discount approval is pending. Apply or close it before opening this SKU.',
  'customer_manager_360.sku_rates.bulk_preview_title': 'Preview bulk open',
  'customer_manager_360.sku_rates.bulk_preview_hint': 'Applied price is checked per SKU. Routing coverage is a separate Activation readiness check and is not validated by this visibility save.',
  'customer_manager_360.sku_rates.bulk_confirm_show': 'Confirm bulk open',
  'customer_manager_360.sku_rates.bulk_ready': 'Ready',
  'customer_manager_360.sku_rates.bulk_blocked_unpriced': 'Awaiting price',
  'customer_manager_360.sku_rates.bulk_blocked_pending_discount': 'Discount approval pending',
  'customer_manager_360.sku_rates.bulk_preview_row_ready':
    'Applied price {price} · routing readiness checked separately',
  'customer_manager_360.sku_rates.bulk_preview_row_unpriced':
    'No applied customer price; this SKU will be skipped.',
  'customer_manager_360.sku_rates.bulk_preview_row_pending_discount':
    'Approval has not applied the requested discount; this SKU will be skipped.',

  // ── Line (namespace) rates (2026-07-31 design §5.2) ──
  //
  // An agreement is struck per (customer × line), not per model. The line is
  // where a customer manager expresses it; the model rows beneath show what
  // each one resolved to.
  'customer_manager_360.namespace_rate.line_rate': 'Line rate',
  'customer_manager_360.namespace_rate.state_agreed': 'Agreed',
  'customer_manager_360.namespace_rate.state_platform': 'Platform rate',
  'customer_manager_360.namespace_rate.save': 'Save line rate',
  'customer_manager_360.namespace_rate.save_for': 'Save the line rate for {namespace}',
  'customer_manager_360.namespace_rate.clear': 'Clear line rate',
  'customer_manager_360.namespace_rate.clear_for': 'Clear the line rate for {namespace}',
  'customer_manager_360.namespace_rate.platform_rate': 'Platform {rate}',
  'customer_manager_360.namespace_rate.cost_floor': 'Cost floor {floor}',
  'customer_manager_360.namespace_rate.sales_floor': 'Sales floor {floor}',
  'customer_manager_360.namespace_rate.floor_unset': 'No floor set',
  'customer_manager_360.namespace_rate.show_line': 'Open whole line',
  'customer_manager_360.namespace_rate.hide_line': 'Hide whole line',
  'customer_manager_360.namespace_rate.show_blocked':
    'Agree a rate for this line before opening it.',
  'customer_manager_360.namespace_rate.breaches_title':
    'These models cost more than the proposed line rate, so nothing was saved:',
  'customer_manager_360.namespace_rate.breach_row': '{sku} (floor {floor})',
  'customer_manager_360.namespace_rate.breach_row_unknown_floor': '{sku}',
  'customer_manager_360.namespace_rate.breaches_hint':
    'Give those models their own rate, or raise the line rate and retry.',
  'customer_manager_360.toast.namespace_rate_updated': 'Line rate updated',
  'customer_manager_360.toast.namespace_rate_cleared': 'Line rate cleared',
  'customer_manager_360.toast.namespace_visibility_updated': 'Line visibility updated',

  // Provenance on a model row: the same number can arrive by inheriting the
  // line or as this model's own exception, and the two look identical on
  // screen — only `rate_source` separates them.
  'customer_manager_360.rate_source.inherited': 'Inherited from line',
  'customer_manager_360.rate_source.sku_override': 'Set for this model',
  'customer_manager_360.rate_source.platform': 'Platform rate',
  'customer_manager_360.rate_source.optout': 'Does not follow line discount',
  'customer_manager_360.rate_source.optout_hint':
    'This SKU is configured not to inherit line-level rate agreements. The line agreement still exists and still applies to other models on this line.',
  'customer_manager_360.rate_source.inherit_blocked':
    'This rate is inherited from the line. Clear it on the line rate above.',
  'customer_manager_360.rate_source.exception_hint':
    'Saving creates a rate for this model only, leaving the line rate.',
  'customer_manager_360.field.owner': 'Owner',
  'customer_manager_360.field.assigned_at': 'Assigned at',
  'customer_manager_360.field.created_at': 'Created at',
  'customer_manager_360.field.verified': 'Email verified',
  'customer_manager_360.field.suspended': 'Suspended at',
  'customer_manager_360.field.deleted': 'Deleted at',
  'customer_manager_360.field.account_id': 'Account ID',
  'customer_manager_360.filter.from': 'From local',
  'customer_manager_360.filter.to': 'To local',
  'customer_manager_360.filter.request_id': 'Request ID',
  'customer_manager_360.filter.status': 'Status',
  'customer_manager_360.filter.lifecycle_status': 'Lifecycle status',
  'customer_manager_360.filter.any': 'Any',
  'customer_manager_360.filter.success': 'Success',
  'customer_manager_360.filter.error': 'Error',
  'customer_manager_360.filter.api_key': 'API Key',
  'customer_manager_360.filter.channel_slug': 'Channel slug',
  'customer_manager_360.filter.apply': 'Filter',
  'customer_manager_360.filter.reset_30d': 'Last 30d',
  'customer_manager_360.requests.error': 'Failed to load request logs',
  'customer_manager_360.pagination.aria_label': 'Request log pagination',
  'customer_manager_360.pagination.range': 'Showing {start}–{end} of {total}',
  'customer_manager_360.column.type': 'Type',
  'customer_manager_360.column.amount': 'Amount',
  'customer_manager_360.column.reason': 'Reason',
  'customer_manager_360.column.created_at': 'Time',
  'customer_manager_360.column.request': 'Request',
  'customer_manager_360.column.api_key': 'API Key',
  'customer_manager_360.column.channel': 'Channel',
  'customer_manager_360.column.visibility': 'Visibility',
  'customer_manager_360.column.model': 'Model',
  'customer_manager_360.column.status': 'Status',
  'customer_manager_360.column.cost': 'Cost',
  'customer_manager_360.cost.pending': 'Rating...',
  'customer_manager_360.column.logs': 'Logs',
  'customer_manager_360.column.name': 'Name',
  'customer_manager_360.column.prefix': 'Prefix',
  'customer_manager_360.column.last_used': 'Last used',
  'customer_manager_360.column.state': 'State',
  'customer_manager_360.column.action': 'Action',
  'customer_manager_360.column.vendor': 'Vendor',
  'customer_manager_360.column.platform_rate': 'Platform rate',
  'customer_manager_360.column.cost_floor': 'Cost floor',
  'customer_manager_360.column.sales_floor': 'Sales floor',
  'customer_manager_360.column.effective_rate': 'Customer rate',
  'customer_manager_360.column.note': 'Note',
  'customer_manager_360.state.active': 'Active',
  'customer_manager_360.state.revoked': 'Revoked',
  'customer_manager_360.revoke.reason': 'Revoke reason',
  'customer_manager_360.action.revoke': 'Revoke',
  'customer_manager_360.action.issue': 'Issue',
  'customer_manager_360.action.redeem': 'Redeem',
  'customer_manager_360.action.inherit': 'Inherit',
  'customer_manager_360.action.save_rate_for': 'Save the customer rate for {sku}',
  'customer_manager_360.action.inherit_rate_for': 'Clear the customer rate for {sku} and inherit',
  'customer_manager_360.action.hide_sku': 'Hide',
  'customer_manager_360.action.show_sku': 'Show',
  'customer_manager_360.action.hide_sku_for': 'Hide {sku}',
  'customer_manager_360.action.show_sku_for': 'Show {sku}',
  'customer_manager_360.action.confirm_show_sku': 'Confirm open',
  'customer_manager_360.action.hide_namespace': 'Hide all',
  'customer_manager_360.action.show_namespace': 'Show all',
  'customer_manager_360.visibility.visible': 'Open',
  'customer_manager_360.visibility.hidden': 'Not open',
  'customer_manager_360.visibility.pricing_required': 'Pricing required',
  'customer_manager_360.visibility.rate_applied': 'Customer rate applied',
  'customer_manager_360.visibility.show_requires_rate':
    'Save and apply a customer rate before opening this SKU.',
  'customer_manager_360.visibility.show_waits_for_approval':
    'Wait for discount approval and refresh the customer before opening this SKU.',
  'customer_manager_360.visibility.clear_requires_hidden':
    'This SKU is open. Hide it before clearing its customer rate.',
  'customer_manager_360.rate.clear_below_line_floor':
    'Clearing this would drop the SKU to the line rate, below its own cost floor. Keep its own rate, or raise the line rate.',
  'customer_manager_360.visibility.bulk_show_partial':
    '{count} SKUs do not have an applied customer rate; bulk show only processes ready SKUs.',
  'customer_manager_360.visibility.bulk_show_blocked':
    'No SKU has an applied customer rate. Finish pricing first.',
  'customer_manager_360.visibility.bulk_result':
    'Bulk result: {applied} applied, {pending} pending approval, {failed} failed',
  'customer_manager_360.rate.cny_per_credit': '{amount}/credit',
  'customer_manager_360.rate.floor_unset': 'No floor set',
  'customer_manager_360.logs.view_lifecycle': 'Lifecycle',
  'customer_manager_360.logs.hide_lifecycle': 'Hide',
  'customer_manager_360.logs.open_global': 'Open logs',
  'customer_manager_360.logs.trace_available': 'Trace',
  'customer_manager_360.logs.trace_unavailable': 'No trace',
  'customer_manager_360.logs.controls.title': 'Log controls',
  'customer_manager_360.logs.controls.hint': 'Scoped to this customer; raw trace bodies stay in the global request-log workflow.',
  'customer_manager_360.logs.controls.visible': 'Visible {count} · Trace {trace}',
  'customer_manager_360.logs.controls.refresh': 'Refresh logs',
  'customer_manager_360.logs.controls.errors_only': 'Errors only',
  'customer_manager_360.logs.controls.expand': 'Expand lifecycle',
  'customer_manager_360.logs.controls.collapse': 'Collapse',
  'customer_manager_360.logs.lifecycle_loading': 'Loading lifecycle…',
  'customer_manager_360.logs.lifecycle_error': 'Failed to load lifecycle',
  'customer_manager_360.logs.lifecycle_empty': 'Open lifecycle to load request events.',
  'customer_manager_360.logs.metric.total': 'Total',
  'customer_manager_360.logs.metric.upstream': 'Upstream',
  'customer_manager_360.logs.metric.client': 'Client',
  'customer_manager_360.logs.metric.billing': 'Billing',
  'customer_manager_360.logs.metric.trace': 'Trace',
  'customer_manager_360.logs.phase': 'Phase',
  'customer_manager_360.logs.event_time': 'Local time',
  'customer_manager_360.logs.elapsed': 'Elapsed',
  'customer_manager_360.logs.duration': 'Duration',
  'customer_manager_360.logs.outcome': 'Outcome',
  // Service level catalogue (#1723/#1725) — admin page.
  'service_levels.title': 'Service levels',
  'service_levels.subtitle': 'Package routing configs as customer-facing levels. A customer manager assigns them by name and never sees the config a level anchors.',
  'service_levels.create_btn': 'New level',
  'service_levels.loading': 'Loading service levels…',
  'service_levels.error.title': 'Could not load service levels',
  'service_levels.error.hint': 'Retry in a moment. If it keeps failing, check ops-api and gateway connectivity.',
  'service_levels.error.retry': 'Retry',
  'service_levels.error.retrying': 'Retrying…',
  'service_levels.empty.title': 'No service levels yet',
  'service_levels.empty.hint': 'Create one and customer managers can assign it by name from Customer 360.',
  'service_levels.empty.action': 'New level',
  'service_levels.col.name': 'Name',
  'service_levels.col.description': 'Customer-facing description',
  'service_levels.col.anchor': 'Anchored routing config',
  'service_levels.col.customers': 'Customers',
  'service_levels.col.status': 'Status',
  'service_levels.col.actions': 'Actions',
  'service_levels.status.active': 'Active',
  'service_levels.status.archived': 'Archived',
  'service_levels.description.empty': 'Not set',
  'service_levels.anchor.none': 'Changes no routing',
  'service_levels.anchor.unavailable': 'Inactive config ({id})',
  'service_levels.anchor.deprecated': '{name} (deprecated — existing members only)',
  'service_levels.validation.anchor_deprecated':
    'That routing config is marked deprecated and cannot anchor a new service level. Levels already anchored to it, and the customers in them, are unaffected.',
  'service_levels.row.manage': 'Edit',
  'service_levels.row.archive': 'Archive',
  'service_levels.row.restore': 'Restore',
  'service_levels.field.name': 'Name',
  'service_levels.field.name_placeholder': 'e.g. Standard',
  'service_levels.field.description': 'Customer-facing description',
  'service_levels.field.description_hint': 'Customer managers read this on Customer 360. Leave it empty to show no description.',
  'service_levels.field.description_placeholder': 'What this level means for the customer',
  'service_levels.field.anchor': 'Anchored routing config',
  'service_levels.field.anchor_hint': 'Leave empty and the level changes no routing — it is only a customer-facing name.',
  'service_levels.field.anchor_error': 'The routing config list is unavailable, so the anchor cannot be changed right now.',
  'service_levels.create_modal.title': 'New service level',
  'service_levels.create_modal.description': 'Name and description are shown to customer managers. The anchored routing config stays admin-only.',
  'service_levels.create_modal.submit': 'Create',
  'service_levels.manage_modal.title': 'Edit level: {name}',
  'service_levels.manage_modal.description': 'Changes apply immediately to every customer already on this level.',
  'service_levels.manage_modal.submit': 'Save',
  'service_levels.archive_modal.title': 'Archive service level',
  'service_levels.archive_modal.body': 'After archiving "{name}", customer managers can no longer assign customers to it. Customers already on it stay where they are.',
  'service_levels.archive_modal.confirm': 'Archive',
  'service_levels.validation.name_required': 'Enter a level name',
  'service_levels.validation.name_taken': 'That name is already taken — pick another',
  'service_levels.validation.anchor_missing': 'That routing config no longer exists or is inactive — pick another',
  'service_levels.toast.created': 'Service level created',
  'service_levels.toast.updated': 'Service level updated',
  'service_levels.toast.archived': 'Service level archived',
  'service_levels.toast.restored': 'Service level restored',
  // Customer 360 service-level assignment (#1723/#1725) — the customer
  // manager's whole routing surface, deliberately free of any routing vocabulary.
  'customer_manager_360.tab.service_level': 'Service level',
  'customer_manager_360.service_level.caption': 'Service level',
  'customer_manager_360.service_level.default_option': 'Default',
  'customer_manager_360.service_level.default_description': 'This customer is on the platform default service, with no level applied.',
  'customer_manager_360.service_level.no_description': 'This level has no description yet.',
  'customer_manager_360.service_level.archived_option': '{name} (archived)',
  'customer_manager_360.service_level.save': 'Save',
  'customer_manager_360.service_level.saved': 'Service level updated',
  'customer_manager_360.service_level.state_default': 'Currently: default',
  'customer_manager_360.service_level.state_level': 'Currently: {name}',
  'customer_manager_360.service_level.state_custom': 'Currently: set up individually',
  'customer_manager_360.service_level.custom_label': 'Custom (set by an admin)',
  'customer_manager_360.service_level.custom_hint': 'An admin set this customer up individually, outside the service levels. Ask an admin to change it.',
  'customer_manager_360.service_level.custom_admin_hint': 'This customer was set up individually rather than through a level. Picking a level below replaces that setup; picking Default clears it.',
  'customer_manager_360.service_level.save_error.archived': 'That level has been archived and can no longer be assigned. Pick another one.',
  'customer_manager_360.service_level.save_error.missing': 'That level no longer exists. Reload the page and pick another one.',
  'customer_manager_360.service_level.save_error.forbidden': 'You are not allowed to change the service level for this customer.',
  'customer_manager_360.service_level.save_error.admin_managed': 'An administrator has set this customer up individually since this page was loaded, so levels no longer apply to them. Reload to see the current setting.',
  'customer_manager_360.service_level.save_error.generic': 'The service level could not be saved. Reload to check the current setting before trying again.',
  'customer_manager_360.service_level.clear_modal.title': 'Clear this customer’s individual setup?',
  'customer_manager_360.service_level.clear_modal.body': 'This customer was set up individually rather than through a level. Moving them to Default discards that setup — this card cannot undo it, and restoring it means looking the original assignment up in the audit log.',
  'customer_manager_360.service_level.clear_modal.confirm': 'Move to Default',
  'customer_manager_360.service_level.error': 'Could not load the service level for this customer.',
  'customer_manager_360.service_level.retry': 'Retry',
  'customer_manager_360.egress_policy.caption': 'Egress policy',
  'customer_manager_360.egress_policy.global': 'Global default',
  'customer_manager_360.egress_policy.global_badge': 'floor',
  'customer_manager_360.egress_policy.overrides': 'Overrides {count} rules',
  'customer_manager_360.egress_policy.change': 'Change',
  'customer_manager_360.egress_policy.unassign': 'Unassign',
  'customer_manager_360.egress_policy.unassign_body': 'Remove {customer} from policy {policy} and return them to the global default.',
  'customer_manager_360.egress_policy.no_email': 'no email',
  'customer_manager_360.egress_policy.assign_confirm_title': 'Change this customer’s egress policy?',
  'customer_manager_360.egress_policy.assign_confirm_body': 'Assign {customer} to policy {policy}.',
  'customer_manager_360.egress_policy.link': 'Open egress policies',
  'customer_manager_360.egress_policy.saved': 'Egress policy assignment updated',
  'customer_manager_360.egress_policy.error': 'Could not load the egress policy for this customer.',
  // Customer 360 custom-routes tab (#1658, restored admin-only by #1785) — the
  // admin's direct routing-config assignment, beside the service-level tab
  // above rather than instead of it. Routing vocabulary is fine here: the tab
  // is gated on TENANT_ROUTING_ASSIGN_ROLES and never reaches a customer
  // manager.
  'customer_manager_360.tab.custom_routes': 'Custom routes',
  'customer_manager_360.task_admission.caption': 'Task admission overlay',
  'customer_manager_360.task_admission.sku': 'SKU',
  'customer_manager_360.task_admission.default': 'SKU default',
  'customer_manager_360.task_admission.overlay': 'Org overlay',
  'customer_manager_360.task_admission.actions': 'Actions',
  'customer_manager_360.task_admission.save': 'Save',
  'customer_manager_360.task_admission.clear': 'Clear',
  'customer_manager_360.task_admission.saved': 'Task admission overlay saved',
  'customer_manager_360.task_admission.cleared': 'Task admission overlay cleared',
  'customer_manager_360.task_admission.no_overlay': 'Using SKU default',
  'customer_manager_360.task_admission.empty': 'No ark video SKUs',
  'customer_manager_360.task_admission.empty_hint': 'Overlays can only be set on SKUs that already publish a task-admission document.',
  'customer_manager_360.task_admission.error': 'Could not load task admission overlays for this customer.',
  'customer_manager_360.task_admission.retry': 'Retry',
  'customer_manager_360.task_admission.invalid': 'Each overlay must be an integer from 1 to 64.',
  'customer_manager_360.task_admission.save_error': 'The overlay could not be saved.',
  'customer_manager_360.task_admission.clear_error': 'The overlay could not be cleared.',
  'customer_manager_360.session_policy.caption': 'Session policy',
  'customer_manager_360.session_policy.content': 'Content-seeded',
  'customer_manager_360.session_policy.random': 'Random',
  'customer_manager_360.session_policy.hint':
    'Random: a new session on every request, so prefix cache does not stick. Use this for customers that send no session id and whose prompts cannot tell conversations apart.',
  'customer_manager_360.session_policy.saved': 'Session policy saved',
  'customer_manager_360.session_policy.save_error': 'The session policy could not be saved.',
  'customer_manager_360.session_policy.error': 'Could not load the session policy for this customer.',
  'customer_manager_360.session_policy.retry': 'Retry',
  'customer_manager_360.custom_routes.custom_title': 'Customer route config',
  'customer_manager_360.custom_routes.route_config': 'Route config',
  'customer_manager_360.custom_routes.config_users': 'Customers using',
  'customer_manager_360.custom_routes.switch_hint':
    'Bind an ordered stack of routing configs. For each SKU the first covering config wins; inherit misses fall through, exclusive terminates. An empty stack follows the global default. SKU target pools and execution policies are maintained in Tenant routing → route config management and are view-only here.',
  'customer_manager_360.custom_routes.following_global': 'Following global default',
  'customer_manager_360.custom_routes.inherited_config_line': 'Effective global default config: {name}',
  'customer_manager_360.custom_routes.manage_configs': 'Open routing config management',
  'customer_manager_360.custom_routes.status.assigned_config': 'Assigned',
  'customer_manager_360.custom_routes.summary.title_named': 'Effective config: {name}',
  'customer_manager_360.custom_routes.summary.view_full': 'View full config',
  'customer_manager_360.custom_routes.summary.following_hint':
    'This customer follows the global default config. Open the full config to inspect its target pools.',
  'customer_manager_360.custom_routes.summary.loading': 'Loading the config SKU routing summary…',
  'customer_manager_360.custom_routes.summary.load_error':
    'SKU routing targets and policies are unavailable. Open the full config before making routing decisions.',
  'customer_manager_360.custom_routes.summary.coverage': 'Enabled SKU coverage',
  'customer_manager_360.custom_routes.summary.coverage_value': '{covered}/{total}',
  'customer_manager_360.custom_routes.summary.coverage_complete': 'All catalog SKUs covered',
  'customer_manager_360.custom_routes.summary.coverage_missing_count': '{count} SKUs missing',
  'customer_manager_360.custom_routes.summary.missing_skus': 'Missing or disabled-only SKUs',
  'customer_manager_360.custom_routes.summary.missing_more': '{count} more',
  'customer_manager_360.custom_routes.summary.sku_routes': 'Config SKU routes',
  'customer_manager_360.custom_routes.summary.sku_routes_hint':
    'These config-level SKU target pools are the complete routing set used for configured SKUs.',
  'customer_manager_360.custom_routes.summary.empty_sku':
    'This config has no SKU target rows. Open the full config to add SKU coverage.',
  'customer_manager_360.custom_routes.summary.column.sku': 'Namespace / SKU',
  'customer_manager_360.custom_routes.summary.column.upstream': 'Upstream path',
  'customer_manager_360.custom_routes.summary.column.dispatch': 'Dispatch',
  'customer_manager_360.custom_routes.summary.column.priority': 'Priority',
  'customer_manager_360.custom_routes.toast.config_assigned': 'Route config assigned',
  'customer_manager_360.custom_routes.add_config': 'Add config',
  'customer_manager_360.custom_routes.add_placeholder': 'Choose a config to append',
  'customer_manager_360.custom_routes.save': 'Save stack',
  'customer_manager_360.custom_routes.dirty': 'Unsaved',
  'customer_manager_360.custom_routes.move_up': 'Move up',
  'customer_manager_360.custom_routes.move_down': 'Move down',
  'customer_manager_360.custom_routes.remove': 'Remove',
  'customer_manager_360.custom_routes.exclusive_not_last':
    'An exclusive config that is not last terminates the walk — configs below it never apply.',
  'customer_manager_360.custom_routes.toast.stack_saved': 'Routing stack saved',
  'customer_manager_requests.kicker': 'Ops v2 · customer manager',
  'customer_manager_requests.title': 'Portfolio request logs',
  'customer_manager_requests.subtitle': 'Query requests across customers assigned to the current customer manager.',
  'customer_manager_requests.generated_at': 'generated {time}',
  'customer_manager_requests.generated_pending': 'waiting for live data',
  'customer_manager_requests.error': 'Failed to load request logs',
  'customer_manager_requests.controls.customers': '{count} customers',
  'customer_manager_requests.controls.hint': 'Scoped to the current customer manager portfolio; lifecycle expansion is still verified per customer.',
  'customer_manager_requests.column.customer': 'Customer',
  'customer_manager_360.gift.issue': 'Issue gift (direct credit)',
  'customer_manager_360.gift.issue_hint':
    'Credits the customer directly — no bearer code. Within the monthly gift quota the credit is immediate; above it a sales-lead approval request is opened and no money moves until it is approved.',
  'customer_manager_360.gift.amount_yuan': 'Amount (CNY, whole yuan)',
  'customer_manager_360.gift.redeem': 'Redeem for customer',
  'customer_manager_360.gift.code': 'Gift card code',
  'customer_manager_360.gift.reason': 'Reason',
  'customer_manager_360.validation.reason': 'Reason is required',
  'customer_manager_360.validation.amount': 'Amount must be greater than 0',
  'customer_manager_360.validation.gift_code': 'Gift card code is required',
  'customer_manager_360.validation.rate_floor': 'Cannot be below cost floor {floor}',
  'customer_manager_360.toast.key_revoked': 'API key revoked',
  'customer_manager_360.gift.credited_result': 'Credited {amount}',
  'customer_manager_360.gift.over_quota_result':
    'Over quota: {amount} requested with {remaining} of {quota} remaining this month — pending sales-lead approval.',
  'customer_manager_360.toast.gift_credited': 'Gift credited',
  'customer_manager_360.toast.gift_over_quota_submitted': 'Over-quota approval request submitted',
  'customer_manager_360.toast.gift_card_redeemed': 'Gift card redeemed',
  // ── Credit lots (gift-balance visibility) ────────────────────
  'customer_manager_360.lots.title': 'Credit lots (consumption order)',
  'customer_manager_360.lots.hint':
    'Lots that still hold money, top to bottom in the order a charge consumes them: expiring gift credit first, cash last. Exhausted lots are not listed.',
  'customer_manager_360.lots.loading': 'Loading…',
  'customer_manager_360.lots.error': 'Failed to load credit lots',
  'customer_manager_360.lots.empty': 'This account holds no outstanding credit lots',
  'customer_manager_360.lots.truncated': 'Showing the first {count}; the account has more lots.',
  'customer_manager_360.lots.column.kind': 'Kind',
  'customer_manager_360.lots.column.source': 'Source',
  'customer_manager_360.lots.column.remaining': 'Remaining',
  'customer_manager_360.lots.column.amount': 'Face value',
  'customer_manager_360.lots.column.expires_at': 'Expires',
  'customer_manager_360.lots.column.owner': 'Credited to',
  'customer_manager_360.lots.kind.promo': 'Gift',
  'customer_manager_360.lots.kind.cash': 'Cash',
  'customer_manager_360.lots.never_expires': 'Never',
  'customer_manager_360.lots.expires_in_days': 'in {days} days',
  'customer_manager_360.lots.expires_today': 'today',
  'customer_manager_360.lots.expired': 'expired',
  'customer_manager_360.toast.sku_rate_updated': 'SKU rate updated',
  'customer_manager_360.toast.sku_rate_saved_still_hidden': 'Customer rate saved; SKU is still closed until you open it',
  'customer_manager_360.toast.sku_visibility_updated': 'SKU visibility updated',
  'customer_manager_360.toast.bulk_applied': 'Applied {count} SKUs',
  'customer_manager_360.toast.bulk_partial': 'Applied {ok}, {failed} failed',
  // ── Below-sales-floor discount approvals (epic #1032 E3-P3, #1106) ──
  'customer_manager_360.sku_rates.pending_discount': 'Discount approval pending',
  'customer_manager_360.sku_rates.below_floor_hint':
    'Below sales floor {floor}; saving submits a discount approval',
  'customer_manager_360.model_plan.banner':
    'This customer’s models and discounts are hosted by model plan “{name}”',
  'customer_manager_360.model_plan.banner_hint':
    'Editing the plan affects every customer bound to it, not just this one.',
  'customer_manager_360.model_plan.edit_plan': 'Edit plan',
  'customer_manager_360.model_plan.clone_rebind': 'Clone the plan and rebind this customer',
  'customer_manager_360.model_plan.lock_tooltip':
    'This customer is hosted by a model plan. Use the banner above.',
  'customer_manager_360.model_plan.managed_toast':
    'This customer’s models and discounts are hosted by model plan “{name}”. Edit the plan instead.',
  'customer_manager_360.model_plan.assign': 'Assign plan',
  'customer_manager_360.model_plan.assign_title': 'Assign a model plan to this customer',
  'customer_manager_360.model_plan.assign_submit': 'Assign',
  'customer_manager_360.model_plan.detach': 'Detach to manual',
  'customer_manager_360.model_plan.detach_title': 'Detach to manual editing?',
  'customer_manager_360.model_plan.detach_body':
    'The plan no longer hosts this customer. Existing rate and visibility rows stay; they are released for manual editing, not deleted.',
  'customer_manager_360.model_plan.detach_confirm': 'Detach',
  'customer_manager_360.model_plan.toast_assigned': 'Model plan assigned',
  'customer_manager_360.model_plan.toast_detached': 'Detached; rows are now manual',
  'customer_manager_360.model_plan.clone_name': 'New plan name',
  'customer_manager_360.toast.discount_approval_submitted':
    'Discount approval submitted for the sales lead to decide',
  'customer_manager_360.toast.bulk_discount_pending':
    '{count} SKUs below the sales floor converted into discount approvals',

  // ── Customer transfer/release approvals (epic #1030 E1-S5, #1041) ──
  'customer_transfer.action': 'Request transfer / release',
  'customer_transfer.pending_chip': 'Transfer pending approval',
  'customer_transfer.dialog.title': 'Request customer transfer / release',
  'customer_transfer.dialog.description':
    'A same-brand sales lead decides this request; ownership stays unchanged until it is approved.',
  'customer_transfer.dialog.customer': 'Customer',
  'customer_transfer.mode.label': 'Mode',
  'customer_transfer.mode.release': 'Release to the pool',
  'customer_transfer.mode.release_hint': 'On approval the customer returns to the unowned pool.',
  'customer_transfer.mode.transfer': 'Transfer to a same-brand manager',
  'customer_transfer.mode.transfer_hint': 'On approval ownership moves atomically to the chosen manager.',
  'customer_transfer.target.label': 'Receiving manager',
  'customer_transfer.target.placeholder': 'Pick a same-brand customer manager',
  'customer_transfer.target.empty': 'No same-brand customer managers available',
  'customer_transfer.target.required': 'Pick a receiving manager',
  'customer_transfer.target.duplicate_name_warning':
    'Several customer managers are named "{name}". Check the sign-in method and email to confirm you picked the right account.',
  'customer_transfer.reason.label': 'Reason',
  'customer_transfer.reason.placeholder': 'Context for the approver (optional)',
  'customer_transfer.reason.hint': 'Up to 500 characters, shown to the approver with the request.',
  'customer_transfer.submit': 'Submit request',
  'customer_transfer.toast.submitted': 'Transfer/release request submitted for approval',
  'customer_transfer.error.duplicate':
    'This customer already has an open transfer/release request; wait for the decision or cancel it first.',

  // ── Customer 360 · CRM contact card + follow-up notes (epic #1035 E6-D5, #1069) ──
  'customer_crm.contact.caption': 'Contact card',
  'customer_crm.contact.edit': 'Edit',
  'customer_crm.contact.empty': 'No contact info yet — hit "Edit" to fill in the card.',
  'customer_crm.contact.field.contact_name': 'Contact name',
  'customer_crm.contact.field.wechat': 'WeChat',
  'customer_crm.contact.field.phone': 'Phone',
  'customer_crm.contact.field.contact_email': 'Email',
  'customer_crm.contact.field.company': 'Company name',
  'customer_crm.contact.field.remark': 'Remark',
  'customer_crm.contact.updated': 'Updated {time} by {user}',
  'customer_crm.contact.dialog.title': 'Edit contact card',
  'customer_crm.contact.dialog.description':
    'One card per customer org; saving replaces the whole card and is audited.',
  'customer_crm.contact.toast.saved': 'Contact card saved',
  'customer_crm.error.title': 'Failed to load CRM data',
  'customer_crm.notes.caption': 'Follow-up notes',
  'customer_crm.notes.composer.placeholder': 'Log a follow-up…',
  'customer_crm.notes.composer.aria': 'New follow-up note',
  'customer_crm.notes.composer.submit': 'Add note',
  'customer_crm.notes.append_only_hint': 'Notes are append-only: once posted they cannot be edited or deleted.',
  'customer_crm.notes.toast.posted': 'Follow-up note posted',
  'customer_crm.notes.empty': 'No follow-up notes yet.',
  'customer_crm.notes.pagination_aria': 'Follow-up notes pages',

  'customer_manager_pool.kicker': 'Ops v2 · customer pool',
  'customer_manager_pool.title': 'Customer Pool',
  'customer_manager_pool.subtitle':
    'Unclaimed active customers live here until a customer manager claims ownership.',
  'customer_manager_pool.admin_subtitle':
    'Unassigned active customers live here until an admin assigns them to a customer manager.',
  'customer_manager_pool.generated_at': 'generated {time}',
  'customer_manager_pool.generated_pending': 'waiting for live data',
  'customer_manager_pool.metric.available': 'Available customers',
  'customer_manager_pool.metric.balance': 'Total RMB book balance',
  'customer_manager_pool.metric.active_keys': 'Active API keys',
  'customer_manager_pool.metric.requests': '30d requests',
  'customer_manager_pool.metric.risks': 'Customers with risk',
  'customer_manager_pool.search.placeholder': 'Search pool customer email / company...',
  'customer_manager_pool.search.aria_label': 'Search pool customer email or company name',
  'customer_manager_pool.search.submit': 'Filter',
  'customer_manager_pool.search.clear': 'Clear',
  'customer_manager_pool.error.title': 'Failed to load customer pool',
  'customer_manager_pool.empty.title': 'No unclaimed customers',
  'customer_manager_pool.empty.body':
    'Every active customer currently has an owner, or no customer is ready for claim.',
  'customer_manager_pool.empty.filtered.title': 'No pool customers match these filters',
  'customer_manager_pool.empty.filtered.body': 'Clear the filter or refine the search.',
  'customer_manager_pool.column.customer': 'Customer',
  'customer_manager_pool.column.org': 'Org',
  'customer_manager_pool.column.created_at': 'Created',
  'customer_manager_pool.column.balance': 'RMB book balance',
  'customer_manager_pool.column.usage': 'Usage',
  'customer_manager_pool.column.keys': 'API keys',
  'customer_manager_pool.column.risks': 'Risks',
  'customer_manager_pool.column.actions': 'Actions',
  'customer_manager_pool.usage.errors': '{count} errors',
  'customer_manager_pool.usage.no_errors': 'no errors',
  'customer_manager_pool.keys.active': '{active}/{total} active',
  'customer_manager_pool.keys.last_used': 'last used {time}',
  'customer_manager_pool.risk.none': 'stable',
  'customer_manager_pool.claim.action': 'Claim',
  'customer_manager_pool.claim.claiming': 'Claiming',
  'customer_manager_pool.claim.claimed': 'Claimed {email}',
  'customer_manager_pool.claim.conflict':
    'This customer was already claimed by another operator. The pool has been refreshed.',
  'customer_manager_pool.assign.select_placeholder': 'Select customer manager',
  'customer_manager_pool.assign.loading_managers': 'Loading managers',
  'customer_manager_pool.assign.no_managers': 'No active managers',
  'customer_manager_pool.assign.action': 'Assign',
  'customer_manager_pool.assign.assigning': 'Assigning',
  'customer_manager_pool.assign.assigned': 'Assigned {email} to {manager}',
  'customer_manager_pool.assign.managers_error': 'Failed to load customer managers.',
  'customer_manager_pool.pagination.aria_label': 'Customer-pool pagination',
  'customer_manager_pool.pagination.range': 'Showing {start}-{end} of {total}',

  // ── Account detail ──────────────────────────────────────────────
  'account_detail.loading_aria': 'Loading account…',
  'account_detail.resolving': 'Resolving customer organization…',
  'account_detail.no_org.notice':
    'This account belongs to no customer organization, so it has no Customer 360 view. Below is the read-only account record.',
  'account_detail.error.title': 'Failed to load account',
  'account_detail.back': 'Back to accounts',
  'account_detail.breadcrumb.accounts': 'Accounts',
  'account_detail.meta.created': 'created {iso}',
  'account_detail.summary.title': 'Summary',
  'account_detail.summary.account_id': 'Account id',
  'account_detail.summary.org_id': 'Org id',
  'account_detail.summary.org_name': 'Org name',
  'account_detail.summary.member_role': 'Member role',
  'account_detail.summary.current_balance': 'Current balance',
  'account_detail.tx.title': 'Recent transactions',
  'account_detail.tx.last_n': 'Last {n} rows',
  'account_detail.tx.empty': 'No ledger activity yet.',
  'account_detail.tx.column.type': 'Type',
  'account_detail.tx.column.amount': 'Amount',
  'account_detail.tx.column.actor': 'Actor',
  'account_detail.tx.column.reason': 'Reason',
  'account_detail.tx.column.created': 'Created',
  'account_detail.adjust.title': 'Balance adjustment',
  'account_detail.adjust.subtitle': 'Signed ledger · negative = debit (allowed)',
  'account_detail.adjust.amount.label': 'Amount (USD)',
  'account_detail.adjust.amount.placeholder': '25 or -12.50',
  'account_detail.adjust.reason.label': 'Reason (required, audit log)',
  'account_detail.adjust.reason.placeholder': 'Customer support / refund / correction…',
  'account_detail.adjust.reason.placeholder_refund':
    'e.g. Order ABC-123 chargeback (reason auto-prefixed with refund:)',
  'account_detail.adjust.reason.placeholder_sla_credit':
    'e.g. 2026-Q2 outage compensation (reason auto-prefixed with sla_credit:)',
  'account_detail.adjust.mode.aria_label': 'Adjustment mode',
  'account_detail.adjust.mode.custom': 'Custom',
  'account_detail.adjust.mode.refund': 'Refund',
  'account_detail.adjust.mode.sla_credit': 'SLA credit',
  'account_detail.adjust.mode.custom.hint':
    'Any signed amount + free-form reason. Negative (debit) allowed.',
  'account_detail.adjust.mode.refund.hint':
    'Positive amount only; reason auto-prefixed with `refund:` so finance can group them at monthly close.',
  'account_detail.adjust.mode.sla_credit.hint':
    'Positive amount only; reason auto-prefixed with `sla_credit:` for SLA incident review.',
  'account_detail.adjust.error.positive_required':
    'Refund / SLA credit must be a positive amount; switch to Custom for debits.',
  'account_detail.adjust.submit': 'Adjust balance',
  'account_detail.adjust.error.amount_required': 'Amount is required',
  'account_detail.adjust.error.amount_non_zero':
    'Amount must be a non-zero USD value (use `-` prefix for debit)',
  'account_detail.adjust.error.reason_required':
    'Reason is required — it lands in the ledger audit log',
  'account_detail.confirm.title': 'Confirm balance adjustment',
  'account_detail.confirm.target_account': 'Target account',
  'account_detail.confirm.current_balance': 'Current balance',
  'account_detail.confirm.adjustment': 'Adjustment',
  'account_detail.confirm.resulting_balance': 'Resulting balance',
  'account_detail.confirm.type_to_confirm': 'Type the amount exactly to confirm ({raw})',
  'account_detail.confirm.idempotency_key': 'Idempotency key:',
  'account_detail.confirm.submitting': 'Submitting…',
  'account_detail.confirm.submit': 'Apply {signed}',
  'account_detail.email_verification.title': 'Email verification',
  'account_detail.email_verification.body':
    'Support actions are audited. Resend first; mark verified only after manual proof.',
  'account_detail.email_verification.status.verified': 'Verified',
  'account_detail.email_verification.status.unverified': 'Unverified',
  'account_detail.email_verification.resend': 'Resend verification',
  'account_detail.email_verification.resending': 'Resending…',
  'account_detail.email_verification.reason_label': 'Manual verify reason',
  'account_detail.email_verification.reason_placeholder': 'Ticket / user proof',
  'account_detail.email_verification.mark_verified': 'Mark verified',
  'account_detail.email_verification.marking': 'Marking…',
  'account_detail.email_verification.support_link': 'Support requests',
  'account_detail.email_verification.toast.resent': 'Verification email resent',
  'account_detail.email_verification.toast.marked': 'Email marked verified',
  'account_detail.email_verification.error.reason_required':
    'Enter a reason before marking verified',

  // ── Account detail / API keys card (Epic #109 / P0.2) ───────────

  // ── Billing anomalies ───────────────────────────────────────────
  'billing_anomalies.title': 'Billing anomalies',
  'billing_anomalies.subtitle':
    'Requests that reached the tenant successfully but still need billing reconciliation or manual follow-up.',
  'billing_anomalies.filter.unresolved': 'Unresolved',
  'billing_anomalies.filter.unresolved.hint': 'still needs settlement',
  'billing_anomalies.filter.resolved': 'Resolved',
  'billing_anomalies.filter.resolved.hint': 'already reconciled or escalated',
  'billing_anomalies.filter.all': 'All',
  'billing_anomalies.filter.all.hint': 'entire anomaly history',
  'billing_anomalies.error.title': 'Failed to load billing anomalies',
  'billing_anomalies.empty.unresolved': 'No unresolved billing anomalies',
  'billing_anomalies.empty.resolved': 'No resolved billing anomalies yet',
  'billing_anomalies.empty.all': 'No billing anomalies recorded',
  'billing_anomalies.empty.hint':
    'Healthy traffic should settle into charged or zero-cost without entering this queue.',
  'billing_anomalies.column.account': 'Account',
  'billing_anomalies.column.request_id': 'Request id',
  'billing_anomalies.column.model': 'Model',
  'billing_anomalies.column.amount': 'Amount',
  'billing_anomalies.column.reason': 'Reason',
  'billing_anomalies.column.replay': 'Replay',
  'billing_anomalies.column.created': 'Created',
  'billing_anomalies.column.resolved': 'Resolved',
  'billing_anomalies.pagination.summary': 'Showing {shown} of {total}',

  // ── Payments orders ─────────────────────────────────────────────
  'payments_orders.title': 'Payment Orders',
  'payments_orders.subtitle':
    "Inspect tenant top-ups and reconcile orders whose ZPay notify didn't land.",
  'payments_orders.status.all': 'All',
  'payments_orders.status.pending': 'Pending',
  'payments_orders.status.paid': 'Paid',
  'payments_orders.status.expired': 'Expired',
  'payments_orders.status.failed': 'Failed',
  'payments_orders.status.refunded': 'Refunded',
  'payments_orders.filter.account_id': 'Account id',
  'payments_orders.filter.from': 'From (ISO-8601)',
  'payments_orders.filter.to': 'To (ISO-8601)',
  'payments_orders.loading_aria': 'Loading payment orders…',
  'payments_orders.error.title': 'Failed to load payment orders',
  'payments_orders.empty': 'No payment orders match the current filters.',
  'payments_orders.column.created': 'Created',
  'payments_orders.column.account': 'Account',
  'payments_orders.column.status': 'Status',
  'payments_orders.column.usd': 'USD',
  'payments_orders.column.cny': 'CNY',
  'payments_orders.column.rate': 'Rate',
  'payments_orders.column.pay': 'Pay',
  'payments_orders.column.paid_at': 'Paid at',
  'payments_orders.column.order_id': 'Order id',
  'payments_orders.column.actions': 'Actions',
  'payments_orders.row.details': 'Details',
  'payments_orders.row.collapse': 'Collapse',
  'payments_orders.row.reconcile': 'Reconcile',
  'payments_orders.showing': 'Showing {n} order(s)',
  'payments_orders.load_more': 'Load more',
  'payments_orders.loading_more': 'Loading…',
  'payments_orders.reconcile_modal.title': 'Reconcile payment order',
  'payments_orders.filter.status_label': 'Status',
  'payments_orders.filter.status_aria_label': 'Filter by status',
  'payments_orders.filter.account_id.placeholder': 'acc_...',
  'payments_orders.filter.to.placeholder': '(no end)',
  'payments_orders.row.reconcile_tooltip.available':
    'Force a ZPay reconciliation; credits immediately if the query succeeds',
  'payments_orders.row.reconcile_tooltip.disabled': 'Reconcile only applies to pending orders',
  'payments_orders.reconcile_modal.body':
    "We'll ask ZPay whether order {orderId} is paid. If yes, we credit {amount} atomically; if no, nothing changes.",
  'payments_orders.reconcile_modal.account': 'Account',
  'payments_orders.reconcile_modal.usd': 'USD',
  'payments_orders.reconcile_modal.cny': 'CNY',
  'payments_orders.reconcile_modal.applied_rate': 'Applied rate',
  'payments_orders.reconcile_modal.note.label': 'Note (optional, audited)',
  'payments_orders.reconcile_modal.note.placeholder':
    'e.g. Customer confirmed ZPay charge but never received notify',
  'payments_orders.reconcile_modal.type_to_confirm': 'Type the order id to confirm',
  'payments_orders.reconcile_modal.type_hint_prefix': 'Paste or type ',
  'payments_orders.reconcile_modal.type_hint_suffix': ' exactly to enable the Reconcile button.',
  'payments_orders.reconcile_modal.submitting': 'Reconciling…',
  'payments_orders.reconcile_modal.submit': 'Reconcile now',
  'payments_orders.reconcile_modal.detail.order_id': 'Order id',
  'payments_orders.reconcile_modal.detail.out_trade_no': 'Out trade no',
  'payments_orders.reconcile_modal.detail.upstream_trade_no': 'Upstream trade no',
  'payments_orders.reconcile_modal.detail.expired_at': 'Expires at',
  'payments_orders.reconcile_modal.detail.empty': '—',
  'payments_orders.reconcile_modal.toast.credited': 'Order {orderId} settled (status={status})',
  'payments_orders.reconcile_modal.toast.upstream':
    'Upstream reports status={status}; order {orderId} not credited',
  'payments_orders.reconcile_modal.toast.already':
    'Order {orderId} already in status={status}; no action taken',

  // ── Payment providers ───────────────────────────────────────────
  'payment_providers.title': 'Payment Providers',
  'payment_providers.subtitle':
    'Manage credentials and endpoint URLs for each payment aggregator. Changes take effect within 60 seconds — platform-api picks up the latest version automatically after rotation.',
  'payment_providers.new': 'New Provider',
  'payment_providers.banner':
    'Changes take effect within 60 seconds — platform-api picks up the latest version automatically after rotation, no redeploy required.',
  'payment_providers.empty.title': 'No payment providers configured yet.',
  'payment_providers.empty.body':
    'Add your first provider to unlock tenant topup. Until then, /v1/billing/topup returns 503.',
  'payment_providers.empty.cta': 'Add Provider',
  'payment_providers.row.rotate': 'Rotate',
  'payment_providers.row.pid': 'PID',
  'payment_providers.row.reveal': 'Reveal',
  'payment_providers.row.revealing': 'Revealing…',
  'payment_providers.row.revealed': 'Revealed',
  'payment_providers.chip.active': 'Active',
  'payment_providers.chip.retired': 'Retired',
  'payment_providers.create.title': 'Add Provider',
  'payment_providers.create.provider': 'Provider',
  'payment_providers.create.pid': 'Merchant ID (PID)',
  'payment_providers.create.secret': 'Secret Key',
  'payment_providers.create.submit_url': 'Submit URL',
  'payment_providers.create.mapi_url': 'MAPI URL',
  'payment_providers.create.query_url': 'Query URL',
  'payment_providers.create.note': 'Note (optional, audited)',
  'payment_providers.create.active_checkbox': 'Make this the active credential for {provider}',
  'payment_providers.create.rotation_warning':
    'An active credential already exists for this provider — adding a new one rotates and retires the prior version.',
  'payment_providers.create.error.pid_required': 'PID is required',
  'payment_providers.create.error.secret_required': 'Secret key is required',
  'payment_providers.loading_aria': 'Loading payment providers…',
  'payment_providers.error.title': 'Failed to load payment providers',
  'payment_providers.create.modal.title.new': 'New {provider} provider',
  'payment_providers.create.modal.title.rotate': 'Rotate {provider} credentials',
  'payment_providers.create.secret.hint':
    'Used for MD5 signing. Plaintext in SQL is intentional — ops self-serves rotation without a deploy.',
  'payment_providers.create.mapi_url.optional_suffix': '(optional)',
  'payment_providers.create.active_hint.rotation':
    'This will retire the current active record. Type-to-confirm required.',
  'payment_providers.create.active_hint.retired':
    'Leaves the current active record in place — this row lands Retired.',
  'payment_providers.create.active_hint.first':
    'No prior active record exists; this row becomes the first active.',
  'payment_providers.create.error.submit_url_required': 'Submit URL is required',
  'payment_providers.create.error.query_url_required': 'Query URL is required',
  'payment_providers.create.error.mapi_url_required_zpay': 'MAPI URL is required for zpay',
  'payment_providers.create.submit.save': 'Save',
  'payment_providers.create.submit.continue': 'Continue',
  'payment_providers.confirm.title.rotation': 'Confirm {provider} credential rotation',
  'payment_providers.confirm.title.save': 'Save {provider} credentials',
  'payment_providers.confirm.body.rotation':
    'You are about to replace the live credentials. Subsequent signing calls will use the new PID + Key within 60 seconds.',
  'payment_providers.confirm.body.save':
    'This record will be saved without affecting the currently-active credential.',
  'payment_providers.confirm.type_prefix': 'Type ',
  'payment_providers.confirm.type_suffix': ' to confirm',
  'payment_providers.confirm.back': 'Back',
  'payment_providers.confirm.submit.saving': 'Saving…',
  'payment_providers.confirm.submit.rotate': 'Rotate Credentials',
  'payment_providers.confirm.submit.save': 'Save',
  'payment_providers.confirm.error.type_mismatch':
    'Type {provider} exactly to confirm the rotation',
  'payment_providers.toast.rotated': 'Rotated {provider} credentials (retired {retiredId})',
  'payment_providers.toast.saved': 'Saved {provider} credentials ({status})',
  'payment_providers.toast.status.active': 'active',
  'payment_providers.toast.status.retired': 'retired',

  // ── Pricing (exchange rate policy) ──────────────────────────────
  'pricing.title': 'RMB Display Rate',
  'pricing.subtitle':
    'Configured RMB display and topup conversion rate (R). The underlying ledger still uses USD credit.',
  'pricing.policy.title': 'Current RMB display rate',
  'pricing.policy.empty': 'No policy set yet. Use the form below to establish the initial rate.',
  'pricing.policy.pair': 'Pair',
  'pricing.policy.current_r': 'Current R',
  'pricing.policy.updated_by': 'Updated by',
  'pricing.policy.updated_at': 'Updated at',
  'pricing.policy.meaning.title': 'What this means',
  'pricing.policy.meaning.body': 'UI displays $1.00 USD credit as ¥{rate}; topups use the same configured conversion.',
  'pricing.market.title': 'Market reference',
  'pricing.market.subtitle': 'Updated via cron every 6h',
  'pricing.market.empty':
    'No market samples observed yet — cron backfills this card once it ships.',
  'pricing.market.latest_rate': 'Latest market rate',
  'pricing.market.source': 'Source',
  'pricing.market.sampled_at': 'Sampled at',
  'pricing.market.spread': 'Spread vs market',
  'pricing.edit.title': 'Edit rate',
  'pricing.edit.subtitle': 'Type-to-confirm · Audited',
  'pricing.edit.new_r': 'New R (CNY per USD)',
  'pricing.edit.note': 'Note (optional, audited)',
  'pricing.edit.submit': 'Save',
  'pricing.confirm.title': 'Confirm exchange rate change',
  'pricing.confirm.previous': 'Previous rate',
  'pricing.confirm.new': 'New rate',
  'pricing.confirm.market': 'Market reference',
  'pricing.confirm.projected_spread': 'Projected spread',
  'pricing.confirm.note': 'Note',
  'pricing.loading_aria': 'Loading pricing policy…',
  'pricing.error.title': 'Failed to load pricing policy',
  'pricing.policy.note_prefix': 'Note:',
  'pricing.edit.rate_placeholder': '7.89',
  'pricing.edit.note_placeholder': 'Aligning with market move',
  'pricing.edit.error.required': 'New rate is required',
  'pricing.edit.error.not_number': 'New rate must be a number',
  'pricing.edit.error.out_of_range': 'New rate must be in ({min}, {max}] — check the decimal point',
  'pricing.confirm.type_hint': 'Type the new rate exactly to confirm ({rate})',
  'pricing.confirm.error.type_mismatch': 'Type the new rate exactly ({rate}) to confirm',
  'pricing.confirm.submit.saving': 'Saving…',
  'pricing.confirm.submit.save': 'Save {rate}',
  'pricing.toast.updated': 'Exchange rate updated to {rate}',

  // ── Revenue ─────────────────────────────────────────────────────
  'revenue.title': 'Revenue',
  'revenue.subtitle':
    'L1 (topup exchange-rate premium) + L2 (token-sales gross margin) across every tenant and model.',
  'revenue.window.today': 'Today',
  'revenue.window.7d': 'Past 7 days',
  'revenue.window.30d': 'Past 30 days',
  'revenue.summary.title': 'Summary',
  'revenue.summary.subtitle': 'L1 premium + L2 gross margin for the selected window.',
  'revenue.summary.topup_gmv': 'Topup GMV',
  'revenue.summary.usage_gmv': 'Usage GMV',
  'revenue.summary.l1_profit': 'L1 profit',
  'revenue.summary.channel_fees': 'Channel fees',
  'revenue.summary.usd_credited': 'USD credited',
  'revenue.summary.l2_profit': 'L2 profit',
  'revenue.summary.upstream_cost': 'Upstream cost',
  'revenue.summary.total': 'Total gross margin',
  'revenue.source.title': 'Balance source split',
  'revenue.source.subtitle': 'Usage charge allocation by source lot for this window.',
  'revenue.source.cash': 'Cash topup',
  'revenue.source.gift_card': 'Gift card',
  'revenue.source.other': 'Other credit',
  'revenue.source.unallocated': 'Unallocated',
  'revenue.source.coverage': 'Allocated coverage',
  'revenue.health.title': 'Balance source ledger',
  'revenue.health.subtitle': 'Source lots, account balances, and signed ledger totals.',
  'revenue.health.status.balanced': 'Balanced',
  'revenue.health.status.needs_attention': 'Needs attention',
  'revenue.health.source_remaining': 'Source remaining',
  'revenue.health.account_balance': 'Account balance',
  'revenue.health.ledger_sum': 'Signed ledger sum',
  'revenue.health.missing_lots': 'Missing credit lots',
  'revenue.health.unallocated_debits': 'Unallocated debits',
  'revenue.health.mismatch_accounts': 'Mismatch accounts',
  'revenue.health.generated_at': 'Generated {time}',
  'revenue.health.error_label': 'Failed to load balance-source health',
  'revenue.daily.title': 'Daily trend',
  'revenue.daily.subtitle': 'Stacked L1 (primary) + L2 (tertiary) profit per day.',
  'revenue.l2.title': 'L2 by model',
  'revenue.l2.subtitle': 'Per-model gross margin on usage. Lossy models flagged automatically.',
  'revenue.l2.currency.label': 'Currency',
  'revenue.l2.currency.aria': 'L2 by model display currency',
  'revenue.l2.column.model': 'Model',
  'revenue.l2.column.usage': 'Usage',
  'revenue.l2.column.usd_charged': 'USD charged',
  'revenue.l2.column.cny_cost': 'CNY cost',
  'revenue.l2.column.charged': 'Charged ({currency})',
  'revenue.l2.column.cost': 'Cost ({currency})',
  'revenue.l2.column.l2_profit': 'L2 profit',
  'revenue.l2.column.l2_pct': 'L2 %',
  'revenue.l2.flag.loss': 'LOSS',
  'revenue.l2.flag.thin': 'THIN',
  'revenue.top_tenants.title': 'Top tenants',
  'revenue.top_tenants.subtitle':
    'Lifetime L1 + L2 contribution per tenant. Sorted by total CNY margin, top {n}.',
  'revenue.top_tenants.column.email': 'Email',
  'revenue.top_tenants.column.topups': 'Topups',
  'revenue.top_tenants.column.l1_profit': 'L1 profit',
  'revenue.top_tenants.column.usage': 'Usage',
  'revenue.top_tenants.column.l2_profit': 'L2 profit',
  'revenue.top_tenants.column.total': 'Total',
  'revenue.summary.window_aria': 'Summary window',
  'revenue.summary.error_label': 'Failed to load summary',
  'revenue.summary.topup.orders': '{n} orders',
  'revenue.summary.usage.api_calls': '{n} API calls',
  'revenue.daily.subtitle_full':
    'Stacked L1 (primary) + L2 (tertiary) profit per day. {days} days.',
  'revenue.daily.error_label': 'Failed to load daily trend',
  'revenue.daily.empty': 'No revenue data yet',
  'revenue.daily.aria_label': 'Daily revenue trend — {days} days',
  'revenue.daily.tooltip': '{date} — L1 {l1} ({topupCount} topups), L2 {l2} ({usageCount} usage)',
  'revenue.l2.error_label': 'Failed to load per-model breakdown',
  'revenue.l2.empty': 'No usage recorded in this window.',
  'revenue.top_tenants.error_label': 'Failed to load top-tenants',
  'revenue.top_tenants.empty': 'No tenants have contributed margin yet.',
  'revenue.top_tenants.no_email': '(no email)',

  // ── Audit log ───────────────────────────────────────────────────
  'audit.title': 'Audit log',
  'audit.subtitle':
    'Append-only trail of every ops-admin mutation. Ordered by `when` descending server-side; filter fields are exact-match only.',
  'audit.filter.action': 'Action',
  'audit.filter.target_type': 'Target type',
  'audit.filter.target_id': 'Target id',
  'audit.filter.ops_user_id': 'Ops user id',
  'audit.filter.target_type.any': 'Any target type',
  'audit.filter.target_type.account': 'account',
  'audit.filter.target_type.gift_card': 'gift-card',
  'audit.filter.target_type.gift_card_batch': 'gift-card-batch',
  'audit.filter.target_type.upstream': 'upstream',
  'audit.filter.target_type.upstream_funding_batch': 'upstream-funding-batch',
  'audit.filter.target_type.model': 'model',
  'audit.filter.target_type.failed_deduction': 'failed-deduction',
  'audit.filter.target_type.exchange_rate_policy': 'exchange-rate-policy',
  'audit.filter.target_type.payment_order': 'payment-order',
  'audit.filter.target_type.payment_provider': 'payment-provider',
  'audit.filter.apply': 'Apply',
  'audit.filter.reset': 'Reset',
  'audit.filter.action.placeholder': 'e.g. balance.adjust',
  'audit.filter.ops_user_id.placeholder': 'ops_xxx',
  'audit.filter.target_id.placeholder': 'exact match',
  'audit.error.title': 'Failed to load audit log',
  'audit.loading_aria': 'Loading audit log…',
  'audit.empty.title': 'No audit rows match',
  'audit.empty.body': 'Try widening the filters or clearing them entirely.',
  'audit.column.when': 'When',
  'audit.column.action': 'Action',
  'audit.column.target_type': 'Target type',
  'audit.column.target_id': 'Target id',
  'audit.column.ops_user': 'Ops user',
  'audit.pagination.range': 'Showing {start}–{end} of {total}',

  // ── Upstream detail summary stats ───────────────────────────────────
  'upstream_detail.summary.source': 'Support Source',
  'upstream_detail.summary.endpoint': 'Endpoint',
  'upstream_detail.summary.accounts': 'Accounts',
  'upstream_detail.summary.accounts_unit': 'accounts',
  'upstream_detail.summary.funding': 'Funding',
  'upstream_detail.summary.funding_remaining': 'remaining',
  'upstream_detail.summary.funding_none': 'No funding',

  // ── Supply access section (epic #1327 PR-6) ─────────────────────────
  'upstream_supply_access.title': 'Supply access',
  'upstream_supply_access.subtitle':
    'Access prerequisites per upstream namespace. Click any unresolved item to fix it — until all four hold, the namespace cannot carry traffic.',
  'upstream_supply_access.namespace.disabled': 'Disabled',
  'upstream_supply_access.namespace.stale': 'Channel row is gone',
  'upstream_supply_access.empty':
    'This upstream has no namespaces yet. Create a channel first, then configure accounts and keys.',
  'upstream_supply_access.load_error': 'Could not read namespaces: {message}',
  'upstream_supply_access.fact.account': 'Account',
  'upstream_supply_access.fact.key': 'API key',
  'upstream_supply_access.fact.egress': 'Transport config',
  'upstream_supply_access.fact.probe': 'Probe protocol',
  'upstream_supply_access.fact.account.ready': 'An enabled supplier account exists',
  'upstream_supply_access.fact.account.blocked': 'No enabled supplier account yet',
  'upstream_supply_access.fact.key.ready': 'An enabled key is bound to this namespace',
  'upstream_supply_access.fact.key.blocked': 'No enabled key is bound to this namespace',
  'upstream_supply_access.fact.egress.ready': 'Every binding resolves to a usable transport',
  'upstream_supply_access.fact.egress.blocked': 'A binding has no usable transport policy',
  'upstream_supply_access.fact.probe.ready': 'A probeable model is available',
  'upstream_supply_access.fact.probe.blocked': 'No classified, probeable model in this namespace',
  'upstream_supply_access.fact.unknown': 'Could not be read',
  'upstream_supply_access.state.ready': 'Ready',
  'upstream_supply_access.state.blocked': 'Not ready',
  'upstream_supply_access.state.unknown': 'Unknown',
  'upstream_supply_access.action.accounts': 'Accounts & keys',
  'upstream_supply_access.action.channels': 'Manage channels',
  'upstream_supply_access.action.funding': 'Manage funding',
  'upstream_supply_access.drawer.accounts.title': 'Accounts & keys',
  'upstream_supply_access.drawer.accounts.description':
    'Supplier accounts, API keys, and each key’s namespace bindings and transport policy.',
  'upstream_supply_access.drawer.channels.title': 'Upstream channels',
  'upstream_supply_access.drawer.channels.description':
    'Namespaces owned by this upstream: create, rename, enable or disable.',
  'upstream_supply_access.drawer.funding.title': 'Funding batches',
  'upstream_supply_access.drawer.funding.description':
    'Prepaid batches for this upstream and how much of each is consumed.',

  // ── Upstream maintenance toggle (P2.11) ────────────────────────────
  'upstream_detail.maintenance.label': 'Operational state',
  'upstream_detail.maintenance.state.enabled': 'Routing traffic',
  'upstream_detail.maintenance.state.disabled': 'In maintenance · gateway has stopped routing',
  'upstream_detail.maintenance.enter.button': 'Enter maintenance',
  'upstream_detail.maintenance.resume.button': 'Resume routing',
  'upstream_detail.maintenance.modal.title_enter': 'Put {name} into maintenance?',
  'upstream_detail.maintenance.modal.title_resume': 'Resume routing to {name}?',
  'upstream_detail.maintenance.modal.body_enter':
    'Takes effect immediately: the gateway selector removes this upstream from the candidate set. In-flight requests continue; new requests route through other candidates. For single-point-of-failure SKUs tenants will 404 (see dashboard alerts).',
  'upstream_detail.maintenance.modal.body_resume':
    'Once resumed, the upstream rejoins the candidate set and the selector allocates traffic by the standard SLA × cost rules.',
  'upstream_detail.maintenance.impact.label': 'Current SKU target references · {count}',
  'upstream_detail.maintenance.impact.none':
    'No active SKU target references; maintenance does not affect any SKU right now.',
  'upstream_detail.maintenance.confirm.type_label': 'Type `maintenance` to confirm',
  'upstream_detail.maintenance.confirm.type_mismatch': 'Type maintenance exactly to confirm',
  'upstream_detail.maintenance.confirm.submit_enter': 'Confirm maintenance',
  'upstream_detail.maintenance.confirm.submit_resume': 'Confirm resume',

  // ── Account lifecycle: suspend / delete (P2.10) ────────────────────
  'account_lifecycle.title': 'Account lifecycle',
  'account_lifecycle.status.active': 'Active',
  'account_lifecycle.status.suspended': 'Suspended',
  'account_lifecycle.status.deleted': 'Deleted',
  'account_lifecycle.suspended.banner': 'Suspended since {iso} · reason: {reason}',
  'account_lifecycle.suspended.no_reason': '(not provided)',
  'account_lifecycle.deleted.banner':
    'Soft-deleted at {iso}; tenant API requests return 403 ACCOUNT_DELETED.',
  'account_lifecycle.suspend.reason_label': 'Reason (optional)',
  'account_lifecycle.suspend.reason_placeholder': 'e.g. Abuse report 2026-04-20',
  'account_lifecycle.suspend.reason_hint':
    'Stored in accounts.suspension_reason for later lookup; an ops_audit row is also written.',
  'account_lifecycle.suspend.button': 'Suspend',
  'account_lifecycle.unsuspend.button': 'Unsuspend',
  'account_lifecycle.delete.button': 'Soft-delete',
  'account_lifecycle.confirm.title_suspend': 'Suspend {email}?',
  'account_lifecycle.confirm.body_suspend':
    'Immediately blocks the tenant: /internal/auth/verify-key will return ACCOUNT_SUSPENDED and every new request is refused. Historical usage_logs and balances are preserved.',
  'account_lifecycle.confirm.title_unsuspend': 'Unsuspend {email}?',
  'account_lifecycle.confirm.body_unsuspend':
    'Clears suspended_at + suspension_reason; the tenant resumes normal routing immediately.',
  'account_lifecycle.confirm.title_delete': 'Soft-delete {email}?',
  'account_lifecycle.confirm.body_delete':
    'Sets deleted_at; tenant API requests return 403 ACCOUNT_DELETED from now on. Historical usage_logs / balance_transactions stay in SQL (needed for reconciliation). Full GDPR erasure (wiping email + password_hash) is a follow-up task.',
  'account_lifecycle.confirm.type_email': 'Type {email} to confirm',
  'account_lifecycle.confirm.type_mismatch': 'Type {email} exactly to confirm',
  'account_lifecycle.confirm.reason_preview': 'Reason preview: {reason}',
  'account_lifecycle.confirm.submit_suspend': 'Confirm suspend',
  'account_lifecycle.confirm.submit_unsuspend': 'Confirm unsuspend',
  'account_lifecycle.confirm.submit_delete': 'Confirm soft-delete',
  'account_lifecycle.toast.suspended': '{email} suspended',
  'account_lifecycle.toast.unsuspended': '{email} unsuspended',
  'account_lifecycle.toast.deleted': '{email} soft-deleted',

  // ── Upstream onboarding wizard (P1.5) ─────────────────────────────
  'upstream_wizard.title': 'New upstream onboarding',
  'upstream_wizard.subtitle':
    'Three steps create the upstream and keys, then open the routing workspace for capability probes and SKU targets.',
  'upstream_wizard.breadcrumb.upstreams': 'Upstreams',
  'upstream_wizard.breadcrumb.new': 'New',
  'upstream_wizard.progress.aria_label': 'Onboarding progress',
  'upstream_wizard.back': 'Back',
  'upstream_wizard.next': 'Next',
  'upstream_wizard.step.basic.title': 'Basics',
  'upstream_wizard.step.basic.heading': 'Create the upstream record',
  'upstream_wizard.step.basic.body':
    'Writes a `upstream_configs` row and stores the API secret. `enabled` stays `false` until the last step flips it.',
  'upstream_wizard.step.basic.name': 'Display name',
  'upstream_wizard.step.basic.name.placeholder': 'anthropic-direct-a',
  'upstream_wizard.step.basic.endpoint': 'Endpoint URL',
  'upstream_wizard.step.basic.endpoint.placeholder': 'https://api.anthropic.com',
  'upstream_wizard.step.basic.priority': 'Priority',
  'upstream_wizard.step.basic.cookie_passthrough': 'Cookie passthrough',
  'upstream_wizard.step.basic.cookie_passthrough.body':
    'Allow tenant cookies to pass through to this upstream.',
  'upstream_wizard.step.basic.cookie_passthrough.hint':
    'Default stays off. Only enable if this upstream truly requires tenant cookies.',
  'upstream_wizard.step.basic.model_discovery': 'Model discovery (optional)',
  'upstream_wizard.step.basic.model_discovery.hint':
    'Defaults to compatible. You can change it later on the upstream detail configuration card.',
  'upstream_wizard.step.basic.submit': 'Create upstream',
  'upstream_wizard.step.basic.submit.pending': 'Creating…',
  'upstream_wizard.step.basic.error.required': 'Name and endpoint are required',
  'upstream_wizard.step.basic.error.priority': 'Priority must be a non-negative integer',
  'upstream_wizard.step.keys.title': 'Add keys',
  'upstream_wizard.step.keys.heading': 'Add API keys for {name}',
  'upstream_wizard.step.keys.body':
    'Add at least one key bucket. The gateway round-robins enabled buckets when forwarding requests.',
  'upstream_wizard.step.keys.count': '{count} key bucket(s) added',
  'upstream_wizard.step.keys.label': 'Label',
  'upstream_wizard.step.keys.label.placeholder': 'e.g. primary-key',
  'upstream_wizard.step.keys.add': 'Add',
  'upstream_wizard.step.keys.toast.added': 'Key bucket added',
  'upstream_wizard.step.keys.error.min_one': 'Add at least one key bucket to continue',
  'upstream_wizard.step.keys.error.secret_required': 'Enter an API key',
  'upstream_wizard.step.models.title': 'Route setup',
  'upstream_wizard.step.models.heading': 'Open route setup for {name}',
  'upstream_wizard.step.models.body':
    'Use the upstream detail page to discover capabilities, run probes, and attach them to SKU targets. The upstream stays out of the hot path until an enabled SKU target exists.',
  'upstream_wizard.step.models.open_detail': 'Open route setup',
  'upstream_wizard.step.probe.title': 'Probe',
  'upstream_wizard.step.probe.heading': 'Probe {name}',
  'upstream_wizard.step.probe.body':
    'Pick a mapped route and run a real request. A failed probe does NOT block enable — but you should investigate first. On success, click Finish to flip enabled=true and open the detail page.',
  'upstream_wizard.step.probe.target.label': 'Probe target',
  'upstream_wizard.step.probe.run': 'Run probe',
  'upstream_wizard.step.probe.run.pending': 'Probing…',
  'upstream_wizard.step.probe.finish': 'Finish + enable',
  'upstream_wizard.step.probe.finish.pending': 'Enabling…',
  'upstream_wizard.step.probe.warning.failed':
    'Probe failed. Confirm before enabling — fix config from the detail page and re-probe.',
  'upstream_wizard.toast.created': 'Upstream {name} created (not yet enabled)',
  'upstream_wizard.toast.attached': '{vendor}/{model} mapped',
  'upstream_wizard.toast.funded': 'Funding batch registered',
  'upstream_wizard.toast.enabled': '{name} is live — gateway will route to it',

  // ── Support: per-tenant request query (P0.4) ─────────────────────
  'support.requests.title': 'Tenant request lookup',
  'support.requests.subtitle':
    'Gateway request log filtered by account, for billing-dispute investigation. Restricted to ops-admin and ops-support roles.',
  'support.requests.filter.account_id': 'Account id',
  'support.requests.filter.account_id.placeholder': 'acc_xxx',
  'support.requests.filter.from': 'From',
  'support.requests.filter.to': 'To',
  'support.requests.filter.request_id': 'Request id (optional, exact match)',
  'support.requests.filter.request_id.placeholder': 'req_xxx',
  'support.requests.filter.apply': 'Apply',
  'support.requests.filter.reset': 'Reset',
  'support.requests.cta.title': 'Enter an account id',
  'support.requests.cta.body':
    'Fill in the target account id above and click Apply. Time range is optional — the default window is the last 24 hours. Setting a request id bypasses the time range and performs an exact lookup.',
  'support.requests.error.title': 'Failed to load request log',
  'support.requests.loading_aria': 'Loading request log…',
  'support.requests.account.account_id': 'Account',
  'support.requests.account.email': 'Email',
  'support.requests.account.org': 'Organization',
  'support.requests.account.org.none': '(no org)',
  'support.requests.empty.title': 'No requests in this range',
  'support.requests.empty.body': 'Widen the time range or clear the request id and try again.',
  'support.requests.column.when': 'When',
  'support.requests.column.request_id': 'Request id',
  'support.requests.column.model': 'Model',
  'support.requests.column.status': 'Status',
  'support.requests.column.latency': 'Latency',
  'support.requests.column.cost': 'Cost (CNY)',
  'support.requests.cost.pending': 'Rating...',
  'support.requests.column.api_key': 'API key',
  'support.requests.column.upstream': 'Upstream',
  'support.requests.row.api_key.unnamed': '(unnamed)',
  'support.requests.row.upstream.deleted': '(deleted)',
  'support.requests.detail.request_id': 'Request id',
  'support.requests.detail.upstream_id': 'Upstream id',
  'support.requests.detail.protocol': 'Protocol',
  'support.requests.detail.created_at': 'Created at',
  'support.requests.detail.input_tokens': 'Input tokens',
  'support.requests.detail.output_tokens': 'Output tokens',
  'support.requests.detail.cache_read': 'Cache read',
  'support.requests.detail.cache_write': 'Cache write',
  'support.requests.detail.unit_kind': 'Billed unit',
  'support.requests.detail.unit_quantity': 'Unit quantity',
  'support.requests.detail.unit_rate': 'Unit price',
  'support.requests.detail.unit_variant': 'Variant',
  'support.requests.detail.unit_amount': 'Unit charge',
  'support.requests.detail.unit_variant.none': '(default)',
  'support.requests.detail.unknown_dimension': 'Other charge',
  'support.requests.detail.unknown_dimension.unnamed': '(unrecognized)',
  'support.requests.detail.unit_unpriced': 'Unit charge status',
  'support.requests.unit_unpriced.unit_price_missing': 'No unit price configured — not charged',
  'support.requests.unit_unpriced.unit_not_delivered': 'Not delivered — not charged',
  'support.requests.unit_unpriced.variant_price_missing':
    'No price row for this tier — delivered but not charged',
  'support.requests.unit_unpriced.unit_surcharges_malformed':
    'Surcharge block malformed — stripped, body still charged',
  'support.requests.unit.call': 'Calls',
  'support.requests.unit.second': 'Seconds',
  'support.requests.unit.character': 'Characters',
  'support.requests.unit.image': 'Images',
  'support.requests.pagination.aria_label': 'Request-log pagination',
  'support.requests.pagination.range': 'Showing {start}–{end} of {total}',

  // ── Tenant announcements (#1396 follow-up) ──────────────────────
  'announcements.title': 'Tenant announcements',
  'announcements.subtitle':
    'Push one kind:"system" notification to every tenant org; it shows up in the client notification centre. Newly listed models, maintenance windows and policy changes go here.',
  'announcements.form.caption': 'New announcement',
  'announcements.field.slug': 'Announcement slug',
  'announcements.field.slug.placeholder': 'claude-opus-5-listed',
  'announcements.field.slug.hint':
    'Idempotency key: 3-64 chars of lowercase letters, digits, dot, underscore or dash. A new slug sends a new announcement; reusing an existing one rewrites that announcement in place and re-delivers it to every client.',
  'announcements.field.slug.invalid':
    'slug must be 3-64 chars of lowercase letters, digits, dot, underscore or dash',
  'announcements.field.slug.generate': 'Generate a new slug',
  'announcements.field.title': 'Title',
  'announcements.field.title.too_long': 'Title is over the length limit',
  'announcements.field.description': 'Body',
  'announcements.field.description.too_long': 'Body is over the length limit',
  'announcements.field.counter': '{count} / {max}',
  'announcements.field.severity': 'Severity',
  // The two actions stated separately, rather than one blanket "not
  // selectable" (#1965) — which stopped being true the moment the test-send
  // button shipped directly under this sentence. A page asserting a reach it
  // does not have is what #1945 was.
  'announcements.audience.note':
    'Publish announcement reaches every tenant org, with no filter of any kind. Test send reaches '
    + 'only the orgs you name one by one. Neither can be unsent.',
  'announcements.severity.info': 'Info',
  'announcements.severity.warning': 'Warning',
  'announcements.severity.critical': 'Critical',
  'announcements.submit': 'Publish announcement',
  'announcements.submit.pending': 'Publishing\u2026',
  'announcements.submit.warning': 'This cannot be unsent.',
  'announcements.confirm.title': 'Publish to every tenant?',
  'announcements.confirm.body':
    'This writes the announcement to every tenant org under fingerprint {fingerprint}, and cannot be unsent. If the slug already exists its previous text is overwritten and re-delivered. Type the slug to confirm.',
  'announcements.confirm.type_slug': 'Type the slug to confirm',
  'announcements.confirm.send': 'Publish',
  'announcements.toast.sent': 'Announcement published',
  // #1965 test send. The recipient list exists only inside this dialog and is
  // destroyed when it closes, so no copy here has to remind an operator to
  // clear a field — that reminder is what #1945 was made of.
  'announcements.test.open': 'Test send to specific orgs…',
  'announcements.test.dialog.title': 'Test send: only the orgs you name',
  // Reusing the real fingerprint is symmetric: the forward promise (a later
  // fleet publish upgrades the draft in place) and the reverse consequence
  // (hitting a slug already in use overwrites a real announcement's text on the
  // spot) come out of the same ON CONFLICT. Stating only the forward half hides
  // the half that cannot be undone.
  'announcements.test.dialog.body':
    'Writes the announcement under exactly the same fingerprint {fingerprint} but only to the '
    + 'orgs you list, so you can proof-read it in a real client. Publishing the same slug to the '
    + 'fleet later rewrites their copy in place — they end up holding one notification with the '
    + 'final text, not two. That cuts both ways: if this slug already belongs to a published '
    + 'announcement, the orgs you name have its text REPLACED by this draft right now. There is '
    + 'no unsend; the only repair is to send the correct text to those same orgs under this same '
    + 'slug, so if you abandon the draft instead they keep the draft wording.',
  'announcements.test.field.orgs': 'Recipients',
  // Says out loud that delivery is org-level even when an EMAIL is what was
  // typed: the email resolves to that person's org, and every member of that
  // org sees the unproofed draft — not just the person named.
  'announcements.test.field.orgs.hint':
    'One email or org id per line (commas work too), up to {max}. An email resolves to that '
    + "person's org, and an announcement goes to the ORG: every member of it sees this unproofed "
    + 'draft, not only the person you named. If any entry resolves to nothing the whole test send '
    + 'is refused, naming each one.',
  'announcements.test.field.orgs.placeholder': 'dean@example.com\norg_xxxxxxxx',
  // Counts ENTRIES, not orgs: two colleagues in one org are two entries and one
  // org, and only the server knows that after resolving. So this says
  // recipients, and the org count comes from the result panel after the send.
  'announcements.test.field.orgs.count':
    '{count} recipients (after removing duplicates); the number of orgs actually written comes '
    + 'back with the result.',
  'announcements.test.field.orgs.empty':
    'The list is empty: a test send needs at least one email or org id.',
  'announcements.test.field.orgs.over_limit':
    'At most {max} recipients; past that it is an announcement rather than a proof-read — publish '
    + 'to the fleet instead.',
  'announcements.test.send': 'Test send to {count} recipients',
  'announcements.test.send.pending': 'Sending…',
  // Both halves are about THIS send, not about the world: "the fleet has not
  // been published to" would be false the moment an operator test sends a
  // correction for a slug that is already out (the flow the dialog itself
  // describes). Raised only when the server CONFIRMED a whitelist send — see
  // the branch in `<Announcements />`.
  'announcements.test.toast.sent': 'Test sent to {count} orgs — this send did not publish to the fleet',
  // The other branch, and it is an error rather than silence: the result panel's
  // alert is below the fold on a filled-in form, so the operator who just
  // clicked needs the interrupting half too. Worded for BOTH bad answers —
  // `mode: 'fleet'` and no mode at all — so it never claims what the server said.
  'announcements.test.toast.unconfirmed':
    'NOT confirmed as a test send — the server did not report a whitelist send, so this may have '
    + 'reached every tenant org and cannot be unsent. Read the result panel below.',
  // The one line an operator most needs to read on this page, so it is
  // translated rather than passed through from the API. Hedged exactly as the
  // server's own copy is: a connection lost after COMMIT cannot be ruled out,
  // and the remedy holds either way.
  'announcements.toast.failed':
    'Almost certainly NOT sent — the broadcast and its audit row rolled back together. '
    + 'Re-sending the same slug is safe; the overwritten count on that re-send confirms '
    + 'whether this attempt had landed.',
  'announcements.result.caption': 'Result',
  'announcements.result.targeted': 'Orgs reached',
  'announcements.result.created': 'Created',
  'announcements.result.resurfaced': 'Overwritten',
  'announcements.result.fingerprint': 'Fingerprint',
  'announcements.result.recycled.tag': 'Heads up',
  // No single cause named (#1965): a test send followed by a fleet publish
  // ALWAYS produces overwrites, so reading every overwrite as "you recycled
  // somebody's slug" would make this line cry wolf in the normal flow and get
  // ignored — while it is also the only signal that another announcement was
  // just destroyed. Both causes, and the second one's consequence intact.
  'announcements.result.recycled':
    'This slug already existed, so those orgs received a rewritten announcement, not a new one. '
    + 'Two things do that: a test send you are now publishing to the fleet, which is expected; or '
    + 'a slug that belonged to a DIFFERENT announcement, whose text this send has just overwritten. '
    + 'There is no unsend, and those orgs have already been re-notified — the only repair is to '
    + 'send the old text again under this same slug. If you are not sure, check what this slug '
    + 'last said in the send history below.',
  'announcements.result.test.caption': 'Test send result (this send did not publish to the fleet)',
  'announcements.result.test.scope.tag': 'Test send',
  // #1945 happened because nobody told the operator the reach was 9. So the
  // coverage is a sentence placed BEFORE the numbers, not a smaller number left
  // for the operator to interpret.
  //
  // SCOPED TO THIS SEND, and the earlier wording was not: it claimed the fleet
  // had not been published to and that no other tenant org had received
  // anything, which are statements about the WORLD made from one response.
  // Test send → publish → spot a typo → test send the fix is a flow this dialog
  // documents, and after it every tenant org holds the slug while this panel
  // insisted none did. `resurfaced === 0` cannot rescue it either — the second
  // test send's resurfaced equals the list length, exactly like the first one's
  // would on a recycled slug.
  'announcements.result.test.scope':
    'This send wrote {count} orgs and nothing else — it did NOT publish to the fleet. Once the '
    + 'copy reads right, use Publish announcement to reach every tenant.',
  'announcements.result.test.shortfall':
    'You named {requested} orgs and {targeted} were written. Check the list before sending again, '
    + 'and do not quote the list size as the reach.',
  // On the TEST panel, `resurfaced > 0` has only the bad cause: this send is
  // the test send, so an existing row under this fingerprint belongs to some
  // other announcement. The fleet panel's two-cause copy would dilute that into
  // something dismissible.
  'announcements.result.test.recycled.tag': 'Serious',
  'announcements.result.test.recycled':
    '{count} of the orgs you named already held an announcement under this slug, and its text has '
    + 'just been replaced by this draft. There is no unsend. This send IS the test send, so it '
    + 'cannot be the harmless "test first, publish after" rewrite — check what this slug last said '
    + 'in the send history below and send that text to the same orgs under this same slug to put '
    + 'it back. If you abandon this draft instead, those orgs keep the draft wording.',
  'announcements.result.mode_mismatch.tag': 'Serious',
  'announcements.result.mode_mismatch':
    'This request asked to reach only the orgs you named, but the server reports it went to the '
    + 'FLEET — the announcement has reached every tenant org and cannot be unsent. Check '
    + 'immediately whether the server predates the whitelist feature.',
  // The other direction — #1945's own shape: the operator believes every
  // customer was told and a handful of orgs were written. This used to land in
  // the neutral panel with only a coverage number to interpret, and
  // interpreting a coverage number is exactly the step nobody took in #1945.
  'announcements.result.mode_mismatch.narrowed':
    'This request asked to reach every tenant org, but the server reports a WHITELIST send: only '
    + '{count} orgs were written and the fleet publish did NOT happen. Do not report this as '
    + '"all customers notified" — check the server, then publish again.',
  'announcements.history.caption': 'Send history',
  'announcements.history.column.sent_at': 'Sent at',
  'announcements.history.column.slug': 'Slug',
  'announcements.history.column.title': 'Title',
  'announcements.history.column.severity': 'Severity',
  'announcements.history.column.scope': 'Scope',
  'announcements.history.scope.fleet': 'Fleet',
  // The number is the LIST LENGTH, not the reach — "Orgs reached" one column
  // over is what was actually written, and the two can differ. So the count
  // carries the word "list": a week later "Test 3" reads as "3 orgs got it",
  // where "list of 3" does not. When the audit metadata has no list, it does not
  // render as 0 — that would be a false claim about the list.
  'announcements.history.scope.whitelist': 'Test · list of {count}',
  'announcements.history.scope.whitelist.unknown': 'Test · list not recorded',
  'announcements.history.column.targeted': 'Orgs reached',
  'announcements.history.column.created': 'Created',
  'announcements.history.column.resurfaced': 'Overwritten',
  // #1947. Every string below describes POLLING — orgs whose client called the
  // notification endpoint after this announcement was written. None of them
  // may be reworded to "read", "delivered" or "received": the server cannot
  // know any of those, and #1945 is what a confident wrong number costs.
  'announcements.history.column.polled': 'Polled since',
  'announcements.history.column.polled.title':
    'Orgs whose client called the notification endpoint after this announcement was written, out of the orgs that still hold it. A lower bound on who COULD have seen it — not proof anyone read it.',
  // The “n/a” sentence deliberately names retention as an EXAMPLE, not as the
  // diagnosis. The predicate behind it is only "no notification row for this
  // fingerprint right now", which has at least four causes — aged out, never
  // sent (a mistyped slug), sent to zero orgs, or every targeted org deleted.
  // Picking one and stating it is the #1945 shape: the surface asserting a
  // cause it has no way to know.
  'announcements.history.polled.note':
    '“Polled since” counts orgs where at least one member’s client called the notification endpoint after the announcement was written — i.e. had the chance to see it. It is not read state and not a delivery receipt: one member polling counts the whole org, and the figure keeps rising as offline clients come back. It is also a CURRENT, per-slug figure rather than a per-row one: every send of the same slug shares it, so a test-send row shows “n/a” instead of a number that is not about that send. “n/a” therefore means this column has no figure for that row — either the row is a test send, or there is no notification row left to measure against (for example, past the 90-day retention window). It never means the announcement reached nobody.',
  'announcements.history.polled.unmeasurable': 'n/a',
  'announcements.history.polled.whitelist.title':
    'A test send has no reach figure of its own: this column is measured per slug, and every send of this slug — including the fleet publish — shares one current value. Showing it here would read as the reach of this test send.',
  // A failed read must not render as the same “—” the loading state uses: a
  // column of dashes reads as "still loading, forever" and tells the operator
  // nothing about a platform that is down.
  'announcements.history.polled.unavailable': '?',
  'announcements.history.polled.unavailable.title':
    'Reachability could not be read; this column is unavailable. It does not mean nobody polled.',
  'announcements.history.polled.error':
    'Reachability data could not be loaded — that column is unavailable. The send history itself is unaffected.',
  'announcements.history.column.operator': 'Operator',
  'announcements.history.empty': 'No announcement has been sent yet.',
  'announcements.history.error': 'Send history could not be loaded — past announcements unknown.',
  // Short on purpose: the alert above the table already says the sentence.
  'announcements.history.error.cell': 'Could not be loaded.',
  'announcements.history.loading': 'Loading…',
  'announcements.history.pagination.aria_label': 'Send history pagination',
  'announcements.quiet.caption': 'Quiet orgs',
  'announcements.quiet.note':
    'Orgs where no member’s client has called the notification endpoint inside the window. A starting point, not a fault list: a team on holiday, a customer who only uses the API, and a broken client all look the same from here. It says nothing about what anyone has read.',
  'announcements.quiet.threshold': 'Silent for at least',
  'announcements.quiet.threshold.days': '{days} days',
  'announcements.quiet.total': '{total} orgs',
  'announcements.quiet.column.org': 'Org',
  'announcements.quiet.column.org_id': 'Org ID',
  'announcements.quiet.column.members': 'Active members',
  'announcements.quiet.column.last_polled': 'Last poll',
  'announcements.quiet.never': 'never polled',
  'announcements.quiet.empty': 'Every tenant org has polled inside the window.',
  'announcements.quiet.loading': 'Loading…',
  'announcements.quiet.error': 'Quiet-org list could not be loaded — silent tenants unknown.',
  'announcements.quiet.error.cell': 'Could not be loaded.',
  'announcements.quiet.pagination.aria_label': 'Quiet-org pagination',

  // ── Support tickets (#1394) ─────────────────────────────────────
  'support.tickets.title': 'Support tickets',
  'support.tickets.subtitle':
    'Tenant-submitted support tickets: read the conversation, view attachments, reply and move the status.',
  'support.tickets.filter.status': 'Status',
  'support.tickets.status.all': 'All',
  'support.tickets.status.open': 'Open',
  'support.tickets.status.processing': 'Processing',
  'support.tickets.status.resolved': 'Resolved',
  'support.tickets.status.closed': 'Closed',
  'support.tickets.type.billing': 'Billing',
  'support.tickets.type.usage': 'Usage',
  'support.tickets.type.api': 'API',
  'support.tickets.type.model': 'Model',
  'support.tickets.type.other': 'Other',
  'support.tickets.filter.org_id': 'Org id',
  'support.tickets.filter.org_id.placeholder': 'org_xxx',
  'support.tickets.filter.q': 'Search',
  'support.tickets.filter.q.placeholder': 'Search description / email',
  'support.tickets.filter.apply': 'Apply',
  'support.tickets.filter.reset': 'Reset',
  'support.tickets.error.title': 'Failed to load tickets',
  'support.tickets.loading_aria': 'Loading tickets…',
  'support.tickets.empty.title': 'No matching tickets',
  'support.tickets.empty.body': 'Adjust the status filter or search terms and try again.',
  'support.tickets.column.id': 'Ticket id',
  'support.tickets.column.org': 'Org',
  'support.tickets.column.type': 'Type',
  'support.tickets.column.status': 'Status',
  'support.tickets.column.email': 'Email',
  'support.tickets.column.attachments': 'Attachments',
  'support.tickets.column.created_at': 'Created at',
  'support.tickets.column.updated_at': 'Updated at',
  'support.tickets.detail.description': 'Description',
  'support.tickets.detail.conversation': 'Conversation',
  'support.tickets.detail.conversation.empty': 'No replies yet.',
  'support.tickets.detail.attachments': 'Attachments',
  'support.tickets.detail.attachments.none': 'No attachments.',
  'support.tickets.detail.attachment_expand': 'Enlarge attachment {name}',
  'support.tickets.detail.attachment_collapse': 'Shrink attachment {name}',
  'support.tickets.detail.attachment_loading_aria': 'Loading attachment image…',
  'support.tickets.detail.download': 'Download',
  'support.tickets.detail.status_label': 'Ticket status',
  'support.tickets.detail.loading_aria': 'Loading ticket detail…',
  'support.tickets.reply.author.user': 'User',
  'support.tickets.reply.author.vendor': 'Support',
  'support.tickets.reply.label': 'Reply to ticket',
  'support.tickets.reply.placeholder': 'Write a reply…',
  'support.tickets.reply.submit': 'Send reply',
  'support.tickets.reply.submitting': 'Sending…',
  'support.tickets.toast.status_updated': 'Ticket status updated',
  'support.tickets.toast.reply_sent': 'Reply sent',
  'support.tickets.pagination.aria_label': 'Ticket pagination',
  'support.tickets.pagination.range': 'Showing {start}–{end} of {total}',

  // ── Admission interception (router#1609 / #1689, rendered #1615) ─
  'requests.admission.title': 'Admission interception',
  'requests.admission.rule': 'Rule',
  'requests.admission.action': 'Action',
  'requests.admission.client_kind': 'Client kind',
  'requests.admission.client_entrypoint': 'Client entrypoint',
  'requests.admission.lane': 'Egress lane',

  // ── Probe modal + result panel ──────────────────────────────────
  'probe.outcome.pass': 'Pass',
  'probe.outcome.fail_shape': 'Fail · shape',
  'probe.outcome.fail_content': 'Fail · content',
  'probe.outcome.fail_upstream_error': 'Fail · upstream error',
  'probe.outcome.fail_timeout': 'Fail · timeout',
  'probe.id_label': 'probe {id}…',
  'probe.latency': 'Latency',
  'probe.cost': 'Cost',
  'probe.upstream_status': 'Upstream status',
  'probe.input_output_tokens': 'Input / Output tokens',
  'probe.cache_read_write': 'Cache read / write',
  'probe.charge_skipped': 'Charge skipped',
  'probe.error': 'Error',
  'probe.modal.title': 'Probe upstream',
  'probe.modal.close_aria': 'Close probe dialog',
  'probe.modal.error.vendor_model_required': 'Vendor and model are required',
  'probe.modal.vendor.label': 'Vendor',
  'probe.modal.vendor.placeholder': 'e.g. anthropic',
  'probe.modal.model.label': 'Model',
  'probe.modal.model.placeholder': 'e.g. claude-sonnet-4-5',
  'probe.modal.protocol.label': 'Protocol',
  'probe.modal.submit': 'Run probe',
  'probe.modal.submitting': 'Probing…',

  // ── Reports ─────────────────────────────────────────────────────
  'reports.title': 'Reports',
  'reports.subtitle': 'Compliance + finance reporting. Month-scoped aggregations, CSV exports.',
  'reports.month_label': 'Month',
  'reports.regulatory.title': 'Regulatory extract',
  'reports.regulatory.body':
    'Per-account monthly usage for PRC AI filing. Only accounts with usage in the window are listed.',
  'reports.regulatory.csv_download': 'Download CSV',
  'reports.regulatory.loading': 'Loading regulatory extract…',
  'reports.regulatory.error': 'Failed to load regulatory extract.',
  'reports.regulatory.empty': 'No account usage recorded in this month.',
  'reports.regulatory.row_count': '{count} accounts',
  'reports.regulatory.truncated':
    'Row count exceeded the export cap; some accounts are omitted. Raise the cap or paginate.',
  'reports.regulatory.column.account_id': 'Account id',
  'reports.regulatory.column.email': 'Email',
  'reports.regulatory.column.registered_at': 'Registered at',
  'reports.regulatory.column.input_tokens': 'Input tokens',
  'reports.regulatory.column.output_tokens': 'Output tokens',
  'reports.regulatory.column.cost_usd': 'Cost (CNY)',
  'reports.monthly.title': 'Monthly reconciliation',
  'reports.monthly.body':
    'Revenue ledger ↔ payment orders for the selected month. Investigate any drift.',
  'reports.monthly.csv_download': 'Download CSV',
  'reports.monthly.view_orders': 'View payment orders',
  'reports.monthly.loading': 'Loading monthly report…',
  'reports.monthly.error': 'Failed to load monthly report.',
  'reports.monthly.drift.title': 'Drift detected.',
  'reports.monthly.drift.body':
    'Cash collected differs from recognised topups by {amount}. Investigate missing notify callbacks or ledger writes.',
  'reports.monthly.tile.paid_cny': 'Paid orders (CNY)',
  'reports.monthly.tile.paid_count': '{count} orders',
  'reports.monthly.tile.topups_cny_gross': 'Topups (CNY gross)',
  'reports.monthly.tile.topups_count': '{count} ledger rows',
  'reports.monthly.tile.usage_usd': 'Usage charged (CNY)',
  'reports.monthly.tile.usage_rows': '{rows} usage rows',
  'reports.monthly.tile.l2_profit': 'L2 profit (CNY)',
  'reports.monthly.tile.l1_profit': 'L1 profit {amount}',
  'reports.monthly.detail.metric': 'Metric',
  'reports.monthly.detail.value': 'Value',
  'reports.monthly.detail.period_start': 'Period start',
  'reports.monthly.detail.period_end': 'Period end',
  'reports.monthly.detail.paid_cny_net': 'Topups (CNY net of channel fees)',
  'reports.monthly.detail.usd_credited': 'USD credited to balances',
  'reports.monthly.detail.ops_adjust_rows': 'Ops adjustment rows',
  'reports.monthly.detail.ops_adjust_usd': 'Ops adjustment USD',
  'reports.monthly.detail.drift_cny': 'Drift (paid − topups, CNY)',

  // ── Webhook events ──────────────────────────────────────────────
  'webhook_events.title': 'Webhook events',
  'webhook_events.subtitle':
    'Inbox of webhook callbacks across providers. Filter, expand raw payloads, mark stuck events processed.',
  'webhook_events.filter.provider': 'Provider',
  'webhook_events.filter.provider_placeholder': 'e.g. zpay',
  'webhook_events.filter.processed': 'Status',
  'webhook_events.filter.processed.all': 'All',
  'webhook_events.filter.processed.pending': 'Pending',
  'webhook_events.filter.processed.done': 'Processed',
  'webhook_events.filter.since': 'Since (local)',
  'webhook_events.loading': 'Loading webhook events…',
  'webhook_events.error': 'Failed to load webhook events.',
  'webhook_events.empty': 'No webhook events match the current filters.',
  'webhook_events.column.created_at': 'Created at',
  'webhook_events.column.provider': 'Provider',
  'webhook_events.column.event_type': 'Event type',
  'webhook_events.column.status': 'Status',
  'webhook_events.column.payload': 'Payload',
  'webhook_events.column.actions': 'Actions',
  'webhook_events.status.processed': 'Processed',
  'webhook_events.status.pending': 'Pending',
  'webhook_events.payload.expand': 'Expand payload',
  'webhook_events.action.mark_processed': 'Mark processed',
  'webhook_events.action.marking': 'Marking…',
  'webhook_events.toast.marked': 'Event marked processed.',

  // ── VPS management ──────────────────────────────────────────────
  'vps.title': 'VPS nodes',
  'vps.subtitle': 'Readonly view of KiwiVM plans, traffic, load, and node risks.',
  'vps.loading': 'Loading VPS nodes…',
  'vps.empty': 'No KiwiVM VPS nodes are configured.',
  'vps.metric.nodes': 'Nodes',
  'vps.metric.nodes_detail': 'VPS count in KiwiVM configuration',
  'vps.metric.unhealthy': 'Unhealthy',
  'vps.metric.unhealthy_detail': 'Nodes marked warning or critical',
  'vps.metric.lowest_traffic': 'Lowest traffic left',
  'vps.metric.lowest_traffic_detail': 'Lowest remaining transfer ratio across nodes',
  'vps.metric.updated': 'Updated',
  'vps.metric.updated_detail': 'Most recent KiwiVM read time',
  'vps.column.node': 'Node',
  'vps.column.status': 'Status',
  'vps.column.plan': 'Plan',
  'vps.column.traffic': 'Traffic',
  'vps.column.billing_cycle': 'Billing cycle',
  'vps.column.load': 'Load',
  'vps.column.disk': 'Disk',
  'vps.column.risks': 'Risks',
  'vps.column.lb': 'LB pools',
  'vps.billing_cycle.reset_hint': 'Traffic reset',
  'vps.lb.empty': 'No pools',
  'vps.lb.purpose.platform': 'Platform',
  'vps.lb.purpose.router': 'Router',
  'vps.lb.purpose.sidecar': 'Sidecar',
  'vps.lb.state.not_in_pool': 'Not in pool',
  'vps.lb.state.enabled': 'Enabled',
  'vps.lb.state.disabled_manual': 'Disabled (manual)',
  'vps.lb.state.disabled_auto': 'Disabled (auto · traffic)',
  'vps.lb.action.join': 'Join',
  'vps.lb.action.enable': 'Enable',
  'vps.lb.action.disable': 'Disable',
  'vps.lb.action.remove': 'Remove',
  'vps.lb.outcome.joined': 'Joined pool',
  'vps.lb.outcome.updated': 'Updated',
  'vps.lb.outcome.enabled': 'Origin enabled',
  'vps.lb.outcome.disabled': 'Origin disabled (drained)',
  'vps.lb.outcome.removed': 'Removed from pool',
  'vps.lb.outcome.absent': 'Not in pool',
  'vps.lb.outcome.held': 'Rebalance hold set',
  'vps.lb.confirm.body': '{action} {node} in the {pool} pool?',
  'vps.lb.confirm.floor_warning': 'This pool will have ≤1 enabled origin remaining.',
  'vps.config.title': 'KiwiVM config',
  'vps.config.subtitle': 'VEID and key preview for the current environment.',
  'vps.config.add': 'Add node',
  'vps.config.loading': 'Loading config…',
  'vps.config.empty': 'No KiwiVM config has been added.',
  'vps.config.column.node': 'Node',
  'vps.config.column.veid': 'VEID',
  'vps.config.column.key': 'Key',
  'vps.config.column.state': 'State',
  'vps.config.column.actions': 'Actions',
  'vps.config.key_configured': 'Configured',
  'vps.config.key_missing': 'Missing',
  'vps.config.enabled': 'Enabled',
  'vps.config.disabled': 'Disabled',
  'vps.config.edit': 'Edit',
  'vps.config.modal.add_title': 'Add KiwiVM VPS',
  'vps.config.modal.edit_title': 'Edit KiwiVM VPS',
  'vps.config.field.id': 'ID',
  'vps.config.field.environment': 'Environment',
  'vps.config.field.label': 'Label',
  'vps.config.field.role': 'Role',
  'vps.config.field.veid': 'VEID',
  'vps.config.field.public_host': 'Host',
  'vps.config.field.api_key': 'API key',
  'vps.config.field.rotate_api_key': 'Rotate API key',
  'vps.config.field.deploy_target': 'CD deploy target',
  'vps.config.field.deploy_ip': 'Deploy IP (IPv4)',
  'vps.config.field.enabled': 'Monitoring enabled',
  'vps.config.cancel': 'Cancel',
  'vps.config.save': 'Save',
  'vps.config.toast.saved': 'VPS config saved',
  'vps.config.toast.veid_required': 'VEID is required',
  'vps.config.toast.key_required': 'API key is required',
  'vps.column.trend': 'Trend',
  'vps.trend.sparkline_aria': '{label} remaining traffic ratio, last 7 days',
  'vps.trend.no_samples': 'No samples yet',
  'vps.trend.burn_per_day': '{value}/day',
  'vps.trend.no_burn': 'Burn rate unavailable',
  'vps.trend.projected': 'Projected exhaustion {date}',
  'vps.trend.no_projection': 'No exhaustion projected at current burn',
  'vps.trend.open_detail': 'View 30-day trend for {label}',
  'vps.detail.subtitle': 'Remaining traffic ratio and daily burn (share of monthly quota), last 30 days.',
  'vps.detail.loading': 'Loading node trend…',
  'vps.detail.empty': 'No historical samples yet. The sampler writes every 5 minutes.',
  'vps.detail.legend.remaining': 'Remaining ratio',
  'vps.detail.legend.daily_burn': 'Daily burn',
  'vps.config.toast.deploy_ip_invalid': 'A valid IPv4 deploy IP is required while deploy target is on',
  'vps.automation.title': 'Low-traffic alert threshold',
  'vps.automation.hint':
    'Sends a Lark alert when a node drops below this remaining-traffic percentage (0.1%–50%); weight rebalancing is unaffected',
  'vps.automation.inputLabel': 'Threshold (%)',
  'vps.automation.save': 'Save',
  'vps.automation.saved': 'Alert threshold updated',
  'vps.automation.invalid': 'Threshold must be between 0.1% and 50%',
  'vps.automation.loadError': 'Failed to load the alert threshold',

  // ── Cron runs ───────────────────────────────────────────────────
  'cron_runs.title': 'Cron runs',
  'cron_runs.subtitle':
    'History of control-loops ticks. Each row is one scheduled invocation across all due handlers.',
  'cron_runs.filter.handler': 'Handler',
  'cron_runs.filter.handler_placeholder': 'e.g. usage_rollup',
  'cron_runs.filter.since': 'Since (local)',
  'cron_runs.filter.since_placeholder': 'YYYY-MM-DD HH:MM',
  'cron_runs.loading': 'Loading cron runs…',
  'cron_runs.error': 'Failed to load cron runs.',
  'cron_runs.empty': 'No cron runs match the current filters.',
  'cron_runs.column.scheduled_at': 'Scheduled at',
  'cron_runs.column.duration': 'Duration',
  'cron_runs.column.due': 'Due',
  'cron_runs.column.ok': 'OK',
  'cron_runs.column.fail': 'Fail',
  'cron_runs.column.outcomes': 'Outcomes',
  'cron_runs.outcomes.expand': 'Expand raw outcomes',
  'cron_runs.manual.title': 'Manual trigger',
  'cron_runs.manual.caveat':
    'Manual triggers call the handler’s underlying idempotent internal endpoints directly. Run history (including manual runs) is recorded best-effort and may be missing or delayed — treat the trigger response (toast) as the source of truth.',
  'cron_runs.manual.run_now': 'Run now',
  'cron_runs.manual.scheduler_only': 'Scheduler only',
  'cron_runs.manual.confirm_title': 'Run {handler} now?',
  'cron_runs.manual.confirm_body':
    'This immediately calls the same idempotent internal endpoints the scheduler uses, so a manual run cannot conflict with the scheduled cadence. An audit row is written for this trigger.',
  'cron_runs.manual.confirm_action': 'Run now',
  'cron_runs.manual.now_ms_label': 'Test clock (optional)',
  'cron_runs.manual.now_ms_hint':
    'Non-production only. Production refuses this field. Leave empty to use wall clock.',
  'cron_runs.manual.toast_success': '{handler} completed ({duration}ms).',
  'cron_runs.manual.toast_failed': '{handler} run failed: {error}',
  'cron_runs.manual.unknown_error': 'unknown error',
  'cron_runs.manual.reason.fx_rate_refresh':
    'The fetch phase hits external market-rate sources whose logic exists only in the cron worker; it cannot be proxied through internal endpoints.',
  'cron_runs.manual.reason.gateway_public_canary':
    'Requires canary credentials and the public gateway base URL, which are configured only in the cron worker env.',
  'cron_runs.manual.reason.supplier_concentration':
    'Observation-only handler: the concentration snapshot only lands in worker logs; there is no state change to re-run.',
  'cron_runs.manual.reason.usage_logs_cn_cleanup':
    'Three-leg protocol (Tokyo uplink gate, CN select, Tokyo confirm-present, CN delete) cannot be driven by a single POST; arm/disarm via USAGE_LOGS_CN_CLEANUP_ARMED.',
  'cron_runs.manual.reason.generic': 'This handler only runs on the schedule.',
  'cron_runs.manual.side_effect.account_balance_alert':
    'Really sends balance-alert mail to the live addresses on this environment’s accounts — dev and staging send for real too, not just production. A mail that has gone out cannot be recalled.',
  'cron_runs.manual.side_effect.upstream_runway_alert':
    'Really posts a runway-alert card into this environment’s Lark ops channel — dev and staging push for real too, not just production.',
  'cron_runs.manual.side_effect.email_notice_reminder':
    'Really posts a card per due notice into this environment’s Lark ops channel — dev and staging push for real too, not just production. It stamps those notices as reminded so the next pass stays quiet. It mails no customer and changes no model — the reminder date drives nothing but this card.',
  'cron_runs.manual.side_effect.routing_default_parity_check':
    'Reads only — it writes to none of the tables it compares, so a re-run is as safe as a page refresh. Its one outward action is a card into this environment’s Lark ops channel, and only when the global default config and the platform default tables actually disagree; an in-sync comparison is completely silent. Triggering it repeatedly during a standing drift posts a card each time.',
  'cron_runs.manual.side_effect.deliver_credit_alerts':
    'Really posts credit-alert cards into this environment’s credit Lark chat — dev and staging push for real too, not just production. That chat is its own, not the ops alert channel. A delivery is stamped only once it lands, so a failed one is retried on the next pass while one that already went out is not repeated by a re-run; an overdue account gets one digest a day until it settles. It mails no customer.',
  'cron_runs.manual.side_effect.finance_pnl_materialize':
    'Writes the finance PnL usage rollup for sealed Asia/Shanghai days. Idempotent: already-materialized days are skipped, and a stored day whose recomputed totals differ is reported as drift and left untouched.',
  'cron_runs.manual.side_effect.promoter_grant_expiry_scan':
    'Really sends grant-expiry mail to bound C-end accounts of the environment you trigger it in — dev and staging send for real too, not just production. A mail that has gone out cannot be recalled. It stamps grant_expiry_notified_at so a re-run does not re-mail the same lot.',
  'cron_runs.manual.side_effect.credit_draw_sweep':
    'Writes credit_draw lots and their charge allocations — it books attribution for spend that has already settled, so it moves no money and changes no balance. Idempotent: a charge already allocated is skipped, and lot totals are re-derived each pass, so a re-run cannot double-attribute a cent. One manual click processes one page of the rolling three-day window; anything older is out of its reach and is not a backfill lever. The scheduled run drains the remainder on the next tick.',
  'cron_runs.manual.side_effect.codex_capacity_alert':
    'Really sends Codex-capacity-alert mail to the live addresses on this environment’s accounts — dev and staging send for real too, not just production. A mail that has gone out cannot be recalled.',
  'cron_runs.manual.side_effect.codex_capacity_rollup':
    'Recomputes Codex-capacity archive buckets for the lookback window and prunes rows older than retention. Idempotent overwrite — a re-run replaces counts, it does not add. It sends no notification.',
  'cron_runs.manual.side_effect.generic':
    'Running this by hand causes a real outward side effect — dev and staging too, not just production.',

  // ── Ops users ───────────────────────────────────────────────────
  'ops_users.title': 'Ops users',
  'ops_users.subtitle':
    'Admin directory for ops user accounts. Disable compromised users, re-enable legitimate ones, force password reset.',
  'ops_users.loading': 'Loading ops users…',
  'ops_users.error': 'Failed to load ops users.',
  'ops_users.empty': 'No ops users found.',
  'ops_users.metric.total': 'total',
  'ops_users.metric.active': 'active',
  'ops_users.metric.disabled': 'disabled',
  'ops_users.metric.locked_out': 'locked out',
  'ops_users.metric.unbound': 'unbound',
  'ops_users.matrix.title': 'Access control matrix',
  'ops_users.matrix.subtitle': 'Compare assignable roles by operator, then open sheets for provisioning and edits.',
  'ops_users.matrix.roles': 'roles',
  'ops_users.column.email': 'Email',
  'ops_users.column.user': 'User',
  'ops_users.column.roles': 'Roles',
  'ops_users.column.github': 'GitHub',
  'ops_users.column.feishu': 'Feishu',
  'ops_user_identity.method.feishu': 'Feishu',
  'ops_user_identity.method.password': 'Password',
  'ops_user_identity.method.github': 'GitHub',
  'ops_users.column.status': 'Status',
  'ops_users.column.created_at': 'Created at',
  'ops_users.column.actions': 'Actions',
  'ops_users.form.email': 'Email',
  'ops_users.form.roles': 'Roles',
  'ops_users.form.github_login': 'GitHub login',
  'ops_users.form.feishu_tenant': 'Feishu tenant',
  'ops_users.form.feishu_open_id': 'Feishu open ID',
  'ops_users.form.feishu_name': 'Feishu display name',
  'ops_users.form.sales_profile': 'Sales profile brand',
  'ops_users.form.sales_profile_hint':
    'One brand per sales user. Deleting or re-branding is blocked while the user still owns customers.',
  'ops_users.form.sales_profile_none': 'No sales profile',
  'ops_users.form.sales_profile_error':
    'Failed to load the sales profile — brand editing is disabled until a retry succeeds.',
  'ops_users.sheet.eyebrow': 'Operator access',
  'ops_users.inspector.empty': 'Select an ops user to inspect identity and security state.',
  'ops_users.inspector.roles': 'Role permissions',
  'ops_users.inspector.feishu': 'Feishu binding',
  'ops_users.inspector.security': 'Session and security',
  'ops_users.inspector.password': 'Password state',
  'ops_users.inspector.password_set': 'Set',
  'ops_users.inspector.password_unset': 'Not set',
  'ops_users.inspector.revoked_before': 'Revoked before',
  'ops_users.github.unbound': 'Unbound',
  'ops_users.feishu.unbound': 'Unbound',
  'ops_users.action.create': 'Create user',
  'ops_users.action.provision': 'New salesperson',
  'ops_users.action.provisioning': 'Provisioning…',
  'ops_users.form.display_name': 'Name',
  'ops_users.provision.intro':
    'One-shot provisioning: creates the ops account, attaches the sales brand, generates a login password and mints the invite code. All four run in one transaction, so a failure leaves no half-built account behind.',
  'ops_users.provision.display_name': 'Name',
  'ops_users.provision.email': 'Email',
  'ops_users.provision.brand': 'Brand',
  'ops_users.provision.grant_label': 'Trial credit (CNY)',
  'ops_users.provision.grant_hint':
    'Prefilled with the recommended value. Whatever is in the box is written onto this invite code.',
  'ops_users.provision.expiry_label': 'Trial credit validity (days)',
  'ops_users.provision.expiry_hint':
    'Prefilled with the recommended value. Whatever is in the box is written onto this invite code.',
  'ops_users.provision.quota_label': 'Daily quota',
  'ops_users.provision.quota_hint':
    'Prefilled with the recommended value. Whatever is in the box is written onto this invite code.',
  'ops_users.provision.plan_label': 'Model plan',
  'ops_users.provision.plan_hint':
    'Optional. Customers who activate with the minted code follow the plan for models and discounts.',
  'ops_users.provision.error.display_name_required': 'Name is required.',
  'ops_users.provision.duplicate_name_warning':
    'An account named "{name}" already exists. Creating another one duplicates the name; tell them apart by sign-in method and email when assigning customers:',
  'ops_users.provision.error.grant':
    'Trial credit must be a number greater than 0 with at most two decimals',
  'ops_users.provision.error.grant_cap': 'Trial credit exceeds the grant amount cap of {max}',
  'ops_users.provision.error.expiry':
    'Trial credit validity must be a positive whole number of days',
  'ops_users.provision.error.quota': 'Daily quota must be a positive integer',
  'ops_users.provision.error.defaults':
    'Could not load the global activation defaults. Close and retry.',
  'ops_users.provision.result.warning':
    'The password is shown once. It cannot be read back after this panel closes — copy it first.',
  'ops_users.provision.result.name': 'Name',
  'ops_users.provision.result.email': 'Email',
  'ops_users.provision.result.password': 'Password',
  'ops_users.provision.result.invite_code': 'Invite code',
  'ops_users.provision.result.brand': 'Brand',
  'ops_users.provision.result.copy_label': 'Copy string (email/password/invite code)',
  'ops_users.provision.result.copy': 'Copy',
  'ops_users.provision.result.copied': 'Copied',
  'ops_users.provision.result.copy_failed': 'Copy failed — select the text and copy manually.',
  'ops_users.provision.result.close': 'Copied, close',
  'ops_users.provision.result.close_confirm':
    'The password cannot be read back after closing. Copied it already?',
  'ops_users.toast.provisioned': 'Salesperson account provisioned.',
  'ops_users.action.permissions': 'Permission matrix',
  'ops_users.action.creating': 'Creating…',
  'ops_users.action.edit': 'Edit',
  'ops_users.action.saving': 'Saving…',
  'ops_users.action.revoke': 'Kick offline',
  'ops_users.action.revoking': 'Revoking…',
  'ops_users.status.active': 'Active',
  'ops_users.status.disabled': 'Disabled',
  'ops_users.status.locked_out': 'Locked out',
  'ops_users.action.disable': 'Disable',
  'ops_users.action.disabling': 'Disabling…',
  'ops_users.action.enable': 'Re-enable',
  'ops_users.action.enabling': 'Enabling…',
  'ops_users.action.reset_password': 'Reset password',
  'ops_users.action.resetting': 'Resetting…',
  'ops_users.action.reset_confirm': 'Confirm reset',
  'ops_users.action.set_password': 'Set password',
  'ops_users.action.setting_password': 'Setting…',
  'ops_users.action.invite_password': 'Send password invite',
  'ops_users.action.inviting_password': 'Sending…',
  'ops_users.action.cancel': 'Cancel',
  'ops_users.reset.eyebrow': 'Destructive confirmation',
  'ops_users.reset.confirm_prompt':
    'Type the email ({email}) to confirm. Reset deletes the credential row and invalidates all live sessions.',
  'ops_users.set_password.eyebrow': 'Admin-set credential',
  'ops_users.set_password.prompt':
    'Set a login password for {email}. Cannot raise the target above your own role. Replacing an existing password signs them out.',
  'ops_users.set_password.password': 'New password',
  'ops_users.set_password.confirm': 'Confirm password',
  'ops_users.set_password.error.length': 'Password must be {min}–{max} characters.',
  'ops_users.set_password.error.mismatch': 'The two passwords do not match.',
  'ops_users.invite_password.eyebrow': 'Email invite',
  'ops_users.invite_password.prompt':
    'Send a one-time set-password link to {email}. This console does not show the token.',
  'ops_users.toast.disabled': 'Ops user disabled.',
  'ops_users.toast.enabled': 'Ops user re-enabled.',
  'ops_users.toast.created': 'Ops user created.',
  'ops_users.toast.updated': 'Ops user updated.',
  'ops_users.toast.revoked': 'Session revocation time updated. Older ops cookies will be rejected.',
  'ops_users.toast.reset': 'Password reset. Older ops cookies will be rejected.',
  'ops_users.toast.set_password': 'Password set.',
  'ops_users.toast.invite_password': 'Password invite sent to the account email.',
  'ops_users.set_password_page.brand': 'Dispatch Deck',
  'ops_users.set_password_page.subtitle': 'Set your ops password',
  'ops_users.set_password_page.password': 'New password',
  'ops_users.set_password_page.confirm': 'Confirm password',
  'ops_users.set_password_page.submit': 'Set password',
  'ops_users.set_password_page.submitting': 'Saving…',
  'ops_users.set_password_page.missing_token': 'This invite link is missing a token.',
  'ops_users.set_password_page.success': 'Password set. You can sign in now.',
  'ops_users.set_password_page.to_login': 'Go to login',

  // ── Change password ────────────────────────────────────────────
  'shell.change_password': 'Change password',
  'change_password.eyebrow': 'Account security',
  'change_password.title': 'Change password',
  'change_password.intro':
    'The current password is required. On success every session for this account is destroyed, so you will sign in again with the new password.',
  'change_password.current': 'Current password',
  'change_password.next': 'New password',
  'change_password.confirm': 'Confirm new password',
  'change_password.error.length': 'New password must be {min}–{max} characters.',
  'change_password.error.mismatch': 'The two new passwords do not match.',
  'change_password.error.same': 'The new password must differ from the current one.',
  'change_password.submit': 'Change password',
  'change_password.submitting': 'Submitting…',
  'change_password.cancel': 'Cancel',
  'change_password.toast.success': 'Password changed. Sign in again with the new password.',

  // ── DR check ───────────────────────────────────────────────────
  'dr_check.title': 'DR readiness drill',
  'dr_check.subtitle':
    'Run an on-demand check against both databases. Probes read + write paths on platform-db, read on gateway-db.',
  'dr_check.runner.title': 'Drill runner',
  'dr_check.runner.body':
    'Click "Run drill" to execute the four-probe sequence. Results display below.',
  'dr_check.action.run': 'Run drill',
  'dr_check.action.running': 'Running…',
  'dr_check.idle': 'No drill results yet. Click "Run drill" to start.',
  'dr_check.toast.pass': 'DR drill: all probes passed.',
  'dr_check.toast.fail': 'DR drill: one or more probes failed.',
  'dr_check.overall.pass_title': 'All probes passed.',
  'dr_check.overall.fail_title': 'One or more probes failed.',
  'dr_check.overall.body': 'Started at {started}, took {duration}ms.',
  'dr_check.column.probe': 'Probe',
  'dr_check.column.outcome': 'Outcome',
  'dr_check.column.latency': 'Latency',
  'dr_check.column.detail': 'Detail',
  'dr_check.probe.pass': 'Pass',
  'dr_check.probe.fail': 'Fail',

  // ── Toast ───────────────────────────────────────────────────────
  'toast.region_aria': 'Notifications',
  'toast.dismiss_aria': 'Dismiss notification',
  'toast.action.view_audit': 'View audit log',

  // ── Namespaces registry (#134 → #143 → Model Market #910) ──────
  // The `channels.*` keys that remain are shared form/brand-binding
  // strings reused by the namespace surfaces + ChannelBrandBindingDrawer.
  'namespaces.title': 'Namespaces',
  'namespaces.subtitle': 'Namespace registry: identity, brands, and suppliers (pricing lives at the SKU grain)',
  'namespaces.create_btn': 'New Namespace',
  'namespaces.search.placeholder': 'Search namespace, slug, supplier…',
  'namespaces.empty': 'No namespaces yet',
  'namespaces.brand_filter_aria': 'Brand filter',
  'namespaces.brand_filter.all': 'All',
  'namespaces.col.display_name': 'Namespace',
  'namespaces.col.vendor': 'Vendor',
  'namespaces.col.brand_profiles': 'Brands',
  'namespaces.col.skus': 'SKUs',
  'namespaces.col.supplier': 'Supplier',
  'namespaces.col.official': 'Official',
  'namespaces.col.created_at': 'Created',
  'namespaces.row.delete': 'Delete namespace',
  'namespaces.delete_modal.title': 'Delete namespace?',
  'namespaces.delete_modal.body':
    '{name} will be permanently deleted; its SKUs and brand bindings are removed with it. This cannot be undone.',
  'namespaces.create_modal.title': 'New Namespace',
  'namespaces.create_modal.vendor': 'Vendor',
  'namespaces.create_modal.select_vendor': 'Select vendor…',
  'namespaces.create_modal.pricing_note':
    'Pricing lives at the SKU grain: a new namespace starts at ×1.0 — after listing models, set an explicit multiplier and cost per SKU in the Model Market.',
  'namespaces.toast.created': 'Namespace created',
  'namespaces.toast.deleted': 'Namespace deleted',
  'namespaces.toast.duplicate': 'A namespace with this slug already exists',
  'namespaces.toast.alias_duplicate': 'A brand alias with this slug already exists',
  'channels.create_modal.display_name': 'Display Name',
  'channels.create_modal.display_name_placeholder': 'Enter namespace display name',
  'channels.create_modal.description': 'Description',
  'channels.create_modal.description_placeholder': 'Short app-facing description',
  'channels.create_modal.description_hint': 'Shown to tenants in app pickers and pricing surfaces. Brand bindings can override it.',
  'channels.create_modal.supplier_name': 'Supplier',
  'channels.create_modal.supplier_placeholder': 'Supplier name',
  'channels.create_modal.slug_hint': 'unique identifier',
  'channels.create_modal.is_official': 'Official namespace',
  'channels.create_modal.is_official_hint': 'Mark as officially certified resource',
  'channels.create_modal.launch_brands': 'Launch Brands',
  'channels.create_modal.launch_brands_hint': 'Set public slugs and aliases from Brand Binding after creation.',
  'channels.brand_profiles.title': 'Brand Visibility',
  'channels.brand_profiles.enabled': 'Visible',
  'channels.brand_profiles.public_slug': 'Public Slug',
  'channels.brand_profiles.display_name': 'Alias Name',
  'channels.brand_profiles.description': 'Description Override',
  'channels.brand_profiles.description_placeholder': 'Use global channel description',
  'channels.brand_profiles.description_fallback': 'Falls back to global description',
  'channels.brand_bindings.eyebrow': 'Brand Binding',
  'channels.brand_bindings.close': 'Close brand binding',
  'channels.brand_bindings.bound_title': 'Bound Brands',
  'channels.brand_bindings.empty_bound': 'No bound brands',
  'channels.brand_bindings.add_title': 'Add Brand',
  'channels.brand_bindings.no_unbound': 'No brands to add',
  'channels.brand_bindings.remove_brand': 'Unbind {brand}',
  'channels.brand_bindings.save': 'Save Binding',
  'channels.brand_bindings.toast_saved': 'Brand binding saved',
  'channels.brand_bindings.summary_title': 'Brand Binding',
  'channels.brand_bindings.summary_hint': 'Only enabled brand bindings are shown here.',
  'channels.brand_bindings.manage': 'Manage Brands',
  'channels.create_modal.cancel': 'Cancel',
  'channels.create_modal.submit': 'Create',
  'channels.create_modal.creating': 'Creating…',

  // ── Namespace detail — SKU listing/delisting (#910) ────────────
  'namespace_detail.not_found': 'Namespace not found',
  'namespace_detail.skus_count': 'SKUs',
  'namespace_detail.edit_info': 'Edit Info',
  'namespace_detail.empty': 'No SKUs yet. Create one from the Model Market.',
  'namespace_detail.col.model': 'Model',
  'namespace_detail.col.sku_name': 'SKU name',
  'namespace_detail.col.listed_at': 'Listed',
  'namespace_detail.row.open_sku': 'Open SKU detail',
  'namespace_detail.row.delist': 'Delist SKU',
  'namespace_detail.edit_modal.title': 'Edit Namespace Info',
  'namespace_detail.edit_modal.display_name_required': 'Display name is required',
  'namespace_detail.harnesses.label': 'Harnesses',
  'namespace_detail.harnesses.hint':
    'Which client harnesses this namespace can be mounted on. Lowercase, starts with a letter, letters/digits/dashes only, at most {max}.',
  'namespace_detail.harnesses.placeholder': 'e.g. claude-code',
  'namespace_detail.harnesses.add': 'Add',
  'namespace_detail.harnesses.remove': 'Remove harness {harness}',
  'namespace_detail.harnesses.empty': 'No harnesses configured',
  'namespace_detail.harnesses.invalid':
    'A harness must be lowercase alphanumeric with dashes, starting with a letter (max 32 chars)',
  'namespace_detail.harnesses.too_many': 'At most {max} harnesses can be configured',
  'namespace_detail.toast.model_removed': 'SKU delisted',
  'namespace_detail.toast.model_remove_blocked':
    'This SKU is still referenced by activation plans or org permissions and cannot be removed yet.',
  'namespace_detail.toast.info_updated': 'Namespace info updated',
  'namespace_detail.delist_modal.title': 'Delist SKU?',
  'namespace_detail.delist_modal.body': '{sku} will be delisted from the market and its SKU row deleted. This cannot be undone.',
  'namespace_detail.delist_modal.pool_loading': 'Counting routing-pool dependents…',
  'namespace_detail.delist_modal.pool_count': '{count} enabled default-pool routing target(s) will lose their mount point',
  'namespace_detail.delist_modal.pool_none': 'No enabled targets in the default routing pool',
  'namespace_detail.delist_modal.overrides_warning': 'Org-level rate/visibility overrides on this SKU (if any) stop applying once it is delisted.',
  'namespace_detail.rename_notice':
    'Namespace slug renamed from {from} to {to}. Stored gateway/status sku_name display snapshots do not refresh automatically — run:',
  'namespace_detail.rename_slug': 'Rename slug',
  'namespace_detail.rename_modal.title': 'Rename namespace slug',
  'namespace_detail.rename_modal.hint':
    'The slug is the namespace half of tenant canonical names (namespace/model) and is globally unique. Bare model-name calls are unaffected; old canonical names 404 immediately.',
  'namespace_detail.rename_modal.current_label': 'Current slug',
  'namespace_detail.rename_modal.new_slug_label': 'New slug',
  'namespace_detail.rename_modal.slug_invalid':
    'Slug must start with a lowercase letter and contain only lowercase letters, digits, and dashes (max 63 chars)',
  'namespace_detail.rename_modal.slug_unchanged': 'New slug matches the current one',
  'namespace_detail.rename_modal.preflight_loading': 'Counting canonical-name calls on the current slug…',
  'namespace_detail.rename_modal.preflight_error_confirm':
    'Canonical usage lookup failed — blast radius unknown. Type the new slug again to force-confirm.',
  'namespace_detail.rename_modal.preflight_zero': 'No canonical-name (old slug) calls detected in the last 10 days.',
  'namespace_detail.rename_modal.preflight_usage':
    'At least {count} requests from {keys} keys used the canonical name in the last 10 days — they will 404 after the rename. Type the new slug again to confirm.',
  'namespace_detail.rename_modal.type_to_confirm_label': 'Type the new slug to confirm',
  'namespace_detail.rename_modal.confirm': 'Rename',
  'namespace_detail.toast.renamed': 'Slug renamed from {from} to {to}; snapshots refreshed',
  'namespace_detail.toast.renamed_refresh_failed':
    'Slug renamed from {from} to {to}, but the snapshot refresh failed — retry from the page banner',
  'namespace_detail.rename_refresh_warn': 'Rename landed, but the gateway sku_name snapshot refresh failed: {error}',
  'namespace_detail.rename_refresh_retry': 'Retry refresh',
  'namespace_detail.rename_refresh_unknown_error': 'unknown error',
  'namespace_detail.toast.refresh_retried': 'Snapshots refreshed ({count} rows)',

  // ── Model Market — SKU list (#910, #1327 PR-8) ─────────────────
  'sku_market.title': 'Model Market',
  'sku_market.subtitle': 'Every SKU (namespace/model): procurement cost → sales cost → sales guide rate, plus listing readiness',
  'sku_market.namespace_filter_aria': 'Namespace filter',
  'sku_market.tabs.all': 'All',
  'sku_market.empty': 'No SKUs yet',
  'sku_market.col.sku': 'SKU',
  'sku_market.col.vendor': 'Vendor',
  'sku_market.col.rate': 'Sales guide rate',
  'sku_market.col.sales_cost': 'Sales cost',
  'sku_market.col.procurement_cost': 'Procurement cost',
  'sku_market.col.sales_cost_brand': 'Sales cost ({brand})',
  'sku_market.procurement.unknown': 'Unknown',
  'sku_market.col.readiness': 'Readiness',
  'sku_market.readiness.ready': 'Ready',
  'sku_market.readiness.unknown': 'Readiness unknown',
  'sku_market.readiness.no_rate': 'Not priced',
  'sku_market.readiness.not_bound': 'Not bound',
  'sku_market.readiness.probe_failing': 'Probe not passing',
  'sku_market.readiness.no_procurement_cost': 'No procurement cost',
  'sku_market.readiness.not_visible': 'Not tenant-visible',
  'sku_market.readiness.truncated': 'More SKUs than one batch allows; readiness is shown for the first 500 only.',
  'sku_market.new_sku': 'New SKU',

  // ── Default key namespace group (design 2026-08-02) ─────────────
  'default_key_namespaces.title': 'Default key namespaces',
  'default_key_namespaces.subtitle': 'Which namespaces newly created tenant API keys are mapped to',
  'default_key_namespaces.panel': 'Default namespace group',
  'default_key_namespaces.hint': 'Checked namespaces become persistent automatic mapping rules on new keys: when a namespace gains a SKU, keys already issued pick it up. A customer never receives a namespace they cannot see. Leave it empty to restore the old behavior, where new keys carry no mappings.',
  'default_key_namespaces.sku_count': '{count} live SKUs',
  'default_key_namespaces.save': 'Save default group',
  'default_key_namespaces.forbidden': 'Configuring default key namespaces requires the admin role',
  'default_key_namespaces.load_error': 'Could not load the default namespace group',
  'default_key_namespaces.toast.saved': 'Default namespace group saved',

  // ── New SKU dialog (#1327 PR-9) ─────────────────────────────────
  'new_sku.title': 'New SKU',
  'new_sku.subtitle': 'List a model under a platform namespace and set the sales guide rate it launches at.',
  'new_sku.namespace_label': 'Platform namespace',
  'new_sku.namespace_hint': 'The namespace sets the SKU name prefix and scopes the model list to its vendor.',
  'new_sku.namespace_placeholder': 'Select a namespace',
  'new_sku.model_label': 'Model',
  'new_sku.model_hint': 'Listed models for this namespace’s vendor that it does not already sell.',
  'new_sku.model_hint_pick_namespace': 'Select a namespace first.',
  'new_sku.model_placeholder': 'Select a model',
  'new_sku.model_none': 'No models left to list in this namespace',
  'new_sku.model_loading': 'Loading the model catalog…',
  'new_sku.model_unavailable': 'Model catalog unavailable',
  'new_sku.models_unavailable':
    'The model catalog could not be read ({error}). Creating a SKU is blocked until it recovers — an empty list right now does not mean there is nothing left to list.',
  'new_sku.rate_label': 'Sales guide rate',
  'new_sku.rate_hint': 'Tenant charge = official list price × this multiplier.',
  'new_sku.rate_invalid': 'The sales guide rate must be a number greater than 0.',
  'new_sku.pricing_gap':
    '{model} has no official list price, so a multiplier cannot bill against anything. Add its pricing in the vendor catalog, then come back and create the SKU.',
  'new_sku.pricing_gap_link': 'Open the model pricing editor',
  'new_sku.submit': 'Create SKU',
  'new_sku.toast.created': 'SKU {sku} created',

  // ── SKU detail (#910) ───────────────────────────────────────────
  'sku_detail.not_found': 'SKU not found or delisted',
  'sku_detail.back': 'Back to Model Market',
  'sku_detail.namespace': 'Namespace',
  'sku_detail.listed_at': 'Listed',
  'sku_detail.pricing.title': 'SKU Pricing',
  'sku_detail.pricing.rate_label': 'Rate multiplier',
  'sku_detail.pricing.rate_hint': 'Tenant charge = list price × this multiplier',
  'sku_detail.pricing.cost_label': 'Upstream cost (¥/USD)',
  'sku_detail.pricing.cost_hint': 'CNY spent upstream per USD of base list price',
  'sku_detail.pricing.save': 'Save pricing',
  'sku_detail.pricing.saving': 'Saving…',
  'sku_detail.pricing.invalid_rate': 'Rate must be a positive number',
  'sku_detail.pricing.invalid_cost': 'Cost must be a positive number',
  'sku_detail.pricing.toast_saved': 'SKU pricing updated',
  'sku_detail.namespace_rate.label': 'Follow line-level rate agreements',
  'sku_detail.namespace_rate.hint':
    'This switch only decides whether THIS SKU inherits per-org namespace rate agreements. It does not edit or delete those agreements.',
  'sku_detail.namespace_rate.state_follows':
    'This SKU follows line-level rate agreements for each org that has one.',
  'sku_detail.namespace_rate.state_own':
    'This SKU ignores line-level rate agreements. Existing namespace agreements are not edited or deleted.',
  'sku_detail.namespace_rate.toast_follows': 'This SKU now follows line-level rate agreements',
  'sku_detail.namespace_rate.toast_own': 'This SKU no longer follows line-level rate agreements',
  'sku_detail.rate_limit.title': 'SKU default limits',
  'sku_detail.rate_limit.hint': 'Used when a tenant has no override. Empty means that dimension is unlimited. Edit in Quota Center.',
  'sku_detail.rate_limit.l1_off': 'L1 is off, so these limits do not take effect.',
  'sku_detail.rate_limit.rpm': 'RPM',
  'sku_detail.rate_limit.tpm': 'TPM',
  'sku_detail.rate_limit.save': 'Save limits',
  'sku_detail.rate_limit.clear': 'Clear',
  'sku_detail.rate_limit.use_clear': 'When both dimensions are empty, use Clear instead of Save.',
  'sku_detail.rate_limit.unlimited': 'Unlimited',
  'sku_detail.rate_limit.invalid': 'Must be a positive integer, or empty for unlimited.',
  'sku_detail.rate_limit.toast_saved': 'SKU limits saved',
  'sku_detail.rate_limit.toast_cleared': 'SKU limits cleared',
  'sku_detail.rate_limit.loading': 'Loading limits…',
  'sku_detail.load_scope.title': 'Load-aware (this SKU)',
  'sku_detail.load_scope.hint': 'This SKU overlay wins over the lane and the global default. Follow means no exception is stored.',
  'sku_detail.load_scope.loading': 'Loading load-aware settings…',
  'sku_detail.load_scope.restore': 'Follow parent',
  'sku_detail.load_scope.toast_saved': 'SKU load-aware settings saved',
  'sku_detail.load_scope.toast_cleared': 'SKU exception cleared',
  'sku_detail.pricing.reprice_warning':
    'The following plans would charge customers at or above the new street price',
  'sku_detail.pricing.reprice_plan': '{name} · {brand} · ×{rate} · {orgs} assigned',
  'sku_detail.pricing.windows.configure': 'Schedule',
  'sku_detail.pricing.windows.none': 'No window / all-day base rate',
  'sku_detail.pricing.windows.active': 'In force: {label}',
  'sku_detail.pricing.windows.base': 'Base ×{value}',
  'sku_detail.pricing.windows.effective': 'Now ×{value}',
  'sku_detail.pricing.windows.dialog_title': 'Sale windows for {model}',
  'sku_detail.pricing.windows.dialog_description': 'Saving replaces the whole sale-window set for this SKU. An empty list clears every window so the base rate applies all day.',
  'sku_detail.pricing.windows.add': 'Add window',
  'sku_detail.pricing.windows.remove': 'Remove window',
  'sku_detail.pricing.windows.label': 'Label',
  'sku_detail.pricing.windows.timezone': 'Time zone',
  'sku_detail.pricing.windows.days': 'Days of week',
  'sku_detail.pricing.windows.day.mon': 'Mon',
  'sku_detail.pricing.windows.day.tue': 'Tue',
  'sku_detail.pricing.windows.day.wed': 'Wed',
  'sku_detail.pricing.windows.day.thu': 'Thu',
  'sku_detail.pricing.windows.day.fri': 'Fri',
  'sku_detail.pricing.windows.day.sat': 'Sat',
  'sku_detail.pricing.windows.day.sun': 'Sun',
  'sku_detail.pricing.windows.start': 'Start',
  'sku_detail.pricing.windows.end': 'End',
  'sku_detail.pricing.windows.cross_midnight': 'End is earlier than start, so the window crosses midnight into the next day.',
  'sku_detail.pricing.windows.multiplier': 'Window multiplier',
  'sku_detail.pricing.windows.enabled': 'Enabled',
  'sku_detail.pricing.windows.save': 'Save schedule',
  'sku_detail.pricing.windows.cancel': 'Cancel',
  'sku_detail.pricing.windows.empty': 'No windows yet. The base multiplier applies all day.',
  'sku_detail.pricing.windows.close': 'Close',
  'sku_detail.pricing.windows.days_required': 'Pick at least one day',
  'sku_detail.pricing.windows.label_required': 'Enter a label',
  'sku_detail.pricing.windows.timezone_required': 'Enter a time zone',
  'sku_detail.pricing.windows.clock_invalid': 'Time must be HH:MM (end may be 24:00)',
  'sku_detail.pricing.windows.start_end_equal': 'Start and end must be different times',
  'sku_detail.pricing.windows.start_not_end_of_day': '24:00 can only be used as the end time',
  'sku_detail.pricing.windows.multiplier_invalid': 'Must be a number greater than 0 and at most {max}',
  'sku_detail.pricing.windows.toast_saved': 'Sale schedule updated',
  'sku_detail.pool.open_tenant_routing': 'Open tenant routing',
  'sku_detail.status.title': '24h availability',
  'sku_detail.status.availability': 'Availability',
  'sku_detail.status.requests_24h': 'Requests (24h)',
  'sku_detail.status.updated_at': 'Updated',
  'sku_detail.status.state.healthy': 'Healthy',
  'sku_detail.status.state.busy': 'Busy',
  'sku_detail.status.state.unavailable': 'Unavailable',
  'sku_detail.status.sparkline_aria': '5-minute success-rate trend',
  'sku_detail.readiness.checking': 'Checking readiness',
  'sku_detail.readiness.unknown': 'Readiness unavailable',
  'sku_detail.readiness.ready': 'Listed · routing ready',
  'sku_detail.key_preset_activation': 'Activate key preset mapping',
  // ── Supply binding (#1327 PR-4) ─────────────────────────────────
  'sku_detail.supply.title': 'Supply',
  'sku_detail.supply.hint': 'This one panel is the whole supply story for this SKU: bound targets first, then the capabilities it could still be bound to. Procurement multipliers are maintained per (upstream channel × upstream model) on the upstream page — binding inherits one, it never sets one, and an unpriced candidate cannot be bound.',
  'sku_detail.supply.column.upstream': 'Upstream',
  'sku_detail.supply.column.probe': 'Probe status',
  'sku_detail.supply.column.multiplier': 'Procurement multiplier',
  'sku_detail.supply.column.action': 'State / action',
  'sku_detail.supply.bind': 'Bind',
  'sku_detail.supply.multiplier_missing': 'Not priced',
  'sku_detail.supply.unpriced_hint': 'Set the procurement multiplier on the upstream page first.',
  'sku_detail.supply.open_upstream': 'Open upstream',
  'sku_detail.supply.reason.protocol_mismatch': 'This capability row carries a malformed protocol value, so it cannot be bound.',
  'sku_detail.supply.reason.egress_incompatible': 'The egress lane / admission / model allowlist contract rejects this binding.',
  'sku_detail.supply.not_synced': 'The gateway could not resolve this SKU as a canonical marketplace identity. For a brand-new namespace this can lag up to 30 seconds; if it persists, check that the namespace slug still matches the SKU name.',
  'sku_detail.supply.toast_bound': 'Supply bound to this SKU',
  'sku_detail.supply.column.upstream_channel': 'Upstream channel',
  'sku_detail.supply.column.upstream_model': 'Upstream model',
  'sku_detail.supply.column.weight': 'Weight',
  'sku_detail.supply.column.enabled': 'Enabled',
  'sku_detail.supply.column.scheduling': 'Scheduling',
  'sku_detail.supply.probe_unpinned': '—',
  'sku_detail.supply.multiplier_unknown': 'Unknown',
  'sku_detail.supply.group.bound': 'Bound ({count})',
  'sku_detail.supply.group.bound_empty': 'Nothing is bound to this SKU yet; bind one of the candidates below.',
  'sku_detail.supply.group.recommended': 'Recommended ({count})',
  'sku_detail.supply.group.recommended_empty': 'No unbound candidate shares this SKU’s own model name.',
  'sku_detail.supply.group.nothing_at_all': 'This SKU has neither bound supply nor any bindable candidate. Configure and probe a lane on the upstream page, or name one by hand with “Add target”.',
  'sku_detail.supply.group.cross_model': 'Cross-model candidates ({count})',
  'sku_detail.supply.save': 'Save',
  'sku_detail.supply.unsaved': 'Unsaved',
  'sku_detail.supply.no_capability_pin': 'No capability pin',
  'sku_detail.supply.issue.legacy_unpinned': 'Legacy target not pinned',
  'sku_detail.supply.issue.not_in_candidates': 'Capability not in candidates',
  'sku_detail.supply.issue.probe_abnormal': 'Probe abnormal',
  'sku_detail.supply.pin': 'Pin capability',
  'sku_detail.supply.pin_title': 'Pin capability',
  'sku_detail.supply.pin_hint': 'This legacy target pins no capability, so the gateway refuses to save its weight and retry order in place. The four-tuple is already decided — pick one live capability under it. Saving writes the pinned binding and deletes the old unpinned row.',
  'sku_detail.supply.pin_empty': 'No live capability exists under this four-tuple; probe the lane on the upstream page first.',
  'sku_detail.supply.pin_confirm': 'Pin and save',
  'sku_detail.supply.pin_select_placeholder': 'Choose a capability',
  'sku_detail.supply.pin_select_hint': 'Saving needs one selected. Entries read as upstream model · capability id · protocol · probe status.',
  'sku_detail.supply.toast_pinned': 'Capability pinned',
  'sku_detail.supply.pin_before_tuning': 'Pin a capability before tuning',
  'sku_detail.supply.pin_load_failed': 'Could not load this upstream’s capabilities, so whether this lane has anything to pin is unknown.',
  'sku_detail.supply.targets_load_failed': 'Could not load this SKU’s routing targets, so no bound rows are shown below. Binding new supply and adding a target are both disabled until it loads. Refresh and retry.',
  'sku_detail.supply.bind_blocked_targets_unknown': 'The routing targets failed to load, so whether this four-tuple already carries an unpinned legacy target is unknown — binding now could overwrite an existing binding. Refresh first.',
  'sku_detail.supply.pin_all_taken': 'Every live capability under this four-tuple is already pinned by another target of this SKU, so this unpinned row is a duplicate — delete it. No re-probe is needed.',
  'sku_detail.supply.bind_hits_legacy_row': 'This four-tuple already carries an unpinned legacy target ({channel}). Binding directly would move that target onto the new capability instead of adding a row, so this opens the pin flow instead.',
  'sku_detail.supply.add_hits_legacy_row': 'This four-tuple already carries an unpinned legacy target ({channel}), so saving would have moved that target instead of adding a row. The pin flow is open instead and the add form has been closed; the weight you entered is not applied — pinning keeps the target’s stored weight.',
  'sku_detail.supply.write_hits_bound_row': 'This SKU is already bound to that capability on this four-tuple ({channel}), so this would overwrite that row’s weight, retry order and enabled flag rather than add a row. Nothing was saved. Edit it directly in the bound list above.',
  'sku_detail.supply.delete_confirm_title': 'Delete this supply binding?',
  'sku_detail.supply.delete_confirm_body': 'Once removed, this SKU’s traffic will stop using that lane.',
  'sku_detail.supply.delete_confirm': 'Delete binding',
  'sku_detail.hint.label': 'Term explanation',
  'sku_detail.hint.sku': 'SKU: one “namespace + model name” sold to tenants, e.g. oauth/claude-sonnet-5. What it sells and where it buys from are two different things.',
  'sku_detail.hint.namespace': 'Namespace: a seller-side product line, e.g. oauth, openai-pro, xai. It is the part of the SKU name before the slash.',
  'sku_detail.hint.upstream': 'Upstream: the supplier. An upstream holds accounts; an account holds key buckets.',
  'sku_detail.hint.upstream_channel': 'Upstream channel: which lane this key is attached to. Upstream + account + key bucket + upstream channel is the four-tuple that answers “who do we buy from”.',
  'sku_detail.hint.capability': 'Capability: what one four-tuple actually serves — which upstream model, over which protocol, and whether the probe passes. One four-tuple can carry several; a binding pins exactly one.',
  'sku_detail.hint.probe_status': 'Probe status: the latest probe result for this capability. Only passed / needs_probe / probe_failed count as a live lane to the gateway.',
  'sku_detail.hint.multiplier': 'Procurement multiplier: the buy-side multiplier maintained per (upstream channel × upstream model) on the upstream page. Without one, binding is refused.',
  'sku_detail.hint.weight': 'Weight: share of traffic within one retry rank. 0 means reserve — it still routes when no weighted sibling exists, and stands by when one does.',
  'sku_detail.hint.retry_order': 'Retry order: the order lanes are tried after a failure, lowest first. Empty means rank automatically by weight.',
  'sku_detail.hint.scheduling': 'Scheduling: how selection uses this supply. Weight sets the share within one retry rank, where 0 means reserve; retry order sets the sequence tried after a failure, lowest first, and empty ranks automatically by weight. Edits take effect only once saved.',
  'sku_detail.hint.policy_mode': 'Execution policy: how far a failed request retries for this SKU (whole chain / network errors only / same cache domain / fail fast). It is a SKU-level setting and applies to every target.',
  'sku_detail.hint.legacy_unpinned': 'Legacy target not pinned: this binding predates capability pinning and carries no capability_id. The gateway refuses to save it in place — pin a capability first.',
  // ── Exclusive coverage repair ───────────────────────────────────
  'sku_detail.exclusive.hint': 'Exclusive routing configs do not inherit the default pool, so a config missing this SKU makes it unavailable to that config’s customers. Copy a default target across in one click.',
  // ── Key presets ─────────────────────────────────────────────────
  'sku_detail.keys.hint': 'Write this SKU as a managed alias onto one key of one organization. The scope is fixed to a single org + single key; there is no global action.',
  // ── Readiness checklist (#1327 PR-4) ────────────────────────────
  'sku_detail.checklist.title': 'Launch readiness',
  'sku_detail.checklist.ready': 'Ready',
  'sku_detail.checklist.not_ready': 'Not ready',
  'sku_detail.checklist.blocked_count': '{count} not ready',
  'sku_detail.checklist.fix': 'Resolve',
  'sku_detail.checklist.pricing': 'Sales pricing',
  'sku_detail.checklist.pricing_hint': 'This SKU carries a tenant rate multiplier above zero.',
  'sku_detail.checklist.binding': 'Supply binding',
  'sku_detail.checklist.binding_hint': 'The default routing pool holds at least one enabled target.',
  'sku_detail.checklist.probe': 'Route liveness',
  'sku_detail.checklist.probe_hint': 'Every bound target still resolves through the live-capability chain.',
  'sku_detail.checklist.cost': 'Procurement cost',
  'sku_detail.checklist.cost_hint': 'Every bound target has an explicit procurement multiplier.',
  'sku_detail.checklist.visibility': 'Tenant visibility',
  'sku_detail.checklist.visibility_hint': 'This SKU is on the public shelf: every organization sees it at the list price unless an override hides it.',
  'sku_detail.checklist.visibility_hint_private': 'Private: organizations reach this SKU only where it has been granted one by one. Put it on the public shelf below.',
  'sku_detail.shelf.label': 'Public shelf',
  'sku_detail.shelf.state_listed': 'Listed for every organization at the list price. Per-org overrides still win.',
  'sku_detail.shelf.state_private': 'Private: reachable only by organizations granted it one by one.',
  'sku_detail.shelf.blocked_pricing': 'Price it first — this model has no list price in force, so there is no rate the shelf could sell at.',
  'sku_detail.shelf.blocked_checking': 'Checking routing readiness…',
  'sku_detail.shelf.blocked_unknown': 'Routing readiness could not be read, so the shelf stays closed. Retry once it resolves.',
  'sku_detail.shelf.blocked_binding': 'Give it supply first — no enabled target is bound in the default routing pool.',
  'sku_detail.shelf.blocked_probe': 'Fix routing first — a bound target no longer resolves through the live-capability chain.',
  'sku_detail.shelf.blocked_cost': 'Record procurement cost first — a bound target has no explicit cost row.',
  'sku_detail.shelf.toast_listed': 'SKU is on the public shelf',
  'sku_detail.shelf.toast_delisted': 'SKU taken off the public shelf',
  'key_preset_activation.title': 'Activate key preset mapping',
  'key_preset_activation.description': 'Preview, confirm, and write for exactly one org, one key, and this SKU.',
  'key_preset_activation.close': 'Close activation drawer',
  'key_preset_activation.fixed_scope': 'Fixed SKU scope',
  'key_preset_activation.fixed_scope_hint': 'The SKU, preset, and bare alias come from this detail page. There is no global scope.',
  'key_preset_activation.exact_only': 'Exact scope only',
  'key_preset_activation.preset': 'Namespace preset',
  'key_preset_activation.namespace': 'Namespace',
  'key_preset_activation.preset_id': 'Internal preset ID',
  'key_preset_activation.alias': 'Bare alias',
  'key_preset_activation.org_id': 'Org ID',
  'key_preset_activation.key_id': 'API key ID',
  'key_preset_activation.key_id_hint': 'Enter the key record ID. Do not enter or paste the plaintext API key.',
  'key_preset_activation.scope_required': 'Org ID and API key ID are both required.',
  'key_preset_activation.preview': 'Preview exact change',
  'key_preset_activation.preview_result': 'Server preview',
  'key_preset_activation.preview_key': 'Verified key: {key}',
  'key_preset_activation.applied_version': 'Applied version',
  'key_preset_activation.current_version': 'Current preset version',
  'key_preset_activation.existing_mapping': 'Existing mapping',
  'key_preset_activation.unresolved_aliases': 'Unresolved preset aliases',
  'key_preset_activation.inferred_tombstone': 'This missing alias came from an incomplete legacy snapshot, not an explicit tenant deletion. A scoped apply may repair it.',
  'key_preset_activation.disposition_label.ready': 'Ready to add',
  'key_preset_activation.disposition_label.ready_to_subscribe': 'Ready to subscribe',
  'key_preset_activation.disposition_label.already_managed': 'Already managed',
  'key_preset_activation.disposition_label.not_subscribed': 'Different preset',
  'key_preset_activation.disposition_label.not_callable': 'Not callable',
  'key_preset_activation.disposition_label.tombstoned': 'Tenant excluded',
  'key_preset_activation.disposition_label.user_override': 'Tenant override',
  'key_preset_activation.disposition_label.managed_conflict': 'Managed conflict',
  'key_preset_activation.disposition.ready': 'A managed mapping can be added. Apply writes only this alias and leaves every other mapping unchanged.',
  'key_preset_activation.disposition.ready_to_subscribe': 'This channel-less key can explicitly adopt this namespace preset. Apply creates the exact key-to-preset subscription and processes only this alias; it does not fan out to other keys.',
  'key_preset_activation.disposition.already_managed': 'This target is already managed by the preset. Apply records an idempotent operation and advances the reconciled version.',
  'key_preset_activation.disposition.not_subscribed': 'This key is subscribed to a different preset. Apply is unavailable.',
  'key_preset_activation.disposition.not_callable': 'This key cannot make a real call to the SKU. Check pricing, visibility, member scope, and routing first.',
  'key_preset_activation.disposition.tombstoned': 'The tenant explicitly removed this alias. The exclusion is preserved and apply is unavailable.',
  'key_preset_activation.disposition.user_override': 'The alias is tenant-customized and will not be overwritten.',
  'key_preset_activation.disposition.managed_conflict': 'The alias is occupied by another managed state and will not be overwritten.',
  'key_preset_activation.confirm_scope': 'I confirm only alias {alias} on key {key} will be processed, preserving all custom mappings and exclusions.',
  'key_preset_activation.confirm_scope_subscribe': 'I confirm key {key} will subscribe to this namespace preset and only alias {alias} will be processed. No other key or alias is changed.',
  'key_preset_activation.apply': 'Apply this mapping',
  'key_preset_activation.applied': 'Key preset mapping applied',
  'key_preset_activation.version_conflict': 'The preset version changed. Nothing was written. Preview again before confirming.',
  'key_preset_activation.repreview_required': 'The previous preview is stale. Run the server preview again and review the new version and disposition.',
  'key_preset_activation.receipt': 'Operation receipt',
  'key_preset_activation.idempotent_replay': 'The server recognized an idempotent replay of the same operation.',
  'key_preset_activation.recorded': 'The operation was recorded for the exact scope.',
  'key_preset_activation.cache_convergence': 'Key cache convergence',
  'key_preset_activation.cache_immediate': 'Immediate',
  'key_preset_activation.cache_degraded': 'Degraded',
  'key_preset_activation.cache_effective_after': 'Effective after',
  'key_preset_activation.cache_revision': 'Cache revision',
  'key_preset_activation.operation_id': 'Operation ID',
  'key_preset_activation.result': 'Result',
  'key_preset_activation.result_label.applied': 'Managed mapping added',
  'key_preset_activation.result_label.already_managed': 'Existing managed mapping confirmed',
  'key_preset_activation.result_label.subscribed_applied': 'Preset subscribed and managed mapping added',
  'key_preset_activation.result_label.subscribed_existing': 'Preset subscribed; existing mapping preserved',
  'key_preset_activation.status_label.applied': 'Applied',
  'key_preset_activation.status_label.rolled_back': 'Rolled back',
  'key_preset_activation.provenance_label.user': 'Tenant managed',
  'key_preset_activation.provenance_label.managed_preset': 'Preset managed',
  'key_preset_activation.preset_version': 'Preset version',
  'key_preset_activation.created_at': 'Applied at',
  'key_preset_activation.rolled_back_at': 'Rolled back at',
  'key_preset_activation.rollback_hint': 'Rollback removes only the managed row created by this operation. If the tenant changed it later, the server refuses rollback and preserves the current value.',
  'key_preset_activation.rollback_hint_already_managed': 'Rollback closes this receipt without deleting the pre-existing managed mapping.',
  'key_preset_activation.rollback_hint_subscribed_applied': 'Rollback removes the managed mapping and the subscription created by this operation only while both remain unchanged and unused by later activations.',
  'key_preset_activation.rollback_hint_subscribed_existing': 'Rollback removes only the subscription created by this operation. The pre-existing tenant mapping is preserved.',
  'key_preset_activation.rollback': 'Rollback this operation',
  'key_preset_activation.rollback_confirm_title': 'Rollback this operation?',
  'key_preset_activation.rollback_confirm_applied': 'This only removes the managed mapping created by this operation while it remains unchanged. If the tenant edited the alias later, the server returns a conflict and preserves the current value.',
  'key_preset_activation.rollback_confirm_already_managed': 'This operation only confirmed an already-managed mapping. Rollback removes the operation marker and does not delete the existing mapping.',
  'key_preset_activation.rollback_confirm_subscribed_applied': 'This removes the managed mapping and adopted preset subscription only if they are still owned by this operation and no later activation depends on them. Otherwise the server preserves current state and returns a conflict.',
  'key_preset_activation.rollback_confirm_subscribed_existing': 'This removes only the preset subscription created by this operation. The existing tenant-managed mapping stays unchanged. A later activation causes a conflict instead of destructive rollback.',
  'key_preset_activation.rollback_confirm': 'Rollback this operation',
  'key_preset_activation.rollback_cancel': 'Keep operation',
  'key_preset_activation.rolled_back': 'Operation rolled back',
  'key_preset_activation.rollback_replayed': 'This operation was already rolled back; state is unchanged',
  'key_preset_activation.non_effects': 'This does not change route assignment, SKU targets, pricing, or org visibility, and it does not update any other key.',

  // ── Model Detail — Namespace Pricing (#134, re-keyed #910) ─────
  'model_detail.channel_pricing.title': 'Carrying namespaces & pricing',
  'model_detail.channel_pricing.empty': 'No namespaces for this vendor yet',
  'model_detail.channel_pricing.col_channel': 'Namespace',
  'model_detail.channel_pricing.col_multiplier': 'Multiplier',
  'model_detail.channel_pricing.col_input_price': 'Input $/M',
  'model_detail.channel_pricing.col_output_price': 'Output $/M',
  'model_detail.channel_pricing.col_supplier': 'Supplier',

  // ── Vendors (#143) ────────────────────────────────────────────
  'shell.nav.vendors': 'Vendors',
  'vendors.title': 'Vendor Management',
  'vendors.subtitle': 'Manage model vendors, official discovery configuration, and key readiness',
  'vendors.create_btn': 'New Vendor',
  'vendors.search.placeholder': 'Search vendors…',
  'vendors.search.aria_label': 'Search vendors',
  'vendors.loading': 'Loading vendors',
  'vendors.error.title': 'Vendor list unavailable',
  'vendors.error.hint': 'The list could not be loaded. Check the service connection and try again.',
  'vendors.error.retry': 'Retry',
  'vendors.error.retrying': 'Retrying…',
  'vendors.empty.title': 'No vendors yet',
  'vendors.empty.hint': 'Register the first model vendor to configure official discovery and credentials.',
  'vendors.empty.action': 'New vendor',
  'vendors.search_empty.title': 'No matching vendors',
  'vendors.search_empty.hint': 'No vendor matches “{query}”. Clear the search to see the full list.',
  'vendors.search_empty.clear': 'Clear search',
  'vendors.table.scroll_hint': 'Scroll horizontally to reach every vendor action.',
  'vendors.col.slug': 'Slug',
  'vendors.col.display_name': 'Vendor Name',
  'vendors.col.discovery_config': 'Discovery configuration',
  'vendors.col.key_readiness': 'Official key',
  'vendors.col.created_at': 'Created',
  'vendors.col.actions': 'Actions',
  'vendors.row.manage': 'Manage',
  'vendors.row.discover': 'Discover',
  'vendors.row.discovering': 'Discovering…',
  'vendors.row.delete': 'Delete vendor',
  'vendors.delete_modal.title': 'Delete vendor?',
  'vendors.delete_modal.body':
    '{name} can be permanently deleted only after its channels, canonical models, SKUs, and protocol/catalog dependencies have been removed. Discovery credentials and snapshots for this vendor will then be deleted. This cannot be undone.',
  'vendors.delete_modal.dependencies_error':
    'Remove this vendor’s channels, canonical models, SKUs, and protocol/catalog dependencies before deleting it.',
  'vendors.delete_modal.busy_error':
    'This vendor is being discovered or updated. Wait for that operation to finish, then retry deletion.',
  'vendors.create_modal.title': 'New Vendor',
  'vendors.create_modal.description': 'Register the vendor first, then add its official model discovery endpoint when needed.',
  'vendors.create_modal.slug': 'Vendor slug',
  'vendors.create_modal.slug_placeholder': 'e.g. anthropic',
  'vendors.create_modal.display_name': 'Display Name',
  'vendors.create_modal.display_name_placeholder': 'e.g. Anthropic',
  'vendors.create_modal.discovery_adapter': 'Discovery protocol',
  'vendors.create_modal.discovery_adapter_hint': 'Choose carefully: this protocol is fixed after creation. Leave it unconfigured only when this vendor will not use automatic discovery.',
  'vendors.create_modal.model_discovery_url': 'Model discovery URL',
  'vendors.create_modal.model_discovery_url_placeholder': 'e.g. https://api.example.com/v1/models',
  'vendors.create_modal.model_discovery_url_hint': 'Use an exact HTTPS origin approved by deployment policy; leave empty to keep it unconfigured.',
  'vendors.create_modal.cancel': 'Cancel',
  'vendors.create_modal.submit': 'Create',
  'vendors.create_modal.creating': 'Creating…',
  'vendors.create_modal.config_error': 'Vendor was not created. Review the values and try again.',
  'vendors.manage_modal.title': 'Manage {name}',
  'vendors.manage_modal.description': 'Maintain the approved discovery URL and rotate its write-only official key. The discovery protocol is fixed.',
  'vendors.manage_modal.slug': 'Vendor slug',
  'vendors.manage_modal.slug_hint': 'The slug is immutable after creation.',
  'vendors.manage_modal.display_name': 'Display name',
  'vendors.manage_modal.discovery_adapter': 'Discovery protocol',
  'vendors.manage_modal.discovery_adapter_hint': 'Fixed at vendor creation to prevent credentials and model records from crossing protocol boundaries.',
  'vendors.manage_modal.model_discovery_url': 'Model discovery URL',
  'vendors.manage_modal.model_discovery_url_hint': 'Use an exact HTTPS origin approved by deployment policy. Changing or clearing this URL invalidates the current key; rotate it before discovery.',
  'vendors.manage_modal.config_section': 'Vendor and discovery configuration',
  'vendors.manage_modal.config_section_hint': 'Save identity and endpoint changes independently from credential rotation.',
  'vendors.manage_modal.config_submit': 'Save configuration',
  'vendors.manage_modal.config_saving': 'Saving configuration…',
  'vendors.manage_modal.config_error': 'Configuration was not saved. Review the values and try again.',
  'vendors.manage_modal.url_change_warning': 'Saving this URL change invalidates the current official key. Rotate the key after the configuration is saved.',
  'vendors.manage_modal.key_section': 'Official credential',
  'vendors.manage_modal.key_section_hint': 'Rotate the write-only credential as a separate operation after configuration is saved.',
  'vendors.manage_modal.key_status': 'Official key status',
  'vendors.manage_modal.official_key': 'Rotate official API key',
  'vendors.manage_modal.official_key_hint': 'Write-only. Paste a new key only when you intend to rotate the credential.',
  'vendors.manage_modal.official_key_placeholder': 'Enter a new official API key',
  'vendors.manage_modal.key_submit': 'Rotate official key',
  'vendors.manage_modal.key_rotating': 'Rotating key…',
  'vendors.manage_modal.key_error': 'The official key was not rotated. The current credential remains unchanged; try again.',
  'vendors.manage_modal.unsaved_config': 'Save or discard configuration changes before rotating the official key.',
  'vendors.manage_modal.manual_section': 'Manual model registration',
  'vendors.manage_modal.manual_section_hint': 'Register a model the official sweep cannot reach (#1882). The model enters the discovery table as a delisted candidate; create it from the Models page afterwards.',
  'vendors.manage_modal.manual_model': 'Model id',
  'vendors.manage_modal.manual_model_placeholder': 'e.g. doubao-seedance-2-0-260128',
  'vendors.manage_modal.manual_display_name': 'Display name (optional)',
  'vendors.manage_modal.manual_reason': 'Reason',
  'vendors.manage_modal.manual_reason_hint': 'Recorded with the registration: why the official sweep cannot provide this model.',
  'vendors.manage_modal.manual_registering': 'Registering…',
  'vendors.manage_modal.manual_submit': 'Register model',
  'vendors.manage_modal.manual_error': 'The model was not registered. Review the values and try again.',
  'vendors.manage_modal.cancel': 'Close',
  'vendors.manage_modal.submit': 'Save changes',
  'vendors.manage_modal.saving': 'Saving…',
  'vendors.adapter.none': 'No protocol configured',
  'vendors.adapter.anthropic': 'Anthropic protocol',
  'vendors.adapter.openai': 'OpenAI protocol',
  'vendors.adapter.gemini': 'Gemini protocol',
  'vendors.discovery.url_not_configured': 'No discovery URL configured',
  'vendors.discovery.blocker.adapter': 'Configure a discovery protocol first',
  'vendors.discovery.blocker.url': 'Configure a model discovery URL first',
  'vendors.discovery.blocker.key_loading': 'Checking official key status',
  'vendors.discovery.blocker.key_error': 'Official key status is unavailable',
  'vendors.discovery.blocker.key_missing': 'Configure an official API key first',
  'vendors.discovery.blocker.in_progress': 'Official model discovery is running',
  'vendors.key.status.loading': 'Checking key',
  'vendors.key.status.error': 'Key status unavailable',
  'vendors.key.status.missing': 'Key missing',
  'vendors.key.status.configured': 'Key configured',
  'vendors.toast.created': 'Vendor created',
  'vendors.toast.model_registered': 'Model {model} registered as a discovery candidate',
  'vendors.toast.deleted': 'Vendor deleted',
  'vendors.toast.duplicate': 'A vendor with this slug already exists',
  'vendors.toast.updated': 'Vendor discovery configuration saved',
  'vendors.toast.updated_and_key_rotated': 'Vendor discovery configuration and official key saved',
  'vendors.toast.key_rotated': 'Official API key rotated',
  'vendors.toast.discovered': 'Official model discovery complete: {seen} seen, {missing} missing',
  'vendors.validation.required': 'This field is required.',
  'vendors.validation.slug': 'Use 1–63 lowercase letters, numbers, or dashes, starting with a letter.',
  'vendors.validation.url': 'Enter a public HTTPS URL on the default port, without credentials, query parameters, or a fragment.',
  'vendors.validation.url_absolute': 'Enter an absolute HTTPS discovery URL, or clear the field.',
  'vendors.validation.url_https': 'Use HTTPS for the discovery URL.',
  'vendors.validation.url_port': 'Use the default HTTPS port; remove the custom port.',
  'vendors.validation.url_credentials': 'Remove the username and password from the discovery URL.',
  'vendors.validation.url_fragment': 'Remove the URL fragment after #.',
  'vendors.validation.url_query': 'Remove all query parameters from the discovery URL. Configure the official key separately; pagination is added by the server.',
  'vendors.validation.url_public': 'Use a public hostname or public IP address; local and reserved addresses are blocked.',
  'vendors.validation.url_origin': 'This origin is not approved for model discovery in this environment. Use an approved origin or ask operations to add it.',
  'vendors.validation.url_length': 'Keep the discovery URL at 2,048 characters or fewer.',
  'vendors.validation.discovery_pair': 'Configure both the discovery protocol and URL, or leave both unconfigured.',
  'vendors.discovery.error.credentials': 'The vendor rejected this key. Rotate the official API key, then try discovery again.',
  'vendors.discovery.error.rate_limited': 'The vendor rate-limited discovery. Wait briefly, then try again.',
  'vendors.discovery.error.key_binding': 'The official key is missing or belongs to an older discovery URL. Rotate the key, then try again.',
  'vendors.discovery.error.configuration': 'The discovery response or configuration is invalid. Verify the approved URL and protocol before retrying.',
  'vendors.discovery.error.unavailable': 'Discovery is temporarily unavailable. Verify the vendor endpoint and try again.',

  // ── Upstream Accounts (upstream detail accounts tab) ────────────
  'upstream_accounts.title': 'Supply workbench',
  'upstream_accounts.subtitle': 'Manage supplier accounts, API keys, upstream channel instances, and ingress / egress policy templates in one view.',
  'upstream_accounts.action.channel_catalog': 'Channel catalog',
  'upstream_accounts.action.template_library': 'Template library',
  'upstream_accounts.metric.accounts': 'Accounts',
  'upstream_accounts.metric.enabled_accounts': '{count} enabled',
  'upstream_accounts.metric.key_buckets': 'Keys',
  'upstream_accounts.metric.enabled_keys': '{count} enabled',
  'upstream_accounts.metric.enabled_keys_short': 'Enabled keys',
  'upstream_accounts.metric.disabled_keys': 'Disabled keys',
  'upstream_accounts.metric.disabled_key_detail': 'Not routable',
  'upstream_accounts.metric.workbench': 'Surface',
  'upstream_accounts.metric.workbench_value': 'Merged',
  'upstream_accounts.metric.workbench_detail': 'Runtime objects stay split',
  'upstream_accounts.metric.rpm': 'RPM',
  'upstream_accounts.metric.discount': 'Discount',
  'upstream_accounts.add_btn': 'New Account',
  'upstream_accounts.empty': 'No accounts',
  'upstream_accounts.count': '{count} account(s)',
  'upstream_accounts.add_modal.title': 'New Supplier Account',
  'upstream_accounts.add_modal.label': 'Account Label',
  'upstream_accounts.add_modal.label.placeholder': 'e.g. hellsoul86@gmail.com',
  'upstream_accounts.add_modal.rpm': 'RPM Limit',
  'upstream_accounts.add_modal.rate_multiplier': 'Account Discount Multiplier',
  'upstream_accounts.add_modal.submit': 'Create',
  'upstream_accounts.add_modal.creating': 'Creating…',
  'upstream_accounts.add_modal.cancel': 'Cancel',
  'upstream_accounts.add_modal.error.label_required': 'Account label is required',
  'upstream_accounts.toast.created': 'Account created',
  'upstream_accounts.toast.deleted': 'Account deleted',
  'upstream_accounts.section.keys': 'API keys & channel egress',
  'upstream_accounts.channels.none': 'No channel binding (matches all)',
  'upstream_accounts.keys.none': 'No keys for this account',
  'upstream_accounts.keys.count': '{count} key(s)',
  'upstream_accounts.keys.add_btn': 'Add Key',
  'upstream_accounts.keys.add_modal.title': 'Add API Key',
  'upstream_accounts.keys.add_modal.auth_secret_ref': 'Secret Key',
  'upstream_accounts.keys.add_modal.auth_secret_ref.placeholder': 'sk-...',
  'upstream_accounts.keys.add_modal.label': 'Label (optional)',
  'upstream_accounts.keys.add_modal.rpm': 'RPM Limit',
  'upstream_accounts.keys.add_modal.submit': 'Create',
  'upstream_accounts.keys.add_modal.creating': 'Creating…',
  'upstream_accounts.keys.add_modal.channels': 'Upstream channel instances',
  'upstream_accounts.keys.add_modal.channels_hint': 'Create the key first, then add upstream-local channel instances from Channels & egress.',
  'upstream_accounts.keys.add_modal.error.secret_required': 'Secret key is required',
  'upstream_accounts.keys.toast.created': 'Key created',
  'upstream_accounts.keys.toast.channels_failed': 'Channel binding failed, please set manually',
  'upstream_accounts.keys.toast.deleted': 'Key deleted',
  'upstream_accounts.keys.toast.updated': 'Key updated',
  'upstream_accounts.keys.toast.channels_updated': 'Channel bindings updated',
  'upstream_accounts.keys.show_secret': 'Show full secret',
  'upstream_accounts.keys.hide_secret': 'Hide full secret',
  'upstream_accounts.keys.edit_secret': 'Edit secret',
  'upstream_accounts.keys.write_only': 'write-only',
  'upstream_accounts.key_table.key': 'Key',
  'upstream_accounts.key_table.rpm': 'RPM',
  'upstream_accounts.key_table.channels': 'Channels & egress',
  'upstream_accounts.key_table.actions': 'Actions',
  'upstream_accounts.bindings.loading': 'Loading bindings',
  'upstream_accounts.bindings.override': 'override',
  'upstream_accounts.keys.edit_modal.title': 'Edit API Key',
  'upstream_accounts.keys.edit_modal.enabled': 'Key enabled',
  'upstream_accounts.keys.edit_modal.error.no_changes': 'No changes to save',
  'upstream_accounts.keys.edit_modal.error.rpm_invalid': 'RPM limit must be a positive integer',
  'upstream_accounts.keys.edit_channels': 'Edit channel bindings',
  'upstream_accounts.keys.configure_channels_egress': 'Channels & egress',
  'upstream_accounts.egress.config_body': 'Choose the strategy saved for this key-channel binding: ingress admission, admission strength, egress handling, backend interface, and protocol-specific advanced fields.',
  'upstream_accounts.egress.manage_templates': 'Manage channel policies',
  'upstream_accounts.egress.default': 'Protocol default direct',
  'upstream_accounts.egress.label': 'Policy template',
  'upstream_accounts.egress.effective': 'Effective egress',
  'upstream_accounts.egress.typed_config': 'Template egress config',
  'upstream_accounts.egress.typed_config_empty': 'The default channel egress applies; the selected template has no typed egress config.',
  'upstream_accounts.egress.binding_override_title': 'Binding override (read-only here)',
  'upstream_accounts.egress.binding_override_body': 'This binding carries a typed egress config override that replaces the profile config at runtime. This sheet edits the blacklist rules only; every other field in an override is read-only and is carried through a save unchanged.',
  'upstream_accounts.egress.binding_override_clear': 'Clear this override',
  'upstream_accounts.egress.binding_override_clear_hint': 'Keeps the profile bound. One save.',
  'upstream_accounts.egress.binding_override_clear_title': 'Clear this binding’s override',
  'upstream_accounts.egress.binding_override_clear_body': 'The override stored on {channel} stops applying and the binding follows its profile again.',
  'upstream_accounts.egress.binding_override_clear_detail': 'The profile stays bound, so this takes one save and the binding is never stored without a profile. There is no undo — the stored document is gone once saved, so copy it from the panel first if you may need it back.',
  'upstream_accounts.egress.binding_override_clear_confirm': 'Clear it and save',
  'upstream_accounts.egress.binding_blacklist_scope':
    'These rules apply to this channel binding ({channel}) alone, not to other bindings using the same profile — which is the reason to arm them here rather than on the profile.',
  'upstream_accounts.egress.binding_blacklist_snapshot':
    'The override saved from these boxes is a snapshot of the profile document: the write face takes full documents only, so a later profile edit does not reach this binding. When the two stop matching, this section turns read-only and says so.',
  'upstream_accounts.egress.binding_blacklist_diverged_title': 'This override no longer matches the profile (read-only here)',
  'upstream_accounts.egress.binding_blacklist_diverged_body':
    'Two possibilities, and this sheet cannot tell them apart: the profile changed after the override was saved, or the override edits more than the blacklist. So no edit path is offered — editing would overwrite whichever difference it is. To replace it with one composed from the current profile, clear the override below and re-arm the rules, or rewrite it through the API. Rules currently armed:',
  'upstream_accounts.egress.binding_blacklist_none_armed': '(none)',
  'upstream_accounts.egress.binding_blacklist_profile_mode':
    "This profile's admission mode does not accept a binding-level blacklist. Composing one sets the document's admission mode to require-cc-black, and that rewrite refuses one more class without changing anything else only when the profile is allow-any-supported (or already require-cc-black): require-cc would lose its whitelist, allow-anthropic would widen, direct would arm a gate the profile opted out of, and a profile with no admission block would gain a whole admission document it never wrote.",
  'upstream_accounts.egress.binding_blacklist_compose_failed':
    'Could not compose an override document from the current profile; the override is unchanged.',
  'upstream_accounts.egress.binding_blacklist_override_fragment':
    'The stored override on this binding is a partial fragment: it merges over the profile at runtime and is in force, but this write face takes full documents only. Every edit here replaces the whole override document, so no rule checkboxes are offered — ticking one would wipe a policy that is live. To arm a rule on this binding, rewrite the override as a full document through the API first.',
  'upstream_accounts.egress.binding_firewall_scope':
    'The ruleset picked here applies to this channel binding ({channel}) alone. A profile backs many bindings, and a ruleset that is right for one SKU namespace can refuse another namespace\'s correct traffic — arming it here is how one namespace gets a firewall without every other one getting it too.',
  'upstream_accounts.egress.binding_firewall_snapshot':
    'Picking a ruleset here saves an override that is a snapshot of the profile document: the write face takes full documents only, so a later profile edit does not reach this binding. Only the ruleset REFERENCE is copied, not its rules — activating a new version on the ruleset still reaches this binding, unless a version is pinned above.',
  'upstream_accounts.egress.binding_firewall_diverged_title': 'This override no longer matches the profile (read-only here)',
  'upstream_accounts.egress.binding_firewall_diverged_body':
    'Two possibilities, and this sheet cannot tell them apart: the profile changed after the override was saved, or the override edits fields this sheet does not compose. So no edit path is offered — editing would overwrite whichever difference it is. To replace it with one composed from the current profile, clear the override below and pick the ruleset again, or rewrite it through the API. Reference currently stored:',
  'upstream_accounts.egress.binding_firewall_none': '(no ruleset referenced — the firewall layer is skipped)',
  'upstream_accounts.egress.binding_firewall_override_fragment':
    'The stored override on this binding is a partial fragment: it merges over the profile at runtime and is in force, but this write face takes full documents only. Every edit here replaces the whole override document, so no ruleset selector is offered — picking one would wipe a policy that is live. To arm a firewall on this binding, rewrite the override as a full document through the API first.',
  'upstream_accounts.egress.binding_firewall_compose_failed':
    'Could not compose an override document from the current profile; the firewall reference is unchanged.',
  'upstream_accounts.egress.binding_override_save_warning': 'Saving clears the binding override on {channels}.',
  'upstream_accounts.egress.binding_override_unsupported': 'The stored binding override on {channels} is a partial fragment. It applies at runtime but this write face takes full documents only, so saving clears it.',
  'upstream_accounts.egress.binding_override_unsupported_title': 'This save clears a live binding override',
  'upstream_accounts.egress.binding_override_unsupported_detail': 'The stored value takes effect at runtime by merging over the profile document, but it cannot be written back through this surface, so any save of this key bucket removes it. Convert it to a full override document through the API to keep it.',
  'upstream_accounts.egress.binding_override_unsupported_confirm': 'Clear it and save',
  'upstream_accounts.egress.unassigned': 'Protocol default direct',
  'upstream_accounts.egress.sheet_title': 'Channels & egress · {key}',
  'upstream_accounts.egress.bound_channels': 'Bound channels',
  'upstream_accounts.egress.no_bound_channels': 'No bound channels. Add one channel instance first.',
  'upstream_accounts.egress.selected_channel': 'Selected channel',
  'upstream_accounts.egress.add_channel': 'Add channel',
  'upstream_accounts.egress.remove_channel': 'Unbind channel',
  'upstream_accounts.egress.unsaved_changes': 'Unsaved changes',
  'upstream_accounts.egress.add_modal_title': 'Add channel binding',
  'upstream_accounts.egress.add_modal_channel_id': 'Upstream channel ID',
  'upstream_accounts.egress.add_modal_channel_hint': 'Choose from this upstream’s shared channel catalog. Maintain catalog rows in the Channels tab.',
  'upstream_accounts.egress.no_available_channels': 'No unbound upstream channels',
  'upstream_accounts.egress.add_modal_profile': 'Initial ingress / egress template',
  'upstream_accounts.egress.add_modal_submit': 'Add binding',
  'upstream_accounts.egress.cascade.title': 'This save cascades over routing objects',
  'upstream_accounts.egress.cascade.body': 'Unbinding the channels below deletes routing objects that still reference them and may interrupt live traffic. Review the impact before confirming.',
  'upstream_accounts.egress.cascade.confirm': 'Delete and save',
  'upstream_accounts.egress.cascade.sku_targets': 'SKU routing targets to delete ({count})',
  'upstream_accounts.egress.cascade.sku_target_item': '{scope} · {sku} · {model} · capability {capability}',
  'upstream_accounts.egress.cascade.capabilities': 'Capabilities marked mismatch ({count})',
  'upstream_accounts.egress.cascade.capability_item': 'Capability for channel {channel} model {model}',
  'upstream_accounts.chip.enabled': 'enabled',
  'upstream_accounts.chip.disabled': 'disabled',
  'upstream_accounts.chip.status_label': 'Status',
  'upstream_accounts.rate_multiplier_short': 'Discount',

  'upstream_detail.channels.subtitle':
    'Shared supply-side channels for this upstream. Accounts and API keys attach to these channels; platform channels remain separate.',
  'upstream_detail.channels.create': 'Add channel',
  'upstream_detail.channels.empty': 'No upstream channels yet. Create one here, then attach account keys to it.',
  'upstream_detail.channels.col.channel': 'Channel',
  'upstream_detail.channels.col.label': 'Label',
  'upstream_detail.channels.col.egress_binding': 'Egress binding',
  'upstream_detail.channels.col.endpoint': 'Endpoint',
  'upstream_detail.channels.col.status': 'Status',
  'upstream_detail.channels.col.actions': 'Actions',
  'upstream_detail.channels.label': 'Channel label',
  'upstream_detail.channels.enabled': 'Enabled',
  'upstream_detail.channels.disabled': 'Disabled',
  'upstream_detail.channels.binding.unmounted': 'Not mounted',
  'upstream_detail.channels.binding.unresolved': 'Binding present, config unreadable',
  'upstream_detail.channels.binding.overridden': 'override',
  'upstream_detail.channels.enable': 'Enable',
  'upstream_detail.channels.disable': 'Disable',
  'upstream_detail.channels.delete': 'Delete channel',
  'upstream_detail.channels.modal.title': 'Add upstream channel',
  'upstream_detail.channels.modal.channel_id': 'Upstream channel ID',
  'upstream_detail.channels.modal.channel_slug': 'Upstream channel label',
  'upstream_detail.channels.modal.hint': 'This is a supply-side channel under the upstream, not a platform sales channel.',
  'upstream_detail.channels.endpoint.label': 'Channel endpoint',
  'upstream_detail.channels.endpoint.inherited': 'Inherited',
  'upstream_detail.channels.endpoint.override': 'Override endpoint',
  'upstream_detail.channels.endpoint.reset': 'Inherit again',
  'upstream_detail.channels.endpoint.hint': 'Only the path may differ — the host must stay the upstream\'s own. Model discovery keeps using the upstream endpoint.',
  'upstream_detail.channels.modal.endpoint': 'Channel endpoint (optional)',
  'upstream_detail.channels.modal.submit': 'Create channel',
  'upstream_detail.channels.toast.created': 'Upstream channel created',
  'upstream_detail.channels.toast.updated': 'Upstream channel updated',
  'upstream_detail.channels.toast.deleted': 'Upstream channel deleted',

  // ── Egress Profiles ───────────────────────────────────────────
  'egress_profiles.title': 'Channel policy templates',
  'egress_profiles.subtitle': 'Global templates define reusable protocol families, ingress admission, admission strength, egress handling, and backend interfaces.',
  'egress_profiles.create_btn': 'New template',
  'egress_profiles.empty': 'No channel policy templates.',
  'egress_profiles.col.name': 'Template',
  'egress_profiles.col.type': 'Lane',
  'egress_profiles.col.usage': 'In use',
  'egress_profiles.usage.none': 'Not in use',
  'egress_profiles.usage.kcb_count': '{count} channel binding(s)',
  'egress_profiles.usage.sku_targets': 'SKU targets {count}',
  'egress_profiles.strategy.legacy': 'legacy',
  'egress_profiles.strategy.legacy_hint': 'No typed egress config on this template yet (pre-migration straggler); open the editor to set one.',
  'egress_profiles.sidecar.editing_disabled': 'Capabilities are unavailable: template creation and config editing are disabled until the sidecar publication is reachable again.',
  'egress_editor.section.pipeline': 'Egress handling',
  'egress_editor.section.pipeline_hint': 'Which lane requests leave on, and how they are shaped for the upstream.',
  'egress_editor.section.fixups': 'Fixups',
  'egress_editor.lane': 'Lane',
  'egress_editor.lane_placeholder': 'Select a lane',
  'egress_editor.mode': 'Mode',
  'egress_editor.mode_placeholder': 'Select a mode',
  'egress_editor.mode_hint.passthrough': 'passthrough: bytes forward unchanged, no protocol rewriting.',
  'egress_editor.mode_hint.fixup': 'fixup: applies only the selected fixups; everything else forwards as-is.',
  'egress_editor.mode_hint.assemble': 'assemble: re-renders the request against the selected target shape.',
  'egress_editor.matching': 'Matching',
  'egress_editor.matching_option_default': 'best-effort (default)',
  'egress_editor.matching_hint.fixup': 'Only affects the sidecar-outage fallback: best-effort with an explicit direct admission forwards raw bytes; every other admission state — including leaving it off — fails closed, as does strict.',
  'egress_editor.matching_hint.assemble': 'strict validates the request shape before rendering and rejects violations; best-effort folds to the target shape and repairs what it can. Assemble always fails closed on sidecar outage.',
  'egress_editor.kind': 'Kind',
  'egress_editor.kind_hint': 'Derived from the lane; not directly configurable.',
  'egress_editor.target': 'Assemble target',
  'egress_editor.target_hint': 'Shape-class id from the lane catalog published by /v1/capabilities, or "latest".',
  'egress_editor.target_placeholder': 'Select a target',
  'egress_editor.target_catalog_empty': 'The lane catalog is empty; only "latest" is selectable until the sidecar publishes shape classes.',
  'egress_editor.fixup_fingerprint': 'Recompute fingerprint',
  'egress_editor.fixup_cch': 'cch fixup',
  'egress_editor.option_unset': 'Unset',
  'egress_editor.option_yes': 'Yes',
  'egress_editor.option_no': 'No',
  'egress_editor.admission_enabled': '2. Configure identity admission',
  'egress_editor.admission_hint.claude_code': 'Leaving this off is NOT admit-everything: the runtime treats absent admission as allow-any-supported, so unrecognized client shapes are still rejected. To truly admit everything, enable this and pick direct.',
  'egress_editor.admission_hint.codex': 'Codex admission is binary: anything other than direct (including leaving this section off) fails closed to Codex-CLI-shaped traffic only.',
  'egress_editor.admission_cc_pin_warning': 'With admission set to direct, this template is incompatible with capability-pinned Claude Code SKU targets (writes will be rejected). Leaving admission off is compatible — it behaves as allow-any-supported.',
  'egress_editor.admission_mode': 'Admission mode',
  'egress_editor.admission_mode_hint.require_cc': 'Only clients recognized as Claude Code are admitted.',
  'egress_editor.admission_mode_hint.require_cc_black':
    'Identical to allow-any-supported, plus a blacklist: with no rule ticked nothing changes, and each ticked rule refuses only the class it names.',
  'egress_editor.admission_mode_hint.allow_anthropic': 'Anthropic-family clients are admitted (including plain Anthropic).',
  'egress_editor.admission_mode_hint.allow_any_supported': 'Any supported client shape is admitted.',
  'egress_editor.admission_mode_hint.direct': 'No client identity gating; requests pass straight through. Minimum level / matched-partial / plain-Anthropic settings are never sent under direct, so those controls are hidden.',
  'egress_editor.admission_mode_hint.codex_armed': 'On the codex lane every non-direct mode behaves identically: the gate stays armed and only Codex-CLI-shaped traffic is admitted; everything else fails closed.',
  'egress_editor.admission_mode_hint.codex_direct': 'Disarms the codex gate: no shape recognition, all traffic passes straight through.',
  'egress_editor.require_cc_default_note': 'Under require-cc each unset dimension applies its own default: minimum level defaults to standard; matched-partial defaults to requiring fingerprint, manifest, and no-conflicts, surfaces limited to main-loop and title-gen, entrypoint classes limited to first-party-interactive. Setting one dimension overrides only that dimension.',
  'egress_editor.blacklist': 'Blacklist rules (blacklist)',
  'egress_editor.blacklist_inert_note':
    'No rule ticked: this mode behaves exactly as allow-any-supported and refuses nothing.',
  'egress_editor.blacklist_profile_scope_warning':
    'Scope: ticking a rule here arms it on every key-channel binding under this profile. SKU namespaces routinely share one profile, and one of them may be bare-API by design and working — that traffic should be armed on its own binding instead (Upstream accounts → Channels and egress), not refused wholesale here.',
  'egress_editor.blacklist_action': 'Action on a match (action)',
  'egress_editor.blacklist_action_hint':
    'synthesize-200: a complete non-streaming Anthropic Message answered locally, settled at zero. model-unavailable: 404 MODEL_NOT_AVAILABLE. reject: 403 REQUEST_NOT_SUPPORTED.',
  'egress_editor.blacklist_rule.first_party_health_probe':
    'First-party connection self-check probe (firstPartyHealthProbe)',
  'egress_editor.blacklist_rule.bare_api_on_cc_lane': 'Bare Anthropic API traffic (bareApiOnCcLane)',
  'egress_editor.blacklist_rule.agent_sdk_on_cc_lane': 'Agent SDK traffic (agentSdkOnCcLane)',
  'egress_editor.blacklist_rule.missing_cc_beta_header': 'No anthropic-beta header (missingCcBetaHeader)',
  'egress_editor.mpi_empty_warning': 'Enabled but no conditions set: nothing will be persisted for matched-partial, and this toggle will read as off when reopened.',
  'egress_editor.admission_minimum_level': 'Minimum level',
  'egress_editor.admission_minimum_level_hint': 'Clients below this level are rejected; unset means no floor.',
  'egress_editor.model_allowlist': 'Model allowlist',
  'egress_editor.model_allowlist_hint': 'One model per line (or comma separated); empty means no allowlist.',
  'egress_editor.model_allowlist_hint.direct': 'One model per line (or comma separated). Under direct the runtime does not check this list, but it still gates compatibility with capability-pinned SKU targets.',
  'egress_editor.mpi_enabled': 'Enable matched-partial integrity policy',
  'egress_editor.mpi_hint': 'Extra integrity checks applied to clients recognized as matched-partial.',
  'egress_editor.mpi_target_level': 'Target level (targetLevel)',
  'egress_editor.mpi_require_fingerprint_match': 'Require fingerprint match (requireFingerprintMatch)',
  'egress_editor.mpi_require_manifest_match': 'Require manifest match (requireManifestMatch)',
  'egress_editor.mpi_require_no_conflicts': 'Require no conflicts (requireNoConflicts)',
  'egress_editor.mpi_allowed_surface_buckets': 'Allowed surface buckets (allowedSurfaceBuckets)',
  'egress_editor.mpi_allowed_entrypoint_classes': 'Allowed entrypoint classes (allowedEntrypointClasses)',
  'egress_editor.mpi_allowed_entrypoints': 'Allowed entrypoints (allowedEntrypoints)',
  'egress_editor.list_hint': 'One entry per line (or comma separated).',
  'egress_editor.codex_adapter': 'Codex adapter (adapter.codex)',
  'egress_editor.codex_adapter_hint': 'Egress adapter parameters for the codex lane.',
  'egress_editor.codex_backend': 'Backend',
  'egress_editor.codex_transport': 'Transport',
  'egress_editor.codex_store': 'Server-side store (store)',
  'egress_editor.codex_drop_max_output_tokens': 'Drop max_output_tokens',
  'egress_editor.codex_originator_override': 'Originator override',
  'egress_editor.codex_base_url': 'Base URL',
  'egress_editor.codex_beta_features': 'Beta features',
  'egress_editor.openai_adapter': 'OpenAI adapter (adapter.openai)',
  'egress_editor.openai_adapter_hint': 'Egress adapter parameters for the OpenAI lane. Fields without a control here are stored on the document and carried through edits untouched.',
  'egress_editor.openai_images': 'Image generation egress (images)',
  'egress_editor.openai_images_hint': 'How an image request leaves for this upstream. Rewritten through Responses: the image is generated from a rewritten prompt at a quality the upstream decides, and the size parameter has no effect. Direct to the official Images API: size and quality reach the upstream verbatim. These are two different deliverables, not two routes to the same image. Pick native only for an upstream measured to honour those parameters.',
  'egress_editor.openai_images_option_unset': 'Unset (default: rewritten through Responses)',
  'egress_editor.openai_images_option.via_responses': 'via-responses (rewritten through Responses)',
  'egress_editor.openai_images_option.native': 'native (direct to the official Images API)',
  'egress_editor.openai_prompt_cache_key': 'Inject prompt_cache_key (session id) for upstream cache affinity',
  'egress_editor.ark_adapter': 'Task adapter (adapter.arkVideo)',
  'egress_editor.ark_adapter_hint': 'How this upstream serves asynchronous generation tasks: which measured task grammar the dial speaks, and how the delivered quantity is calibrated for billing.',
  'egress_editor.ark_vendor': 'Vendor adapter (vendor)',
  'egress_editor.ark_vendor_hint': 'The upstream adapter whose measured task grammar and response quirks apply. Each option stands on a real measurement.',
  'egress_editor.ark_coefficient': 'Delivery quantity coefficient (deliveryQuantityCoefficient)',
  'egress_editor.ark_coefficient_hint': 'Settlement bills the terminal report multiplied by this factor. Leave empty for 1.0 (pass-through). Must be a positive number.',
  'egress_editor.ark_spellings': 'Parameter spellings (parameterSpellings)',
  'egress_editor.ark_spellings_hint': 'Canonical value → this upstream\'s wire spelling. Stored on the document and carried through edits; no form control yet.',
  'egress_editor.ark_spellings_empty': 'Parameter spellings: pass-through (canonical values are sent verbatim).',
  'egress_editor.grok_adapter': 'Grok video adapter (adapter.grokVideo)',
  'egress_editor.grok_adapter_hint': 'How this upstream serves Grok video: which measured Pool grammar the dial speaks, and how the delivered quantity is calibrated for billing.',
  'egress_editor.grok_vendor': 'Vendor adapter (vendor)',
  'egress_editor.grok_vendor_hint': 'The upstream adapter whose measured Pool /xai/v1/video grammar applies. Each option stands on a real measurement.',
  'egress_editor.grok_coefficient': 'Delivery quantity coefficient (deliveryQuantityCoefficient)',
  'egress_editor.grok_coefficient_hint': 'Settlement bills the terminal report multiplied by this factor. Leave empty for 1.0 (pass-through). Must be a positive number.',
  'egress_editor.grok_image_adapter': 'Grok image adapter (adapter.grokImage)',
  'egress_editor.grok_image_adapter_hint': 'How this upstream serves Grok still images: which measured Pool grammar the dial speaks. Images are a synchronous POST, not a video task.',
  'egress_editor.grok_image_vendor': 'Vendor adapter (vendor)',
  'egress_editor.grok_image_vendor_hint': 'The upstream adapter whose measured Pool /xai/v1/images/generations grammar applies. Each option stands on a real measurement.',
  'egress_editor.degraded.title': 'Capabilities unavailable — editing disabled',
  'egress_editor.degraded.body': 'The protocol sidecar capabilities publication is unreachable, so the config write path is closed. The stored document is shown read-only; no defaults are substituted.',
  'egress_editor.summary.unconfigured': 'legacy / unconfigured',
  'egress_editor.error.target_required': 'Assemble mode requires a target (a catalog shape-class id or "latest").',
  'egress_editor.section.inbound': 'Inbound layers',
  'egress_editor.section.inbound_hint': 'A request entering this lane passes 1. the firewall ruleset first, then 2. identity admission. The two are serial, not alternatives.',
  'egress_editor.firewall': '1. Firewall ruleset (pre-layer)',
  'egress_editor.firewall_hint': 'Rules are editable online: an edit produces a new draft version, activation takes effect immediately, and neither a release nor a gateway restart is involved.',
  'egress_editor.firewall_ruleset': 'Referenced ruleset',
  'egress_editor.firewall_ruleset_none': 'None (skip this layer)',
  'egress_editor.firewall_list_unavailable': 'The ruleset list could not be read; the current reference is left untouched.',
  'egress_editor.firewall_version': 'Version',
  'egress_editor.firewall_version_hint': 'Follow the active version and this config changes the moment the ruleset activates a new one. Pin a version and nothing the ruleset activates affects this config.',
  'egress_editor.firewall_version_follow': 'Follow the active version (currently v{version})',
  'egress_editor.firewall_version_follow_unarmed': 'Follow the active version (this ruleset has none activated)',
  'egress_editor.firewall_version_pinned': 'Pinned to v{version}',
  'egress_editor.firewall_skip_note': 'No ruleset referenced means this layer is genuinely skipped and the request goes straight to 2. That is not what leaving 2 unticked means.',
  'egress_editor.firewall_profile_scope_warning': 'This is a profile-level reference: it applies to every binding that uses this profile. A ruleset that is right for one namespace can refuse another namespace’s legitimate traffic.',
  'egress_editor.firewall_manage_link': 'Manage rulesets and versions →',
  'egress_editor.layers.title': 'How the two layers relate',
  'egress_editor.layers.order': 'Order: 1. the firewall, then 2. identity admission. A request the firewall allows still has to pass admission.',
  'egress_editor.layers.allow_not_exempt': 'An allow in 1 does not exempt a request from 2: it only means the firewall did not stop it. Admission still refuses what it would have refused.',
  'egress_editor.layers.only_stricter': 'Layer 1 can only refuse more. It never relaxes any judgement made in 2, and it cannot open a door 2 keeps shut.',
  'egress_editor.layers.off_semantics': 'The two "off" states differ: no ruleset in 1 means the layer is skipped, while 2 left unticked runs as allow-any-supported and still refuses unrecognized clients.',
  'firewall_editor.order_hint': 'Order is priority: rules are matched top to bottom and evaluation stops at the first match, so nothing below it is considered.',
  'firewall_editor.add_rule': 'Add rule',
  'firewall_editor.empty': 'No rules yet. At least one is required to save.',
  'firewall_editor.position': 'Rule {position}',
  'firewall_editor.position_hint': 'Moving a rule only changes the local draft; saving mints a new version and leaves the running one alone.',
  'firewall_editor.move_up': 'Move up one position',
  'firewall_editor.move_down': 'Move down one position',
  'firewall_editor.remove_rule': 'Remove this rule',
  'firewall_editor.rule_id': 'Rule ID',
  'firewall_editor.rule_action': 'Action',
  'firewall_editor.rule_action_hint': 'allow passes the request and stops evaluation; deny refuses it; synthesize has the gateway answer directly without reaching an upstream.',
  'firewall_editor.rule_description': 'Description',
  'firewall_editor.rule_description_hint': 'Required. It is excluded from the rule hash, so editing it mints no new hash — but it is the only thing that explains this rule to whoever reads an interception record months from now.',
  'firewall_editor.rule_deny_as': 'Denial shape',
  'firewall_editor.rule_deny_as_hint': 'reject refuses outright; model-unavailable refuses in the shape of an unavailable model.',
  'firewall_editor.rule_synthesize_as': 'Synthesis shape',
  'firewall_editor.rule_synthesize_as_hint': 'synthesize-200 has the gateway return a synthesized 200 response.',
  'firewall_editor.rule_when': 'Match condition (when)',
  'firewall_editor.rule_when_hint': 'A JSON document in the expression DSL. It is parsed on save, not on every keystroke.',
  'firewall_rulesets.title': 'Firewall rulesets',
  'firewall_rulesets.subtitle': 'Admission firewall rulesets, their versions, and what is armed.',
  'firewall_rulesets.create_btn': 'New ruleset',
  'firewall_rulesets.empty': 'No rulesets yet.',
  'firewall_rulesets.explain.title': 'What this page manages',
  'firewall_rulesets.explain.online': 'This is an online-editable pre-layer: write a version, activate it, done. No release, no code change, no gateway restart.',
  'firewall_rulesets.explain.frozen': 'A version is frozen once written: editing produces a new draft, and only the one active_version points at is read by a live request. Rolling back is activating an older version.',
  'firewall_rulesets.explain.order': 'Rule order is priority: matched top to bottom, stopping at the first hit. Reordering is likewise only possible by submitting a new version.',
  'firewall_rulesets.col.name': 'Ruleset',
  'firewall_rulesets.col.scope': 'Scope',
  'firewall_rulesets.col.active': 'Live',
  'firewall_rulesets.col.latest': 'Latest version',
  'firewall_rulesets.col.updated': 'Updated',
  'firewall_rulesets.col.actions': 'Actions',
  'firewall_rulesets.scope.shared': 'Shared',
  'firewall_rulesets.scope.binding': 'Binding-owned',
  'firewall_rulesets.scope_hint.shared': 'Shared: any profile or binding may reference it, and a change reaches every referrer.',
  'firewall_rulesets.scope_hint.binding': 'Binding-owned: it belongs to one key bucket x channel, which bounds what a change can reach.',
  'firewall_rulesets.status.unarmed': 'No version armed',
  'firewall_rulesets.status.active': 'Live on v{version}',
  'firewall_rulesets.status.draft_pending': 'Unactivated draft',
  'firewall_rulesets.action.delete': 'Delete ruleset',
  'firewall_rulesets.field.name': 'Name',
  'firewall_rulesets.field.scope': 'Scope',
  'firewall_rulesets.field.owner_key_bucket': 'Owning key bucket ID',
  'firewall_rulesets.field.owner_channel': 'Owning channel ID',
  'firewall_rulesets.field.owner_hint': 'Required for a binding-owned ruleset; it is what bounds the blast radius.',
  'firewall_rulesets.field.extends': 'Extends (ruleset ID)',
  'firewall_rulesets.field.extends_hint': 'Optional. The parent ruleset’s rules are evaluated ahead of this one’s.',
  'firewall_rulesets.field.note': 'Note',
  'firewall_rulesets.field.note_hint': 'Written for the next person: what this version changed, and why.',
  'firewall_rulesets.create.title': 'New ruleset',
  'firewall_rulesets.create.hint': 'What this creates is a first draft version. It affects no live request until you activate it on the detail page.',
  'firewall_rulesets.create.submit': 'Create as draft',
  'firewall_rulesets.issues.title': 'This write was refused. {count} issue(s):',
  'firewall_rulesets.version.col.version': 'Version',
  'firewall_rulesets.version.col.state': 'State',
  'firewall_rulesets.version.col.note': 'Note',
  'firewall_rulesets.version.col.hash': 'Resolved hash',
  'firewall_rulesets.version.col.author': 'Author / time',
  'firewall_rulesets.version.col.actions': 'Actions',
  'firewall_rulesets.version.empty': 'No versions yet.',
  'firewall_rulesets.version.author_unknown': 'not recorded',
  'firewall_rulesets.version.state.active': 'Live',
  'firewall_rulesets.version.state.draft': 'draft',
  'firewall_rulesets.version.action.inspect': 'Inspect this version',
  'firewall_rulesets.version.action.rearm': 'Re-arm',
  'firewall_rulesets.version.action.activate': 'Activate',
  'firewall_rulesets.editor.unreadable': 'This version’s rule document cannot be round-tripped by this editor, so editing is not offered. It is still the live policy — handle it elsewhere rather than letting the editor overwrite it with a guess.',
  'firewall_rulesets.editor.draft_note': 'Editing on top of v{version}. Saving mints a new draft version and does not change what is currently live.',
  'firewall_rulesets.editor.save': 'Save as a new draft version',
  'firewall_rulesets.detail.back': 'Back to rulesets',
  'firewall_rulesets.detail.disarm': 'Disarm (activate nothing)',
  'firewall_rulesets.detail.versions': 'Version history',
  'firewall_rulesets.detail.diff': 'Difference from what is live (v{from} → v{to})',
  'firewall_rulesets.detail.editor': 'Edit the rule chain',
  'firewall_rulesets.detail.unarmed_hint': 'Nothing is armed, so a config referencing this ruleset is treated as skipping the layer.',
  'firewall_rulesets.detail.armed_hint': 'Configs referencing this ruleset and following the active version are evaluating requests against this version.',
  'firewall_rulesets.diff.unreadable': 'One side is not a readable rule array, so a rule-by-rule comparison is not possible.',
  'firewall_rulesets.diff.identical': 'No difference from what is live.',
  'firewall_rulesets.diff.no_active': 'Nothing is live to compare against: activating is this rule chain’s first time in production.',
  'firewall_rulesets.diff.kind.added': 'Added',
  'firewall_rulesets.diff.kind.removed': 'Removed',
  'firewall_rulesets.diff.kind.changed': 'Changed',
  'firewall_rulesets.diff.kind.moved': 'Moved',
  'firewall_rulesets.diff.kind.unchanged': 'Unchanged',
  'firewall_rulesets.detail.replay': 'Replay v{version}',
  'firewall_rulesets.replay.intro': 'Take a batch of real request samples and have both v{version} and the currently armed version judge them, sample by sample. This is what the activation gate reads: without a replay covering the exact chain about to go live, the server refuses to arm it.',
  'firewall_rulesets.replay.field': 'Sample corpus (JSON)',
  'firewall_rulesets.replay.hint': 'An array, or an object with a samples array. Each entry looks like headers plus body. At most {max} entries. Redaction is the submitter’s job, and it has to be substitution rather than deletion — a rule asking whether a header EXISTS still has to see one. The samples themselves are never stored.',
  'firewall_rulesets.replay.submit': 'Replay against v{version}',
  'firewall_rulesets.replay.none': 'Nothing replayed in this session yet.',
  'firewall_rulesets.replay.error.not_json': 'This is not valid JSON.',
  'firewall_rulesets.replay.error.not_array': 'The top level must be an array, or an object with a samples array.',
  'firewall_rulesets.replay.error.empty': 'An empty corpus is not evidence: the server refuses it, and so does this.',
  'firewall_rulesets.replay.error.too_many': 'At most {max} samples per call; this has {count}.',
  'firewall_rulesets.replay.error.not_object': 'Sample {index} is not an object.',
  'firewall_rulesets.replay.error.bad_header': 'Header {header} on sample {index} is not a string or an array of strings. The server refuses it rather than silently dropping it.',
  'firewall_rulesets.replay.context_dropped': 'These context fields were not sent: {keys}. Only client_format / lane / stream / endpoint reach the engine; anything else is dropped, or a submitter could invent a field a rule reads and steer their own verdict.',
  'firewall_rulesets.replay.record.version': 'Candidate v{version}',
  'firewall_rulesets.replay.record.baseline': 'Baseline v{version}',
  'firewall_rulesets.replay.record.no_baseline': 'No baseline',
  'firewall_rulesets.replay.record.samples': '{count} samples',
  'firewall_rulesets.replay.unresolvable': 'The baseline does not resolve: the armed version’s chain cannot be flattened right now, so this ruleset is passing everything through at this moment. Nothing was compared — the report covers the candidate alone and zero_difference is recorded as “not compared”. This is a legal state, not a failure: fixing it is exactly what activating a resolvable version does.',
  'firewall_rulesets.replay.not_compared': 'Nothing was compared: no version is armed, so the report lists the candidate’s verdicts only.',
  'firewall_rulesets.replay.zero_difference': 'zero_difference',
  'firewall_rulesets.replay.zero_yes': 'true (no differences)',
  'firewall_rulesets.replay.zero_no': 'false (differences found)',
  'firewall_rulesets.replay.zero_unknown': 'not compared',
  'firewall_rulesets.replay.zero_note': 'A report field; the gate does not read it. “True” is not clearance to arm — it says nothing at all about requests this corpus never exercised.',
  'firewall_rulesets.replay.fingerprint': 'Corpus fingerprint',
  'firewall_rulesets.replay.fingerprint_note': 'It answers “was this the same batch of samples”, not “was this the same batch of meanings”: a different corpus exercising the same rules fingerprints differently. It is not the record’s id and it is not a dedupe key.',
  'firewall_rulesets.replay.context_absent': '—',
  'firewall_rulesets.replay.applied_context_note': 'The line under each sample is what $.derived.* actually held while that sample was judged — not what you wrote in the corpus. The two differ: keys outside the whitelist were dropped, and a key you omitted is null, so a rule read null rather than the lane you thought you were testing. A mismatch means this replay measured something other than what you meant to measure.',
  'firewall_rulesets.replay.col.sample': 'Sample',
  'firewall_rulesets.replay.col.diff': 'Difference',
  'firewall_rulesets.replay.col.candidate': 'Candidate verdict',
  'firewall_rulesets.replay.col.baseline': 'Baseline verdict',
  'firewall_rulesets.replay.diff.unchanged': 'Unchanged',
  'firewall_rulesets.replay.diff.pass_to_intercept': 'Pass → intercept',
  'firewall_rulesets.replay.diff.intercept_to_pass': 'Intercept → pass',
  'firewall_rulesets.replay.diff.action_changed': 'Action changed',
  'firewall_rulesets.replay.diff.rule_changed': 'Matched rule changed',
  'firewall_rulesets.replay.diff.errored': 'Errored',
  'firewall_rulesets.replay.outcome.passthrough': 'Passthrough',
  'firewall_rulesets.replay.outcome.match': 'Match',
  'firewall_rulesets.replay.outcome.error': 'Error',
  'firewall_rulesets.replay.verdict.none': 'Not compared',
  'firewall_rulesets.replay.no_rule': 'No rule matched',
  'firewall_rulesets.replay.description_absent': 'Per-sample results carry the rule id and never its description: the server omits it on purpose — a description restates the criterion in readable form, and readable is evadable. The id resolves against the version document.',
  'firewall_rulesets.activate.title': 'Activate v{version}?',
  'firewall_rulesets.activate.body': 'This switches the live version of "{name}" to this one.',
  'firewall_rulesets.activate.blast': 'It takes effect immediately for every config referencing this ruleset and following the active version. No release is involved.',
  'firewall_rulesets.activate.confirm': 'Activate',
  'firewall_rulesets.activate.evidence.nothing': 'nothing (no version is armed)',
  'firewall_rulesets.activate.evidence.rearm': 'This re-arms the version already armed. The gate does not ask for a replay.',
  'firewall_rulesets.activate.evidence.disarm': 'Disarming is never gated — it is the fastest rollback there is.',
  'firewall_rulesets.activate.evidence.ready': 'This session replayed v{version}: record {id}, baseline {baseline}.',
  'firewall_rulesets.activate.evidence.stale': 'The replay on hand was measured against baseline {baseline}, but {active} is armed now. The gate reads evidence against what is armed — replaying again is the safer move.',
  'firewall_rulesets.activate.evidence.missing': 'This session has no replay for this version. The gate requires one covering the exact chain about to go live, or it refuses.',
  'firewall_rulesets.activate.evidence.advisory': 'This is a local reading only. The gate lives on the server and also sees replays someone else ran, and chain hashes rewritten by a parent activation. Its answer is the one that counts.',
  'firewall_rulesets.activate.gate.title': 'The gate refused this activation',
  'firewall_rulesets.activate.gate.hint': 'The server’s own words, unedited — they name which version the replay was measured against and which one is armed now:',
  'firewall_rulesets.disarm.title': 'Disarm this ruleset?',
  'firewall_rulesets.disarm.body': 'Nothing stays armed, configs referencing it are treated as skipping the layer, and the whole version history is kept.',
  'firewall_rulesets.disarm.confirm': 'Disarm',
  'firewall_rulesets.delete.title': 'Delete ruleset?',
  'firewall_rulesets.delete.body': 'This deletes "{name}" and its entire version history. Configs still referencing it lose this layer of protection.',
  'firewall_rulesets.toast.created': 'Ruleset created as a draft; nothing is live yet',
  'firewall_rulesets.toast.deleted': 'Ruleset deleted',
  'firewall_rulesets.toast.version_created': 'New draft version saved; nothing is live yet',
  'firewall_rulesets.toast.activated': 'Activated v{version}',
  'firewall_rulesets.toast.disarmed': 'Disarmed; no version is active',
  'egress_profiles.col.strategy': 'Strategy',
  'egress_profiles.col.status': 'Status',
  'egress_profiles.col.updated': 'Updated',
  'egress_profiles.col.actions': 'Actions',
  'egress_profiles.status.enabled': 'Enabled',
  'egress_profiles.status.disabled': 'Disabled',
  'egress_profiles.action.edit': 'Edit template',
  'egress_profiles.action.enable': 'Enable template',
  'egress_profiles.action.disable': 'Disable template',
  'egress_profiles.action.delete': 'Delete template',
  'egress_profiles.action.delete_blocked': 'In use by channel bindings — cannot delete',
  'egress_profiles.delete.title': 'Delete channel policy template',
  'egress_profiles.delete.body': 'This permanently deletes the template "{name}".',
  'egress_profiles.modal.create_title': 'New channel policy template',
  'egress_profiles.modal.edit_title': 'Edit channel policy template',
  'egress_profiles.modal.name': 'Template Name',
  'egress_profiles.modal.wire_api': 'OpenAI wire API',
  'egress_profiles.modal.wire_api.both': 'Both (default)',
  'egress_profiles.modal.wire_api.chat_completions': 'chat/completions only',
  'egress_profiles.modal.wire_api.responses': 'responses only',
  'egress_profiles.modal.create_hint': 'Once created, the template can be referenced from upstream channel bindings.',
  'egress_profiles.modal.save_warning_count': 'Saving affects {count} channel bindings inheriting this template.',
  'egress_profiles.modal.save_warning_none': 'No channel bindings inherit this template yet.',
  'egress_profiles.modal.create_submit': 'Create Template',
  'egress_profiles.modal.edit_submit': 'Save Template',
  'egress_profiles.toast.created': 'Channel policy template created',
  'egress_profiles.toast.updated': 'Channel policy template updated',
  'egress_profiles.toast.enabled': 'Channel policy template enabled',
  'egress_profiles.toast.disabled': 'Channel policy template disabled',
  'egress_profiles.toast.deleted': 'Channel policy template deleted',
  'egress_profiles.sidecar.title': 'Protocol sidecar',
  'egress_profiles.sidecar.ready': 'Ready',
  'egress_profiles.sidecar.degraded': 'Degraded',
  'egress_profiles.sidecar.ready_hint': 'Gateway is reading caller-owned transform and stream-format capabilities from the trusted sidecar.',
  'egress_profiles.sidecar.degraded_hint': 'Gateway cannot prove sidecar readiness; protocol transforms should be treated as unavailable until this clears.',
  'egress_profiles.sidecar.api': 'API',
  'egress_profiles.sidecar.config_hash': 'Config Hash',
  'egress_profiles.instance_note.cta': 'Open upstreams',

  // ── Key Buckets (#143) ────────────────────────────────────────
  'key_buckets.title': 'API Key Buckets',
  'key_buckets.add_btn': 'Add Key',
  'key_buckets.empty': 'No key buckets yet',
  'key_buckets.col.label': 'Label',
  'key_buckets.col.key_preview': 'Key Preview',
  'key_buckets.col.status': 'Status',
  'key_buckets.enabled': 'Enabled',
  'key_buckets.disabled': 'Disabled',
  'key_buckets.add_modal.title': 'Add API Key Bucket',
  'key_buckets.add_modal.label': 'Label',
  'key_buckets.add_modal.label_placeholder': 'e.g. Production key #1',
  'key_buckets.add_modal.cancel': 'Cancel',
  'key_buckets.add_modal.submit': 'Add',
  'key_buckets.add_modal.creating': 'Adding…',
  'key_buckets.toast.created': 'Key bucket added',
  'key_buckets.toast.deleted': 'Key bucket deleted',
  'key_buckets.confirm_delete': 'Confirm',

  // ── Credit lines (epic #1033 E4-C1, #1125) ──────────────────────
  'credit.section.title': 'Credit line',
  'credit.section.apply': 'Apply for credit',
  'credit.section.temporary_apply': 'Temporary credit',
  'credit.section.empty': 'No credit applications yet.',
  'credit.section.load_error': 'Failed to load credit details',
  'credit.section.temporary_applications.title': 'Temporary credit applications',
  'credit.section.temporary_applications.empty': 'No temporary credit applications yet.',
  'credit.section.temporary_applications.load_error': 'Failed to load temporary credit applications',
  'credit.section.temporary_applications.col.created_at': 'Submitted',
  'credit.section.temporary_applications.col.requested': 'Requested limit',
  'credit.section.temporary_applications.col.expected_arrival': 'Expected arrival',
  'credit.section.temporary_applications.col.status': 'Status',
  'credit.section.temporary_applications.col.grant': 'Grant',
  'credit.section.history_link': 'View credit grant history',
  'credit.overview.status.prepaid': 'No credit drawn · prepaid remaining {amount}',
  'credit.overview.status.long_term':
    'Long-term credit in use · used {used} / {cap} · owed {debt}',
  'credit.overview.status.temporary':
    'Temporary credit in use · used {used} / {cap} · owed {debt}',
  'credit.overview.status.over_limit': 'Spend exceeds available credit {over} · owed {debt}',
  'credit.overview.kpi.prepaid': 'Prepaid balance',
  'credit.overview.kpi.long_term': 'Long-term limit',
  'credit.overview.kpi.temporary': 'Temporary credit',
  'credit.overview.kpi.consumption': 'Cumulative spend',
  'credit.overview.applied_rate': 'Locked rate {rate}',
  'credit.overview.layer.prepaid': 'Prepaid',
  'credit.overview.layer.long_term': 'Long-term credit',
  'credit.overview.layer.temporary': 'Temporary credit',
  'credit.overview.layer.over_limit': 'Over limit',
  'credit.overview.layer.used_of_cap': '{used} / {cap}',
  'credit.overview.layer.limit': 'Limit {limit}',
  'credit.overview.timeline.empty': 'No statements or temporary-credit draws in recent months.',
  'credit.overview.timeline.summary':
    'Last {months} months · paid on time {onTime}/{closed} · longest overdue {maxLate} days · temporary credit used {tmp} times · currently unpaid',
  'credit.overview.timeline.col.period': 'Period',
  'credit.overview.timeline.col.bar': 'Bill and payment',
  'credit.overview.timeline.col.amount': 'Billed',
  'credit.overview.timeline.col.due': 'Due',
  'credit.overview.timeline.col.paid': 'Paid on',
  'credit.overview.timeline.col.overdue_days': 'Days late',
  'credit.overview.timeline.col.status': 'Status',
  'credit.overview.timeline.kind.temporary': 'Temporary credit',
  'credit.overview.timeline.kind.one_shot': 'One-shot bill',
  'credit.overview.status_chip.paid_on_time': 'Paid on time',
  'credit.overview.status_chip.paid_late': 'Paid {days}d late',
  'credit.overview.status_chip.overdue_unpaid': 'Overdue unpaid',
  'credit.overview.status_chip.not_due': 'Not due',
  'credit.overview.status_chip.no_payment_due': 'No payment due',
  'credit.overview.status_chip.draw': 'Draw',
  'credit.overview.dash': '—',
  // Contract read-back (#1957).
  'credit.contract.title': 'Signed contract',
  'credit.contract.download': 'Download',
  'credit.contract.empty': 'No contract on file for this customer.',
  // #1958: when the list is filtered by the application's key, an empty
  // result means "that reference resolves to nothing", not "this customer
  // has no contract" — the latter is a claim this view cannot back.
  'credit.contract.empty_filtered': 'The contract this application cites was not found.',
  'credit.kpi.limit': 'Credit limit',
  'credit.kpi.cycle': 'Payment terms',
  'credit.kpi.grace': 'Days after statement',
  'credit.kpi.days': '{days} days',
  'credit.kpi.inherited': 'Inherits platform default',
  'credit.kpi.custom': 'Customer override',
  'credit.kpi.none': 'No credit / prepaid only',
  'credit.col.status': 'Status',
  'credit.col.limit': 'Requested limit',
  'credit.col.applied_rate': 'Locked rate',
  'credit.col.cycle': 'Requested cycle',
  'credit.col.reason': 'Reason',
  'credit.col.requester': 'Requester',
  'credit.col.created_at': 'Submitted',
  'credit.status.pending_approval': 'Pending approval',
  'credit.status.approved': 'Approved',
  'credit.status.rejected': 'Rejected',
  'credit.apply.title': 'Apply for a credit line',
  'credit.apply.approval.stage': 'stage {n} {role}',
  'credit.apply.approval.stage_sep': ', ',
  'credit.apply.approval.role_sep': '/',
  'credit.apply.description':
    '{stages}-stage approval: {chain}. You cannot approve your own request. Submit does not change the customer’s credit line.',
  'credit.apply.customer': 'Customer',
  'credit.apply.limit_label': 'Credit limit (CNY)',
  'credit.apply.limit_hint': 'Platform default ceiling {max}',
  'credit.apply.cycle_label': 'Payment terms (days after statement)',
  'credit.apply.cycle_hint': '{min}–{max} days. Common values: 15 / 30 / 60. Due date is statement period end plus this many days.',
  'credit.apply.reason_label': 'Reason',
  'credit.apply.reason_hint': '{min}–{max} characters. Why this customer should get this line and terms.',
  'credit.apply.error.reason': 'Enter a reason of {min}–{max} characters after trimming spaces.',
  'credit.apply.contract_label': 'Contract (required)',
  'credit.apply.contract_hint': 'Up to {mb}. A contract is mandatory for credit.',
  'credit.apply.hint.needs_approval':
    'Submit sends this to approval. The line does not change until a different operator approves it.',
  'credit.apply.confirm': 'Submit',
  'credit.apply.retry': 'Retry submit',
  'credit.apply.pending_retry': 'Contract uploaded — only the application submit needs retrying.',
  'credit.apply.error.limit': 'Enter a valid credit limit (a non-negative amount).',
  'credit.apply.error.cycle': 'Enter payment terms as a whole number of days between {min} and {max}.',
  'credit.apply.error.contract_required': 'Attach the contract file.',
  'credit.apply.error.oversize': 'Attachment exceeds the {mb} limit.',
  'credit.apply.error.upload_failed': 'Contract upload failed: {detail}',
  'credit.apply.toast.pending': 'Credit application submitted for approval',
  'credit.temporary.apply.title': 'Apply for temporary credit',
  'credit.temporary.apply.description':
    'A short bridge while a remittance is in flight. Cap ¥{cap}. {stages}-stage approval: {chain}. You cannot approve your own request. Submit does not activate the line.',
  'credit.temporary.apply.limit_label': 'Credit limit (CNY)',
  'credit.temporary.apply.limit_hint': 'Hard cap ¥{cap}. Above that, apply for long-term credit.',
  'credit.temporary.apply.expected_amount_label': 'Expected arrival (CNY)',
  'credit.temporary.apply.expected_amount_hint': 'What the customer said they are sending. Used to flag a shortfall if less than 70% arrives.',
  'credit.temporary.apply.expected_date_label': 'Expected arrival date',
  'credit.temporary.apply.expected_date_hint': 'The grant expires five calendar days after this date (UTC+8).',
  'credit.temporary.apply.evidence_text_label': 'Payment evidence (text)',
  'credit.temporary.apply.evidence_text_hint': 'A note is enough — email screenshot, WeChat excerpt, or a verbal promise restated. No grade is assigned.',
  'credit.temporary.apply.evidence_file_label': 'Payment evidence (file, optional)',
  'credit.temporary.apply.evidence_file_hint': 'Optional attachment, up to {mb}. Text-only is accepted.',
  'credit.temporary.apply.error.limit': 'Enter a positive credit limit.',
  'credit.temporary.apply.error.expected_amount': 'Enter a positive expected arrival amount.',
  'credit.temporary.apply.error.expected_date': 'Enter the expected arrival date.',
  'credit.temporary.apply.error.evidence': 'Add a text note, an attachment, or both.',
  'credit.temporary.apply.toast.pending': 'Temporary credit application submitted; waiting for finance approval',
  'credit.temporary.detail.limit': 'Requested limit',
  'credit.temporary.detail.expected_amount': 'Expected arrival',
  'credit.temporary.detail.expected_date': 'Expected arrival date',
  'credit.temporary.detail.applied_rate': 'Locked rate',
  'credit.temporary.evidence.title': 'Payment evidence',
  'credit.temporary.evidence.download': 'Download',
  'credit.temporary.evidence.empty': 'No payment-evidence file on record for this customer.',
  'credit.exemption.open': 'Emergency exemption',
  'credit.exemption.title': 'Open an emergency exemption',
  'credit.exemption.description':
    'Pauses the overdue ladder and restores already-approved credit to spendability until the expiry instant. The customer is told: exempted until that instant; unpaid debt then stops service. Expiry is not a second grace period. Does not clear the debt. Super-admin only; expiry and reason are both required.',
  'credit.exemption.expires_label': 'Expires at',
  'credit.exemption.expires_hint':
    'Required. The exemption ends at this instant, or sooner if a super-admin revokes it.',
  'credit.exemption.reason_label': 'Reason',
  'credit.exemption.reason_hint': 'Required. Kept on the history row so the open can be reconstructed.',
  'credit.exemption.confirm': 'Open exemption',
  'credit.exemption.revoke': 'Revoke exemption',
  'credit.exemption.revoke_title': 'Revoke the live exemption?',
  'credit.exemption.revoke_body':
    'Spendability returns to the pre-exemption overlay immediately. Overdue stage and debt are unchanged.',
  'credit.exemption.revoke_confirm': 'Revoke',
  'credit.exemption.revoke_toast': 'Exemption revoked.',
  'credit.exemption.error.expires': 'Set an expiry instant. Permanent exemptions are refused.',
  'credit.exemption.error.reason': 'Enter a reason.',
  'credit.exemption.chip': 'Manual exemption · {days} days left',
  'credit.overdue.stage.frozen': 'Overdue · new grants frozen',
  'credit.overdue.stage.stop_notice': 'Stop-service notice sent',
  'credit.overdue.stage.stopped': 'Credit not counting toward spend',
  'credit.overdue.stage.collections': 'Collections',
  'credit.overdue.page.title': 'Credit monitor',
  'credit.overdue.page.caption': 'Credit exposure in this view',
  'credit.overdue.page.empty': 'No credit accounts.',
  'credit.overdue.page.load_error': 'Failed to load the credit monitor',
  'credit.overdue.col.account': 'Customer',
  'credit.overdue.col.stage': 'Stage',
  'credit.overdue.col.days': 'Days past due',
  'credit.overdue.col.exemption': 'Exemption',
  'credit.overdue.heartbeat.ok': 'Ladder last ran · scanned {scanned}',
  'credit.overdue.heartbeat.stale': 'Ladder cron is stale — overdue customers may never stop',
  'credit.monitor.kpi.total_limit': 'Total credit (this view)',
  'credit.monitor.kpi.used': 'Drawn credit (this view)',
  'credit.monitor.kpi.overdue_total': 'Overdue total (this view)',
  'credit.monitor.overdue_count': 'Overdue {count} accounts',
  'credit.monitor.todo.overdue': 'Overdue',
  'credit.monitor.todo.due_soon': 'Due within 7 days',
  'credit.monitor.todo.temporary_expiring': 'Temporary credit expiring',
  'credit.monitor.filter.all': 'All',
  'credit.monitor.sort.label': 'Sort',
  'credit.monitor.sort.default': 'Default',
  'credit.monitor.sort.usage': 'Drawn amount',
  'credit.monitor.sort.name': 'Name',
  'credit.monitor.col.layers': 'Limit mix and draw',
  'credit.monitor.col.status': 'Status',
  'credit.monitor.col.next_due': 'Next due',
  'credit.monitor.status.overdue': 'Overdue {days}d',
  'credit.monitor.next_due.overdue': '{date} overdue',
  'credit.monitor.next_due.due': '{date}',
  'credit.monitor.next_due.temporary': '{date} expires',
  'credit.monitor.expand': 'Expand billing timeline',
  'credit.monitor.collapse': 'Collapse billing timeline',
  'credit.monitor.table.caption': 'Accounts',
  'credit.monitor.table.empty_filtered': 'No accounts match this filter.',
  'credit.history.title': 'Credit grant history',
  'credit.history.subtitle':
    'Every grant in this view, including ended ones. Totals are the full filtered set, not this page.',
  'credit.history.summary.scope': 'Totals cover the full filtered visible set, not the rows on this page.',
  'credit.history.kpi.total': 'Total (this view)',
  'credit.history.kpi.active': 'Active (this view)',
  'credit.history.kpi.ended': 'Ended (this view)',
  'credit.history.kpi.avg_days': 'Avg days to repay temporary (this view)',
  'credit.history.filter.customer': 'Customer',
  'credit.history.filter.customer_placeholder': 'Customer name / email / org id',
  'credit.history.filter.salesperson': 'Salesperson',
  'credit.history.filter.salesperson_placeholder': 'Salesperson name / email / ops user id',
  'credit.history.filter.grant_type': 'Grant type',
  'credit.history.filter.status': 'Status',
  'credit.history.filter.end_reason': 'End reason',
  'credit.history.filter.starts_from': 'Effective from',
  'credit.history.filter.starts_to': 'Effective to',
  'credit.history.filter.all': 'All',
  'credit.history.status.all': 'All',
  'credit.history.status.active': 'Active',
  'credit.history.status.ended': 'Ended',
  'credit.history.type.long_term': 'Long-term',
  'credit.history.type.temporary': 'Temporary',
  'credit.history.type.temporary_bump': 'Temporary bump',
  'credit.history.end_reason.repaid': 'Repaid',
  'credit.history.end_reason.expired': 'Expired',
  'credit.history.end_reason.manual': 'Ended manually',
  'credit.history.end_reason.superseded': 'Superseded',
  'credit.history.col.customer': 'Customer',
  'credit.history.col.salesperson': 'Salesperson',
  'credit.history.col.grant_type': 'Type',
  'credit.history.col.starts_at': 'Effective from',
  'credit.history.col.ends_at': 'Ended at',
  'credit.history.col.amount': 'Amount',
  'credit.history.col.used': 'Used',
  'credit.history.col.repayment': 'Repayment',
  'credit.history.col.days_to_topup': 'Days to top-up',
  'credit.history.used.tooltip':
    'Active = current debt; ended = debt immediately before the transaction that ended the grant.',
  'credit.history.used.measured_now': 'measured now',
  'credit.history.used.measured_ended': 'as of end',
  'credit.history.repayment.tooltip':
    'Repaid = inbound during the grant window + settlements on this grant\'s one-shot statement; outstanding = live debt while active, unpaid bill remainder when expired, 0 when repaid, carried into the replacing grant when superseded',
  'credit.history.repayment.repaid': 'Repaid',
  'credit.history.repayment.outstanding': 'Outstanding',
  'credit.history.repayment.settled': 'Settled',
  'credit.history.repayment.partial': 'Partial {pct}%',
  'credit.history.repayment.unpaid': 'Unpaid',
  'credit.history.repayment.carried_over': 'Carried over',
  'credit.history.repayment.billed': 'Billed · {status}',
  'credit.history.repayment.statement.open': 'open',
  'credit.history.repayment.statement.closed': 'closed',
  'credit.history.amount.rate_tooltip': 'Locked rate {rate} · {usd}',
  'credit.history.ends.ongoing': 'Ongoing',
  'credit.history.ends.scheduled': 'Scheduled end',
  'credit.history.ends.actual': 'Actually ended',
  'credit.history.days.day': 'Day {n}',
  'credit.history.expand': 'Expand events',
  'credit.history.collapse': 'Collapse events',
  'credit.history.events.empty': 'No ops events on this grant.',
  'credit.history.table.caption': 'Grants',
  'credit.history.empty': 'No grants match this filter.',
  'credit.history.load_error': 'Failed to load credit grant history',
  'credit.history.pagination.aria_label': 'Credit grant history pagination',
  'credit.history.pagination.range': 'Showing {start}–{end} of {total}',
  'credit.history.audience.customer': 'Customer',
  'credit.history.audience.internal': 'Internal',
  'credit.history.event.unknown': 'Unknown event',
  'credit.history.event.temporary_credit_expiring': 'Temporary credit expiring',
  'credit.history.event.temporary_credit_arrival_shortfall': 'Arrival shortfall',
  'credit.history.event.temporary_credit_one_shot_issued': 'One-shot bill issued',
  'credit.history.event.credit_due_soon_7d': 'Due in 7 days',
  'credit.history.event.credit_due_soon_3d': 'Due in 3 days',
  'credit.history.event.credit_due_today': 'Due today',
  'credit.history.event.credit_overdue_frozen': 'Overdue frozen',
  'credit.history.event.credit_stop_notice': 'Stop notice',
  'credit.history.event.credit_service_stopped': 'Service stopped',
  'credit.history.event.credit_collections': 'Collections',
  'credit.history.event.credit_overdue_cleared': 'Overdue cleared',
  'credit.history.event.credit_exemption_opened': 'Exemption opened',
  'credit.history.event.credit_exemption_revoked': 'Exemption revoked',
  'credit.history.event.credit_overdue_reminder': 'Overdue reminder',
  'credit.temporary.evidence.empty_filtered':
    'The payment-evidence file this application cites was not found.',
  'billing_statements.title': 'Billing statements',
  'billing_statements.subtitle':
    'Monthly AR statements for credit customers. Read-only; "overdue" comes from the dunning verdict, not from a due-date comparison.',
  'billing_statements.filter.account': 'Customer account',
  'billing_statements.filter.account_placeholder': 'acc_…',
  'billing_statements.filter.status': 'Status',
  'billing_statements.filter.overdue': 'Overdue only',
  'billing_statements.status.all': 'All',
  'billing_statements.status.open': 'Open',
  'billing_statements.status.closed': 'Closed',
  'billing_statements.col.account': 'Customer',
  'billing_statements.col.period': 'Period',
  'billing_statements.col.closing_ar': 'Closing AR',
  'billing_statements.col.due_date': 'Due date',
  'billing_statements.col.status': 'Status',
  'billing_statements.col.owner': 'Owner',
  'billing_statements.owner.unassigned': 'Unassigned',
  'billing_statements.empty': 'No statements match these filters.',
  'billing_statements.load_error': 'Failed to load billing statements',
  'billing_statements.load_more': 'Load more',
  'credit.settings.title': 'Credit defaults',
  'credit.settings.subtitle':
    'Platform credit defaults: the within-defaults apply cap, default payment terms, and the monthly statement anchor day.',
  'credit.settings.panel': 'Defaults',
  'credit.settings.forbidden': 'Only admins can configure credit defaults.',
  'credit.settings.load_error': 'Failed to load credit defaults',
  'credit.settings.limit_label': 'Default credit limit (USD)',
  'credit.settings.limit_hint': 'Currently {current}',
  'credit.settings.grace_label': 'Default payment terms (days after statement)',
  'credit.settings.grace_hint':
    'Fallback due-date offset when the account has no approved payment terms. Due date is period end plus these days.',
  'credit.settings.anchor_label': 'Statement anchor day (UTC+8)',
  'credit.settings.anchor_hint': 'The monthly day the cycle aligns to, 1–28.',
  'credit.settings.save': 'Save',
  'credit.settings.error.limit': 'Enter a valid default credit limit (a non-negative amount).',
  'credit.settings.error.grace': 'Enter a valid default payment terms (a non-negative number of days).',
  'credit.settings.error.anchor': 'The statement anchor day must be between 1 and 28.',
  'credit.settings.toast.saved': 'Credit defaults saved',

  'credit.mail.title': 'Credit mail',
  'credit.mail.empty': 'No credit letters have been processed for this customer yet.',
  'credit.mail.load_error': 'Could not load credit-mail history.',
  'credit.mail.mute': 'Mute credit mail',
  'credit.mail.unmute': 'Unmute credit mail',
  'credit.mail.mute_toast': 'Credit mail muted for this customer.',
  'credit.mail.unmute_toast': 'Credit mail unmuted for this customer.',
  'credit.mail.muted_chip': 'Credit mail muted',
  'credit.mail.col.when': 'When',
  'credit.mail.col.result': 'Result',
  'credit.mail.col.template': 'Template',
  'credit.mail.col.subject': 'Subject',
  'credit.mail.col.to': 'To',
  'credit.mail.col.reason': 'Reason',
  'credit.mail.result.sent': 'Sent',
  'credit.mail.result.suppressed': 'Suppressed',
  'credit.mail.result.failed': 'Failed',
  'credit.mail.reason.unmapped_event': 'Unrecognized mail event; not sent',
  'credit.mail.reason.no_recipient': 'No usable recipient address',
  'credit.mail.reason.reserved_invalid_recipient': 'Recipient address is invalid (reserved domain)',
  'credit.mail.reason.missing_due_date': 'Statement has no due date; not sent',
  'credit.mail.reason.zero_debt': 'No amount owed; not sent',
  'credit.mail.reason.mail_disabled': 'Credit mail has been turned off by an administrator',
  'credit.mail.reason.account_muted': 'Credit mail is muted for this customer',
  'credit.mail.reason.stale': 'Event expired; not sent',
  'credit.mail.reason.superseded': 'Replaced by a newer event; not sent',
  'credit.mail.reason.provider_unavailable': 'Mail provider is unavailable',
  'credit.mail.reason.render_failed': 'Could not build the letter',
  'credit.mail.reason.send_failed': 'Send failed',
  'credit.mail.switch.caption': 'Credit customer mail',
  'credit.mail.switch.hint':
    'Stops every credit letter on the next pass without a deploy. Pending events stay queued while mail is off.',
  'credit.mail.switch.toggle': 'Send credit customer mail',
  'credit.mail.switch.on': 'Sending',
  'credit.mail.switch.off': 'Stopped',
  'credit.mail.switch.load_error': 'Could not load the credit-mail switch.',
  'credit.mail.switch.enable_title': 'Resume credit customer mail?',
  'credit.mail.switch.enable_body':
    'The next pass will send queued credit letters to customers who are not muted.',
  'credit.mail.switch.disable_title': 'Stop credit customer mail?',
  'credit.mail.switch.disable_body':
    'The next pass will not send. Queued events stay pending and are recorded as suppressed.',
  'credit.mail.switch.confirm': 'Confirm',
  'credit.mail.switch.reason_label': 'Reason',
  'credit.mail.switch.reason_hint': 'Optional. Shown on the switch and in the audit log.',
  'credit.mail.switch.enabled_toast': 'Credit customer mail is on.',
  'credit.mail.switch.disabled_toast': 'Credit customer mail is off.',

  // ── SKU activation readiness (#1162) ───────────────────────────
  'sku_activation.title': 'SKU activation readiness',
  'sku_activation.subtitle': 'Server-owned activation plans for platform shelf, supply, SKU routing, key aliases, cache convergence, audit rollback, and public smoke evidence.',
  'sku_activation.read_only': 'Plan gated',
  'sku_activation.production.title': 'Production environment selected',
  'sku_activation.production.body': 'Creating or completing a plan records production activation state. Every completion is revalidated by the owning service; verify the SKU, tenant, and upstream scope.',
  'sku_activation.scope.title': 'Check scope',
  'sku_activation.scope.no_mutation': 'This action only runs readiness and never calls apply or rollback endpoints.',
  'sku_activation.form.scope_required': 'SKU IDs and at least one upstream are required.',
  'sku_activation.plan.id': 'Activation plan ID',
  'sku_activation.plan.load_hint': 'Load an existing server-owned plan.',
  'sku_activation.plan.load': 'Load plan',
  'sku_activation.plan.create': 'Create plan',
  'sku_activation.plan.clear': 'Clear plan',
  'sku_activation.plan.title': 'Activation plan',
  'sku_activation.plan.checklist': 'Activation mutation checklist',
  'sku_activation.plan.required_default': 'Owner mutation is required.',
  'sku_activation.plan.mirror': 'Mirror',
  'sku_activation.plan.not_required': 'Already satisfied; no owner receipt required.',
  'sku_activation.plan.completed': 'Owner receipt validated and completed.',
  'sku_activation.plan.completion_placeholder': 'Paste the focused owner endpoint response evidence.',
  'sku_activation.plan.complete': 'Validate and complete',
  'sku_activation.plan.required_first': 'Create or load an activation plan first.',
  'sku_activation.plan.environment': 'Environment',
  'sku_activation.plan.status_label': 'Plan status',
  'sku_activation.plan.status.active': 'Active',
  'sku_activation.plan.status.completed': 'Completed',
  'sku_activation.plan.status.cancelled': 'Cancelled',
  'sku_activation.plan.org': 'Organization',
  'sku_activation.plan.key': 'API key',
  'sku_activation.plan.upstreams': 'Allowed upstreams',
  'sku_activation.plan.skus': 'SKUs',
  'sku_activation.plan.configs': 'Routing configs',
  'sku_activation.plan.none': 'None',
  'sku_activation.plan.kind.platform_shelf': 'Platform shelf',
  'sku_activation.plan.kind.supply_capability': 'Supply capability',
  'sku_activation.plan.kind.routing_coverage': 'Routing coverage',
  'sku_activation.plan.kind.key_alias': 'Key alias',
  'sku_activation.plan.mutation_status.pending': 'Pending',
  'sku_activation.plan.mutation_status.completed': 'Completed',
  'sku_activation.plan.requirement_status.required': 'Required',
  'sku_activation.plan.requirement_status.not_required': 'Not required',
  'sku_activation.plan.receipt': 'Receipt',
  'sku_activation.plan.revision': 'Revision',
  'sku_activation.plan.mirror_status.pending': 'Pending',
  'sku_activation.plan.mirror_status.delivered': 'Delivered',
  'sku_activation.plan.mirror_status.failed': 'Failed',
  'sku_activation.plan.mirror_status.not_applicable': 'Not applicable',
  'sku_activation.plan.mirror_status.unknown': 'Unknown',
  'sku_activation.plan.reason.platform_listed': 'The platform model is already listed.',
  'sku_activation.plan.reason.platform_not_listed': 'The platform model is not listed.',
  'sku_activation.plan.reason.key_alias_ready': 'The key alias already maps to this SKU.',
  'sku_activation.plan.reason.key_alias_missing': 'The key alias is missing or maps to another SKU.',
  'sku_activation.plan.reason.supply_missing': 'No routable exact capability chain exists.',
  'sku_activation.plan.reason.supply_ready': 'A routable exact capability chain already exists.',
  'sku_activation.plan.reason.routing_missing': 'No routing target has a routable exact capability chain.',
  'sku_activation.plan.reason.routing_ready': 'A routing target has a routable exact capability chain.',
  'sku_activation.plan.reason.server': 'Server reason: {reason}',
  'sku_activation.plan.completion_aria': 'Completion evidence for mutation {mutation}',
  'sku_activation.server_code': 'Server code',
  'sku_activation.form.environment': 'Environment',
  'sku_activation.form.org_id': 'Org ID',
  'sku_activation.form.api_key_id': 'API Key ID',
  'sku_activation.form.sku_ids': 'SKU IDs',
  'sku_activation.form.upstream_ids': 'Allowed upstream IDs',
  'sku_activation.form.config_ids': 'Routing config IDs',
  'sku_activation.form.evidence': 'Evidence JSON',
  'sku_activation.form.evidence_hint': 'Paste mutation-owned cache, audit, rollback, and smoke receipts, then rerun readiness.',
  'sku_activation.form.evidence_invalid': 'Evidence must be a valid JSON object.',
  'sku_activation.form.optional': 'Optional; leave empty for environment-level facts.',
  'sku_activation.form.key_hint': 'Optional; when set, both canonical and bare model paths are checked.',
  'sku_activation.form.list_hint': 'Separate with spaces, commas, or new lines; up to 20.',
  'sku_activation.form.upstream_hint': 'Explicit scope is recommended to prevent supplier crossover; empty becomes a blocker.',
  'sku_activation.form.config_hint': 'Optional; empty uses the tenant’s currently active configs.',
  'sku_activation.form.sku_required': 'Enter at least one SKU ID.',
  'sku_activation.form.org_required_for_key': 'Org ID is required when API Key ID is set.',
  'sku_activation.action.run': 'Run read-only check',
  'sku_activation.action.running': 'Checking…',
  'sku_activation.action.inspect': 'Evidence',
  'sku_activation.action.open': 'Open',
  'sku_activation.action.open_source': 'Open source of truth',
  'sku_activation.toast.completed': 'Readiness check completed',
  'sku_activation.empty.title': 'Waiting for a scope',
  'sku_activation.empty.body': 'The result follows seven fixed stages and automatically locates the first blocker.',
  'sku_activation.result.title': 'Readiness verdict',
  'sku_activation.result.ready': 'Ready',
  'sku_activation.result.blocked': 'Not ready',
  'sku_activation.result.fingerprint': 'Evidence fingerprint',
  'sku_activation.result.checked_at': 'Checked at',
  'sku_activation.result.first_blocker': 'First blocker',
  'sku_activation.result.none': 'None',
  'sku_activation.evidence.title': 'Evidence and action',
  'sku_activation.evidence.empty': 'Select a check to inspect its evidence.',
  'sku_activation.evidence.action': 'Focused endpoint',
  'sku_activation.evidence.read_only_note': 'This pane only identifies the action surface; it never performs a write.',
  'sku_activation.evidence.none': 'This check has no additional evidence.',
  'sku_activation.evidence.rollback': 'Rollback receipt',
  'sku_activation.evidence.rollback_none': 'No mutation was claimed, or no verifiable CAS rollback packet exists.',
  'sku_activation.lineage.title': 'Request-path lineage',
  'sku_activation.lineage.empty': 'No canonical or bare routing lineage is available for this scope.',
  'sku_activation.lineage.default': 'Default SKU pool',
  'sku_activation.lineage.no_target': 'No target',
  'sku_activation.stage.platform_shelf': 'Platform shelf',
  'sku_activation.stage.supply': 'Upstream supply',
  'sku_activation.stage.routing': 'SKU routing',
  'sku_activation.stage.key_alias': 'Key aliases',
  'sku_activation.stage.cache': 'Cache convergence',
  'sku_activation.stage.audit_rollback': 'Audit and rollback',
  'sku_activation.stage.smoke': 'Public smoke',
  'sku_activation.status.ready': 'Ready',
  'sku_activation.status.warning': 'Warning',
  'sku_activation.status.blocked': 'Blocked',
  'sku_activation.status.waiting': 'Waiting',
  'sku_activation.status.degraded': 'Degraded',
  'sku_activation.status.not_applicable': 'Not applicable',

  // ── Operator-authored customer mail (#1911) ─────────────────────
  'email_notices.title': 'Email notices',
  'email_notices.subtitle':
    'Write a mail by hand and send it to a chosen audience. Audience, copy and timing are independent: selecting by model is one audience policy, and a shelf announcement is one piece of copy — neither defines the tool.',
  'email_notices.new': 'New notice',
  'email_notices.views_label': 'Switch between notices and templates',
  'email_notices.view.notices': 'Notices',
  'email_notices.view.templates': 'Templates',
  'email_notices.tabs_label': 'Filter notices by status',
  'email_notices.tab.all': 'All',
  'email_notices.panel.list': 'Notices',
  'email_notices.panel.delivery': 'Delivery by address',
  'email_notices.empty': 'No notices under this filter.',
  'email_notices.loading': 'Loading notice…',
  'email_notices.error.load': 'Could not load notices',
  'email_notices.error.load_hint':
    'The endpoint returned no list. Retry, or check that ops-api can reach platform-api.',
  'email_notices.error.generic': 'That did not go through. Try again.',
  'email_notices.column.task_title': 'Task title',
  'email_notices.column.subject': 'Subject',
  'email_notices.column.audience': 'Audience',
  'email_notices.column.status': 'Status',
  'email_notices.column.gates': 'Send gates',
  'email_notices.column.created': 'Created',

  'email_notices.status.draft': 'Draft',
  'email_notices.status.sent': 'Sent',
  'email_notices.status.cancelled': 'Cancelled',

  'email_notices.section.copy': 'Content',
  'email_notices.section.copy_hint':
    'The task title is yours alone; the subject and body are what a customer reads. The body is free text — a template is only a starting draft.',
  'email_notices.field.task_title': 'Task title (internal)',
  'email_notices.field.task_title_hint':
    'Never leaves this page. It is how you pick this one out of forty, e.g. "Claude Code Opus4.6 delist notice".',
  'email_notices.field.subject': 'Subject (customer-facing)',
  'email_notices.field.subject_hint': 'This line lands in a paying customer\'s inbox verbatim.',
  'email_notices.field.body': 'Body',
  'email_notices.field.body_hint':
    'Sent as written; nothing is appended. Placeholders available: {placeholders}',
  'email_notices.placeholder_unknown':
    'These placeholders are not available: {names}. A send would deliver them braces and all, so they are refused here rather than in an inbox.',
  'email_notices.field.remind_on': 'Reminder date (memo)',
  'email_notices.field.remind_on_hint':
    'A memo and nothing else: on that date a card is posted to the ops channel. No mail is sent, no status moves, no model changes. Leave blank for no reminder.',

  'email_notices.template.load_label': 'Start from a template',
  'email_notices.template.load_hint':
    'Copies the template\'s subject and body into the two fields below. After that they are ordinary text with no link back.',
  'email_notices.template.load_placeholder': 'Pick a template',
  'email_notices.template.load_action': 'Load',

  'email_notices.section.audience': 'Audience',
  'email_notices.section.audience_hint':
    'Choose how the audience is drawn, then fill in that policy\'s parameters. Today there is one; adding another leaves existing notices untouched.',
  'email_notices.field.audience_type': 'Audience policy',
  'email_notices.field.audience_type_hint': 'Switching policy clears the current one\'s parameters.',
  'email_notices.audience.models_used': 'Accounts that called selected models',
  'email_notices.models_loading': 'Loading the model catalog…',
  'email_notices.models_empty': 'No models available to select.',
  'email_notices.selected_count': '{count} model(s) selected.',
  'email_notices.models_search_label': 'Filter models',
  'email_notices.models_search_placeholder': 'Filter by vendor or model, e.g. anthropic',
  'email_notices.models_select_filtered': 'Select all {count} shown',
  'email_notices.models_clear_filtered': 'Clear {count} shown',
  'email_notices.models_bulk_scope':
    'Select and clear act only on the {shown} model(s) currently shown; anything selected outside the filter is left alone. Empty the search box to act on all {total}.',
  'email_notices.models_no_match': 'No model matches the current filter.',
  'email_notices.field.window_days': 'Lookback (days)',
  'email_notices.field.window_days_hint':
    'Reaches accounts that called one of the selected models within this window, up to {max} days.',
  'email_notices.audience.preview_action': 'How many people is this?',
  'email_notices.audience.preview_hint':
    'Reads only, writes nothing, and needs no saved draft. Ask as often as you like.',
  'email_notices.audience.preview_incomplete':
    'Pick at least one model and a lookback window first.',
  'email_notices.audience.preview_count':
    '{count} account(s) match right now (looking back {days} days, from {from}).',
  'email_notices.audience.preview_none':
    'No account matched in the {days} days from {from}.',
  'email_notices.audience.preview_more': '…and {count} more not listed.',
  'email_notices.audience.preview_not_frozen':
    'An estimate for this moment; nothing is frozen. Section 3 recomputes and lists every address before a send.',

  'email_notices.section.recipients': 'Recipients',
  'email_notices.section.recipients_hint':
    'See exactly who this reaches first. Recomputing writes nothing, so look as often as you like; freezing turns the list into the rows that will actually be mailed.',
  'email_notices.gate.recipients_open': 'List not frozen',
  'email_notices.gate.recipients_frozen': 'List frozen ({count} addresses)',
  'email_notices.recipients.compute': 'Compute recipients',
  'email_notices.recipients.recompute': 'Recompute',
  'email_notices.recipients.export': 'Export CSV',
  'email_notices.recipients.window': 'Looking back {days} days, from {from}.',
  'email_notices.recipients.summary':
    '{total} candidates, {excluded} struck out, {kept} to freeze.',
  'email_notices.recipients.column.email': 'Email',
  'email_notices.recipients.column.brand': 'Brand',
  'email_notices.recipients.column.requests': 'Requests',
  'email_notices.recipients.column.last_used': 'Last used',
  'email_notices.recipients.column.status': 'Status',
  'email_notices.recipients.column.action': 'Action',
  'email_notices.recipients.none': 'No account matched this audience within the window.',
  'email_notices.recipients.exclude': 'Strike out',
  'email_notices.recipients.restore': 'Restore',
  'email_notices.recipients.finalize': 'Freeze these {count} recipients',
  'email_notices.recipients.blocked_audience_dirty': 'The audience has unsaved changes. The count above is for the selection on screen; the freeze recomputes server-side from the saved one. Save the draft so both mean the same people.',
  'email_notices.recipients.locked': 'This notice is no longer a draft; its recipient list is fixed.',
  'email_notices.recipients.not_computed':
    'Recipients have not been computed. Press "Compute recipients" to see who this would reach.',

  'email_notices.section.send': 'Send',
  'email_notices.section.send_hint':
    'The bulk domain has no sending history, so the first batch must be warmed up small. Both gates must pass before a real send.',
  'email_notices.gate.one_passed': 'Gate 1 recipients frozen',
  'email_notices.gate.one_blocked': 'Gate 1 recipients not frozen',
  'email_notices.gate.two_passed': 'Gate 2 test send succeeded',
  'email_notices.gate.two_blocked': 'Gate 2 no successful test send',
  'email_notices.gate.one_short': 'List',
  'email_notices.gate.two_short': 'Test',
  'email_notices.gate.two_short_recorded': 'Test on record',
  'email_notices.send.test_to': 'Test send to',
  'email_notices.send.test_hint':
    'Send yourself one. A test exercises the real bulk credential, the real template and the real SMTP conversation — the same sender every recipient gets.',
  'email_notices.send.test_action': 'Send a test',
  'email_notices.send.action': 'Send to {count} addresses',
  'email_notices.send.blocked_terminal': 'This notice is cancelled and can no longer be sent.',
  'email_notices.send.blocked_recipients': 'Freeze the recipient list first.',
  'email_notices.send.blocked_empty_list':
    'The frozen recipient list is empty, so there is no address to send to. Recompute in section 3, or widen the lookback window.',
  'email_notices.send.blocked_test': 'Get one test send to succeed first.',
  'email_notices.send.blocked_all_sent': 'Every address on the list has been delivered.',
  'email_notices.send.confirm_title': 'Send this mail for real?',
  'email_notices.send.confirm_consequence':
    'This press mails "{subject}" to {count} real customer addresses. A sent mail cannot be recalled.',
  'email_notices.send.confirm_idempotence':
    'Addresses already delivered will not receive it twice — the primary key is the idempotency key, which is what makes a retry safe.',
  'email_notices.send.confirm_type_label': 'Type the recipient count to confirm:',
  'email_notices.send.confirm_action': 'Send now',
  'email_notices.send.progress': 'Sending {done} / {total} — {sent} sent, {failed} failed. Keep this page open.',

  'email_notices.delivery.sent_count': '{count} delivered',
  'email_notices.delivery.failed_count': '{count} failed',
  'email_notices.delivery.retry_all': 'Retry all {count} failed',
  'email_notices.delivery.retry': 'Retry',
  'email_notices.delivery.column.attempts': 'Attempts',
  'email_notices.delivery.column.error': 'Last error',
  'email_notices.delivery.empty': 'No recipients frozen yet, so there is nothing to deliver.',
  'email_notices.recipient_status.pending': 'Pending',
  'email_notices.recipient_status.sent': 'Delivered',
  'email_notices.recipient_status.failed': 'Failed',

  'email_notices.cancel.action': 'Cancel notice',
  'email_notices.cancel.confirm_title': 'Cancel this notice?',
  'email_notices.cancel.confirm_body':
    'Cancelling sends nothing. Customers who already received it are not un-told — if a correction is warranted, send a new notice.',
  'email_notices.cancel.confirm_action': 'Cancel notice',

  'email_notices.drawer.new_title': 'New email notice',
  'email_notices.drawer.new_description':
    'Write the content and pick the audience first; recipients and sending open once the draft is saved.',
  'email_notices.drawer.title': '{title}',
  'email_notices.drawer.description': '{id} · {status}',
  'email_notices.drawer.create': 'Save draft',
  'email_notices.drawer.save': 'Save changes',
  'email_notices.drawer.gates_locked':
    'Sections 3 and 4 need a saved draft — recipients and sending both address one stored notice. Save the draft first.',
  'email_notices.drawer.edit_clears_gates':
    'Saving changes clears the frozen recipient list and the test send: a list computed against the old audience, and a rehearsal of the old copy, are not evidence about this one.',

  'email_notices.toast.created': 'Draft created.',
  'email_notices.toast.updated': 'Changes saved; both gates were reset.',
  'email_notices.toast.finalized': 'Recipient list frozen ({count} addresses).',
  'email_notices.toast.test_sent': 'Test send delivered to {to}; the send is unlocked.',
  'email_notices.toast.sent': '{sent} sent, {failed} failed, {deferred} deferred.',
  'email_notices.toast.cancelled': 'Notice cancelled.',

  // ── Editable mail copy (#1911) ──────────────────────────────────
  'email_templates.panel': 'Mail templates',
  'email_templates.empty': 'No templates yet.',
  'email_templates.error.load': 'Could not load templates',
  'email_templates.column.name': 'Template',
  'email_templates.column.key': 'Key',
  'email_templates.column.subject': 'Current subject',
  'email_templates.column.updated': 'Updated',
  'email_templates.save': 'Save template',
  'email_templates.drawer.title_none': 'Mail template',
  'email_templates.field.subject': 'Subject',
  'email_templates.field.body': 'Body',
  'email_templates.allowed_label': 'Placeholders this template may use',
  'email_templates.allowed_hint':
    'Substituted at send time. Anything outside this list is refused here rather than delivered braces and all.',
  'email_templates.test.caption': 'Test send',
  'email_templates.test.hint':
    'A test send uses the SAVED version. Save first if you want to check an edit.',
  'email_templates.test.to': 'Test send to',
  'email_templates.test.action': 'Send a test',
  'email_templates.toast.saved': 'Template saved.',
  'email_templates.toast.test_sent': 'Test send delivered to {to}.',
  'email_templates.name.balance_alert_warning': 'Balance alert · warning',
  'email_templates.name.balance_alert_critical': 'Balance alert · critical',
  'email_templates.name.balance_alert_recovered': 'Balance alert · recovered',
  'email_templates.name.notice_delist': 'Delist notice starting draft',
  'email_templates.name.notice_list': 'Listing notice starting draft',
  'email_templates.hint.balance_alert_warning':
    'Mailed by an unattended cron. An edit takes effect on the next pass.',
  'email_templates.hint.balance_alert_critical':
    'Mailed by an unattended cron, and this is the most urgent of the three balance tiers. An edit takes effect on the next pass.',
  'email_templates.hint.balance_alert_recovered':
    'Mailed by an unattended cron. An edit takes effect on the next pass.',
  'email_templates.hint.notice_delist':
    'Only a starting draft for a new notice — it is never sent on its own. Edit freely after loading it.',
  'email_templates.hint.notice_list':
    'Only a starting draft for a new notice — it is never sent on its own. Edit freely after loading it.',
  'email_templates.name.credit_due_soon_7d': 'Credit · due in 7 days',
  'email_templates.name.credit_due_soon_3d': 'Credit · due in 3 days',
  'email_templates.name.credit_due_today': 'Credit · due today',
  'email_templates.name.credit_frozen': 'Credit · new grants frozen',
  'email_templates.name.credit_overdue_reminder': 'Credit · overdue reminder',
  'email_templates.name.credit_stopped': 'Credit · service stopped',
  'email_templates.name.credit_restored': 'Credit · restored after settlement',
  'email_templates.hint.credit_due_soon_7d':
    'Mailed by the overdue ladder, unattended. An edit takes effect on the next letter.',
  'email_templates.hint.credit_due_soon_3d':
    'Mailed by the overdue ladder, unattended. An edit takes effect on the next letter.',
  'email_templates.hint.credit_due_today':
    'Mailed by the overdue ladder, unattended. Sent on the due date; it does not claim credit is already frozen.',
  'email_templates.hint.credit_frozen':
    'Mailed by the overdue ladder, unattended. Matches the D+1 freeze, not the due date itself.',
  'email_templates.hint.credit_overdue_reminder':
    'Mailed by the overdue ladder, unattended. Every 3 days from D+1; freeze day and stop day are skipped.',
  'email_templates.hint.credit_stopped':
    'Mailed by the overdue ladder, unattended. Matches the D+7 stop.',
  'email_templates.hint.credit_restored':
    'Mailed by the overdue ladder, unattended. Sent once when live debt clears.',
  'email_templates.name.credit_temp_expiring_3d': 'Credit · temporary credit expires in 3 days',
  'email_templates.name.credit_temp_expiring_1d': 'Credit · temporary credit expires in 1 day',
  'email_templates.hint.credit_temp_expiring_3d':
    'Mailed when temporary credit reaches the T-3 window. Same calendar as the internal 3-day card.',
  'email_templates.hint.credit_temp_expiring_1d':
    'Mailed when temporary credit reaches the T-1 window. Same calendar as the internal 1-day card.',
  'email_templates.name.codex_capacity_elevated': 'Codex capacity · elevated',
  'email_templates.name.codex_capacity_recovered': 'Codex capacity · recovered',
  'email_templates.hint.codex_capacity_elevated':
    'Mailed when Codex-lane at-capacity stays elevated. Round 2 owns the send; this copy is the reminder, not a measurement.',
  'email_templates.hint.codex_capacity_recovered':
    'Mailed when Codex-lane at-capacity has cleared. Round 2 owns the send; this copy is the reminder, not a measurement.',

  // ── Customer balance alerts, operator surface (#1892) ────────────
  'balance_alerts.title': 'Balance alerts',
  'balance_alerts.subtitle':
    'Balance alerts mail paying customers automatically. This page is that channel\'s instrument and its brake: the global switch, who the next pass would mail, who has already been mailed, and per-account mutes.',
  'balance_alerts.tabs_label': 'Switch the balance-alert view',
  'balance_alerts.tab.preview': 'Who is next',
  'balance_alerts.tab.history': 'What was sent',
  'balance_alerts.tab.suppressions': 'Muted accounts',
  'balance_alerts.panel.preview': 'Target population (computed, nothing sent)',
  'balance_alerts.panel.history': 'Send history (delivered)',
  'balance_alerts.panel.suppressions': 'Per-account suppression',

  'balance_alerts.error.generic': 'That did not go through. Try again.',
  'balance_alerts.error.load': 'Could not load this',
  'balance_alerts.error.load_hint':
    'The endpoint returned nothing. Retry, or check that ops-api can reach platform-api.',

  'balance_alerts.switch.caption': 'Global switch',
  'balance_alerts.switch.state_open': 'Enabled',
  'balance_alerts.switch.state_closed': 'Not enabled (switched off)',
  'balance_alerts.switch.state_never_opened': 'Not enabled (never opened)',
  'balance_alerts.switch.headline_open':
    'Balance alerts are enabled: the next pass will mail real customers.',
  'balance_alerts.switch.headline_closed':
    'Balance alerts are NOT enabled — somebody switched them off. Until they are switched back on, not one mail goes out.',
  'balance_alerts.switch.headline_never_opened':
    'Balance alerts are NOT enabled — they ship closed and nobody has ever opened them. No balance alert has ever reached a customer.',
  'balance_alerts.switch.attribution': 'Last changed by {who}, {when}.',
  'balance_alerts.switch.attribution_never':
    'Nobody has ever moved this switch; it still stands where the migration seeded it.',
  'balance_alerts.switch.actor_unknown': '(operator not recorded)',
  'balance_alerts.switch.reason_shown': 'Reason: {reason}',
  'balance_alerts.switch.toggle_label': 'Enable balance alerts',
  'balance_alerts.switch.toggle_hint':
    'The switch lives in the database and takes effect on the very next pass — no deploy. Every change is mirrored into ops_audit.',
  'balance_alerts.switch.reason_label': 'Reason (optional)',
  'balance_alerts.switch.reason_hint':
    'Kept on the switch and in the audit trail. It is how the next person to open this page knows why it is where it is.',
  'balance_alerts.switch.enable_title': 'Open the balance alerts?',
  'balance_alerts.switch.enable_consequence':
    'Once open, the next pass mails real paying customers about their own money. A sent mail cannot be recalled.',
  'balance_alerts.switch.enable_detail':
    'This press sends nothing by itself — the scheduled pass does. Check "Who is next" first and make sure that list is the list you mean to mail.',
  'balance_alerts.switch.enable_action': 'Open the alerts',
  'balance_alerts.switch.disable_title': 'Close the balance alerts?',
  'balance_alerts.switch.disable_consequence':
    'From the next pass on, no balance alert mail goes out at all.',
  'balance_alerts.switch.disable_detail':
    'Mail already sent is unaffected and no alert state is touched: customers are still over the line, this channel just stops speaking.',
  'balance_alerts.switch.disable_action': 'Close the alerts',
  'balance_alerts.switch.error': 'Could not read the switch',
  'balance_alerts.switch.error_hint':
    'Nothing here reads "enabled" or "not enabled" until the real state loads — guessing either way would mislead somebody about whether mail is going out right now.',

  'balance_alerts.severity.warning': 'Warning',
  'balance_alerts.severity.critical': 'Critical',
  'balance_alerts.verdict.healthy': 'Healthy',
  'balance_alerts.verdict.indeterminate': 'Not enough data',

  'balance_alerts.column.account': 'Account',
  'balance_alerts.column.verdict': 'Verdict',
  'balance_alerts.column.spendable': 'Spendable',
  'balance_alerts.column.days_left': 'Days left',
  'balance_alerts.column.burn': 'Burn',
  'balance_alerts.column.next_pass': 'Next pass',
  'balance_alerts.column.actions': 'Actions',
  'balance_alerts.column.sent_at': 'Sent at',
  'balance_alerts.column.message': 'Message',
  'balance_alerts.column.spendable_at_send': 'Spendable at send',
  'balance_alerts.column.days_left_at_send': 'Days left at send',
  'balance_alerts.column.brand': 'Brand',
  'balance_alerts.column.reason': 'Reason',
  'balance_alerts.column.suppressed_at': 'Muted at',
  'balance_alerts.column.suppressed_by': 'Muted by',

  'balance_alerts.preview.banner_open':
    'A forecast, not a record: if a pass ran now, {count} of the accounts below would be mailed. Nothing has been sent.',
  'balance_alerts.preview.banner_closed':
    'A forecast, not a record: {count} accounts are over the line, but the global switch is closed, so this pass would mail none of them.',
  'balance_alerts.preview.empty': 'No account is over the line right now.',
  'balance_alerts.preview.burn_per_day': '{amount}/day',
  'balance_alerts.preview.window': 'Measured over the last {days} days, from {from}.',
  'balance_alerts.preview.recompute': 'Recompute',
  'balance_alerts.preview.suppress': 'Mute alerts',

  'balance_alerts.next_pass.mails': 'Would mail',
  'balance_alerts.next_pass.escalates': 'Would escalate',
  'balance_alerts.next_pass.suppressed': 'Muted — nothing sent',
  'balance_alerts.next_pass.channel_closed': 'Switch closed — nothing sent',
  'balance_alerts.next_pass.already_alerted': 'Already told ({severity})',

  'balance_alerts.history.banner':
    'A record, not a forecast: each of the {count} rows below reached an inbox, and both money columns are the numbers as they stood AT SEND TIME, not now.',
  'balance_alerts.history.empty': 'No balance alert has been sent yet.',
  'balance_alerts.history.coverage_complete':
    'That is the complete record — all {count} balance alerts ever sent are listed here.',
  'balance_alerts.history.coverage_capped':
    'Showing the {count} most recent only. There may be older records that are not shown here.',
  'balance_alerts.history.kind_recovery': 'All clear (clearing {severity})',
  'balance_alerts.history.escalated': 'Escalated from warning',

  'balance_alerts.suppressions.empty': 'No account has its balance alerts muted.',
  'balance_alerts.suppressions.semantics':
    'A mute is not a permanent silence: it clears the account\'s alert state and the pass mails and writes nothing while it stands; lift it and, if the account is still over the line, the customer receives a brand-new alert.',
  'balance_alerts.suppressions.no_email': '(account no longer exists)',
  'balance_alerts.suppressions.restore': 'Restore alerts',

  'balance_alerts.suppress.confirm_title': 'Mute balance alerts for {email}?',
  'balance_alerts.suppress.confirm_consequence':
    'This clears the account\'s current alert state, including when their shortfall started — that instant survives only in the send history.',
  'balance_alerts.suppress.confirm_not_permanent':
    'This is not a permanent silence: lift the mute and, if they are still over the line, they receive a brand-new alert — because as far as they know, nothing was ever announced.',
  'balance_alerts.suppress.confirm_action': 'Mute alerts',
  'balance_alerts.suppress.reason_label': 'Reason (optional)',
  'balance_alerts.suppress.reason_hint': 'Kept on the suppression row and in the audit trail.',

  'balance_alerts.restore.confirm_title': 'Restore balance alerts for this account?',
  'balance_alerts.restore.confirm_consequence':
    'If this account is still over the line, the next pass mails them a fresh alert.',
  'balance_alerts.restore.confirm_detail':
    'This press sends nothing itself, and nothing is replayed for the muted period — the next pass judges the account from scratch.',
  'balance_alerts.restore.confirm_action': 'Restore alerts',

  'balance_alerts.toast.enabled': 'Balance alerts opened; the next pass will mail.',
  'balance_alerts.toast.disabled': 'Balance alerts closed; nothing goes out from the next pass on.',
  'balance_alerts.toast.suppressed': 'Balance alerts muted for {email}.',
  'balance_alerts.toast.suppressed_cleared':
    'Balance alerts muted for {email}, and their active alert state was cleared.',
  'balance_alerts.toast.restored':
    'Alerts restored; if the account is still over the line the next pass sends a fresh alert.',
  'balance_alerts.toast.restore_noop': 'That account was not muted — nothing changed.',

  'codex_capacity.title': 'Codex capacity alerts',
  'codex_capacity.subtitle':
    'This channel mails paying customers when Codex is at capacity. The page is its instrument and its brake: the global switch, the live latch, the knobs, a dry-run of the next pass, incidents, send history, mutes, excluded SKUs, and a whitelist test-send.',
  'codex_capacity.tabs_label': 'Switch the Codex capacity-alert view',
  'codex_capacity.tab.settings': 'Settings',
  'codex_capacity.tab.preview': 'Preview (no send, no write)',
  'codex_capacity.tab.incidents': 'Incidents',
  'codex_capacity.tab.history': 'Send history',
  'codex_capacity.tab.suppressions': 'Muted accounts',
  'codex_capacity.tab.excluded_skus': 'Excluded SKUs',
  'codex_capacity.tab.test_send': 'Test send',

  'codex_capacity.panel.settings': 'Alert knobs',
  'codex_capacity.panel.preview': 'Dry-run of this pass (computes, writes nothing, sends nothing)',
  'codex_capacity.panel.incidents': 'Incidents (newest first)',
  'codex_capacity.panel.history': 'Send history (delivered)',
  'codex_capacity.panel.suppressions': 'Per-account mute',
  'codex_capacity.panel.excluded_skus': 'SKU exclusion list',
  'codex_capacity.panel.test_send': 'Whitelist test send',

  'codex_capacity.error.generic': 'That did not go through. Try again.',
  'codex_capacity.error.load': 'Could not load this',
  'codex_capacity.error.load_hint':
    'The endpoint returned nothing. Retry, or check that ops-api can reach platform-api.',

  'codex_capacity.switch.caption': 'Global switch',
  'codex_capacity.switch.state_open': 'Enabled',
  'codex_capacity.switch.state_closed': 'Not enabled (switched off)',
  'codex_capacity.switch.state_never_opened': 'Not enabled (never opened)',
  'codex_capacity.switch.headline_open':
    'Codex capacity alerts are enabled: the next pass will mail real customers if the window is over the line.',
  'codex_capacity.switch.headline_closed':
    'Codex capacity alerts are NOT enabled — somebody switched them off. Until they are switched back on, not one mail goes out.',
  'codex_capacity.switch.headline_never_opened':
    'Codex capacity alerts are NOT enabled — they ship closed and nobody has ever opened them. No Codex capacity alert has ever reached a customer.',
  'codex_capacity.switch.attribution': 'Last changed by {who}, {when}.',
  'codex_capacity.switch.attribution_never':
    'Nobody has ever moved this switch; it still stands where the migration seeded it.',
  'codex_capacity.switch.actor_unknown': '(operator not recorded)',
  'codex_capacity.switch.reason_shown': 'Reason: {reason}',
  'codex_capacity.switch.toggle_label': 'Enable Codex capacity alerts',
  'codex_capacity.switch.toggle_hint':
    'The switch lives in the database and takes effect on the very next pass — no deploy. Every change is mirrored into ops_audit.',
  'codex_capacity.switch.reason_label': 'Reason (optional)',
  'codex_capacity.switch.reason_hint':
    'Kept on the switch and in the audit trail. It is how the next person to open this page knows why it is where it is.',
  'codex_capacity.switch.enable_title': 'Open Codex capacity alerts?',
  'codex_capacity.switch.enable_consequence':
    'Once open, the next pass mails real paying customers if Codex is at capacity. A sent mail cannot be recalled.',
  'codex_capacity.switch.enable_detail':
    'This press sends nothing by itself — the scheduled pass does. Check Preview first and make sure that list is the list you mean to mail.',
  'codex_capacity.switch.enable_action': 'Open the alerts',
  'codex_capacity.switch.disable_title': 'Close Codex capacity alerts?',
  'codex_capacity.switch.disable_consequence':
    'From the next pass on, no Codex capacity alert mail goes out at all.',
  'codex_capacity.switch.disable_detail':
    'Mail already sent is unaffected and the latch is not touched: the ratio is still what it is, this channel just stops speaking.',
  'codex_capacity.switch.disable_action': 'Close the alerts',
  'codex_capacity.switch.error': 'Could not read the switch',
  'codex_capacity.switch.error_hint':
    'Nothing here reads "enabled" or "not enabled" until the real state loads — guessing either way would mislead somebody about whether mail is going out right now.',

  'codex_capacity.toast.enabled': 'Codex capacity alerts opened; the next pass will mail if over the line.',
  'codex_capacity.toast.disabled': 'Codex capacity alerts closed; nothing goes out from the next pass on.',
  'codex_capacity.toast.settings_saved': 'Settings saved.',
  'codex_capacity.toast.suppressed': 'Codex capacity alerts muted for {account}.',
  'codex_capacity.toast.restored': 'Mute lifted for {account}.',
  'codex_capacity.toast.restore_noop': 'That account was not muted — nothing changed.',
  'codex_capacity.toast.excluded_added': 'SKU {sku} excluded from the alert and the tenant series.',
  'codex_capacity.toast.excluded_removed': 'SKU {sku} is back in the alert and the tenant series.',
  'codex_capacity.toast.excluded_noop': 'That SKU was not on the exclusion list — nothing changed.',
  'codex_capacity.toast.test_sent': 'Test send finished: {sent} delivered, {failed} failed.',

  'codex_capacity.state.caption': 'Current latch',
  'codex_capacity.state.phase_clear': 'Clear',
  'codex_capacity.state.phase_firing': 'Firing',
  'codex_capacity.state.window_ratio': 'Trailing window: {pct}% of {count} requests.',
  'codex_capacity.state.window_undersampled':
    'Trailing window is undersampled: {count} requests (need {min}). Treated as unknown, not as fine.',
  'codex_capacity.state.clear_below_since': 'Below the recover line since {when}.',
  'codex_capacity.state.clear_below_since_none': 'Not currently stamped as below the recover line.',
  'codex_capacity.state.next_step.disabled':
    'Alerts are not enabled. No mail and no phase change until the switch is opened.',
  'codex_capacity.state.next_step.archive_stale':
    'The archive is behind. No fire, recover, or catch-up mail until rollup writes a fresh bucket. A falling request count here is not "it got quiet".',
  'codex_capacity.state.next_step.catch_up':
    'This pass will still send elevated mail to {count} accounts that have not received it for this incident.',
  'codex_capacity.state.archive_stale':
    'Archive is stale. Latest bucket started {when}. This is not a recovery.',
  'codex_capacity.state.next_step.quiet_hours':
    'Quiet hours are in effect. Observation continues; no mail and no phase change until the window ends.',
  'codex_capacity.state.next_step.cooldown':
    'Cooldown after the last recovery is still running. A new fire waits; observation continues.',
  'codex_capacity.state.next_step.fire':
    'This pass would fire: send elevated mail and open an incident (ratio at or above {pct}%).',
  'codex_capacity.state.next_step.recover':
    'This pass would recover: send recovery mail and close the incident (ratio still below {pct}%).',
  'codex_capacity.state.next_step.firing_remaining':
    'If it stays below {pct}% for {minutes} more minutes, send recovery mail.',
  'codex_capacity.state.next_step.firing_no_stamp':
    'Ratio is not below the recover line ({pct}%). Recovery mail waits until it stays below that line for {minutes} minutes.',
  'codex_capacity.state.next_step.undersampled':
    'Not enough requests in the window ({count} < {min}) — treated as unknown, not as fine. No mail this pass.',
  'codex_capacity.state.next_step.hold':
    'Ratio is below the fire threshold ({pct}%). No mail this pass.',
  'codex_capacity.state.error': 'Could not read the current latch',
  'codex_capacity.state.error_hint':
    'The live phase is not guessed while this read is down — a guessed "clear" would hide an open incident.',

  'codex_capacity.settings.save': 'Save settings',
  'codex_capacity.settings.reason_label': 'Reason (optional)',
  'codex_capacity.settings.reason_hint': 'Kept on the settings row and in the audit trail.',
  'codex_capacity.settings.error.required': 'This field is required.',
  'codex_capacity.settings.error.number': 'Must be a number.',
  'codex_capacity.settings.error.integer': 'Must be a whole number.',
  'codex_capacity.settings.error.fire_pct':
    'Fire threshold must be greater than {min} and at most {max} percent.',
  'codex_capacity.settings.error.clear_pct_min': 'Recover threshold must be at least {min} percent.',
  'codex_capacity.settings.error.clear_pct_vs_fire':
    'Recover threshold must be less than or equal to the fire threshold.',
  'codex_capacity.settings.error.window_minutes': 'Observation window must be at least {min} minutes.',
  'codex_capacity.settings.error.clear_window_minutes':
    'Recover confirmation window must be at least {min} minutes.',
  'codex_capacity.settings.error.retention_hours': 'Archive retention must be at least {min} hours.',
  'codex_capacity.settings.error.rollup_lookback_minutes':
    'Rollup lookback must be at least {min} minutes.',
  'codex_capacity.settings.error.min_window_requests': 'Minimum window requests must be at least 0.',
  'codex_capacity.settings.error.audience_days': 'Audience lookback must be {min} to {max} days.',
  'codex_capacity.settings.error.max_sends_per_pass': 'Max sends per pass must be at least 1.',
  'codex_capacity.settings.error.cooldown_hours': 'Cooldown must be at least 0 hours (0 = off).',
  'codex_capacity.settings.error.quiet_hours_pair':
    'Quiet-hours start and end must both be set, or both left empty (off).',
  'codex_capacity.settings.error.quiet_hours_range': 'Quiet-hours must be {min} to {max} (hour of day).',
  'codex_capacity.settings.error.quiet_hours_tz': 'Quiet-hours timezone must not be empty.',
  'codex_capacity.settings.error.window_exceeds_retention':
    'Observation window cannot be longer than archive retention ({max} minutes).',
  'codex_capacity.settings.error.clear_window_exceeds_retention':
    'Recover confirmation window cannot be longer than archive retention ({max} minutes).',
  'codex_capacity.settings.fire_pct.label': 'Fire threshold (%)',
  'codex_capacity.settings.fire_pct.hint':
    'Trailing-window at-capacity ratio at or above this percent opens an incident. Greater than {min}, at most {max}. Unit: percent.',
  'codex_capacity.settings.clear_pct.label': 'Recover threshold (%)',
  'codex_capacity.settings.clear_pct.hint':
    'While firing, a ratio below this percent starts the recover clock. At least {min}, and no higher than the fire threshold. Unit: percent.',
  'codex_capacity.settings.window_minutes.label': 'Observation window (minutes)',
  'codex_capacity.settings.window_minutes.hint':
    'How far back the trailing window looks when computing the ratio. At least {min} minutes.',
  'codex_capacity.settings.clear_window_minutes.label': 'Recover confirmation window (minutes)',
  'codex_capacity.settings.clear_window_minutes.hint':
    'How long the ratio must stay below the recover threshold before recovery mail goes out. At least {min} minutes. This is wall-clock duration, not a single window reading.',
  'codex_capacity.settings.min_window_requests.label': 'Minimum requests in the window',
  'codex_capacity.settings.min_window_requests.hint':
    'Below this sample size the window is unknown, not fine — no fire, no recover. Unit: 2xx requests.',
  'codex_capacity.settings.audience_days.label': 'Audience lookback (days)',
  'codex_capacity.settings.audience_days.hint':
    'Who gets mailed: accounts that used Codex in this many days. {min} to {max}.',
  'codex_capacity.settings.max_sends_per_pass.label': 'Max sends per pass',
  'codex_capacity.settings.max_sends_per_pass.hint':
    'Hard cap on how many mails one pass delivers. Extras wait for the next pass as catch-up. At least 1.',
  'codex_capacity.settings.cooldown_hours.label': 'Cooldown (hours)',
  'codex_capacity.settings.cooldown_hours.hint':
    'After a recovery, block a new fire for this many hours. 0 = off. Unit: hours.',
  'codex_capacity.settings.quiet_start.label': 'Quiet hours start (hour)',
  'codex_capacity.settings.quiet_start.hint':
    'Hour of day 0–23 in the timezone below. Empty together with end = off. A start later than end wraps midnight.',
  'codex_capacity.settings.quiet_end.label': 'Quiet hours end (hour)',
  'codex_capacity.settings.quiet_end.hint':
    'Hour of day 0–23. Empty together with start = off. Inside the window: observation only, no mail, no phase change.',
  'codex_capacity.settings.quiet_tz.label': 'Quiet hours timezone',
  'codex_capacity.settings.quiet_tz.hint': 'IANA timezone name, e.g. Asia/Shanghai.',
  'codex_capacity.settings.retention_hours.label': 'Archive retention (hours)',
  'codex_capacity.settings.retention_hours.hint':
    'How long Codex capacity buckets are kept. At least {min} hours.',
  'codex_capacity.settings.rollup_lookback.label': 'Rollup lookback (minutes)',
  'codex_capacity.settings.rollup_lookback.hint':
    'How far back a rollup pass re-reads usage logs. At least {min} minutes.',

  'codex_capacity.preview.banner':
    'A dry-run, not a send: this pass would {decision}. {count} accounts would be mailed. Nothing has been sent and nothing has been written.',
  'codex_capacity.preview.banner_closed':
    'A dry-run, not a send: the global switch is closed, so this pass would mail nobody even if the window is over the line.',
  'codex_capacity.preview.empty': 'This pass would mail nobody.',
  'codex_capacity.preview.recompute': 'Recompute',
  'codex_capacity.preview.decision_fire': 'fire (elevated mail, open an incident)',
  'codex_capacity.preview.decision_recover': 'recover (recovery mail, close the incident)',
  'codex_capacity.preview.decision_hold': 'hold (no mail this pass unless catch-up applies)',
  'codex_capacity.preview.decision_catch_up':
    'catch up (elevated mail to accounts still owed on this incident)',
  'codex_capacity.preview.decision_archive_stale':
    'skip (archive is stale — no fire, recover, or catch-up)',
  'codex_capacity.column.actions': 'Actions',
  'codex_capacity.preview.column.account': 'Account',
  'codex_capacity.preview.column.email': 'Email (masked)',
  'codex_capacity.preview.masked_note':
    'Addresses are already masked by the backend. This page does not try to restore them.',
  'codex_capacity.preview.suppressed': '{count} muted accounts were held out of this list.',

  'codex_capacity.incidents.empty': 'No incident has been opened yet.',
  'codex_capacity.incidents.column.started': 'Started',
  'codex_capacity.incidents.column.ended': 'Ended',
  'codex_capacity.incidents.column.duration': 'Duration',
  'codex_capacity.incidents.column.fire_ratio': 'Fire ratio',
  'codex_capacity.incidents.column.peak': 'Peak ratio',
  'codex_capacity.incidents.column.elevated': 'Elevated mailed',
  'codex_capacity.incidents.column.recovered': 'Recovered mailed',
  'codex_capacity.incidents.open': 'Still open',
  'codex_capacity.incidents.view_history': 'Send history',
  'codex_capacity.incidents.duration_minutes': '{minutes} min',

  'codex_capacity.history.banner':
    'A record, not a forecast: each of the {count} rows below reached an inbox.',
  'codex_capacity.history.empty': 'No Codex capacity alert has been sent yet.',
  'codex_capacity.history.coverage_complete':
    'That is the complete record — all {count} mails listed here.',
  'codex_capacity.history.coverage_capped':
    'Showing the {count} most recent only. There may be older records that are not shown here.',
  'codex_capacity.history.filter': 'Filtered to incident {id}.',
  'codex_capacity.history.filter_clear': 'Show all',
  'codex_capacity.history.column.sent_at': 'Sent at',
  'codex_capacity.history.column.account': 'Account',
  'codex_capacity.history.column.kind': 'Kind',
  'codex_capacity.history.column.window': 'Window % at send',
  'codex_capacity.history.column.incident': 'Incident',
  'codex_capacity.history.kind.elevated': 'Elevated',
  'codex_capacity.history.kind.recovered': 'Recovered',
  'codex_capacity.history.test_send': 'Test send',
  'codex_capacity.history.test_send_hint':
    'A whitelist rehearsal. It did not arm or clear the latch.',

  'codex_capacity.suppressions.empty': 'No account has its Codex capacity alerts muted.',
  'codex_capacity.suppressions.semantics':
    'A mute silences this named customer on the next pass. It does not recall mail already sent.',
  'codex_capacity.suppressions.add': 'Mute an account',
  'codex_capacity.suppressions.account_id': 'Account id',
  'codex_capacity.suppressions.reason': 'Reason (optional)',
  'codex_capacity.suppressions.confirm_title': 'Mute Codex capacity alerts for {account}?',
  'codex_capacity.suppressions.confirm_consequence':
    'The next pass will skip this named customer. Mail already sent is unaffected.',
  'codex_capacity.suppressions.confirm_action': 'Mute alerts',
  'codex_capacity.suppressions.restore_title': 'Lift the mute for {account}?',
  'codex_capacity.suppressions.restore_consequence':
    'If an incident is open, the next pass may mail this customer the elevated (or recovery) letter they were owed.',
  'codex_capacity.suppressions.restore_action': 'Lift mute',
  'codex_capacity.suppressions.column.account': 'Account',
  'codex_capacity.suppressions.column.reason': 'Reason',
  'codex_capacity.suppressions.column.created': 'Muted at',
  'codex_capacity.suppressions.column.by': 'Muted by',
  'codex_capacity.suppressions.remove': 'Lift mute',

  'codex_capacity.excluded.empty': 'No SKU is excluded.',
  'codex_capacity.excluded.semantics':
    'An excluded SKU is cut from the alert evaluator AND from the tenant 24h series. Token-zero 2xx on a per-call SKU is design state, not an at-capacity signal.',
  'codex_capacity.excluded.add': 'Exclude a SKU',
  'codex_capacity.excluded.sku': 'SKU name',
  'codex_capacity.excluded.confirm_title': 'Exclude {sku}?',
  'codex_capacity.excluded.confirm_consequence':
    'This SKU will leave both the alert window and the tenant Codex capacity chart.',
  'codex_capacity.excluded.confirm_action': 'Exclude SKU',
  'codex_capacity.excluded.remove_title': 'Put {sku} back in?',
  'codex_capacity.excluded.remove_consequence':
    'This SKU will count in the alert window and the tenant series again.',
  'codex_capacity.excluded.remove_action': 'Remove exclusion',
  'codex_capacity.excluded.column.sku': 'SKU',
  'codex_capacity.excluded.column.created': 'Excluded at',
  'codex_capacity.excluded.column.by': 'Excluded by',
  'codex_capacity.excluded.remove': 'Remove',

  'codex_capacity.test_send.account_ids': 'Account ids',
  'codex_capacity.test_send.account_ids_hint':
    'One id per line, or comma-separated. Every id must resolve or the call sends nothing.',
  'codex_capacity.test_send.kind': 'Which letter',
  'codex_capacity.test_send.kind_elevated': 'Elevated (at capacity)',
  'codex_capacity.test_send.kind_recovered': 'Recovered (all clear)',
  'codex_capacity.test_send.submit': 'Send test mail',
  'codex_capacity.test_send.need_accounts': 'Name at least one account id.',
  'codex_capacity.test_send.confirm_title': 'Send a real Codex capacity test mail?',
  'codex_capacity.test_send.confirm_consequence':
    'This is a real mail delivery to the live addresses on those accounts. A mail that has gone out cannot be recalled. Dev and staging send for real too, not just production.',
  'codex_capacity.test_send.confirm_detail':
    'History rows are marked test-send and the latch is not touched. That does not make the mail itself a drill.',
  'codex_capacity.test_send.confirm_action': 'Send real mail',

  'egress_lines.title': 'Edge gateways',
  'egress_lines.subtitle': 'Which serving gateway hits which upstream, and which outbound path it takes. Distinct from “VPS nodes”, which is the host registry.',
  'egress_lines.authority_note':
    'Cell paths come from each node’s current-state snapshot (aggregated by upstream region), not a centre verdict, and never from the node’s globally selected path for a different dest. Snapshots older than 5 minutes are marked stale and dimmed.',
  'egress_lines.matrix.empty_cell_hint':
    '“No traffic” means this gateway has not recently hit an upstream in that dest. “Unknown (stale snapshot)” means there was traffic but the snapshot is too old. Same-region dests are always direct.',
  'egress_lines.matrix.presence_note':
    'The default list is instances with traffic or a snapshot in the last {minutes} minutes. During a rolling deploy both old and new stay visible; superseded instances fold into the historical group and remain expandable for 24h.',
  'egress_lines.matrix.title': 'Line matrix',
  'egress_lines.matrix.empty': 'No serving-node traffic in the last 24h, and no probe snapshots. Probes do not report while the exit is inactive.',
  'egress_lines.matrix.node': 'Node',
  'egress_lines.matrix.freshness': 'Freshness',
  'egress_lines.matrix.freshness_ago': 'data {seconds}s ago',
  'egress_lines.matrix.freshness_unknown': 'no capture time',
  'egress_lines.matrix.stale': 'stale',
  'egress_lines.matrix.traffic_1h': 'Last 1h',
  'egress_lines.matrix.traffic_24h': 'Last 24h',
  'egress_lines.matrix.exit': 'Exit mix',
  'egress_lines.matrix.exit_empty': 'no traffic',
  'egress_lines.matrix.requests': '{count} req',
  'egress_lines.matrix.tokens': '{count} tok',
  'egress_lines.badge.excellent': 'excellent',
  'egress_lines.badge.good': 'good',
  'egress_lines.badge.degraded': 'degraded',
  'egress_lines.badge.breaker': 'breaker',
  'egress_lines.badge.same_region': 'same-region direct',
  'egress_lines.badge.no_traffic': 'no traffic',
  'egress_lines.badge.stale_unknown': 'unknown (stale snapshot)',
  'egress_lines.badge.empty': 'no data',
  'egress_lines.matrix.serving': 'serving',
  'egress_lines.matrix.historical': 'historical',
  'egress_lines.matrix.empty_serving': 'No serving instances right now. Superseded instances are in the historical group below.',
  'egress_lines.matrix.historical_group': 'Historical instances in the last 24h ({count})',
  'egress_lines.replication.title': 'Replication links',
  'egress_lines.replication.empty': 'No replication-health samples yet. Uninitialized environments stay silent.',
  'egress_lines.replication.link': 'Link',
  'egress_lines.replication.direction': 'Direction',
  'egress_lines.replication.downlink.direction': 'Downlink · Tokyo → Shanghai · config-plane table set',
  'egress_lines.replication.uplink.direction': 'Uplink · Shanghai → Tokyo · usage-plane table set',
  'egress_lines.replication.status': 'Status',
  'egress_lines.replication.lag': 'Backlog',
  'egress_lines.replication.lag_hint':
    'lag_bytes is the WAL the slot still retains (restart_lsn), not undelivered data; it jumps with any bulk write and only pages crit at 2 GiB. apply_lag_bytes (confirmed_flush_lsn) is what the subscriber has not yet confirmed — CN for the downlink, Tokyo for the uplink (via the CN publisher slot): warn at 512 MiB, crit at 2 GiB. A few MB oscillating with writes is normal.',
  'egress_lines.replication.lag_hint_label': 'What backlog means',
  'egress_lines.replication.lag_thresholds': 'warn {warn} / crit {crit}',
  'egress_lines.replication.delay': 'Delay',
  'egress_lines.replication.delay_hint_label': 'What delay means',
  'egress_lines.replication.delay_hint':
    'Age of the subscriber’s latest_end_time. Uplink is sampled in Tokyo; downlink is pulled from the CN gateway over a read-only internal endpoint. If that pull fails or the sample is older than 10 minutes, status is “unknown”, not “ok”. Backlog is slot capacity; delay is time — two different signals.',
  'egress_lines.replication.drift': 'Drift',
  'egress_lines.replication.sampled': 'Sampled',
  'egress_lines.replication.sampled_ago': '{seconds}s ago',
  'egress_lines.replication.sampled_unknown': 'no sample time',
  'egress_lines.replication.status.absent': 'absent',
  'egress_lines.replication.status.ok': 'ok',
  'egress_lines.replication.status.warn': 'warn',
  'egress_lines.replication.status.crit': 'crit',
  'egress_lines.replication.status.unknown': 'unknown/stale',
  'egress_lines.links.title': 'Line list',
  'egress_lines.links.empty': 'No exits are registered yet.',
  'egress_lines.links.id': 'Line',
  'egress_lines.links.region': 'Region',
  'egress_lines.links.endpoint': 'Endpoint',
  'egress_lines.links.status': 'Status',
  'egress_lines.links.heartbeat': 'Heartbeat',
  'egress_lines.links.note': 'Note',
  'egress_lines.links.toggle': 'Enable line',
  'egress_lines.links.toast.disabled': 'Line disabled',
  'egress_lines.links.toast.enabled': 'Line re-enabled',
  'egress_lines.links.confirm.disable_title': 'Disable this exit line?',
  'egress_lines.links.confirm.enable_title': 'Re-enable this exit line?',
  'egress_lines.links.confirm.body': 'This writes the stored status of {id}. Heartbeat-derived degraded is not rewritten by this action.',
  'egress_lines.links.confirm.disable': 'Disable line',
  'egress_lines.links.confirm.enable': 'Enable line',
  'egress_lines.links.dispatch': 'Dispatch',
  'egress_lines.links.dispatch.standby': 'standby',
  'egress_lines.links.dispatch.admitted': 'admitted',
  'egress_lines.links.dispatch.admit': 'Admit traffic',
  'egress_lines.links.dispatch.revoke': 'Revoke admission',
  'egress_lines.links.dispatch.lag_note':
    'Admission is not instant: gateways observe dispatch eligibility through a 60-second snapshot, so traffic may start or stop up to 60 seconds later.',
  'egress_lines.links.dispatch.hint_label': 'What dispatch means',
  'egress_lines.links.dispatch.hint':
    'Standby is registered but carries no tenant traffic; admitted enters the candidate set. Gateways observe this change in at most 60 seconds.',
  'egress_lines.links.dispatch.confirm.title': 'Admit this exit line?',
  'egress_lines.links.dispatch.confirm.body':
    '{id} will enter the candidate set and start carrying real tenant traffic. Gateways observe this change through a 60-second snapshot, so traffic may start up to 60 seconds later.',
  'egress_lines.links.dispatch.confirm.admit': 'Admit traffic',
  'egress_lines.links.dispatch.toast.enabled':
    'Traffic admitted. Gateways may take up to 60 seconds to start sending traffic.',
  'egress_lines.links.dispatch.toast.disabled':
    'Admission revoked. The line is standby again and leaves the candidate set. Gateways may take up to 60 seconds to stop sending traffic.',
  'egress_lines.node.title': 'Node {node}',
  'egress_lines.node.subtitle': 'Gateway region {region}',
  'egress_lines.node.paths': 'Candidate path scores',
  'egress_lines.node.paths_empty': 'This node has no snapshot yet.',
  'egress_lines.node.path': 'Path',
  'egress_lines.node.sort_key': 'Sort key',
  'egress_lines.node.success': 'Success',
  'egress_lines.node.samples': 'Samples',
  'egress_lines.node.qualified': 'Qualified',
  'egress_lines.node.selected': 'Selected',
  'egress_lines.node.dwell': 'Dwell start',
  'egress_lines.node.yes': 'yes',
  'egress_lines.node.no': 'no',
  'egress_lines.node.current': 'current',
  'egress_lines.node.live': 'Live path',
  'egress_lines.node.live_chip': 'live',
  'egress_lines.node.selection': 'Current selection {path}, dwell started {dwell}',
  'egress_lines.node.timeline': 'Switch timeline',
  'egress_lines.node.timeline_empty': 'No switch events yet.',
  'egress_lines.node.when': 'When',
  'egress_lines.node.dest': 'Dest',
  'egress_lines.node.from': 'From',
  'egress_lines.node.to': 'To',
  'egress_lines.node.reason': 'Reason',
  'egress_lines.node.scores': 'Scores',
  'egress_lines.traffic.title': 'Outbound decisions (gateway × upstream)',
  'egress_lines.traffic.empty': 'No per-upstream traffic samples yet. Cold start stays direct; rows appear after exploration or production traffic.',
  'egress_lines.traffic.hint':
    'Under each serving gateway: which upstreams it hits, the live path, why challengers lost, and recent health. Reasons are reported by the adjudicator, not inferred from numbers here.',
  'egress_lines.traffic.table_caption': 'The same data as a flat table, for copying path and upstream ids.',
  'egress_lines.traffic.upstream': 'Upstream',
  'egress_lines.traffic.upstream_unknown': 'unknown upstream',
  'egress_lines.traffic.ttfb': 'p50 TTFT',
  'egress_lines.traffic.last_sample': 'Last sample',
  'egress_lines.traffic.last_explore': 'Last explore',
  'egress_lines.node.traffic': 'Measured (path × upstream)',
  'egress_lines.node.upstream': 'Upstream',
  'egress_lines.node.ttfb': 'p50 TTFT',
  'egress_lines.node.last_explore': 'Last explore',
  'egress_lines.decision.live': 'Live path',
  'egress_lines.decision.candidate': 'Candidate',
  'egress_lines.decision.health': 'Recent',
  'egress_lines.decision.health_empty': 'No samples on this path yet',
  'egress_lines.decision.same_region': 'same region',
  'egress_lines.decision.cross_region': 'cross-region',
  'egress_lines.decision.region_unknown': 'region unknown',
  'egress_lines.decision.samples': '{count} samples',
  'egress_lines.decision.no_samples': 'no samples',
  'egress_lines.decision.success': '{pct}% success',
  'egress_lines.decision.ttfb': 'p50 TTFT {ms}ms',
  'egress_lines.decision.reason.same_region_direct': 'same-region direct',
  'egress_lines.decision.reason.no_evidence_direct': 'no evidence, defaulting to direct',
  'egress_lines.decision.reason.pinned': 'ops pin',
  'egress_lines.decision.reason.breaker_open': 'peer breaker is open',
  'egress_lines.decision.reason.faster': 'faster: {selected} vs {path} {vs}',
  'egress_lines.decision.reason.hold_margin': 'margin not enough, holding ({selected} vs {vs})',
  'egress_lines.decision.reason.hold_unopposed': 'no challenger with evidence, holding — wait for exploration, do not tune the margin',
  'egress_lines.decision.reason.hold_dwell': 'dwell not elapsed, holding (challenger {vs})',
  'egress_lines.decision.reason.bootstrap': 'first pick',
  'egress_lines.decision.reason.recovery': 'incumbent unqualified, moved to a better path',
  'egress_lines.decision.reason.unknown': 'no decision yet (older snapshot)',
  'egress_lines.tabs_label': 'Edge gateway views',
  'egress_lines.tab.matrix': 'Line matrix',
  'egress_lines.tab.policies': 'Egress policies',
  'egress_lines.tab.clusters': 'Cluster members',
  'egress_lines.policies.list_title': 'Policies',
  'egress_lines.policies.new': 'New',
  'egress_lines.policies.new_name': 'Policy name',
  'egress_lines.policies.floor_badge': 'floor',
  'egress_lines.policies.rule_count': '{count} rules',
  'egress_lines.policies.assigned_count': '{count} customers assigned',
  'egress_lines.policies.unassigned_customers': 'Every unassigned customer',
  'egress_lines.policies.assigned_title': 'Assigned customers',
  'egress_lines.policies.assign': 'Assign',
  'egress_lines.policies.unassign': 'Unassign',
  'egress_lines.policies.assign_placeholder': 'Search by email or company name',
  'egress_lines.policies.no_email': 'no email',
  'egress_lines.policies.assign_confirm_title': 'Assign this customer?',
  'egress_lines.policies.assign_confirm_body': 'Assign {customer} to policy {policy}.',
  'egress_lines.policies.unassign_confirm_title': 'Remove this assignment?',
  'egress_lines.policies.unassign_confirm_body': 'Remove {customer} from policy {policy}.',
  'egress_lines.policies.no_assignments': 'No customers assigned yet',
  'egress_lines.policies.inherit_note': 'Rules match by specificity. Misses inherit the global default; grey rows are inherited and read-only.',
  'egress_lines.policies.emergency': 'Emergency direct',
  'egress_lines.policies.emergency_confirm_title': 'Turn on emergency direct?',
  'egress_lines.policies.emergency_confirm_body': 'All traffic under this policy will go direct until the switch is turned off.',
  'egress_lines.policies.preview': 'Preview',
  'egress_lines.policies.save': 'Save and apply',
  'egress_lines.policies.add_rule': 'Add a rule',
  'egress_lines.policies.delete_rule': 'Delete',
  'egress_lines.policies.col.index': '#',
  'egress_lines.policies.col.ingress': 'Ingress',
  'egress_lines.policies.col.upstream': 'Upstream',
  'egress_lines.policies.col.via': 'Via',
  'egress_lines.policies.col.fallback': 'If cluster is down',
  'egress_lines.policies.col.source': 'Source',
  'egress_lines.policies.col.note': 'Note',
  'egress_lines.policies.source.own': 'This policy',
  'egress_lines.policies.source.inherited': 'Inherited · global default',
  'egress_lines.policies.via.direct': 'Direct',
  'egress_lines.policies.via.cluster': '{region} cluster',
  'egress_lines.policies.fallback.direct': 'Fall back to direct',
  'egress_lines.policies.fallback.none': 'Fail',
  'egress_lines.policies.fallback.na': '—',
  'egress_lines.policies.upstream.any': '*',
  'egress_lines.policies.upstream.region': 'region = {region}',
  'egress_lines.policies.upstream.region_legacy': 'region = {region} (legacy)',
  'egress_lines.policies.ingress.any': '*',
  'egress_lines.policies.ingress.gateway': '{region} gateway',
  'egress_lines.policies.saved': 'Policy saved and applied',
  'egress_lines.policies.created': 'Policy created',
  'egress_lines.policies.assigned': 'Customer assigned',
  'egress_lines.policies.unassigned': 'Assignment removed',
  'egress_lines.policies.create_name_required': 'Enter a policy name',
  'egress_lines.policies.empty': 'No egress policies yet',
  'egress_lines.policies.version': 'v{version}',
  'egress_lines.policies.preview_title': 'Preview where this request goes',
  'egress_lines.policies.preview_org': 'Customer',
  'egress_lines.policies.preview_ingress': 'Ingress',
  'egress_lines.policies.preview_upstream': 'Upstream',
  'egress_lines.policies.preview_resolve': 'Resolve',
  'egress_lines.policies.preview.via': 'Verdict',
  'egress_lines.policies.preview.matched': 'Matched rule',
  'egress_lines.policies.preview.inherited': 'Inherited',
  'egress_lines.policies.preview.inherited_yes': 'Yes (global default)',
  'egress_lines.policies.preview.inherited_no': 'No (this policy hit)',
  'egress_lines.policies.preview.fallback': 'If cluster is down',
  'egress_lines.policies.preview.members': 'Cluster members',
  'egress_lines.policies.preview.member_id': 'egress_id',
  'egress_lines.policies.preview.member_eligible': 'Eligible',
  'egress_lines.policies.preview.member_reason': 'Reason',
  'egress_lines.policies.preview.yes': 'yes',
  'egress_lines.policies.preview.no': 'no',
  'egress_lines.policies.preview.rule_index': 'rule {index}',
  'egress_lines.clusters.title': '{region} cluster · {count} members',
  'egress_lines.clusters.empty': 'No egress links yet',
  'egress_lines.clusters.heartbeat_age': 'Heartbeat age',
  'egress_lines.clusters.reason': 'Health reason',
  'egress_lines.clusters.age_seconds': '{seconds}s ago',
  'egress_lines.clusters.age_unknown': 'unknown',
  'egress_lines.policies.issue.same_region': 'The cluster cannot equal the ingress region',
  'egress_lines.policies.issue.empty_rules': 'At least one rule is required',
  'egress_lines.policies.version_conflict': 'This policy was updated elsewhere. Reload, then save again.',
  'egress_lines.policies.delete': 'Delete policy',
  'egress_lines.policies.delete_confirm_title': 'Delete this policy?',
  'egress_lines.policies.delete_confirm_body': 'An unassigned non-default policy will be permanently deleted.',
  'egress_lines.policies.deleted': 'Policy deleted',
  'egress_lines.policies.preview_ingress_empty': 'No ingress regions available',
  'egress_lines.policies.preview.policy_version': 'Policy version',
  'egress_lines.policies.preview.cached_at': 'Node cache',
  'egress_lines.policies.preview.selected_path': 'This node’s path',
  'egress_lines.policies.preview.selected_reason': 'Selection reason',
  'egress_lines.policies.preview.cluster_down': 'Cluster empty',
  'egress_lines.policies.preview.cluster_down_yes': 'yes',
  'egress_lines.policies.preview.cluster_down_no': 'no',
  'egress_lines.policies.preview.reason.policy_direct': 'policy direct',
  'egress_lines.policies.preview.reason.policy_cluster': 'policy cluster',
  'egress_lines.policies.preview.reason.emergency_direct': 'emergency direct',
  'egress_lines.policies.preview.reason.cluster_down': 'cluster down',
  'egress_lines.policies.preview.reason.token_unset': 'exit token unset',
  'egress_lines.member_reason.ok': 'eligible',
  'egress_lines.member_reason.standby': 'standby (not admitted)',
  'egress_lines.member_reason.stale_heartbeat': 'stale heartbeat',
  'egress_lines.member_reason.breaker_open': 'breaker open',
  'egress_lines.member_reason.probe_failing': 'probe failing',
  'egress_lines.member_reason.same_region': 'same-region excluded',

  'upstream_detail.config.region': 'Region',
  'upstream_detail.config.region.hint': 'Where the endpoint actually answers from. Automatic suggestion (RTT triangulation) is a later enhancement — this release does not invent one.',
  'upstream_detail.config.pin': 'Pinned egress line',
  'upstream_detail.config.pin.empty': 'Not pinned (scored selection)',
  'upstream_detail.config.pin.badge': 'PIN',
  'upstream_detail.config.pin.hint': 'Honoured before scoring. The pinned link must be derived-active or the pin is ignored — pin is not a health-check bypass.',

  'tenant_routing.effective.egress_path.direct': 'Egress: same-region direct ({gateway} → {dest})',
  'tenant_routing.effective.egress_path.via': 'Egress: via {path} to {dest} (this node {gateway})',
  'tenant_routing.effective.egress_path.unknown': 'Egress: upstream region unknown, treating as direct',
  'tenant_routing.effective.egress_path.no_scores': 'Egress: no scores yet, this node goes direct',
  'tenant_routing.effective.egress_path.pinned': 'Egress: pinned {path} to {dest}',

  // ── Health perspective ──────────────────────────────────────────
  'health_perspective.title': 'Health perspective',
  'health_perspective.sub': 'Per SKU: who is carrying it, how well, and at what margin',
  'health_perspective.window.5m': '5 min',
  'health_perspective.window.1h': '1 hour',
  'health_perspective.window.24h': '24 hours',
  'health_perspective.window.7d': '7 days',
  'health_perspective.window.aria': 'Time window',
  'health_perspective.window.bounds': '{from} to {to}',
  'health_perspective.auto_refresh': 'Auto refresh',
  'health_perspective.refresh': 'Refresh',
  'health_perspective.refreshing': 'Refreshing',
  'health_perspective.generated_at': 'Data as of {time}',
  'health_perspective.routing_source': 'Links from routing config "{name}"',
  'health_perspective.routing_source_unknown': 'No global default routing config found; links are empty',
  'health_perspective.loading': 'Assembling the health view',
  'health_perspective.error.title': 'Cannot read health data',
  'health_perspective.error.hint': 'At least one of the traffic, billing and routing layers did not answer. Retry; if it keeps failing, check the settlement and probe jobs under /system/cron-runs.',
  'health_perspective.degraded.title': 'Some data is missing',
  'health_perspective.degraded.traffic': 'Traffic layer failed: health and observed share cannot be judged. Shown as unknown, not zero.',
  'health_perspective.degraded.money_sku': 'SKU billing layer failed: margins are blank, not substituted with zero.',
  'health_perspective.degraded.money_upstream': 'Upstream billing layer failed: upstream margins are blank, not substituted with zero.',
  'health_perspective.degraded.routing': 'Routing layer failed: the retry ladders are empty; the SKU rows still render.',
  'health_perspective.degraded.upstreams': 'Upstream list failed: legs can only be named by channel, not by supplier. Nothing else is affected.',

  'health_perspective.summary.margin': 'Blended margin',
  'health_perspective.summary.requests': 'Requests',
  'health_perspective.summary.downstream_error_rate': 'Downstream failures',
  'health_perspective.summary.tenant_error_rate': 'Customer-facing failures',
  'health_perspective.summary.tenant_error_hint': 'The share of requests the customer finally saw fail, including the ones our own gate refused on purpose. A request the retry ladder rescued succeeded and is not counted here. It judges neither supply nor us — read the upstream and downstream cards beside it for those.',
  'health_perspective.summary.supply_error_rate': 'Upstream failures',
  'health_perspective.summary.fallback_rate': 'Fallback rate',
  'health_perspective.summary.first_byte': 'Avg first byte',
  'health_perspective.summary.revenue': 'Revenue',
  'health_perspective.summary.cost': 'Upstream cost',
  'health_perspective.summary.profit': 'Gross profit',
  'health_perspective.summary.unrouted': 'Unrouted SKUs',
  'health_perspective.summary.single_point': 'Single-point SKUs',
  'health_perspective.summary.unhealthy_upstreams': 'Unhealthy upstreams',
  'health_perspective.summary.unsettled': 'Unsettled',
  'health_perspective.summary.unsettled_hint': 'This window has traffic but no rated ledger rows yet — rating settles asynchronously, which is normal on a short window. No 0% is shown here; that would claim we sold at cost.',

  'health_perspective.scope.aria': 'Table scope',
  'health_perspective.scope.traffic': 'With traffic',
  'health_perspective.scope.problems': 'Needs attention',
  'health_perspective.scope.all': 'All',
  'health_perspective.scope.count': 'Table shows {shown} / {total} SKUs',
  'health_perspective.scope.note': 'The KPIs above always cover the whole fleet. Scope filters the table only and never changes a headline number.',
  'health_perspective.scope.empty.title': 'No SKU in this scope',
  'health_perspective.scope.empty.hint': 'Nothing in the window matches this scope. Switch to All to see the zero-traffic catalog SKUs.',

  'health_perspective.table.title': 'Retry ladder per SKU',
  'health_perspective.table.col.sku': 'Downstream SKU',
  'health_perspective.table.col.outcome': 'Outcome mix',
  'health_perspective.table.col.requests': 'Requests',
  'health_perspective.table.col.observed_share': 'Observed share',
  'health_perspective.table.col.supply': 'Upstream failures',
  'health_perspective.table.col.downstream': 'Downstream failures',
  'health_perspective.table.col.latency': 'First byte',
  'health_perspective.table.col.margin': 'Margin',
  'health_perspective.table.denominator': '{errors} / {requests}',
  'health_perspective.table.cell.supply_detail': 'failed {errors} · rescued {fallbacks}',
  'health_perspective.table.cell.worst_leg': 'worst leg {channel} {rate}',
  'health_perspective.table.cell.slow_share': 'slow {rate}',
  'health_perspective.table.cell.downstream_detail': '{errors} / {requests} attributable',
  'health_perspective.table.supply_chip.ok': 'Supply healthy',
  'health_perspective.table.supply_chip.warn': 'Supply wobbling',
  'health_perspective.table.supply_chip.bad': 'Supply broken',
  'health_perspective.table.supply_chip.no_traffic': 'Supply idle',
  'health_perspective.table.supply_chip.unknown': 'Supply unknown',
  'health_perspective.table.subject_note':
    'A SKU is graded on downstream failures: requests refused on their own merits (admission, authentication, malformed payload, balance, account unavailable) leave BOTH sides of that fraction, because a gate doing its job is not our outage. An unavailable model counts AGAINST us: we accepted the tenant and the request and then had nowhere to route it, which is our problem and not the gate’s credit. Supply is judged from this SKU’s OWN legs — how much each one carried and how much of that failed — not from a SKU-wide total: a leg that serves other SKUs fine while failing every request THIS one sends it has really happened. The customer-facing rate is listed separately; it is a fact about one customer and grades nobody.',
  'health_perspective.table.default_order': 'Sorted worst grade first, then by requests; click a column header to change it',
  'health_perspective.table.endpoint_unknown': 'unknown',
  'health_perspective.table.empty.title': 'Nothing to show for this window',
  'health_perspective.table.empty.hint': 'No traffic and no routing targets in the window. Try a longer window, or check the global default pool under /supply/tenant-routing.',
  'health_perspective.table.rung.active': 'First attempt {pct}',
  'health_perspective.table.rung.reserve': 'Fallback only',
  'health_perspective.table.rung.disabled': 'Disabled',
  'health_perspective.table.rung.volume': 'dialled {requests} ({share})',
  'health_perspective.table.rung.attempt_failed': 'attempt failures {attempts} (429 {rateLimited})',
  'health_perspective.table.rung.failed': 'failed {errors} ({rate})',
  'health_perspective.table.rung.failed_at_least': 'failed ≥{errors} (≥{rate})',
  'health_perspective.table.rung.refused': 'refused {refused}',
  'health_perspective.table.rung.failed_unknown': 'failure rate unknown (this leg did not report how many never reached an upstream, so gate refusals cannot be separated from real failures)',
  'health_perspective.table.rung.no_traffic': 'no traffic',
  'health_perspective.table.rung.legs_unavailable': 'This gateway cannot report per-SKU legs, so how much each leg actually carried is unknown',
  'health_perspective.table.rung.bar_aria': 'carried {share} of this SKU ({requests} requests), of which {refused} never reached an upstream and {errors} genuinely failed ({rate}); configured for {configured}',
  'health_perspective.table.rung.bar_aria_unknown': 'carried {share} of this SKU ({requests} requests), failure rate unknown; configured for {configured}',
  'health_perspective.table.ladder_axis_note': 'Every rung is drawn on one shared axis whose full width is this SKU’s requests (each is exactly one first attempt): bar length = the share of requests that leg was dialled for (first attempts that got 429 and were rescued included), the grey part = requests our gate refused before any upstream was reached (not that leg’s failures), the red part = what genuinely failed on it (failed dials the retry chain moved past included), the tick = the first-attempt share it was configured for. A first-attempt rung whose bar does not line up with its tick is configured-versus-actual divergence; a fallback-only rung is dialled as a retry and is not compared.',

  'health_perspective.unattributed.title': 'Requests with no SKU resolved',
  'health_perspective.unattributed.share': '{value} of fleet requests',
  'health_perspective.unattributed.refused': 'Ended before any upstream',
  'health_perspective.unattributed.rate_limited': 'Rate-limited 429',
  'health_perspective.unattributed.server_errors': 'Upstream failures',
  'health_perspective.unattributed.remainder': 'The rest (not failures)',
  'health_perspective.unattributed.composition_unavailable':
    'The rest are NOT refused requests, and this read cannot break them down: free count_tokens calls (answered from a local estimate, never forwarded, never billed) and requests still in flight both land here, and the traffic overview has no route or error-code grouping. Do not read the whole bucket as refusals.',
  'health_perspective.unattributed.not_graded':
    'These requests resolved no SKU, so they have no retry ladder, they count towards no SKU’s failure rate, and they carry no health grade — a free local estimate and a balance refusal share no verdict.',

  'health_perspective.refusal.title': 'Refusal reasons',
  'health_perspective.refusal.request_not_supported': 'Request not supported',
  'health_perspective.refusal.authentication_failed': 'Authentication failed',
  'health_perspective.refusal.invalid_request': 'Invalid request',
  'health_perspective.refusal.insufficient_balance': 'Insufficient balance',
  'health_perspective.refusal.account_unavailable': 'Account unavailable',
  'health_perspective.refusal.model_not_available': 'Model not available',
  'health_perspective.refusal.uncoded': 'No code recorded',
  'health_perspective.refusal.excluded': 'not counted against us',
  'health_perspective.refusal.counted': 'counted against us',
  'health_perspective.refusal.entry': '{label} {count}',
  'health_perspective.refusal.rule': 'Only refusals of things we never owed the caller leave our failure rate: request not supported, authentication failed, invalid request, insufficient balance, account unavailable. Everything else counts against us — "model not available" means we had nothing to route to, an unrecorded code is still ours, and any code introduced later is ours by default.',
  'health_perspective.refusal.unknown': 'This gateway did not report the error-code split, so there is no way to tell which of these refusals were our own doing. Handled the conservative way: nothing is deducted and every one of them counts against our failure rate.',
  'health_perspective.refusal.unknown_short': 'code split unknown, nothing deducted',
  'health_perspective.refusal.more': '+{count} more',

  'health_perspective.bar.aria': '{requests} requests: {success} succeeded, {refused} refused before any upstream was dialled, {failed} failed on us; {serverErrors} upstream failures, of which {fallbacks} were rescued by the fallback ladder',
  'health_perspective.bar.counts': 'ok {success} · refused {refused} · ours {failed}',
  'health_perspective.bar.client': 'client-facing {rate} (refusals included) · 429 {rateLimited} · upstream 429 rescued {upstreamRateLimited}',
  'health_perspective.bar.legend.success': 'Succeeded',
  'health_perspective.bar.legend.refused': 'Refused before upstream (excluded from the downstream rate)',
  'health_perspective.bar.legend.failed': 'Failed on us',
  'health_perspective.bar.legend.supply': 'Under-track = upstream failures',
  'health_perspective.bar.legend.rescued': 'of which rescued by fallback',
  'health_perspective.bar.legend.scale': 'Bar length = requests on a SQUARE-ROOT scale, not linear: it ranks magnitude only — read the exact counts in the columns',

  'health_perspective.grade.ok': 'Healthy',
  'health_perspective.grade.warn': 'Warning',
  'health_perspective.grade.bad': 'Critical',
  'health_perspective.grade.idle': 'No traffic',
  'health_perspective.grade.unknown': 'Unknown',

  'health_perspective.node.requests': 'Requests',
  'health_perspective.node.observed_share': 'Observed share',
  'health_perspective.node.downstream_error_rate': 'Downstream failures (ours)',
  'health_perspective.node.refused_before_upstream': 'Refused before upstream',
  'health_perspective.node.tenant_error_rate': 'Customer-facing failures',
  'health_perspective.node.rate_limited_rate': 'Rate-limited 429',
  'health_perspective.node.upstream_rate_limited': 'Upstream 429 rescued',
  'health_perspective.node.upstream_rate_limited_note': 'attempt failures {attempts} · upstream refused 401/402/403/429 {refused}',
  'health_perspective.node.server_error_rate': 'Upstream failures',
  'health_perspective.node.fallback_rate': 'Fallback rate',
  'health_perspective.node.first_byte': 'Avg first byte',
  'health_perspective.node.margin': 'Margin',
  'health_perspective.node.revenue': 'Revenue',
  'health_perspective.node.cost': 'Upstream cost',
  'health_perspective.node.profit': 'Gross profit',
  'health_perspective.node.coverage': 'Upstreams able to serve',
  'health_perspective.node.coverage.covered': 'Multi-point',
  'health_perspective.node.coverage.single_point': 'Single point',
  'health_perspective.node.coverage.unrouted': 'Unrouted',
  'health_perspective.node.targets_active': 'First-attempt targets',
  'health_perspective.node.targets_reserve': 'Fallback targets',
  'health_perspective.node.targets_disabled': 'Disabled targets',
  'health_perspective.node.policy': 'Retry policy',
  'health_perspective.node.policy.full_chain': 'Full chain',
  'health_perspective.node.policy.network_only': 'Network errors only',
  'health_perspective.node.policy.same_cache_domain': 'Same cache domain',
  'health_perspective.node.policy.fail_fast': 'Fail fast',
  'health_perspective.node.serves_skus': 'SKUs carried',
  'health_perspective.node.model_count': 'Models on this channel',
  'health_perspective.node.channel': 'Upstream channel',
  'health_perspective.node.upstream_model': 'Upstream model',
  'health_perspective.node.capability': 'Capability status',
  'health_perspective.node.legacy_bucket': 'Requests with no SKU resolved',
  'health_perspective.node.unattributed_upstream': 'Unattributed upstream',
  'health_perspective.node.fallback_fired': 'Fallback fired',

  'health_perspective.inspector.empty.title': 'Pick a row',
  'health_perspective.inspector.empty.hint': 'Click any SKU, or any upstream model on its ladder, to see its health, ladder and margin here.',
  'health_perspective.inspector.section.traffic': 'Observed traffic',
  'health_perspective.inspector.section.traffic_channel': 'Observed traffic (channel-level, not this model’s own)',
  'health_perspective.inspector.borrowed_note': 'These are the CHANNEL’s totals, not this upstream model’s own — the traffic overview has no channel-by-model grain. For how much one SKU actually sent this leg and how much of it failed, read that SKU’s ladder in the table.',
  'health_perspective.inspector.section.money': 'Margin',
  'health_perspective.inspector.section.supply': 'Supply structure',
  'health_perspective.inspector.section.chain': 'Retry ladder',
  'health_perspective.inspector.chain.priority': 'Priority {value}',
  'health_perspective.inspector.chain.capability': 'capability {status}',
  'health_perspective.inspector.chain.empty': 'This SKU has no target in the global default pool.',
  'health_perspective.inspector.divergence.title': 'Configured and observed disagree',
  'health_perspective.inspector.divergence.hint': 'Configured for {configured}, actually carried {observed}. Carrying more than declared usually means the first attempt is failing and requests are falling down the ladder; carrying less points at the breaker or the pinned capability.',
  'health_perspective.inspector.open_sku': 'Open SKU detail',
  'health_perspective.inspector.open_upstream': 'Open upstream detail',
  'health_perspective.inspector.open_traffic': 'Drill down in traffic monitor',
  'health_perspective.inspector.open_routing': 'Open routing config',

  'health_perspective.risks.title': 'Risks the gateway triaged',
  'health_perspective.risks.empty': 'Nothing crossed a threshold in this window.',
  'health_perspective.risks.kind.error_rate': 'Error rate',
  'health_perspective.risks.kind.fallback_rate': 'Fallback rate',
  'health_perspective.risks.kind.slow_first_byte_rate': 'Slow first byte rate',
  'health_perspective.risks.threshold': 'Now {current}, threshold {threshold}',

  'quotas.title': 'Quota Center',
  'quotas.sub': 'See and fix every tenant quota in one place: start from the customer, then choose the layer. Unlimited by default.',
  'quotas.banner': 'Quota bypass is on. Operator {by}, since {at}. {count} request(s) would have been refused.',
  'quotas.banner.unknown_operator': 'unknown',
  'quotas.bypass.title': 'Emergency bypass',
  'quotas.bypass.hint': 'Admit every RPM/TPM and task-admission refusal in one switch · live cluster-wide within 30s · audited',
  'quotas.bypass.on': 'Bypass on — quotas paused; would-be refusals are counted',
  'quotas.bypass.off': 'Bypass off — quotas are enforced',
  'quotas.bypass.by': 'Operator {by}',
  'quotas.bypass.at': 'Since {at}',
  'quotas.bypass.would_reject': '{count} would-be refusal(s) since it was turned on',
  'quotas.bypass.admin_only': 'The site-wide banner is visible to every role; only ops-admin can toggle',
  'quotas.search.title': 'Find a customer / SKU',
  'quotas.search.org_placeholder': 'Search customers: name / email / org id …',
  'quotas.search.sku_placeholder': 'Search SKUs: model / namespace …',
  'quotas.search.hint': 'Selecting opens the drawer; no raw ids to remember',
  'quotas.search.tpm_unit': 'TPM in millions of tokens per minute (M)',
  'quotas.drawer.task_lane': 'Managed by concurrency admission',
  'quotas.task_lane.edit_protocol': 'Edit default concurrency (model protocol)',
  'quotas.task_lane.ignores_rpm': 'This SKU should not have RPM/TPM',
  'quotas.task_lane.clear_leftover': 'Clear leftover RPM/TPM',
  'quotas.image_lane.rpm_only': 'Image SKUs are limited by RPM only',
  'quotas.image_lane.ignores_tpm': 'This SKU does not use TPM',
  'quotas.search.org_empty': 'No matching customers',
  'quotas.search.sku_empty': 'No matching SKUs',
  'quotas.search.has_override': 'Dedicated',
  'quotas.search.has_sku_default': 'Has default',
  'quotas.dedicated.title': 'Customers with dedicated quotas',
  'quotas.dedicated.search': 'Filter by name / email',
  'quotas.dedicated.org': 'Customer',
  'quotas.dedicated.org_all': 'Org-wide RPM / TPM',
  'quotas.dedicated.sku_count': 'Dedicated SKUs',
  'quotas.dedicated.refusals_24h': 'Refused (24h)',
  'quotas.dedicated.updated': 'Last updated',
  'quotas.dedicated.open': 'Open →',
  'quotas.dedicated.empty': 'No customer has a dedicated quota yet',
  'quotas.dedicated.none_match': 'No matching customers',
  'quotas.dedicated.more': '+{n}',
  'quotas.refusals.title': 'Recently limited customers',
  'quotas.refusals.window.1h': '1 hour',
  'quotas.refusals.window.24h': '24 hours',
  'quotas.refusals.window.7d': '7 days',
  'quotas.refusals.org': 'Customer',
  'quotas.refusals.sku': 'SKU',
  'quotas.refusals.kind': 'Reason',
  'quotas.refusals.count': 'Count',
  'quotas.refusals.last': 'Last seen',
  'quotas.refusals.limit': 'Effective limit at the time',
  'quotas.refusals.empty': 'No customer was limited in this window.',
  'quotas.refusals.loading': 'Loading…',
  'quotas.refusals.adjust': 'Adjust →',
  'quotas.refusals.kind.l1_reject_minute': 'RPM/TPM exceeded',
  'quotas.refusals.kind.task_admission_reject': 'Task concurrency exceeded',
  'quotas.refusals.kind.quota_bypass_would_reject': 'Bypass admit',
  'quotas.refusals.kind.task_admission_bypass_would_reject': 'Bypass admit',
  'quotas.editor.title': 'Default limits (apply to every customer)',
  'quotas.editor.tab.hint': 'A customer-specific row always wins over these defaults',
  'quotas.editor.tab.global': 'Global fallback *×*',
  'quotas.editor.tab.sku': 'Per SKU *×sku',
  'quotas.editor.global_audience': 'New or unconfigured customers',
  'quotas.editor.global_audience_hint': 'Each SKU is counted independently',
  'quotas.editor.cover_counts': '{skus} SKU(s) and {orgs} customer(s) currently overlay this',
  'quotas.editor.covering': 'Customers overlaying it',
  'quotas.editor.covering_none': '—',
  'quotas.editor.group_meta': '{count} SKUs · {overrides} with a row',
  'quotas.editor.rpm': 'RPM',
  'quotas.editor.tpm': 'TPM',
  'quotas.editor.save': 'Save',
  'quotas.editor.clear': 'Clear (= unlimited)',
  'quotas.editor.inherit': 'Inherit',
  'quotas.editor.fill_peak': 'Fill from 7-day peak hour × {multiplier}',
  'quotas.editor.fill_peak.multiplier': 'Multiplier',
  'quotas.editor.fill_peak.need_org': 'Select a customer first',
  'quotas.editor.none': 'No rows at this layer yet.',
  'quotas.editor.source.org_sku': 'Customer-specific',
  'quotas.editor.source.org_all': 'Customer-wide',
  'quotas.editor.source.sku': 'SKU default',
  'quotas.editor.source.global': 'Global fallback',
  'quotas.editor.source.none': 'Unlimited',
  'quotas.defaults.footer': 'Customer and customer×SKU layers live in the customer drawer, not as separate tables.',
  'quotas.drawer.org_all': 'Customer-wide limit org×*',
  'quotas.drawer.org_all_hint': 'One shared bucket across every SKU; blank = unset, each SKU uses its default',
  'quotas.drawer.org_all_preview': 'This customer’s SKUs together stay under {rpm} RPM; SKUs with a dedicated value are unaffected',
  'quotas.drawer.filter': 'Filter SKU name / namespace',
  'quotas.drawer.only_overridden': 'Only dedicated rows',
  'quotas.drawer.rule': 'Effective = dedicated → customer-wide → SKU default → global; RPM and TPM fall back independently',
  'quotas.drawer.sku': 'SKU',
  'quotas.drawer.effective_rpm': 'Effective RPM',
  'quotas.drawer.effective_tpm': 'Effective TPM',
  'quotas.drawer.source': 'Source',
  'quotas.drawer.org_sku_rpm': 'Dedicated RPM',
  'quotas.drawer.org_sku_tpm': 'Dedicated TPM',
  'quotas.drawer.task': 'Task-admission cap',
  'quotas.drawer.refusals_1h': 'Last 1h',
  'quotas.drawer.refusals_stat': 'Refused (1h)',
  'quotas.drawer.peak_stat': '7-day peak RPM',
  'quotas.drawer.sku_stat': 'Visible SKUs',
  'quotas.drawer.rejected': 'rej {count}',
  'quotas.drawer.save': 'Save all changes',
  'quotas.drawer.discard': 'Discard',
  'quotas.drawer.dirty': 'Unsaved changes — effective columns preview the draft',
  'quotas.drawer.clean': 'No unsaved changes',
  'quotas.drawer.saved': 'Saved · live cluster-wide within 30s',
  'quotas.drawer.unsaved_title': 'Discard unsaved changes?',
  'quotas.drawer.unsaved_body': 'Closing the drawer drops the quota draft that has not been saved.',
  'quotas.drawer.unsaved_confirm': 'Discard',
  'quotas.link_edit': 'Edit in Quota Center →',
  'quotas.toast.saved': 'Saved',
  'quotas.toast.cleared': 'Cleared',
  'quotas.toast.invalid': 'RPM must be an integer ≥ 1; TPM is in millions of tokens per minute (M), up to 3 decimal places; either may be blank.',
  'quotas.toast.invalid_row': 'Invalid RPM/TPM on {row}. Nothing was saved.',
  'quotas.toast.bypass_on': 'Quota bypass is on',
  'quotas.toast.bypass_off': 'Quota bypass is off',
  'quotas.toast.peak_filled': 'Filled from 7-day peak hour; confirm before saving',
  'quotas.toast.peak_failed': 'Could not read peak usage for this customer',
  'guaranteed.title': 'Guaranteed service',
  'guaranteed.subtitle': 'Four mechanisms per SKU: off / shadow (record only) / remove. Remove retires first pick, keeps the leg at the tail of the retry chain, and can heal.',
  'guaranteed.create_sku': 'Add SKU',
  'guaranteed.create_org': 'Add customer overlay',
  'guaranteed.create_sku_title': 'Add SKU',
  'guaranteed.create_org_title': 'Add customer overlay',
  'guaranteed.customer': 'Customer',
  'guaranteed.sku': 'SKU',
  'guaranteed.note': 'Note',
  'guaranteed.delete': 'Delete',
  'guaranteed.delete_confirm': 'Delete this guaranteed-service entry? This scope goes back to ordinary routing.',
  'guaranteed.edit_note': 'Edit note',
  'guaranteed.save': 'Save',
  'guaranteed.cancel': 'Cancel',
  'guaranteed.close': 'Close',
  'guaranteed.org_placeholder': 'Search name, org id, or email',
  'guaranteed.sku_placeholder': 'Select text SKUs',
  'guaranteed.note_placeholder': 'Optional note',
  'guaranteed.empty': 'No guaranteed-service entries yet',
  'guaranteed.saved': 'Saved',
  'guaranteed.deleted': 'Deleted',
  'guaranteed.config_count': '{sku} SKUs · {org} customer overlays',
  'guaranteed.col.sku': 'SKU / note',
  'guaranteed.col.probe': 'Probe',
  'guaranteed.col.chain': 'In-chain breaker · ultimate fallback',
  'guaranteed.col.jev': 'Jev judge',
  'guaranteed.col.stats': 'Last 24h',
  'guaranteed.stats_hint_label': 'Last 24h',
  'guaranteed.stats_hint': 'Fallback retries: requests the ultimate fallback sent down the retry chain after the first leg failed. Still failed: the whole chain failed. Shadow would-retry: how many would have retried while fallback was shadow (no call was placed).',
  'guaranteed.stat.requests': 'Requests',
  'guaranteed.stat.retry': 'Fallback retries',
  'guaranteed.stat.failed': 'Still failed',
  'guaranteed.stat.shadow': 'Shadow would retry',
  'guaranteed.probe_spend.title': 'Probe spend · last {hours}h',
  'guaranteed.probe_spend.window': '{since} – {until}',
  'guaranteed.probe_spend.col.leg': 'Downstream SKU / upstream leg',
  'guaranteed.probe_spend.col.shots': 'Shots',
  'guaranteed.probe_spend.col.tokens': 'Input / output tokens',
  'guaranteed.probe_spend.col.logged': 'At list price',
  'guaranteed.probe_spend.col.procurement': 'Procurement cost',
  'guaranteed.probe_spend.col.charged': 'System-account charge (list price)',
  'guaranteed.probe_spend.col.allocated': 'Debited from supplier wallet',
  'guaranteed.probe_spend.col.status': 'Reconcile',
  'guaranteed.probe_spend.total': 'Total',
  'guaranteed.probe_spend.no_sku': '(no SKU)',
  'guaranteed.probe_spend.leg_count': '{count} legs',
  'guaranteed.probe_spend.unpriced': '{count} with no procurement price',
  'guaranteed.probe_spend.pre_fix': '{count} before the fix',
  'guaranteed.probe_spend.count': '{count}',
  'guaranteed.probe_spend.wallet_note': 'The supplier wallet moves by "Debited from supplier wallet"; it should equal procurement cost (converted to CNY at that day\'s rate).',
  'guaranteed.probe_spend.status.pending': '{count} awaiting allocation',
  'guaranteed.probe_spend.status.mismatch_count': 'Off by {diff} charges',
  'guaranteed.probe_spend.status.match': 'Matches',
  'guaranteed.probe_spend.status.mismatch': 'Off by {diff}',
  'guaranteed.probe_spend.status.uncharged': 'Not charged',
  'guaranteed.probe_spend.status.no_funding_batch': '{count} with no funding batch',
  'guaranteed.probe_spend.status.no_funding_batch_hint': 'The allocator has processed them, but this upstream has no funding batch, so the wallet balance cannot be debited',
  'guaranteed.probe_spend.status.unbilled': '{count} owed, not charged',
  'guaranteed.probe_spend.status.unbilled_hint': 'Post-fix probe shots that burned tokens but were never charged (no list or procurement price); the supplier wallet did not move for them',
  'guaranteed.probe_spend.status.unknown': 'Ledger unreadable',
  'guaranteed.probe_spend.empty': 'No probe spend in this window.',
  'guaranteed.probe_spend.loading': 'Loading…',
  'guaranteed.probe_spend.error': 'Could not read probe spend.',
  'guaranteed.probe_spend.copy': 'Copy SQL',
  'guaranteed.probe_spend.expand_all': 'Expand all',
  'guaranteed.probe_spend.collapse_all': 'Collapse all',
  'guaranteed.probe_spend.reconcile.title': 'Reconcile against the tables',
  'guaranteed.probe_spend.reconcile.columns': 'At list price = sum of usage_logs.cost_usd_micros. System-account charge (list price) = system-account charges with reason=probe (negated balance_transactions.amount_usd_micros) and their count. Procurement cost = cost_usd_micros × upstream_sku_cost_multiplier × upstream_account_rate_multiplier over rows with a full procurement snapshot (USD). Debited from supplier wallet = cny_upstream_cost_micros of those charges\' revenue_ledger slices carrying a source_batch_id (CNY at the batch rate) and how many charges are allocated — this is the column the supplier wallet moves by. Reconcile walks the whole chain by counts, FX-free: chargeable shots = charges = allocated charges and charged amount = the chargeable rows\' list price is Matches; post-fix rows that burned tokens but were never charged (no list or procurement price, unpriced_shots) are N owed, not charged; charged correctly and settled by the allocator but with no funding batch on the upstream (only settled revenue_ledger rows with a NULL source_batch_id, overflow_charges) are N with no funding batch — the wallet cannot move until a batch is recorded; charged correctly but not yet reached by the allocator (its own cron; only the charge-time placeholder) is N awaiting allocation; both 0 is Not charged; anything else is Off. N before the fix = rows with no cost_snapshot_source (written before probe charging, never meant to be charged); N with no procurement price = post-fix rows that burned tokens without a list or procurement price. The two tables belong to different services; the two queries below are where the page\'s numbers come from, with the window filled in, ready to run.',
  'guaranteed.probe_spend.reconcile.log_sql': 'Probe log (gateway · usage_logs)',
  'guaranteed.probe_spend.reconcile.ledger_sql': 'Charges and allocation (platform · balance_transactions / revenue_ledger)',
  'guaranteed.probe_spend.reconcile.ledger_missing': 'The platform ledger could not be read this time; Charged and Allocated show —, and Reconcile reads Ledger unreadable.',
  'guaranteed.probe.mode': 'Mode / shape',
  'guaranteed.probe.interval': 'Cadence',
  'guaranteed.probe.minutes': 'min',
  'guaranteed.probe.criteria': 'Criteria',
  'guaranteed.probe.shots': 'shots/round',
  'guaranteed.probe.every': 'every',
  'guaranteed.probe.consecutive': 'consecutive',
  'guaranteed.probe.fail_rounds': 'failed rounds → remove',
  'guaranteed.probe.pass_rounds': 'passing rounds → recover',
  'guaranteed.probe_shape': 'Probe shape',
  'guaranteed.probe_shape_ping': 'ping',
  'guaranteed.probe_shape_arithmetic': 'Arithmetic',
  'guaranteed.probe_shape_echo': 'Echo',
  'guaranteed.probe_shape_custom': 'Custom',
  'guaranteed.probe_custom_text': 'Custom text',
  'guaranteed.probe_custom_max': 'max tokens',
  'guaranteed.probe_custom_empty': 'Custom probe text is required',
  'guaranteed.probe_custom_invalid': 'Custom text must be at most 200 characters and contain no control characters',
  'guaranteed.probe_custom_max_invalid': 'max_tokens must be an integer from 1 to 64',
  'guaranteed.chain_mode': 'In-chain breaker',
  'guaranteed.fallback_mode': 'Ultimate fallback',
  'guaranteed.fallback': 'Ultimate fallback',
  'guaranteed.inherit': 'Inherit',
  'guaranteed.jev.mode': 'Mode',
  'guaranteed.jev.criteria': 'Hits / window',
  'guaranteed.jev.hits': 'hits',
  'guaranteed.jev.seconds': 's',
  'guaranteed.jev_trip_min_hits': 'Jev hits to trip',
  'guaranteed.jev_trip_window_seconds': 'Jev window (s)',
  'guaranteed.overlay.fallback': 'Ultimate fallback',
  'guaranteed.overlay.retries': '24h fallback retries',
  'guaranteed.overlay.requests': 'requests',
  'guaranteed.overlay.note': 'Note',
  'guaranteed.overlay.title': 'Customer overlays',
  'guaranteed.overlay.hint': 'Only ultimate fallback is overridable; the other three follow the SKU',
  'guaranteed.overlay.empty': 'No customer overlays · every customer follows the SKU rows above',
  'guaranteed.section.legs': 'Leg status',
  'guaranteed.section.events': 'Events',
  'guaranteed.section.config': 'Configuration',
  'guaranteed.leg.empty': 'No physical legs for configured SKUs and no manually removed legs yet',
  'guaranteed.leg.scope': 'Physical legs of configured SKUs, plus manually removed legs · {count}',
  'guaranteed.leg.chip.ok': 'Healthy',
  'guaranteed.leg.chip.shadow': 'Shadow · would remove',
  'guaranteed.leg.chip.tripped': 'Removed',
  'guaranteed.leg.chip.tripped_unarmed': 'Removal record (mechanisms off)',
  'guaranteed.leg.verdict.unarmed_short': 'mechanisms off, not routing-effective',
  'guaranteed.leg.verdict.unarmed': 'No mechanism on this SKU is in act mode, so this record does not affect routing; switch one to act or clear it',
  'guaranteed.leg.next_probes_off': 'probes off',
  'guaranteed.leg.next_due': 'next round',
  'guaranteed.leg.partly_disabled': '{disabled}/{total} targets disabled',
  'guaranteed.leg.partly_disabled_tip': 'Some routing targets of this physical leg are manually disabled; the rest still route',
  'guaranteed.leg.unconfigured': 'Not in guaranteed service',
  'guaranteed.leg.unconfigured_tip': 'This SKU has no guaranteed-service config; only its manually removed legs are listed here for maintenance (probe now, try, re-add)',
  'guaranteed.leg.partly_disabled_notes': 'Notes on the disabled targets: {notes}',
  'guaranteed.leg.manual_probe': 'Last probe HTTP {code} · {ago}',
  'guaranteed.leg.probe_now_tip': 'Probe now: one round with the SKU probe config, counted toward this leg\'s probe state (failed / passed rounds; may remove or restore). A manually removed leg only records the result and is never re-added automatically.',
  'guaranteed.leg.chip.manual': 'Manually removed by operator',
  'guaranteed.leg.chip.immune': 'Immunity {minutes} min',
  'guaranteed.leg.unprobed': 'Not probed',
  'guaranteed.leg.count.ok': 'Healthy {count}',
  'guaranteed.leg.count.shadow': 'Shadow {count}',
  'guaranteed.leg.chip.failing': 'Probe failed · below trip line',
  'guaranteed.leg.count.failing': 'Probe failed {count}',
  'guaranteed.leg.verdict.failing': 'Probe failed',
  'guaranteed.leg.rounds_ratio': '{failed}/{threshold} rounds in a row',
  'guaranteed.leg.rounds_streak': '{failed} failed rounds in a row',
  'guaranteed.leg.weight': 'weight {weight}',
  'guaranteed.leg.weight_zero': 'weight 0 (no first pick)',
  'guaranteed.leg.weight_override': 'Override weight {weight}',
  'guaranteed.leg.weight_override_zero': 'Override weight 0 (no first pick)',
  'guaranteed.leg.weight_global': 'Global default weight {weight}',
  'guaranteed.leg.weight_global_zero': 'Global default weight 0 (no first pick)',
  'guaranteed.leg.weight_default': 'SKU default weight {weight}',
  'guaranteed.leg.weight_default_zero': 'SKU default weight 0 (no first pick)',
  'guaranteed.leg.weight_org_also': 'also first pick in {count} customer configs',
  'guaranteed.leg.weight_org_only': 'first pick in {count} customer configs',
  'guaranteed.leg.count.tripped': 'Removed {count}',
  'guaranteed.leg.count.manual': 'Manually removed {count}',
  'guaranteed.leg.count.immune': 'Immunity {count}',
  'guaranteed.leg.search': 'Search',
  'guaranteed.leg.search_placeholder': 'SKU / upstream / model',
  'guaranteed.leg.abnormal': 'Abnormal only',
  'guaranteed.leg.refreshed': 'Refreshed {seconds}s ago',
  'guaranteed.leg.col.status': 'Status',
  'guaranteed.leg.col.upstream': 'Upstream / model',
  'guaranteed.leg.col.evidence': 'Verdict and evidence',
  'guaranteed.leg.col.last': 'Last probe',
  'guaranteed.leg.col.next': 'Next probe',
  'guaranteed.leg.col.backoff': 'Backoff',
  'guaranteed.leg.col.members': 'Shared routing targets',
  'guaranteed.leg.col.actions': 'Actions',
  'guaranteed.leg.minutes_ago': '{minutes} min ago',
  'guaranteed.leg.minutes_in': 'in {minutes} min',
  'guaranteed.leg.never': '—',
  'guaranteed.leg.members_tip': 'This physical leg (upstream × account × channel × model) is referenced by {count} routing targets (default layer + each routing-config layer). Removal and recovery apply to all of them at once',
  'guaranteed.leg.legend': 'Lamps: green healthy (not removed; whether it takes first pick is decided by routing weight: only weight > 0 joins the first pick; “weight 0 (no first pick)” is never picked first) · yellow shadow (would remove, routing unchanged) · yellow probe failed (latest probe not 2xx, below the trip line) · red removed (retired from first pick, kept at the chain tail) · grey manually removed (skipped by first pick and the retry chain) · blue immunity (protection after a clear). “Shared routing targets” = how many routing targets reference this physical leg; removal and recovery apply to all of them at once.',
  'guaranteed.leg.trigger.probe': 'Probe',
  'guaranteed.leg.trigger.chain': 'In-chain breaker',
  'guaranteed.leg.trigger.jev': 'Jev judge',
  'guaranteed.leg.trigger.shadow': '{trigger} (shadow)',
  'guaranteed.leg.verdict.immune': 'Ops cleared the removal · {ago} · not removed again for {minutes} min (5xx breaker still applies)',
  'guaranteed.leg.verdict.serving': 'Not removed · {detail}',
  'guaranteed.leg.verdict.not_removed': 'not actually removed',
  'guaranteed.leg.reset': 'Clear removal',
  'guaranteed.leg.reset_shadow': 'Clear shadow verdict',
  'guaranteed.leg.reset_title': 'Clear removal',
  'guaranteed.leg.reset_body': 'Clear the trip and shadow state on every member of this physical leg.',
  'guaranteed.leg.immunity': 'Protection',
  'guaranteed.leg.immunity.0': 'No protection',
  'guaranteed.leg.immunity.5': '5 minutes (default)',
  'guaranteed.leg.immunity.10': '10 minutes',
  'guaranteed.leg.immunity.30': '30 minutes',
  'guaranteed.leg.immunity.60': '60 minutes',
  'guaranteed.leg.probe_now': 'Probe now',
  'guaranteed.leg.probe_now_done': 'Probed one round · HTTP {code}',
  'guaranteed.leg.probe_now_done_nocode': 'Probed one round',
  'guaranteed.leg.disable': 'Disable target permanently',
  'guaranteed.leg.enable': 'Add back',
  'guaranteed.leg.enable_title': 'Add the target back',
  'guaranteed.leg.enable_body': 'Re-enable every member of this leg so first pick and the retry chain can use it again.',
  'guaranteed.leg.enable_done': 'Target added back',
  'guaranteed.leg.namespace_other': 'Other',
  'guaranteed.leg.locate': 'Open in tenant routing',
  'guaranteed.leg.locate_title': 'Where these routing targets live',
  'guaranteed.leg.locate.group_live': 'Takes traffic',
  'guaranteed.leg.locate.group_dead': 'No traffic · {count}',
  'guaranteed.leg.locate.default_name': 'SKU default targets',
  'guaranteed.leg.locate.orgs': '{count} customers assigned',
  'guaranteed.leg.locate.targets': '{enabled} enabled · {disabled} disabled',
  'guaranteed.leg.locate.deprecated': 'Deprecated',
  'guaranteed.leg.reach.global': 'Global default route',
  'guaranteed.leg.reach.override': 'Emergency override',
  'guaranteed.leg.reach.org_assigned': 'Customer config',
  'guaranteed.leg.reach.default_fallthrough': 'SKU default targets (fallback when global does not cover)',
  'guaranteed.leg.reach.unassigned_config': 'Config with no customers',
  'guaranteed.leg.reach.shadowed_by_global_exclusive': 'SKU default targets (shadowed by the global default route)',
  'guaranteed.leg.reach.unknown': 'Location unknown',
  'guaranteed.leg.members_where': '{total} targets: {parts}',
  'guaranteed.leg.members_where_unreachable': 'no traffic {count}',
  'guaranteed.leg.members_where_unreachable_dep_all': 'no traffic {count} (all in deprecated configs)',
  'guaranteed.leg.members_where_unreachable_dep': 'no traffic {count} ({deprecated} in deprecated configs)',
  'guaranteed.leg.tag.unreachable': 'No traffic',
  'guaranteed.leg.tag.unreachable_tip': 'Every routing target of this leg sits where no request can reach it (a config with no customers, or SKU default targets shadowed by the global default route). The scheduler no longer probes it; probe-now still works.',
  'guaranteed.leg.tag.live_deprecated': 'Has deprecated config',
  'guaranteed.leg.tag.live_deprecated_tip': 'Some routing targets of this leg sit in a config marked deprecated that still has customers, so it still takes traffic and is still probed.',
  'guaranteed.leg.unreachable_toggle': 'Legs that take no traffic (only in configs with no customers or shadowed SKU default targets) · {count}',
  'guaranteed.mode.off': 'Off',
  'guaranteed.mode.shadow': 'Shadow',
  'guaranteed.mode.act': 'Remove',
  'guaranteed.shadow_note': 'Shadow does not place a call, so it cannot prove the chain would have recovered the request.',
  'guaranteed.events.window': 'Last 10 days · {count}',
  'guaranteed.events.empty': 'No events',
  'guaranteed.events.sku': 'SKU',
  'guaranteed.events.upstream': 'Upstream',
  'guaranteed.events.kind': 'Kind',
  'guaranteed.events.all_sku': 'All SKUs',
  'guaranteed.events.all_upstream': 'All upstreams',
  'guaranteed.events.all_kind': 'All kinds',
  'guaranteed.events.kind.target_probe_trip': 'Removed',
  'guaranteed.events.kind.target_probe_recover': 'Probe · recovered',
  'guaranteed.events.kind.chain_trip_suppressed': 'Chain suppressed',
  'guaranteed.events.kind.shadow_trip': 'Shadow · would remove',
  'guaranteed.events.kind.shadow_recover': 'Shadow · recovered',
  'guaranteed.events.kind.guaranteed_shadow_enter': 'Shadow · would retry',
  'guaranteed.events.kind.trip_skipped_immune': 'Immunity skip',
  'guaranteed.events.kind.jev_verdict': 'Jev verdict',
  'guaranteed.events.kind.judge_error': 'Jev judge error',
  'guaranteed.events.chip.probe_recover': 'Probe · recovered',
  'guaranteed.events.chip.shadow_recover': 'Shadow · recovered',
  'guaranteed.events.chip.probe_trip': 'Probe · removed',
  'guaranteed.events.chip.chain_trip': 'In-chain breaker · removed',
  'guaranteed.events.chip.jev_trip': 'Jev · removed',
  'guaranteed.events.chip.jev_verdict_trip': 'Jev verdict · removed',
  'guaranteed.events.chip.jev_verdict_would': 'Jev verdict · shadow remove',
  'guaranteed.events.chip.jev_verdict_record': 'Jev verdict · record',
  'guaranteed.events.chip.shadow_probe': 'Shadow · probe would remove',
  'guaranteed.events.chip.shadow_chain': 'Shadow · chain would remove',
  'guaranteed.events.chip.shadow_jev': 'Shadow · Jev would remove',
  'guaranteed.events.chip.shadow_enter': 'Shadow · would retry',
  'guaranteed.events.chip.immune': 'Immunity skip',
  'guaranteed.events.chip.chain_suppressed': 'Chain suppressed',
  'guaranteed.events.chip.judge_error': 'Jev judge error',
  'guaranteed.events.col.time': 'Time',
  'guaranteed.events.col.kind': 'Kind',
  'guaranteed.events.col.sku': 'SKU',
  'guaranteed.events.col.upstream': 'Upstream / model',
  'guaranteed.events.col.customer': 'Customer',
  'guaranteed.events.col.details': 'Details',
  'guaranteed.events.col.region': 'Region',
  'guaranteed.events.reason.probe_disabled': 'probes off, chain removal suppressed',
  'guaranteed.events.reason.immune': 'within immunity',
  'guaranteed.events.verdict.upstream_server': 'judged upstream fault',
  'guaranteed.events.verdict.client': 'judged client fault',
  'guaranteed.events.verdict.unknown': 'undecided',
  'guaranteed.events.detail.fail_ok': 'fail {fail} / ok {ok}',
  'guaranteed.events.detail.hits': '{hits} hits',
  'guaranteed.events.detail.orgs': '{orgs} customers',
  'guaranteed.events.detail.rounds': '{rounds} rounds',
  'guaranteed.org_direct': 'This org id will be submitted as typed',
  'guaranteed.sku_group_other': 'Other',
  'tenant_routing.probe.dot_never': 'Never probed',
  'tenant_routing.leg.chip.tripped_probe': 'Removed · probe',
  'tenant_routing.leg.chip.tripped_chain': 'Removed · chain',
  'tenant_routing.leg.chip.tripped_jev': 'Removed · Jev',
  'tenant_routing.leg.tip.ok': 'Healthy — not removed; routing weight decides whether it takes the first pick.',
  'tenant_routing.leg.tip.failing': 'Probe failed, below the trip line — routing unchanged until failures reach the threshold.',
  'tenant_routing.leg.tip.shadow': 'Shadow verdict: would be removed, but shadow mode only records and leaves routing alone.',
  'tenant_routing.leg.tip.tripped': 'Removed — retired from the first pick, kept at the retry-chain tail, returns on its own once recovery conditions hold.',
  'tenant_routing.leg.tip.tripped_unarmed': 'Removal on record, but no mechanism on this SKU is in act mode, so routing is unaffected.',
  'tenant_routing.leg.tip.immune': 'Immunity active, {minutes} min left; it will not be removed meanwhile.',
  'tenant_routing.leg.tip.manual': 'Target manually disabled (skipped by both the first pick and the retry chain)',
  'tenant_routing.leg.tip.manual_also_tripped': 'Also has a removal record: {evidence}',
  'tenant_routing.leg.tip.manual_also_shadow': 'Also has a shadow verdict: {evidence}',
  'tenant_routing.leg.tip.unprobed': 'Not probed yet; routing is unaffected.',
  'tenant_routing.leg.other_region.tripped': '{region}: removed ({trigger})',
  'tenant_routing.leg.other_region.tripped_unarmed': '{region}: removal on record, mechanisms off ({trigger})',
  'tenant_routing.leg.other_region.shadow': '{region}: shadow would remove ({trigger})',
  'tenant_routing.leg.other_region.last_probe': 'last probed {ago}',
  'tenant_routing.leg.other_region.judged': 'judged {ago}',
  'tenant_routing.leg.other_region.with_when': '{state} · {when}',
  'tenant_routing.breaker.trip_duration.permanent': 'Permanent (disable target)',
  'tenant_routing.breaker.permanent_title': 'Disable target',
  'tenant_routing.breaker.permanent_body': 'The target stays out of first pick and the retry chain until someone adds it back. This is not a timed breaker.',
  'tenant_routing.breaker.permanent_confirm': 'Disable target',
};
